import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarProviderLogo } from "../common/CalendarProviderLogo";
import { AddOutlined, AddReactionOutlined, AttachFileOutlined, AutoAwesomeOutlined, DeleteOutlineOutlined, EditOutlined, ExpandLessOutlined, ExpandMoreOutlined, FilterListOutlined, LinkOutlined, ReplyOutlined, SaveOutlined, SortOutlined, ThumbUpOutlined } from "@mui/icons-material";
import { workspaceOwners, workspacePriorities } from "../../../data/workspaceConfig";
import { countByStatuses, getFunctionalConfig, getInitialWorkspaceDraft, getNextStatus } from "../../../data/workspaceFunctionalConfig";
import { canReviewOverviewRecord, getOverviewActivity, getOverviewRecords, isOverviewApproval, isOverviewClosed, isOverviewTicket } from "./homeOverviewData";
import {
  addWorkspaceRecordComment,
  deleteWorkspaceRecordComment,
  endWorkspaceRecordViewSession,
  disconnectWorkspaceCalendar,
  getWorkspaceCalendarConnections,
  getWorkspaceExternalCalendarEvents,
  getWorkspaceHomeDashboard,
  getWorkspaceRecordTimeline,
  getWorkspacePeople,
  pingWorkspaceRecordPresence,
  startWorkspaceRecordViewSession,
  startWorkspaceCalendarConnection,
  toggleWorkspaceRecordCommentLike,
  toggleWorkspaceRecordCommentReaction,
  updateWorkspaceRecordComment,
  updateWorkspacePerson,
  getWorkspaceBoards,
  createWorkspaceBoard,
  updateWorkspaceBoard,
  deleteWorkspaceBoard,
} from "../../../services/workspaceApi";
import { ModuleTabStrip } from "../common/WorkspaceChrome";
import { WorkspaceErrorNotice } from "../common/WorkspaceErrorNotice";
import { WorkGanttView } from "../common/WorkGanttView";
import { WorkKanbanView } from "../common/WorkKanbanView";
import { WorkspaceInlineLoader } from "../common/WorkspaceInlineLoader";
import { WorkListView } from "../common/WorkListView";
import { WorkspaceEditor, WorkspaceRichText } from "../common/WorkspaceEditor";
import { WorkspaceUserAvatar, WorkspaceUserHoverCard } from "../common/WorkspaceUserHoverCard";
import { AdministrationView } from "../sections/administration/AdministrationView";
import { PeopleView } from "../sections/people/PeopleView";
import { SettingsView } from "../sections/settings/SettingsView";
import { DashboardFunctionalView } from "./DashboardFunctionalView";
import { ProjectsView } from "../sections/projects/ProjectsView";
import { TrainingDeliveryView } from "../sections/training/TrainingDeliveryView";

const closedStatuses = ["Done", "Completed", "Archived", "Won", "Lost", "Paid", "Refunded", "Approved", "Rejected", "Cancelled", "Published", "Exported", "Ready"];
const toTicketRouteSegment = (record) => `${record?.key || record?.id || ""}`.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const homeActivityPageSize = 25;
const defaultClientPortalRoles = ["Viewer", "Ticket Requester", "Ticket Editor", "Approver", "Client Admin"];
const defaultActionableTicketStatus = "To Do";
const primaryTicketWorkflow = ["Idea", "To Do", "In Progress", "In Review", "Done"];
const workTypeOptions = [
  { value: "Ticket", color: "task", description: "A general ticket. Its identifier starts at TICKET-1." },
  { value: "Epic", color: "epic", description: "A larger outcome made up of related work." },
  { value: "Feature", color: "feature", description: "A customer or product capability to deliver." },
  { value: "Story", color: "story", description: "A small user-focused requirement." },
  { value: "Task", color: "task", description: "A discrete piece of delivery work." },
  { value: "Bug", color: "bug", description: "A defect that needs investigation or repair." },
  { value: "Support Request", color: "support", description: "A client or internal support request." },
  { value: "Incident", color: "incident", description: "An operational issue requiring response." },
  { value: "Improvement", color: "improvement", description: "A measurable refinement to existing work." },
];
const projectPermissionOptions = [
  { key: "work.ticket.view", label: "View tickets" },
  { key: "work.ticket.create", label: "Create tickets" },
  { key: "work.ticket.edit", label: "Edit tickets" },
  { key: "work.ticket.transition", label: "Move status" },
  { key: "work.ticket.delete", label: "Delete tickets" },
  { key: "project.settings.manage", label: "Project settings" },
  { key: "project.members.manage", label: "Project members" },
  { key: "schedule.manage", label: "Schedule" },
  { key: "reports.view", label: "Reports" },
  { key: "client.portal.manage", label: "Client portal" },
];
const defaultProjectRoles = [
  { id: "project-owner", name: "Project Owner", accessLevel: "Owner", description: "Owns scope, people, permissions, and delivery rules.", permissions: projectPermissionOptions.map((option) => option.key) },
  { id: "project-manager", name: "Project Manager", accessLevel: "Manager", description: "Runs delivery, assigns work, and manages the board.", permissions: ["work.ticket.view", "work.ticket.create", "work.ticket.edit", "work.ticket.transition", "project.members.manage", "schedule.manage", "reports.view"] },
  { id: "contributor", name: "Contributor", accessLevel: "Member", description: "Works on assigned tickets and updates progress.", permissions: ["work.ticket.view", "work.ticket.create", "work.ticket.edit", "work.ticket.transition"] },
  { id: "viewer", name: "Viewer", accessLevel: "Viewer", description: "Can inspect project work without changing it.", permissions: ["work.ticket.view", "reports.view"] },
];
const defaultProjectPermissions = {
  visibility: "members",
  formulasVisibleTo: "owner",
  clientPortalDefault: false,
  requireApprovalForStatus: false,
};
const projectSettingsTabSections = {
  Settings: "appearance",
  Access: "access",
  Roles: "roles",
  Permissions: "permissions",
  Workflow: "workflow",
  "Client portal": "portal",
};
const defaultProjectWorkflowStates = primaryTicketWorkflow.map((name, index) => ({
  id: name.toLowerCase().replace(/\s+/g, "-"),
  name,
  category: ["backlog", "todo", "in_progress", "review", "done"][index] || "todo",
  order: index,
  isInitial: name === defaultActionableTicketStatus,
}));
const projectWorkflowTemplates = [
  {
    id: "basic",
    name: "Basic delivery",
    description: "Simple project flow for most teams.",
    states: defaultProjectWorkflowStates,
  },
  {
    id: "software",
    name: "Software delivery",
    description: "Backlog, development, QA, review, release.",
    states: [
      { id: "backlog", name: "Backlog", category: "backlog", order: 0, isInitial: false },
      { id: "to-do", name: "To Do", category: "todo", order: 1, isInitial: true },
      { id: "in-development", name: "In Development", category: "in_progress", order: 2, isInitial: false },
      { id: "qa", name: "QA", category: "review", order: 3, isInitial: false },
      { id: "client-review", name: "Client Review", category: "review", order: 4, isInitial: false },
      { id: "released", name: "Released", category: "done", order: 5, isInitial: false },
    ],
  },
  {
    id: "client-service",
    name: "Client service",
    description: "Request intake through client approval.",
    states: [
      { id: "requested", name: "Requested", category: "backlog", order: 0, isInitial: false },
      { id: "accepted", name: "Accepted", category: "todo", order: 1, isInitial: true },
      { id: "in-progress", name: "In Progress", category: "in_progress", order: 2, isInitial: false },
      { id: "waiting-on-client", name: "Waiting on Client", category: "review", order: 3, isInitial: false },
      { id: "approved", name: "Approved", category: "review", order: 4, isInitial: false },
      { id: "completed", name: "Completed", category: "done", order: 5, isInitial: false },
    ],
  },
  {
    id: "approval",
    name: "Approval flow",
    description: "Useful for reviews, changes, and formal signoff.",
    states: [
      { id: "draft", name: "Draft", category: "backlog", order: 0, isInitial: false },
      { id: "submitted", name: "Submitted", category: "todo", order: 1, isInitial: true },
      { id: "under-review", name: "Under Review", category: "review", order: 2, isInitial: false },
      { id: "changes-requested", name: "Changes Requested", category: "in_progress", order: 3, isInitial: false },
      { id: "approved", name: "Approved", category: "done", order: 4, isInitial: false },
    ],
  },
];
const getProjectWorkflowTemplate = (templateId = "basic") =>
  projectWorkflowTemplates.find((template) => template.id === templateId) || projectWorkflowTemplates[0];
const cloneWorkflowStates = (states = defaultProjectWorkflowStates) => states.map((state, index) => ({
  id: state.id || `${state.name || `state-${index + 1}`}`.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, ""),
  name: state.name || `State ${index + 1}`,
  category: state.category || "todo",
  order: Number.isFinite(Number(state.order)) ? Number(state.order) : index,
  isInitial: state.isInitial === true || state.is_initial === true,
}));
const createDefaultProjectWorkflow = () => ({
  mode: "template",
  templateId: "basic",
  states: cloneWorkflowStates(getProjectWorkflowTemplate("basic").states),
});
const normalizeProjectRoles = (roles = []) => {
  const source = Array.isArray(roles) && roles.length ? roles : defaultProjectRoles;
  return source.map((role, index) => ({
    id: `${role.id || role.name || `project-role-${index + 1}`}`.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || `project-role-${index + 1}`,
    name: role.name || `Project role ${index + 1}`,
    accessLevel: role.accessLevel || "Member",
    description: role.description || "",
    permissions: Array.isArray(role.permissions) ? role.permissions.filter((permission) => projectPermissionOptions.some((option) => option.key === permission)) : [],
    archived: role.archived === true,
  }));
};
const normalizeProjectPermissions = (permissions = {}) => ({
  ...defaultProjectPermissions,
  ...(permissions && typeof permissions === "object" && !Array.isArray(permissions) ? permissions : {}),
});
const normalizeProjectWorkflow = (workflow = {}) => {
  const mode = workflow?.mode === "custom" ? "custom" : "template";
  const templateId = workflow?.templateId || workflow?.template_id || "basic";
  const template = getProjectWorkflowTemplate(templateId);
  const states = mode === "custom" && Array.isArray(workflow?.states) && workflow.states.length ? workflow.states : template.states;
  return {
    mode,
    templateId: template.id,
    states: cloneWorkflowStates(states),
  };
};
const commentReactionGroups = [
  { label: "Popular", emojis: ["👍", "👎", "❤️", "😂", "🙂", "😮", "😢", "😡", "🎉", "👀", "🔥", "✅"] },
  { label: "Smileys", emojis: ["😀", "😃", "😄", "😁", "😊", "😍", "😘", "😎", "🤔", "🙄", "😬", "😴", "🤯", "🥳", "😇", "🤝"] },
  { label: "Hands", emojis: ["👏", "🙌", "🙏", "👌", "✌️", "🤞", "🤟", "🤘", "👋", "💪", "🫶", "🤌"] },
  { label: "Work", emojis: ["💡", "🚀", "⭐", "📌", "📎", "📝", "💬", "⏳", "⚠️", "❌", "🔒", "📣"] },
];
const commentReactionOptions = commentReactionGroups.flatMap((group) => group.emojis.map((icon) => ({ key: `u_${Array.from(icon).map((char) => char.codePointAt(0).toString(16)).join("_")}`, icon, label: icon })));
const getCommentReactionKey = (emoji) => commentReactionOptions.find((option) => option.icon === emoji)?.key || `u_${Array.from(`${emoji || "👍"}`).map((char) => char.codePointAt(0).toString(16)).join("_")}`;
const commentReactionLabels = {
  "👍": "Thumbs up",
  "👎": "Thumbs down",
  "❤️": "Heart",
  "😂": "Laugh",
  "🙂": "Smile",
  "😮": "Surprised",
  "😢": "Sad",
  "😡": "Angry",
  "🎉": "Celebrate",
  "👀": "Watching",
  "🔥": "Fire",
  "✅": "Done",
  "👏": "Clap",
  "🙌": "Raised hands",
  "🙏": "Thanks",
  "💡": "Idea",
  "🚀": "Launch",
  "⭐": "Star",
  "📌": "Pinned",
};
const getCommentTimeValue = (comment = {}) => {
  const rawTime = comment.createdAt || comment.at || comment.updatedAt || comment.editedAt || "";
  const parsedTime = new Date(rawTime).getTime();
  return Number.isFinite(parsedTime) ? parsedTime : 0;
};
const sortCommentsNewestFirst = (comments = []) => (Array.isArray(comments) ? comments : [])
  .map((comment, index) => ({ comment, index }))
  .sort((first, second) => {
    const timeDifference = getCommentTimeValue(second.comment) - getCommentTimeValue(first.comment);
    return timeDifference || first.index - second.index;
  })
  .map(({ comment }) => comment);
const buildRecordDetailDraft = (record) => ({
  title: record?.title || "",
  type: record?.type || "Task",
  description: record?.description || "",
  owner: record?.owner || "",
  priority: record?.priority || "Medium",
  status: record?.status || "To Do",
  due: record?.due || "",
  sprint: record?.sprint || "",
  epic: record?.epic || "",
  storyPoints: record?.storyPoints || 0,
  severity: record?.severity || "",
  projectRef: record?.projectRef || "",
  assignees: asList(record?.assignees).join(", "),
  labels: asList(record?.labels).join(", "),
  parentRecordId: record?.parentRecordId || "",
  linkedRecordIds: record?.linkedRecordIds || [],
  linkedItems: Array.isArray(record?.linkedItems) && record.linkedItems.length
    ? record.linkedItems
    : (record?.linkedRecordIds || []).map((recordId) => ({ recordId, relationship: record?.linkedWorkType || "relates to" })),
  linkedWorkType: record?.linkedWorkType || "relates to",
  linkedWorkUrl: record?.linkedWorkUrl || "",
});
const buildRecordDetailPayload = (detailDraft, getProjectIdForRef) => ({
  title: detailDraft?.title || "",
  description: detailDraft?.description || "",
  owner: detailDraft?.owner || "",
  priority: detailDraft?.priority || "Medium",
  status: detailDraft?.status || "",
  due: detailDraft?.due || "",
  type: detailDraft?.type || "Ticket",
  sprint: detailDraft?.sprint || "",
  epic: detailDraft?.epic || "",
  storyPoints: detailDraft?.storyPoints || 0,
  severity: detailDraft?.severity || "",
  projectRef: detailDraft?.projectRef || "",
  projectId: getProjectIdForRef(detailDraft?.projectRef || ""),
  assignees: detailDraft?.assignees || "",
  labels: detailDraft?.labels || "",
  parentRecordId: detailDraft?.parentRecordId || "",
  linkedRecordIds: detailDraft?.linkedRecordIds || [],
  linkedItems: detailDraft?.linkedItems || [],
  linkedWorkType: detailDraft?.linkedWorkType || "relates to",
  linkedWorkUrl: detailDraft?.linkedWorkUrl || "",
});
const serializeRecordDetailPayload = (payload) => JSON.stringify({
  ...payload,
  linkedRecordIds: Array.isArray(payload?.linkedRecordIds) ? [...payload.linkedRecordIds].sort() : [],
  linkedItems: Array.isArray(payload?.linkedItems) ? [...payload.linkedItems].sort((left, right) => `${left.recordId}:${left.relationship}`.localeCompare(`${right.recordId}:${right.relationship}`)) : [],
});
const getTicketCommentDraftStorageKey = ({ workspaceSlug = "workspace", userId = "", recordId = "" }) =>
  `lyriclious-workspace-ticket-comment-draft:${workspaceSlug}:${userId}:${recordId}`;
const linkedWorkRelationOptions = ["is blocked by", "blocks", "is cloned by", "clones", "is duplicated by", "duplicates", "relates to"];
const unplannedTicketStatuses = new Set(["Idea", "New", "Backlog"]);
const getDefaultTicketStatus = (workflow = [], fallback = defaultActionableTicketStatus) => {
  const initialStatus = workflow.find((state) => state.isInitial)?.name;
  if (initialStatus && !unplannedTicketStatuses.has(initialStatus)) return initialStatus;
  return workflow.find((state) => state.name === defaultActionableTicketStatus)?.name
    || initialStatus
    || workflow[0]?.name
    || fallback;
};
const generalizedBoardLanes = [
  { key: "backlog", label: "Backlog" },
  { key: "todo", label: "To Do" },
  { key: "in_progress", label: "In Progress" },
  { key: "review", label: "Review" },
  { key: "done", label: "Done" },
];
const boardDueBucketOptions = [
  { key: "overdue", label: "Overdue" },
  { key: "today", label: "Due today" },
  { key: "this_week", label: "Due this week" },
  { key: "upcoming", label: "Upcoming" },
  { key: "no_due", label: "No due date" },
];
const homeFocusOptions = [
  { key: "today", label: "Today’s focus", emptyLabel: "today" },
  { key: "week", label: "This week’s focus", emptyLabel: "this week" },
  { key: "month", label: "This month’s focus", emptyLabel: "this month" },
];
const workBoardLaneAccent = {
  backlog: "#94a3b8",
  todo: "#2563eb",
  in_progress: "#f97316",
  review: "#22c55e",
  done: "#16a34a",
};
const teamAnalyticsCapacityPerDay = 8;
const teamAnalyticsPalette = ["#2563eb", "#16a34a", "#f97316", "#7c3aed", "#0f766e", "#dc2626"];
const teamAnalyticsTypePoints = {
  bug: 2,
  defect: 2,
  feature: 5,
  improvement: 3,
  incident: 3,
  request: 2,
  story: 3,
  support: 2,
  task: 1,
  ticket: 1,
};
const teamAnalyticsPriorityWeights = {
  low: 0.85,
  medium: 1,
  normal: 1,
  high: 1.2,
  urgent: 1.45,
  critical: 1.55,
};
const getLocalGreeting = (date = new Date()) => {
  const hour = date.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};
const normalizeProjectLookupValue = (value) => `${value || ""}`.trim().toLowerCase();
const isProjectLikeRecord = (record) => record?.moduleName === "Projects" || /project/i.test(`${record?.type || ""}`);
const isPlaceholderProjectName = (value) => /^project\s*\d+$/i.test(`${value || ""}`.trim());
const humanizeProjectSlug = (value) => `${value || ""}`
  .trim()
  .replace(/[-_]+/g, " ")
  .replace(/\s+/g, " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());
const getReadableProjectName = (record = {}) =>
  record.displayTitle ||
  (isPlaceholderProjectName(record.title) && record.projectSlug
    ? humanizeProjectSlug(record.projectSlug)
    : record.title || humanizeProjectSlug(record.projectSlug) || record.projectRef || "");
const getProjectLookupValues = (record = {}) => [
  record.id,
  record._id,
  record.title,
  record.projectSlug,
  record.projectRef,
  record.key,
].map(normalizeProjectLookupValue).filter(Boolean);
const nonProjectReferenceLabels = new Set([
  "bug",
  "defect",
  "epic",
  "feature",
  "improvement",
  "incident",
  "request",
  "story",
  "support",
  "task",
  "ticket",
]);
const resolveProjectDisplayName = (records = [], projectRef = "", contextRecord = {}) => {
  const lookupValues = [
    projectRef,
    contextRecord.projectRef,
    contextRecord.projectId,
    contextRecord.projectSlug,
    contextRecord.project,
    contextRecord.projectName,
  ].map(normalizeProjectLookupValue).filter(Boolean);
  if (!lookupValues.length) return "";
  const project = records
    .filter(isProjectLikeRecord)
    .find((record) => getProjectLookupValues(record).some((value) => lookupValues.includes(value)));
  if (project) return getReadableProjectName(project);
  const explicitName = contextRecord.projectName || contextRecord.projectTitle || contextRecord.projectLabel;
  if (explicitName && !nonProjectReferenceLabels.has(normalizeProjectLookupValue(explicitName))) {
    return explicitName;
  }
  const fallback = projectRef || contextRecord.projectRef || "";
  const normalizedFallback = normalizeProjectLookupValue(fallback);
  const normalizedType = normalizeProjectLookupValue(contextRecord.type);
  if (!fallback || normalizedFallback === normalizedType || nonProjectReferenceLabels.has(normalizedFallback)) return "";
  return fallback;
};
const createEmptyWorkBoardDraft = () => ({
  id: "",
  name: "",
  projectId: "",
  projectName: "",
  view: "kanban",
  filters: {
    assigneeIds: [],
    projects: [],
    types: [],
    statuses: [],
    priorities: [],
    labels: [],
    severities: [],
    teams: [],
    sprints: [],
    epics: [],
    dueBuckets: [],
    flagged: [],
  },
  sharedWith: [],
});
const normalizeWorkBoardFilters = (filters = {}) => ({
  assigneeIds: Array.isArray(filters.assigneeIds) ? filters.assigneeIds.filter(Boolean) : Array.isArray(filters.assignee_ids) ? filters.assignee_ids.filter(Boolean) : [],
  projects: Array.isArray(filters.projects) ? filters.projects.filter(Boolean) : Array.isArray(filters.project_refs) ? filters.project_refs.filter(Boolean) : [],
  types: Array.isArray(filters.types) ? filters.types.filter(Boolean) : [],
  statuses: Array.isArray(filters.statuses) ? filters.statuses.filter(Boolean) : [],
  priorities: Array.isArray(filters.priorities) ? filters.priorities.filter(Boolean) : [],
  labels: Array.isArray(filters.labels) ? filters.labels.filter(Boolean) : [],
  severities: Array.isArray(filters.severities) ? filters.severities.filter(Boolean) : [],
  teams: Array.isArray(filters.teams) ? filters.teams.filter(Boolean) : [],
  sprints: Array.isArray(filters.sprints) ? filters.sprints.filter(Boolean) : [],
  epics: Array.isArray(filters.epics) ? filters.epics.filter(Boolean) : [],
  dueBuckets: Array.isArray(filters.dueBuckets) ? filters.dueBuckets.filter(Boolean) : Array.isArray(filters.due_buckets) ? filters.due_buckets.filter(Boolean) : [],
  flagged: Array.isArray(filters.flagged) ? filters.flagged.filter(Boolean) : [],
});

const getComparableBoardFilters = (filters = {}) => {
  const normalized = normalizeWorkBoardFilters(filters);
  return Object.fromEntries(
    Object.entries(normalized).map(([key, values]) => [
      key,
      Array.isArray(values) ? [...values].map((value) => `${value}`.trim()).sort((left, right) => left.localeCompare(right)) : values,
    ]),
  );
};

const areWorkBoardFiltersEqual = (left = {}, right = {}) => JSON.stringify(getComparableBoardFilters(left)) === JSON.stringify(getComparableBoardFilters(right));
const boardFilterAllOptionsByKey = ({ workspaceBoardPeople = [], workProjectOptions = [], workBoardTypeOptions = [], workspaceTeamOptions = [], workSprintOptions = [], workEpicOptions = [], severityOptions = [], workBoardFlagOptions = [] } = {}) => ({
  assigneeIds: workspaceBoardPeople.map((person) => person.userId),
  projects: workProjectOptions,
  types: workBoardTypeOptions,
  statuses: generalizedBoardLanes.map((lane) => lane.key),
  priorities: workspacePriorities,
  severities: severityOptions,
  teams: workspaceTeamOptions,
  sprints: workSprintOptions,
  epics: workEpicOptions,
  dueBuckets: boardDueBucketOptions.map((option) => option.key),
  flagged: workBoardFlagOptions,
});
const hydrateWorkBoardDraft = (board) => ({
  id: board?.id || "",
  name: board?.name || "",
  projectId: "",
  projectName: "",
  view: "kanban",
  filters: normalizeWorkBoardFilters(board?.filters),
  sharedWith: Array.isArray(board?.sharedWith) ? board.sharedWith.filter(Boolean) : [],
});
const formatHomeActivityTime = (value) => {
  if (!value || value === "Recent") return "Recent";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString([], {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};
const formatWorkspaceCommentTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return `${value}`;
  return date.toLocaleString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
const formatWorkspaceCommentExactTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return `${value}`;
  return date.toLocaleString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });
};
const renderCommentCreatedTime = (comment = {}) => {
  const rawTime = comment.createdAt || comment.at || "";
  const visibleTime = formatWorkspaceCommentTime(rawTime);
  const exactTime = formatWorkspaceCommentExactTime(rawTime);
  const parsedTime = rawTime ? new Date(rawTime) : null;
  const isoTime = parsedTime && !Number.isNaN(parsedTime.getTime()) ? parsedTime.toISOString() : undefined;
  if (!visibleTime) return null;
  return (
    <time aria-label={`Created on ${exactTime || visibleTime}`} dateTime={isoTime} title={exactTime || visibleTime}>
      {visibleTime}
    </time>
  );
};
const renderEditedCommentLabel = (comment = {}) => {
  const editTime = formatWorkspaceCommentTime(comment.editedAt || comment.updatedAt || "");
  if (!editTime) return null;
  return (
    <em aria-label={`Edited on ${editTime}`} title={`Edited on ${editTime}`}>
      Edited
    </em>
  );
};
const moduleRecordAliases = {
  Home: ["General", "My Work", "Tasks & Tickets"],
  Work: ["Tasks & Tickets", "My Work"],
  CRM: ["Clients / CRM"],
  Knowledge: ["Docs"],
  Analytics: ["Reports", "Dashboard"],
  Administration: ["Settings"],
};

const broadTabs = ["Overview", "Board", "Pipeline", "Reports"];
const modalSurfaceStyle = {
  background: "#ffffff",
  border: "1px solid #dce8e4",
  borderRadius: 12,
  boxShadow: "0 24px 70px rgba(15, 32, 27, 0.22)",
  maxHeight: "calc(100vh - 48px)",
  overflowY: "auto",
  width: "min(760px, 100%)",
};
const modalContentStyle = { padding: 24 };
const modalLayerStyle = {
  position: "fixed",
  zIndex: 2147483647,
  inset: 0,
  display: "grid",
  placeItems: "center",
  overflowY: "auto",
  background: "rgba(17, 28, 25, 0.52)",
  padding: 24,
};
const ModalPortal = ({ children }) => createPortal(
  <div className="workspace-shell workspace-portal-scope">
    {React.cloneElement(children, { style: { ...(children.props.style || {}), ...modalLayerStyle } })}
  </div>,
  document.body
);

function TicketMentionTextarea({ attachmentDisabled = false, autoFocus = false, mentionOptions = [], onAttachmentChange, onChange, placeholder, rows = 3, value }) {
  return (
    <WorkspaceEditor
      autoFocus={autoFocus}
      attachmentDisabled={attachmentDisabled}
      className="ticket-mention-field"
      compact
      mentionOptions={mentionOptions}
      onAttachmentChange={onAttachmentChange}
      minRows={rows}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      toolbar
      value={value}
    />
  );
}

function TicketReplyComposer({ attachmentPreview = null, attachmentDisabled = false, authorName, canSubmit = false, draft, mentionOptions, onAttachmentChange, onCancel, onChange, onSubmit, saving }) {
  return (
    <div className="ticket-inline-reply">
      <span className="ticket-reply-connector" aria-hidden="true" />
      <div className="ticket-reply-panel">
        <div className="ticket-reply-context"><span className="ticket-reply-avatar" aria-hidden="true">{authorName.slice(0, 1).toUpperCase()}</span><span>Replying to <strong>{authorName}</strong></span></div>
        <TicketMentionTextarea attachmentDisabled={attachmentDisabled} autoFocus mentionOptions={mentionOptions} onAttachmentChange={onAttachmentChange} onChange={onChange} placeholder={`Reply to ${authorName}… Use @ to mention a teammate`} rows={4} value={draft} />
        {attachmentPreview}
        <div className="ticket-reply-actions"><button className="primary-action" disabled={!canSubmit || saving} onClick={onSubmit} title="Post reply" type="button">Reply</button><button className="secondary-action" disabled={saving} onClick={onCancel} title="Cancel reply" type="button">Cancel</button></div>
      </div>
    </div>
  );
}

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error("The selected file could not be read."));
  reader.onload = () => resolve(reader.result);
  reader.readAsDataURL(file);
});
const commentAttachmentLimitBytes = 1024 * 1024;
const formatFileSize = (bytes = 0) => {
  const value = Number(bytes) || 0;
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  if (value >= 1024) return `${Math.ceil(value / 1024)} KB`;
  return `${value} B`;
};
const renameImageFileForMime = (fileName = "image", mimeType = "image/jpeg") => {
  const cleanName = `${fileName || "image"}`.replace(/\.[^.]+$/, "");
  const extension = mimeType === "image/webp" ? "webp" : mimeType === "image/png" ? "png" : "jpg";
  return `${cleanName}.${extension}`;
};
const loadImageForCompression = (file) => new Promise((resolve, reject) => {
  const image = new Image();
  const objectUrl = URL.createObjectURL(file);
  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error("The selected image could not be compressed."));
  };
  image.src = objectUrl;
});
const canvasToBlob = (canvas, mimeType, quality) => new Promise((resolve) => {
  canvas.toBlob((blob) => resolve(blob), mimeType, quality);
});
const compressImageFileToLimit = async (file, limitBytes = commentAttachmentLimitBytes) => {
  if (!file?.type?.startsWith("image/") || file.size <= limitBytes) return file;

  const image = await loadImageForCompression(file);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Image compression is not available in this browser.");

  const outputMimeType = file.type === "image/png" && file.size <= limitBytes * 1.4 ? "image/png" : "image/jpeg";
  let scale = Math.min(1, 1600 / Math.max(image.naturalWidth || image.width, image.naturalHeight || image.height));
  let quality = 0.82;
  let bestBlob = null;

  for (let attempt = 0; attempt < 10; attempt += 1) {
    canvas.width = Math.max(320, Math.round((image.naturalWidth || image.width) * scale));
    canvas.height = Math.max(320, Math.round((image.naturalHeight || image.height) * scale));
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const blob = await canvasToBlob(canvas, outputMimeType, outputMimeType === "image/png" ? undefined : quality);
    if (!blob) break;
    bestBlob = blob;
    if (blob.size <= limitBytes) {
      return new File([blob], renameImageFileForMime(file.name, outputMimeType), { type: outputMimeType, lastModified: Date.now() });
    }
    quality = Math.max(0.48, quality - 0.08);
    scale *= 0.82;
  }

  if (bestBlob?.size <= limitBytes) {
    return new File([bestBlob], renameImageFileForMime(file.name, outputMimeType), { type: outputMimeType, lastModified: Date.now() });
  }

  throw new Error("This image could not be compressed below 1 MB. Please choose a smaller image.");
};
const prepareCommentAttachment = async (file) => {
  if (!file) return null;
  if (!file.type.startsWith("image/") && file.size > commentAttachmentLimitBytes) {
    throw new Error("Non-image comment attachments must be 1 MB or smaller.");
  }
  const preparedFile = file.type.startsWith("image/")
    ? await compressImageFileToLimit(file, commentAttachmentLimitBytes)
    : file;
  if (preparedFile.size > commentAttachmentLimitBytes) {
    throw new Error("Comment attachments must be 1 MB or smaller.");
  }
  return {
    data: await readFileAsDataUrl(preparedFile),
    id: `pending-attachment-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    isImage: preparedFile.type.startsWith("image/"),
    mimeType: preparedFile.type || "application/octet-stream",
    name: preparedFile.name || file.name || "Attachment",
    originalSizeBytes: file.size || 0,
    previewUrl: preparedFile.type.startsWith("image/") ? URL.createObjectURL(preparedFile) : "",
    sizeBytes: preparedFile.size || 0,
  };
};

function WorkTypeMark({ type }) {
  const workType = workTypeOptions.find((item) => item.value === type);
  const iconByType = {
    Epic: <path d="m12 2 2.1 6.2L20 10l-5.9 1.8L12 18l-2.1-6.2L4 10l5.9-1.8L12 2Z" />,
    Feature: <><rect height="12" rx="1.75" width="17" x="3.5" y="6" /><circle cx="8" cy="12" r="1.3" /><path d="M12 12h4.5" /></>,
    Story: <><path d="M5 4.5h6.2A2.8 2.8 0 0 1 14 7.3V20H7a2 2 0 0 0-2 2V4.5Z" /><path d="M19 4.5h-6.2A2.8 2.8 0 0 0 10 7.3V20h7a2 2 0 0 1 2 2V4.5Z" /></>,
    Task: <><rect height="17" rx="2" width="17" x="3.5" y="3.5" /><path d="m7.4 12 3 3 6.3-6.2" /></>,
    Bug: <><path d="M9 8.5V6.8a3 3 0 0 1 6 0v1.7" /><rect height="10" rx="4" width="9" x="7.5" y="8.5" /><path d="M4.5 10.5h3M4.5 15.5h3m9 0h3m-3-5h3M12 8.5V4M9.5 19.5 8 21m6.5-1.5L16 21" /></>,
    "Support Request": <><circle cx="12" cy="12" r="8.5" /><path d="M8.4 9.4a3.7 3.7 0 0 1 7.1 1.4c0 2.7-3.5 2.5-3.5 4.8M12 18h.01" /></>,
    Incident: <><path d="M12 3 21 20H3L12 3Z" /><path d="M12 9v4.5M12 17h.01" /></>,
    Improvement: <><path d="M5 19 19 5M9 5h10v10" /><path d="M5 11v8h8" /></>,
  };
  const icon = iconByType[workType?.value];

  return <i aria-hidden="true" className={`work-type-mark is-${icon ? workType.color : "default"}`}><svg fill="none" viewBox="0 0 24 24">{icon || <><rect height="15" rx="2" width="15" x="4.5" y="4.5" /><path d="M8.5 12h7" /></>}</svg></i>;
}

const tabCreateActions = {
  Tickets: ["Create ticket", "Ticket"],
  Sprints: ["Create sprint", "Sprint"], Epics: ["Create epic", "Epic"], Backlog: ["Add backlog item", "Backlog Item"],
  Approvals: ["Create approval", "Approval"], Automation: ["Create automation", "Automation"], Templates: ["Create template", "Template"],
  Active: ["Create project", "Client Project"], Planning: ["Create project", "Client Project"], "On Hold": ["Create project", "Client Project"],
  Milestones: ["Create milestone", "Milestone"], Calendar: ["Create event", "1:1 Meeting"], Bookings: ["Create booking", "1:1 Meeting"],
  Availability: ["Add availability", "Availability"], Rooms: ["Add room", "Room"], Requests: ["Create request", "Request"],
};

const moduleTabCreateActions = {
  "Schedule:Calendar": ["Create event", "1:1 Meeting"], "Schedule:Bookings": ["Create booking link", "Booking Link"], "Schedule:Availability": ["Add working hours", "Working Hours"], "Schedule:Rooms": ["Add room", "Meeting Room"], "Schedule:Requests": ["Create scheduling request", "Schedule Request"], "Schedule:History": ["Create event", "1:1 Meeting"],
  "Projects:Overview": ["Create project", "Client Project"], "Projects:Active": ["Create project", "Client Project"], "Projects:Planning": ["Create project", "Client Project"], "Projects:On Hold": ["Create project", "Client Project"], "Projects:Completed": ["Create project", "Client Project"], "Projects:Milestones": ["Create milestone", "Milestone"], "Projects:Files": ["Add project file", "Project File"], "Projects:Reports": ["Create project report", "Project Report"],
  "CRM:Overview": ["Create lead", "Lead"], "CRM:Pipeline": ["Create deal", "Deal"], "CRM:Leads": ["Create lead", "Lead"], "CRM:Clients": ["Add client", "Client"], "CRM:Deals": ["Create deal", "Deal"], "CRM:Contacts": ["Add contact", "Contact"], "CRM:Follow Ups": ["Log follow up", "Follow Up"], "CRM:Contracts": ["Create contract", "Contract"], "CRM:Reports": ["Create CRM report", "CRM Report"],
  "Training:Overview": ["Create training request", "Training Request"], "Training:Companies": ["Add company", "Company"], "Training:Requests": ["Create training request", "Training Request"], "Training:Programs": ["Create program", "Program"], "Training:Batches": ["Create batch", "Batch"], "Training:Sessions": ["Schedule session", "Session"], "Training:Instructors": ["Add instructor", "Instructor"], "Training:Attendance": ["Record attendance", "Attendance"], "Training:Certificates": ["Issue certificate", "Certificate"], "Training:Reports": ["Create training report", "Training Report"],
  "Knowledge:Overview": ["Create document", "Document"], "Knowledge:Documents": ["Create document", "Document"], "Knowledge:SOPs": ["Create SOP", "SOP"], "Knowledge:Templates": ["Create template", "Template"], "Knowledge:Policies": ["Create policy", "Policy"], "Knowledge:Client Docs": ["Create client document", "Client Doc"], "Knowledge:Project Docs": ["Create project document", "Project Doc"], "Knowledge:Wiki": ["Create wiki page", "Wiki"], "Knowledge:Versions": ["Create version", "Version"],
  "Finance:Overview": ["Create invoice", "Invoice"], "Finance:Invoices": ["Create invoice", "Invoice"], "Finance:Payments": ["Record payment", "Payment"], "Finance:Expenses": ["Add expense", "Expense"], "Finance:Payouts": ["Create payout", "Payout"], "Finance:Commissions": ["Create commission", "Commission"], "Finance:Subscriptions": ["Create subscription", "Subscription"], "Finance:Reports": ["Create finance report", "Finance Report"],
  "Finance:Payroll": ["Create payroll record", "Payroll"],
  "Marketing:Overview": ["Create campaign", "Campaign"], "Marketing:Campaigns": ["Create campaign", "Campaign"], "Marketing:Email Marketing": ["Create email campaign", "Email Campaign"], "Marketing:WhatsApp Campaigns": ["Create WhatsApp campaign", "WhatsApp Campaign"], "Marketing:SMS Campaigns": ["Create SMS campaign", "SMS Campaign"], "Marketing:Audience & Contacts": ["Add audience", "Audience"], "Marketing:Templates": ["Create message template", "Message Template"], "Marketing:Consent & Preferences": ["Add consent rule", "Consent Rule"], "Marketing:Automations": ["Create marketing automation", "Marketing Automation"], "Marketing:Deliverability": ["Create deliverability review", "Deliverability Review"], "Marketing:Analytics": ["Create marketing report", "Analytics"], "Marketing:Blogs": ["Create blog", "Blog"], "Marketing:Landing Pages": ["Create landing page", "Landing Page"], "Marketing:SEO": ["Create SEO item", "SEO"], "Marketing:Coupons": ["Create coupon", "Coupon"], "Marketing:Forms": ["Create form", "Form"],
  "Analytics:Overview": ["Create dashboard", "Custom Dashboard"], "Analytics:Productivity": ["Create productivity dashboard", "Productivity"], "Analytics:Projects": ["Create project dashboard", "Projects"], "Analytics:People": ["Create people dashboard", "People"], "Analytics:CRM": ["Create CRM dashboard", "CRM"], "Analytics:Training": ["Create training dashboard", "Training"], "Analytics:Finance": ["Create finance dashboard", "Finance"], "Analytics:Marketing": ["Create marketing dashboard", "Marketing"], "Analytics:Exports": ["Schedule export", "Export"],
  "Administration:Overview": ["Create administration request", "Workspace"], "Administration:Workspace": ["Update workspace", "Workspace"], "Administration:Users": ["Create user request", "User"], "Administration:Roles": ["Create role", "Role"], "Administration:Permissions": ["Create permission", "Permission"], "Administration:Integrations": ["Add integration", "Integration"], "Administration:Security": ["Create security policy", "Security"], "Administration:Audit Logs": ["Create audit review", "Audit Log"], "Administration:Billing": ["Create billing request", "Billing"], "Administration:Branding": ["Update branding", "Branding"], "Administration:Automation": ["Create automation", "Automation"],
};

const tabFocusCopy = {
  Calendar: "Schedule commitments and keep conflicts visible.", Bookings: "Manage booking links, hosts, and confirmed appointments.", Availability: "Set working hours, availability blocks, and coverage.", Rooms: "Keep meeting rooms and virtual spaces ready for use.", Requests: "Review incoming requests and assign the next action.", History: "Maintain a searchable record of completed and changed events.",
  Overview: "Monitor the operational health, ownership, and next priorities for this area.", Active: "Track work currently moving through delivery.", Planning: "Prepare scope, owners, and dates before delivery starts.", "On Hold": "Review blocked work and decide the next delivery action.", Completed: "Keep delivered work, outcomes, and follow-up accessible.", Milestones: "Manage delivery checkpoints and stakeholder commitments.", Files: "Keep project files organized with their delivery context.",
  Pipeline: "Track deal stages, ownership, and next revenue actions.", Leads: "Capture, qualify, and progress prospective customers.", Clients: "Maintain active client context, ownership, and delivery links.", Deals: "Manage commercial opportunities through to a decision.", Contacts: "Maintain the people connected to each client relationship.", "Follow Ups": "Keep every sales and relationship follow-up on schedule.", Contracts: "Track contracts, review status, and commercial obligations.",
  Companies: "Manage company relationships connected to training delivery.", Programs: "Define the learning offerings available to each cohort.", Batches: "Organize learners, dates, and instructors into delivery batches.", Sessions: "Schedule and track each teaching or workshop session.", Instructors: "Maintain instructor capacity and delivery ownership.", Students: "Manage learner enrolment and program progress.", Attendance: "Record participation and identify attendance risk.", Certificates: "Track certificate readiness and issuance status.",
  Documents: "Keep controlled documents discoverable and current.", SOPs: "Maintain repeatable operating procedures and review ownership.", Templates: "Manage reusable formats for consistent team execution.", Policies: "Maintain approved policies and their review lifecycle.", "Client Docs": "Organize documents shared with client stakeholders.", "Project Docs": "Keep delivery documentation connected to project work.", Wiki: "Build a maintained internal knowledge base.", Versions: "Track document versions, review history, and archival.",
  Invoices: "Track invoice preparation, approval, and collection.", Payments: "Record incoming payments and outstanding balances.", Expenses: "Capture spend with the right financial context.", Payouts: "Manage payout approvals and settlement status.", Commissions: "Track commission calculations and approval status.", Subscriptions: "Manage recurring revenue and renewal commitments.",
  Blogs: "Plan, draft, and publish long-form content.", "Landing Pages": "Manage conversion pages and their campaign alignment.", SEO: "Track SEO work, metadata, and optimization priorities.", Campaigns: "Plan campaign delivery, ownership, and performance.", Coupons: "Manage promotional codes and commercial rules.", Forms: "Create forms that capture qualified information.",
  Productivity: "Monitor delivery throughput, workload, and team focus.", Exports: "Schedule, manage, and audit data exports.",
  Workspace: "Manage workspace-level identity and operating controls.", Users: "Manage user access requests and workspace membership.", Roles: "Maintain reusable role definitions and scope.", Permissions: "Control granular access across workspace capabilities.", Integrations: "Manage connected systems and integration ownership.", Security: "Manage workspace security policies and reviews.", "Audit Logs": "Review auditable workspace activity and changes.", Billing: "Manage billing controls, plan changes, and requests.", Branding: "Manage brand assets and workspace presentation.", Automation: "Define operational automations and their owners.",
  Reports: "Create reporting views that turn activity into decisions.", Analytics: "Review performance signals and reporting trends.",
};

const modulePlaybooks = {
  Schedule: {
    signals: ["Requests waiting", "Today confirmed", "Room conflicts", "Rescheduled"],
    actions: ["Create booking", "Block availability", "Add room", "Review requests"],
    checklist: ["Availability is updated", "Meeting owner is assigned", "Room or link is attached", "History is clean"],
  },
  Projects: {
    signals: ["Active delivery", "Client review", "On-hold risk", "Milestones due"],
    actions: ["Create project", "Add milestone", "Upload file", "Create report"],
    checklist: ["Owner assigned", "Deadline visible", "Files attached", "Client status updated"],
  },
  Work: {
    signals: ["Blocked tickets", "Review queue", "Sprint load", "Incidents open"],
    actions: ["Create ticket", "Create sprint", "Create epic", "Create template", "Move to approval"],
    checklist: ["Priority set", "Owner assigned", "Sprint or epic linked", "Status current", "History reviewed"],
  },
  CRM: {
    signals: ["Open leads", "Deals in negotiation", "Won clients", "Follow-ups due"],
    actions: ["Add lead", "Add client", "Log follow-up", "Create contract"],
    checklist: ["Lead source captured", "Next action set", "Owner assigned", "Contract status clear"],
  },
  Training: {
    signals: ["Requests open", "Batches active", "Sessions due", "Certificates pending"],
    actions: ["Add request", "Create batch", "Schedule session", "Issue certificate"],
    checklist: ["Instructor assigned", "Students mapped", "Attendance tracked", "Certificate status updated"],
  },
  Finance: {
    signals: ["Pending invoices", "Overdue payments", "Payout reviews", "Commission rules"],
    actions: ["Create invoice", "Record payment", "Add expense", "Approve payout"],
    checklist: ["Amount verified", "Client/project linked", "Payment status updated", "Payout owner assigned"],
  },
  Marketing: {
    signals: ["Draft content", "Campaigns active", "SEO reviews", "Forms converting"],
    actions: ["Create blog", "Add campaign", "Create coupon", "Review analytics"],
    checklist: ["Audience defined", "CTA attached", "SEO metadata ready", "Analytics reviewed"],
  },
  Analytics: {
    signals: ["Dashboards ready", "Exports scheduled", "People insights", "Finance trends"],
    actions: ["Create dashboard", "Schedule export", "Review trend", "Share report"],
    checklist: ["Data source selected", "Owner assigned", "Export format ready", "Review cadence set"],
  },
  Knowledge: {
    signals: ["Draft docs", "Review queue", "Published ready", "Version risk"],
    actions: ["Create document", "Add SOP", "Create template", "Review policy"],
    checklist: ["Owner assigned", "Visibility set", "Linked record selected", "Review date added"],
  },
};

const scheduleTabDefinitions = {
  Calendar: {
    eyebrow: "Schedule control",
    title: "Calendar",
    description: "Confirmed meetings, sessions, interviews, reviews, and dated commitments.",
    createLabel: "Create event",
    defaultType: "1:1 Meeting",
    defaultStatus: "Scheduled",
  },
  Bookings: {
    eyebrow: "Booking operations",
    title: "Bookings",
    description: "Public/internal booking links, booked slots, confirmation state, and host coverage.",
    createLabel: "Create booking link",
    defaultType: "Booking Link",
    defaultStatus: "Active",
  },
  Availability: {
    eyebrow: "Coverage rules",
    title: "Availability",
    description: "Working hours, unavailable slots, exceptions, buffers, and team availability setup.",
    createLabel: "Add availability",
    defaultType: "Working Hours",
    defaultStatus: "Active",
  },
  Rooms: {
    eyebrow: "Resources",
    title: "Rooms",
    description: "Physical rooms, virtual rooms, equipment resources, capacity, and booking conflicts.",
    createLabel: "Add room",
    defaultType: "Meeting Room",
    defaultStatus: "Active",
  },
  Requests: {
    eyebrow: "Scheduling intake",
    title: "Requests",
    description: "New meeting, reschedule, cancel, room, client-call, and training-session requests.",
    createLabel: "Create request",
    defaultType: "Schedule Request",
    defaultStatus: "Requested",
  },
};
const scheduleTypeOptionsByTab = {
  Calendar: ["1:1 Meeting", "Group Meeting", "Client Review", "Daily Standup", "Training Session", "Interview", "Workshop", "Demo Call"],
  Bookings: ["Booking Link", "Booked Slot", "Round Robin", "Collective Meeting", "Paid Consultation", "Interview Slot"],
  Availability: ["Working Hours", "Availability Block", "Unavailable Slot", "Holiday Exception", "Buffer Rule"],
  Rooms: ["Meeting Room", "Training Room", "Interview Room", "Virtual Room", "Equipment Resource"],
  Requests: ["Schedule Request", "Reschedule Request", "Cancel Request", "Room Booking Request", "Training Session Request", "Client Call Request"],
};
const scheduleDurationOptions = [15, 30, 45, 60, 90, 120, 150];
const scheduleVisibilityOptions = ["Workspace", "Team only", "Private host", "Client visible"];
const scheduleStatusOptionsByTab = {
  Calendar: ["Scheduled", "Confirmed", "Rescheduled", "Completed", "Cancelled"],
  Bookings: ["Active", "Scheduled", "Confirmed", "Disabled", "Cancelled", "Completed"],
  Availability: ["Active", "Scheduled", "Blocked", "Cancelled"],
  Rooms: ["Active", "Scheduled", "Blocked", "In Maintenance", "Disabled"],
  Requests: ["Requested", "Pending", "Approved", "Rejected", "Scheduled", "Cancelled"],
};
const scheduleRecordPattern = /meeting|event|session|interview|call|standup|review|workshop|demo/i;
const bookingRecordPattern = /booking|booked|round robin|collective|consultation|slot/i;
const availabilityRecordPattern = /availability|working hours|unavailable|holiday|exception|buffer|block/i;
const roomRecordPattern = /room|resource|equipment/i;
const requestRecordPattern = /request|reschedule|cancel/i;
const getScheduleContext = (record = {}) => record.pageContext && typeof record.pageContext === "object" ? record.pageContext : {};
const getScheduleTimeMinutes = (value = "") => {
  const match = `${value || ""}`.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
};
const getScheduleEndMinutes = (record = {}) => {
  const context = getScheduleContext(record);
  const explicitEnd = getScheduleTimeMinutes(context.endTime);
  if (explicitEnd !== null) return explicitEnd;
  const start = getScheduleTimeMinutes(context.startTime);
  if (start === null) return null;
  const duration = Number(context.durationMinutes || context.duration || 30);
  return start + (Number.isFinite(duration) ? Math.max(15, duration) : 30);
};
const scheduleRecordsOverlap = (left = {}, right = {}) => {
  if (!left.due || left.due !== right.due) return false;
  const leftStart = getScheduleTimeMinutes(getScheduleContext(left).startTime);
  const rightStart = getScheduleTimeMinutes(getScheduleContext(right).startTime);
  const leftEnd = getScheduleEndMinutes(left);
  const rightEnd = getScheduleEndMinutes(right);
  if (leftStart === null || rightStart === null || leftEnd === null || rightEnd === null) return false;
  return leftStart < rightEnd && rightStart < leftEnd;
};
const formatScheduleDate = (value) => {
  if (!value) return "No date";
  const date = /^\d{4}-\d{2}-\d{2}$/.test(`${value}`) ? new Date(`${value}T00:00:00`) : new Date(value);
  if (Number.isNaN(date.getTime())) return `${value}`;
  return date.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" });
};
const formatScheduleTimeRange = (record = {}) => {
  const context = getScheduleContext(record);
  const start = context.startTime || "";
  const end = context.endTime || "";
  if (start && end) return `${start}–${end}`;
  if (start && context.durationMinutes) return `${start} · ${context.durationMinutes} min`;
  if (start) return start;
  return "Time not set";
};
const isScheduleEventRecord = (record = {}) => {
  const type = `${record.type || ""}`;
  return scheduleRecordPattern.test(type) && !bookingRecordPattern.test(type) && !availabilityRecordPattern.test(type) && !roomRecordPattern.test(type) && !requestRecordPattern.test(type);
};
const isBookingRecord = (record = {}) => bookingRecordPattern.test(`${record.type || ""}`);
const isBookingLinkRecord = (record = {}) => {
  const type = `${record.type || ""}`;
  const context = getScheduleContext(record);
  return /booking link|round robin|collective|paid consultation/i.test(type)
    || (/booking/i.test(type) && !/booked|slot/i.test(type))
    || (Boolean(context.bookingUrl) && !/booked|slot/i.test(type));
};
const isBookedSlotRecord = (record = {}) => isBookingRecord(record) && !isBookingLinkRecord(record);
const isAvailabilityRecord = (record = {}) => availabilityRecordPattern.test(`${record.type || ""}`);
const isRoomRecord = (record = {}) => roomRecordPattern.test(`${record.type || ""}`);
const isScheduleRequestRecord = (record = {}) => requestRecordPattern.test(`${record.type || ""}`) || ["Requested", "Pending"].includes(record.status);

const getRecordCountForSignal = (records, signal) => {
  const text = signal.toLowerCase();
  if (text.includes("blocked") || text.includes("risk")) return records.filter((record) => record.status === "Blocked" || record.status === "On Hold").length;
  if (text.includes("sprint")) return new Set(records.map((record) => record.sprint).filter(Boolean)).size;
  if (text.includes("review") || text.includes("approval")) return records.filter((record) => /review|pending|approval/i.test(`${record.status} ${record.type}`)).length;
  if (text.includes("due") || text.includes("today")) return records.filter((record) => record.due === new Date().toISOString().slice(0, 10)).length;
  if (text.includes("active") || text.includes("open")) return records.filter((record) => !closedStatuses.includes(record.status)).length;
  if (text.includes("won") || text.includes("confirmed") || text.includes("ready")) return records.filter((record) => /won|confirmed|ready|published/i.test(record.status)).length;
  return records.length;
};

const asList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return `${value || ""}`.split(",").map((item) => item.trim()).filter(Boolean);
};
const normalizeLabelToken = (value = "") => `${value || ""}`.trim().replace(/\s+/g, " ").slice(0, 80);
const uniqueLabelList = (values = []) => {
  const seen = new Set();
  return values.reduce((labels, value) => {
    const label = normalizeLabelToken(value);
    const key = label.toLowerCase();
    if (!label || seen.has(key)) return labels;
    seen.add(key);
    labels.push(label);
    return labels;
  }, []);
};
const parseLabelInput = (value) => uniqueLabelList(asList(value));

function TicketLabelPicker({ commitOnBlur = true, label = "Labels", onChange, onCommit, options = [], outputFormat = "string", placeholder = "Add label", value }) {
  const [input, setInput] = useState("");
  const selectedLabels = parseLabelInput(value);
  const selectedKeys = new Set(selectedLabels.map((item) => item.toLowerCase()));
  const query = normalizeLabelToken(input);
  const normalizedOptions = uniqueLabelList(options);
  const visibleOptions = normalizedOptions
    .filter((option) => !selectedKeys.has(option.toLowerCase()))
    .filter((option) => !query || option.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 7);
  const hasExactOption = normalizedOptions.some((option) => option.toLowerCase() === query.toLowerCase());
  const canCreateQuery = Boolean(query) && !selectedKeys.has(query.toLowerCase());
  const emitLabels = (labels, { commit = false } = {}) => {
    const nextLabels = uniqueLabelList(labels);
    const nextValue = outputFormat === "array" ? nextLabels : nextLabels.join(", ");
    onChange?.(nextValue);
    if (commit) onCommit?.(nextValue, nextLabels);
    return nextValue;
  };
  const addLabels = (labels, options = {}) => {
    const nextLabels = uniqueLabelList([...selectedLabels, ...labels]);
    return emitLabels(nextLabels, options);
  };
  const commitInput = (options = {}) => {
    if (!query) return null;
    const nextValue = addLabels([query], options);
    setInput("");
    return nextValue;
  };
  const removeLabel = (labelToRemove) => {
    emitLabels(selectedLabels.filter((item) => item.toLowerCase() !== `${labelToRemove}`.toLowerCase()), { commit: true });
  };
  const handleInputChange = (event) => {
    const rawValue = event.target.value;
    if (!/[,\n]/.test(rawValue)) {
      setInput(rawValue);
      return;
    }
    const parts = rawValue.split(/[,\n]/);
    const nextInput = parts.pop() || "";
    addLabels(parts);
    setInput(nextInput);
  };

  return (
    <div className="ticket-label-picker">
      <span className="ticket-label-picker-title">{label}</span>
      <div className="ticket-label-picker-box">
        <div className="ticket-label-chip-row">
          {selectedLabels.map((selectedLabel) => (
            <span className="ticket-label-chip" key={selectedLabel}>
              {selectedLabel}
              <button aria-label={`Remove ${selectedLabel} label`} onClick={() => removeLabel(selectedLabel)} type="button">x</button>
            </span>
          ))}
          {canCreateQuery ? <span className="ticket-label-chip is-pending">{query}</span> : null}
        </div>
        <input
          aria-label={label}
          onBlur={() => {
            if (commitOnBlur) commitInput();
          }}
          onChange={handleInputChange}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === "Tab") {
              if (!query) return;
              event.preventDefault();
              commitInput({ commit: true });
            }
            if (event.key === "Backspace" && !input && selectedLabels.length) {
              removeLabel(selectedLabels[selectedLabels.length - 1]);
            }
          }}
          placeholder={placeholder}
          value={input}
        />
        {input ? (
          <div className="ticket-label-suggestions" role="listbox">
            {canCreateQuery && !hasExactOption ? (
              <button onMouseDown={(event) => { event.preventDefault(); addLabels([query], { commit: true }); setInput(""); }} type="button">
                <strong>Create label</strong>
                <span>{query}</span>
              </button>
            ) : null}
            {visibleOptions.map((option) => (
              <button aria-selected={false} key={option} onMouseDown={(event) => { event.preventDefault(); addLabels([option], { commit: true }); setInput(""); }} role="option" type="button">
                <strong>{option}</strong>
                <span>Use existing label</span>
              </button>
            ))}
            {!visibleOptions.length && !canCreateQuery ? <p>No labels match.</p> : null}
          </div>
        ) : null}
      </div>
      <small>Type a label, then press Enter or comma.</small>
    </div>
  );
}
const isWorkItemRecord = (record) =>
  Boolean(record) && (
    ["Tickets", "Backlog"].includes(record.tab)
    || Boolean(record.ticketPrefix)
    || (/task|ticket|bug|request|support|incident|feature|story|improvement/i.test(`${record.type || ""}`) && !/epic|sprint|template|automation|approval/i.test(`${record.type || ""}`))
  );
const parsePortalMembersInput = (value = "") =>
  `${value || ""}`
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [name = "", email = "", role = "Viewer", status = "Invited"] = line.split("|").map((item) => item.trim());
      return { name, email, role: role || "Viewer", status: status || "Invited" };
    })
    .filter((member) => member.name || member.email);

const sumStoryPoints = (records) =>
  records.reduce((total, record) => total + Number(record.storyPoints || 0), 0);

const getAnalyticsPoints = (record = {}) => {
  const explicitPoints = Number(record.storyPoints || 0);
  if (explicitPoints > 0) return explicitPoints;
  const typeKey = `${record.type || "task"}`.trim().toLowerCase();
  const matchedType = Object.keys(teamAnalyticsTypePoints).find((key) => typeKey.includes(key));
  return matchedType ? teamAnalyticsTypePoints[matchedType] : 1;
};

const normalizeAnalyticsIdentity = (value = "") => `${value || ""}`.trim().toLowerCase();

const getAnalyticsRecordIdentities = (record = {}) => [
  record.owner,
  record.ownerUserId,
  record.creator,
  record.creatorId,
  ...asList(record.assignees),
].map(normalizeAnalyticsIdentity).filter(Boolean);

const getAnalyticsMemberIdentities = (member = {}) => [
  member.id,
  member._id,
  member.userId,
  member.name,
  member.email,
  member.workEmail,
  member.primaryEmail,
].map(normalizeAnalyticsIdentity).filter(Boolean);

const analyticsRecordBelongsToMember = (record = {}, member = {}) => {
  const recordIdentities = new Set(getAnalyticsRecordIdentities(record));
  return getAnalyticsMemberIdentities(member).some((identity) => recordIdentities.has(identity));
};

const getAnalyticsDateKey = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(`${value}`)) return `${value}`.slice(0, 10);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : calendarDateKey(parsed);
};

const getAnalyticsWorkingDaysUntil = (todayKey, dueKey) => {
  if (!dueKey || dueKey < todayKey) return 1;
  const cursor = calendarDateFromKey(todayKey);
  const end = calendarDateFromKey(dueKey);
  let days = 0;
  while (cursor <= end) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) days += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return Math.max(1, days);
};

const getAnalyticsPriorityWeight = (record = {}) =>
  teamAnalyticsPriorityWeights[`${record.priority || "medium"}`.trim().toLowerCase()] || 1;

const getAnalyticsTimeliness = (record = {}) => {
  const dueKey = getAnalyticsDateKey(record.due);
  const closedKey = getAnalyticsDateKey(record.updatedAt || record.createdAt);
  if (!dueKey || !closedKey) return { multiplier: 1, days: 0, label: "No due baseline" };
  const dueTime = calendarDateFromKey(dueKey).getTime();
  const closedTime = calendarDateFromKey(closedKey).getTime();
  const dayDelta = Math.round((dueTime - closedTime) / 86400000);
  if (dayDelta > 0) return { multiplier: Math.min(1.2, 1 + dayDelta * 0.02), days: dayDelta, label: `${dayDelta}d early` };
  if (dayDelta < 0) return { multiplier: Math.max(0.65, 1 + dayDelta * 0.05), days: dayDelta, label: `${Math.abs(dayDelta)}d late` };
  return { multiplier: 1, days: 0, label: "On time" };
};

const formatAnalyticsNumber = (value, digits = 1) => {
  const number = Number(value || 0);
  return Number.isInteger(number) ? `${number}` : number.toFixed(digits);
};

const getAnalyticsStatusTone = (occupancyPercent) => {
  if (occupancyPercent >= 115) return "over";
  if (occupancyPercent >= 90) return "full";
  if (occupancyPercent >= 60) return "steady";
  return "light";
};

const groupWorkRecords = (records, field, fallback = "Unassigned") =>
  records.reduce((groups, record) => {
    const key = record[field] || fallback;
    if (!groups[key]) groups[key] = [];
    groups[key].push(record);
    return groups;
  }, {});

function MetricTile({ label, value, note }) {
  return (
    <article>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{note}</small>
    </article>
  );
}

const calendarDateKey = (date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, "0"),
  String(date.getDate()).padStart(2, "0"),
].join("-");

const calendarDateFromKey = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return new Date();
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const addCalendarDays = (value, days) => {
  const date = calendarDateFromKey(value);
  date.setDate(date.getDate() + days);
  return calendarDateKey(date);
};

const calendarEventCategory = (record, isDeliveryDate = false) => {
  const signal = `${record.type || ""} ${record.status || ""} ${record.title || ""}`.toLowerCase();
  if (/leave|pto|absence/.test(signal)) return "Leave";
  if (/approval|review|sign.?off/.test(signal)) return "Approval";
  if (isDeliveryDate || /task|ticket|bug|story|feature|incident|request/.test(signal)) return "Delivery";
  return "Meeting";
};

function HomeCalendarPlanner({ calendarRecords, taskRecords, userName }) {
  const todayKey = calendarDateKey(new Date());
  const [monthCursor, setMonthCursor] = useState(() => calendarDateFromKey(todayKey));
  const [selectedDate, setSelectedDate] = useState(todayKey);
  const [view, setView] = useState("month");
  const [category, setCategory] = useState("All");
  const [showCompleted, setShowCompleted] = useState(false);
  const [calendarConnections, setCalendarConnections] = useState([]);
  const [calendarProviders, setCalendarProviders] = useState({});
  const [externalEvents, setExternalEvents] = useState([]);
  const [calendarIntegrationError, setCalendarIntegrationError] = useState("");
  const [calendarIntegrationBusy, setCalendarIntegrationBusy] = useState("");
  const [calendarEventsLoading, setCalendarEventsLoading] = useState(false);
  const currentMonth = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1);
  const currentMonthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth()).padStart(2, "0")}`;
  useEffect(() => {
    getWorkspaceCalendarConnections().then((response) => {
      setCalendarConnections(response.connections || []);
      setCalendarProviders(response.configured || {});
    }).catch(() => setCalendarIntegrationError("Calendar connections could not be loaded."));
  }, []);
  useEffect(() => {
    const [year, month] = currentMonthKey.split("-").map(Number);
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 8);
    let mounted = true;
    setCalendarEventsLoading(true);
    getWorkspaceExternalCalendarEvents({ start: start.toISOString(), end: end.toISOString() })
      .then((response) => {
        if (!mounted) return;
        setExternalEvents(response.events || []);
        const errorMessage = (response.connectionErrors || [])
          .map((item) => `${item.provider === "google" ? "Google Calendar" : "Outlook"}: ${item.message}`)
          .join(" ");
        setCalendarIntegrationError(errorMessage);
      })
      .catch(() => {
        if (!mounted) return;
        setExternalEvents([]);
        setCalendarIntegrationError("Calendar events could not be loaded. Please reconnect the calendar and try again.");
      })
      .finally(() => {
        if (mounted) setCalendarEventsLoading(false);
      });
    return () => { mounted = false; };
  }, [currentMonthKey]);
  const calendarIds = new Set(calendarRecords.map((record) => record.id).filter(Boolean));
  const scheduleEvents = calendarRecords
    .filter((record) => record.due)
    .map((record) => ({
      ...record,
      category: calendarEventCategory(record),
      date: record.due,
      source: "schedule",
    }));
  const deliveryEvents = taskRecords
    .filter((record) => record.due && !calendarIds.has(record.id))
    .map((record) => ({
      ...record,
      category: calendarEventCategory(record, true),
      date: record.due,
      source: "delivery",
    }));
  const externalBusyEvents = externalEvents.filter((event) => event.start).map((event) => ({
    ...event,
    date: calendarDateKey(new Date(event.start)),
    category: "Meeting",
    source: "external",
    owner: userName,
    type: `${event.provider === "microsoft" ? "Outlook" : "Google"} Calendar`,
  }));
  const allEvents = [...scheduleEvents, ...deliveryEvents, ...externalBusyEvents]
    .filter((record) => showCompleted || !closedStatuses.includes(record.status))
    .sort((a, b) => `${a.date}-${a.title}`.localeCompare(`${b.date}-${b.title}`));
  const categories = ["All", "Meeting", "Delivery", "Approval", "Leave"];
  const visibleEvents = category === "All" ? allEvents : allEvents.filter((event) => event.category === category);
  const eventsForDate = (key) => visibleEvents.filter((event) => event.date === key);
  const firstCellDate = new Date(currentMonth);
  firstCellDate.setDate(1 - currentMonth.getDay());
  const monthDays = Array.from({ length: 42 }, (_, index) => {
    const date = new Date(firstCellDate);
    date.setDate(firstCellDate.getDate() + index);
    return date;
  });
  const selectedEvents = eventsForDate(selectedDate);
  const weekStart = calendarDateFromKey(selectedDate);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const weekDays = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + index);
    return date;
  });
  const upcomingEvents = visibleEvents
    .filter((event) => event.date >= todayKey)
    .slice(0, 10);
  const openWork = taskRecords.filter((record) => !closedStatuses.includes(record.status));
  const owners = Object.entries(openWork.reduce((total, record) => {
    const owner = record.owner || "Unassigned";
    total[owner] = (total[owner] || 0) + 1;
    return total;
  }, {}))
    .sort(([, left], [, right]) => right - left)
    .slice(0, 4);
  const maximumOwnerLoad = Math.max(...owners.map(([, count]) => count), 1);
  const collisionCount = Object.values(allEvents.reduce((total, event) => {
    const owner = event.owner || userName || "Unassigned";
    const key = `${event.date}-${owner}`;
    total[key] = (total[key] || 0) + 1;
    return total;
  }, {})).filter((count) => count > 1).length;
  const overdueCount = taskRecords.filter((record) => record.due && record.due < todayKey && !closedStatuses.includes(record.status)).length;
  const selectDate = (date) => {
    setSelectedDate(calendarDateKey(date));
    if (date.getMonth() !== currentMonth.getMonth() || date.getFullYear() !== currentMonth.getFullYear()) {
      setMonthCursor(new Date(date.getFullYear(), date.getMonth(), 1));
    }
  };
  const shiftMonth = (amount) => {
    const next = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + amount, 1);
    setMonthCursor(next);
    setSelectedDate(calendarDateKey(next));
  };
  const eventLabel = (event) => event.source === "external" ? `${event.type} · personal calendar` : event.source === "delivery" ? `${event.priority || "Normal"} priority` : event.type || "Scheduled";
  const renderEventMarker = (event) => event.source === "external" ? (
    <span
      aria-label={`${event.provider === "microsoft" ? "Outlook" : "Google"} Calendar event`}
      className="calendar-event-provider-mark"
      role="img"
      title={`${event.provider === "microsoft" ? "Outlook" : "Google"} Calendar event`}
    >
      <CalendarProviderLogo provider={event.provider} size={14} />
    </span>
  ) : <i className={`event-${event.category.toLowerCase()}`} aria-hidden="true" />;
  const connectCalendar = async (provider) => {
    setCalendarIntegrationBusy(provider);
    setCalendarIntegrationError("");
    try {
      const { authorizationUrl } = await startWorkspaceCalendarConnection(provider);
      window.location.assign(authorizationUrl);
    } catch (error) { setCalendarIntegrationError(error?.response?.data?.message || `Could not connect ${provider}.`); setCalendarIntegrationBusy(""); }
  };
  const disconnectCalendar = async (provider) => {
    setCalendarIntegrationBusy(provider);
    try {
      await disconnectWorkspaceCalendar(provider);
      setCalendarConnections((current) => current.filter((connection) => connection.provider !== provider));
      setExternalEvents((current) => current.filter((event) => event.provider !== provider));
    } catch (error) { setCalendarIntegrationError(error?.response?.data?.message || `Could not disconnect ${provider}.`); }
    finally { setCalendarIntegrationBusy(""); }
  };

  const renderEventChip = (event, compact = false) => (
    <button
      className={`home-calendar-event event-${event.category.toLowerCase()} ${compact ? "is-compact" : ""}`}
      key={event.id || `${event.date}-${event.title}`}
      onClick={() => setSelectedDate(event.date)}
      title={`${event.title} · ${eventLabel(event)}`}
      type="button"
    >
      {renderEventMarker(event)}
      <span>{event.title}</span>
    </button>
  );

  return (
    <section className="functional-panel home-calendar-planner">
      <div className="home-calendar-toolbar">
        <div>
          <span>Planning intelligence</span>
          <h3>Delivery calendar</h3>
          <p>Schedules, delivery dates, approvals, leave, and team capacity in one operational view.</p>
        </div>
        <div className="home-calendar-toolbar-actions">
          <div className="home-calendar-integrations" aria-label="Personal calendar connections">
            {[{ id: "google", label: "Google" }, { id: "microsoft", label: "Outlook" }].map((provider) => {
              const connection = calendarConnections.find((item) => item.provider === provider.id && item.status === "connected");
              if (connection) return <button key={provider.id} className="is-connected calendar-provider-action" disabled={calendarIntegrationBusy === provider.id} onClick={() => disconnectCalendar(provider.id)} title={`Disconnect ${provider.label}`} type="button"><CalendarProviderLogo provider={provider.id} />{calendarIntegrationBusy === provider.id ? "…" : `${provider.label} connected`}</button>;
              return calendarProviders[provider.id] ? <button key={provider.id} className="calendar-provider-action" disabled={Boolean(calendarIntegrationBusy)} onClick={() => connectCalendar(provider.id)} type="button"><CalendarProviderLogo provider={provider.id} />{calendarIntegrationBusy === provider.id ? "Connecting…" : `Connect ${provider.label}`}</button> : null;
            })}
          </div>
          <button className="home-calendar-today" onClick={() => { setMonthCursor(calendarDateFromKey(todayKey)); setSelectedDate(todayKey); }} type="button">Today</button>
          <div className="home-calendar-navigation" aria-label="Calendar month navigation">
            <button aria-label="Previous month" onClick={() => shiftMonth(-1)} type="button">‹</button>
            <strong>{currentMonth.toLocaleDateString([], { month: "long", year: "numeric" })}</strong>
            <button aria-label="Next month" onClick={() => shiftMonth(1)} type="button">›</button>
          </div>
        </div>
      </div>
      {calendarIntegrationError ? <p className="home-calendar-integration-error" role="status">{calendarIntegrationError}</p> : null}
      {!calendarIntegrationError && calendarConnections.some((connection) => connection.status === "connected") ? <p className="home-calendar-integration-status" role="status">{calendarEventsLoading ? "Loading your read-only personal calendar events…" : externalBusyEvents.length ? `${externalBusyEvents.length} read-only personal calendar ${externalBusyEvents.length === 1 ? "event is" : "events are"} included for this period.` : "No personal calendar events are scheduled in this calendar period."}</p> : null}

      <div className="home-calendar-controls">
        <div className="home-calendar-segmented" aria-label="Calendar view">
          {["month", "week", "agenda"].map((option) => <button aria-pressed={view === option} className={view === option ? "is-active" : ""} key={option} onClick={() => setView(option)} type="button">{option}</button>)}
        </div>
        <div className="home-calendar-filters" aria-label="Calendar event filters">
          {categories.map((option) => <button aria-pressed={category === option} className={category === option ? "is-active" : ""} key={option} onClick={() => setCategory(option)} type="button">{option === "All" ? "All items" : option}</button>)}
          <label><input checked={showCompleted} onChange={(event) => setShowCompleted(event.target.checked)} type="checkbox" /> Closed</label>
        </div>
      </div>

      <div className="home-calendar-layout">
        <div className="home-calendar-main">
          {view === "month" ? (
            <div className="home-month-calendar">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => <b key={day}>{day}</b>)}
              {monthDays.map((date) => {
                const key = calendarDateKey(date);
                const events = eventsForDate(key);
                const outside = date.getMonth() !== currentMonth.getMonth();
                return <article className={`${outside ? "is-outside " : ""}${key === selectedDate ? "is-selected " : ""}${key === todayKey ? "is-today" : ""}`.trim()} key={key}>
                  <button className="home-calendar-day" onClick={() => selectDate(date)} type="button"><span>{date.getDate()}</span>{events.length ? <em>{events.length}</em> : null}</button>
                  <div>{events.slice(0, 3).map((event) => renderEventChip(event, true))}{events.length > 3 ? <button className="home-calendar-more" onClick={() => selectDate(date)} type="button">+{events.length - 3} more</button> : null}</div>
                </article>;
              })}
            </div>
          ) : null}
          {view === "week" ? (
            <div className="home-week-calendar">
              {weekDays.map((date) => {
                const key = calendarDateKey(date);
                const events = eventsForDate(key);
                return <article className={key === selectedDate ? "is-selected" : ""} key={key}><button onClick={() => selectDate(date)} type="button"><span>{date.toLocaleDateString([], { weekday: "short" })}</span><strong>{date.getDate()}</strong></button><div>{events.length ? events.map((event) => renderEventChip(event)) : <p>Clear</p>}</div></article>;
              })}
            </div>
          ) : null}
          {view === "agenda" ? (
            <div className="home-calendar-agenda">
              {upcomingEvents.map((event) => <button key={event.id || `${event.date}-${event.title}`} onClick={() => setSelectedDate(event.date)} type="button"><time dateTime={event.date}><strong>{calendarDateFromKey(event.date).toLocaleDateString([], { month: "short", day: "numeric" })}</strong><span>{calendarDateFromKey(event.date).toLocaleDateString([], { weekday: "short" })}</span></time>{renderEventMarker(event)}<div><strong>{event.title}</strong><span>{event.category} · {eventLabel(event)} · {event.owner || "Unassigned"}</span></div><em>{event.status || "Scheduled"}</em></button>)}
              {!upcomingEvents.length ? <p className="empty-state">No upcoming items match these calendar filters.</p> : null}
            </div>
          ) : null}
        </div>

        <aside className="home-calendar-sidepanel">
          <section>
            <div className="home-calendar-side-heading"><span>Selected day</span><strong>{calendarDateFromKey(selectedDate).toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" })}</strong></div>
            <div className="home-calendar-day-agenda">
              {selectedEvents.map((event) => <button key={event.id || `${event.date}-${event.title}`} onClick={() => setSelectedDate(event.date)} type="button">{renderEventMarker(event)}<div><strong>{event.title}</strong><span>{event.category} · {eventLabel(event)}</span></div><em>{event.owner || "Unassigned"}</em></button>)}
              {!selectedEvents.length ? <p>No commitments or delivery dates on this day.</p> : null}
            </div>
          </section>
          <section className="home-calendar-capacity">
            <div className="home-calendar-side-heading"><span>Team capacity</span><strong>Open delivery load</strong></div>
            {owners.map(([owner, count]) => <div key={owner}><span>{owner}</span><i><b style={{ width: `${Math.max(14, Math.round((count / maximumOwnerLoad) * 100))}%` }} /></i><em>{count}</em></div>)}
            {!owners.length ? <p>No active delivery work.</p> : null}
          </section>
          <section className="home-calendar-signals">
            <div><span>Scheduling collisions</span><strong>{collisionCount}</strong><small>Multiple commitments for one owner</small></div>
            <div><span>Overdue delivery</span><strong>{overdueCount}</strong><small>Open items past due</small></div>
          </section>
        </aside>
      </div>
      <footer className="home-calendar-legend"><span><i className="event-meeting" />Meetings</span><span><i className="event-delivery" />Delivery dates</span><span><i className="event-approval" />Approvals</span><span><i className="event-leave" />Leave</span><small>{visibleEvents.length} visible calendar items</small></footer>
    </section>
  );
}

function GeneralFunctionalView({
  activeTab,
  records,
  activity,
  currentUser,
  error,
  isLoading,
  onNavigateModule = () => {},
  onRecordRouteChange = () => {},
  onUpdateRecord = async () => {},
  onOverviewRecordsLoaded,
  setActiveTab = () => {},
  forceScope = "",
}) {
  const [greetingDate, setGreetingDate] = useState(() => new Date());
  const [homeApprovalTab, setHomeApprovalTab] = useState("leaves");
  const [activityFilter, setActivityFilter] = useState("All");
  const [activityLimit, setActivityLimit] = useState(homeActivityPageSize);
  const [homeWorkView, setHomeWorkView] = useState("kanban");
  const [homeFocusRangePreference, setHomeFocusRangePreference] = useState("auto");
  const canManageTeam = currentUser?.canManageTeam === true || /\b(owner|admin|manager|lead|hr|human resources)\b/i.test(`${currentUser?.accessLevel || ""} ${currentUser?.workspaceRole || ""}`);
  const homeScope = forceScope === "team" && canManageTeam ? "team" : "personal";
  const [homeTeamDashboard, setHomeTeamDashboard] = useState(null);
  const [homeTeamDashboardLoading, setHomeTeamDashboardLoading] = useState(false);
  const [homeTeamDashboardError, setHomeTeamDashboardError] = useState("");
  const [homeWorkFilters, setHomeWorkFilters] = useState({ projects: [], statuses: [], types: [], priorities: [], labels: [], severities: [], teams: [], sprints: [], epics: [], dueBuckets: [], flagged: [] });
  const [homeFilterPanelOpen, setHomeFilterPanelOpen] = useState(false);
  const [activeHomeFilterField, setActiveHomeFilterField] = useState("projects");
  const [homeSortKey, setHomeSortKey] = useState("updated_desc");
  const homeFilterPanelRef = useRef(null);
  const [approvalPeople, setApprovalPeople] = useState([]);
  const [approverSavingId, setApproverSavingId] = useState("");
  const [approverError, setApproverError] = useState("");
  const [approvalDecisionId, setApprovalDecisionId] = useState("");
  useEffect(() => {
    setHomeFocusRangePreference("auto");
  }, [homeScope]);
  useEffect(() => {
    let timerId;
    const scheduleGreetingUpdate = () => {
      const now = new Date();
      const nextBoundary = new Date(now);
      if (now.getHours() < 12) nextBoundary.setHours(12, 0, 0, 0);
      else if (now.getHours() < 17) nextBoundary.setHours(17, 0, 0, 0);
      else {
        nextBoundary.setDate(nextBoundary.getDate() + 1);
        nextBoundary.setHours(0, 0, 0, 0);
      }
      timerId = window.setTimeout(() => {
        setGreetingDate(new Date());
        scheduleGreetingUpdate();
      }, Math.max(1000, nextBoundary.getTime() - now.getTime() + 50));
    };
    const refreshGreeting = () => setGreetingDate(new Date());
    scheduleGreetingUpdate();
    window.addEventListener("focus", refreshGreeting);
    document.addEventListener("visibilitychange", refreshGreeting);
    return () => {
      window.clearTimeout(timerId);
      window.removeEventListener("focus", refreshGreeting);
      document.removeEventListener("visibilitychange", refreshGreeting);
    };
  }, []);
  useEffect(() => {
    getWorkspacePeople().then((data) => setApprovalPeople(data.people || [])).catch(() => setApprovalPeople([]));
  }, []);
  useEffect(() => {
    let active = true;
    setHomeTeamDashboardLoading(true);
    setHomeTeamDashboardError("");
    getWorkspaceHomeDashboard(homeScope)
      .then((data) => {
        if (!active) return;
        setHomeTeamDashboard(data);
        onOverviewRecordsLoaded?.(data.records || []);
      })
      .catch((dashboardError) => { if (active) setHomeTeamDashboardError(dashboardError?.response?.data?.message || "Could not load the Home dashboard."); })
      .finally(() => { if (active) setHomeTeamDashboardLoading(false); });
    return () => { active = false; };
  }, [homeScope, currentUser?.id, records, onOverviewRecordsLoaded]);
  useEffect(() => {
    setActivityLimit(homeActivityPageSize);
  }, [activityFilter]);
  useEffect(() => {
    if (!homeFilterPanelOpen) return undefined;
    const closeOnOutsideInteraction = (event) => {
      if (!homeFilterPanelRef.current?.contains(event.target)) setHomeFilterPanelOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setHomeFilterPanelOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideInteraction);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideInteraction);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [homeFilterPanelOpen]);
  const assignLeaveApprover = async (record, selectedName) => {
    const approver = approvalPeople.find((person) => person.name === selectedName);
    const requester = approvalPeople.find((person) => person.name === record.owner);
    if (!approver || !requester) return;
    setApproverSavingId(record.id);
    setApproverError("");
    try {
      await updateWorkspacePerson(requester.id, { managerName: approver.name, managerUser: approver.userId });
      await onUpdateRecord(record.id, { approvalRole: approver.name });
    } catch (assignmentError) {
      setApproverError(assignmentError?.response?.data?.message || "Could not assign the approver.");
    } finally {
      setApproverSavingId("");
    }
  };
  const decideApproval = async (record, decision) => {
    setApprovalDecisionId(record.id);
    setApproverError("");
    try {
      await onUpdateRecord(record.id, { status: decision });
    } catch (decisionError) {
      setApproverError(decisionError?.response?.data?.message || "Could not save the approval decision.");
    } finally {
      setApprovalDecisionId("");
    }
  };
  const userName = currentUser?.name || "Workspace user";
  const isOwnedOrAssignedToCurrentUser = (record) => [record.owner, ...asList(record.assignees)]
    .filter(Boolean)
    .some((assignee) => `${assignee}`.trim().toLowerCase() === userName.trim().toLowerCase());
  const overviewMembers = homeScope === "team" ? homeTeamDashboard?.members || [] : null;
  const visibleRecords = getOverviewRecords(homeTeamDashboard?.scope === homeScope ? homeTeamDashboard.records || [] : [], currentUser, overviewMembers);
  const homeProjectLookupRecords = [...records, ...visibleRecords];
  const getHomeProjectDisplayName = (record) => resolveProjectDisplayName(homeProjectLookupRecords, record?.projectRef, record);
  const isProjectRecord = (record) =>
    record.moduleName === "Projects" || /project/i.test(`${record.type || ""}`);
  const taskRecords = visibleRecords.filter((record) =>
    ["Tasks & Tickets", "My Work", "Work"].includes(record.moduleName) || /task|ticket|bug/i.test(record.type)
  );
  const isAssignedToCurrentUser = isOwnedOrAssignedToCurrentUser;
  const myTaskRecords = taskRecords.filter(isAssignedToCurrentUser);
  const todayKey = calendarDateKey(new Date());
  const todayLabel = calendarDateFromKey(todayKey).toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
  const homeFilterOptions = {
    projects: Array.from(new Set(taskRecords.map((record) => record.projectRef).filter(Boolean))).sort().map((projectRef) => ({
      key: projectRef,
      label: resolveProjectDisplayName(homeProjectLookupRecords, projectRef) || projectRef,
    })),
    statuses: Array.from(new Set(taskRecords.map((record) => record.status).filter(Boolean))).sort(),
    types: Array.from(new Set(taskRecords.map((record) => record.type).filter(Boolean))).sort(),
    priorities: Array.from(new Set(taskRecords.map((record) => record.priority).filter(Boolean))).sort(),
    labels: Array.from(new Set(taskRecords.flatMap((record) => asList(record.labels)))).sort(),
    severities: Array.from(new Set(taskRecords.map((record) => record.severity).filter(Boolean))).sort(),
    teams: Array.from(new Set(taskRecords.map((record) => record.team).filter(Boolean))).sort(),
    sprints: Array.from(new Set(taskRecords.map((record) => record.sprint).filter(Boolean))).sort(),
    epics: Array.from(new Set(taskRecords.map((record) => record.epic).filter(Boolean))).sort(),
    dueBuckets: boardDueBucketOptions,
    flagged: ["Flagged", "Not flagged"],
  };
  const isHomeRecordInDueBucket = (record, bucket) => {
    if (bucket === "no_due") return !record.due;
    if (!record.due) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(`${record.due}T00:00:00`);
    if (Number.isNaN(due.getTime())) return false;
    if (bucket === "overdue") return due < today;
    if (bucket === "today") return due.getTime() === today.getTime();
    const weekEnd = new Date(today);
    weekEnd.setDate(today.getDate() + 7);
    if (bucket === "this_week") return due > today && due <= weekEnd;
    return due > weekEnd;
  };
  const homeFilteredTaskRecords = taskRecords.filter((record) => {
    const includes = (key, value) => !homeWorkFilters[key].length || homeWorkFilters[key].includes(value);
    const matchesLabels = !homeWorkFilters.labels.length || homeWorkFilters.labels.some((label) => asList(record.labels).includes(label));
    const matchesDue = !homeWorkFilters.dueBuckets.length || homeWorkFilters.dueBuckets.some((bucket) => isHomeRecordInDueBucket(record, bucket));
    return includes("projects", record.projectRef)
      && includes("statuses", record.status)
      && includes("types", record.type)
      && includes("priorities", record.priority)
      && matchesLabels
      && includes("severities", record.severity)
      && includes("teams", record.team)
      && includes("sprints", record.sprint)
      && includes("epics", record.epic)
      && matchesDue
      && includes("flagged", record.flagged ? "Flagged" : "Not flagged");
  });
  const toggleHomeWorkFilter = (key, value) => setHomeWorkFilters((current) => ({
    ...current,
    [key]: current[key].includes(value) ? current[key].filter((item) => item !== value) : [...current[key], value],
  }));
  const homeFilterDefinitions = [
    ["projects", "Projects"], ["epics", "Epics"], ["sprints", "Sprints"], ["statuses", "Status"], ["types", "Ticket types"], ["priorities", "Priorities"], ["severities", "Severities"], ["teams", "Teams"], ["dueBuckets", "Due date"], ["flagged", "Flagged"], ["labels", "Labels"],
  ];
  const activeHomeFilterDefinition = homeFilterDefinitions.find(([key]) => key === activeHomeFilterField) || homeFilterDefinitions[0];
  const activeHomeFilterOptions = homeFilterOptions[activeHomeFilterDefinition[0]] || [];
  const homeAppliedFilterCount = homeFilterDefinitions.filter(([key]) => homeWorkFilters[key].length).length;
  const sortHomeWorkRecords = (items = []) => [...items].sort((left, right) => {
    if (homeSortKey === "title_asc") return `${left.title || ""}`.localeCompare(`${right.title || ""}`);
    if (homeSortKey === "due_asc") return `${left.due || "9999-12-31"}`.localeCompare(`${right.due || "9999-12-31"}`);
    if (homeSortKey === "priority_desc") return workspacePriorities.indexOf(right.priority) - workspacePriorities.indexOf(left.priority);
    const leftTime = new Date(left[homeSortKey === "created_desc" ? "createdAt" : "updatedAt"] || 0).getTime();
    const rightTime = new Date(right[homeSortKey === "created_desc" ? "createdAt" : "updatedAt"] || 0).getTime();
    return rightTime - leftTime;
  });
  const calendarRecords = visibleRecords.filter((record) =>
    record.moduleName === "Schedule" || record.tab === "Meetings" || /meeting|event|calendar|session/i.test(record.type)
  );
  const blockedRecords = visibleRecords.filter((record) => record.status === "Blocked");
  const openTasks = myTaskRecords.filter((record) => !closedStatuses.includes(record.status));
  const completedTasks = myTaskRecords.filter((record) => record.status === "Done");
  const reviewItems = visibleRecords.filter(isOverviewApproval);
  const dueToday = visibleRecords.filter((record) =>
    !isProjectRecord(record) && record.due === todayKey
  );
  const todayCalendarRecords = calendarRecords.filter((record) => record.due === todayKey);
  const sortByDue = (items) => [...items].sort((a, b) => `${a.due || "9999-12-31"}`.localeCompare(`${b.due || "9999-12-31"}`));
  const overdueRecords = visibleRecords.filter((record) => record.due && record.due < todayKey && !closedStatuses.includes(record.status));
  const getHomeBoardLaneKey = (record) => {
    const status = `${record.status || ""}`.trim().toLowerCase();
    if (/backlog|idea|triage|new|needs information/.test(status)) return "backlog";
    if (/review|qa|approval|pending|uat|client review/.test(status)) return "review";
    if (/progress|doing|active|build|develop|fix|working|blocked|hold|waiting/.test(status)) return "in_progress";
    if (/done|closed|resolved|completed|cancelled|duplicate|archived|paid|approved|rejected|published|exported|ready/.test(status)) return "done";
    return "todo";
  };
  const homeBoardLanes = generalizedBoardLanes.map((lane) => ({
    ...lane,
    records: homeFilteredTaskRecords.filter((record) => getHomeBoardLaneKey(record) === lane.key),
  }));
  const priorityQueue = sortByDue(homeFilteredTaskRecords.filter((record) => !closedStatuses.includes(record.status)))
    .sort((a, b) => workspacePriorities.indexOf(b.priority) - workspacePriorities.indexOf(a.priority))
    .slice(0, 8);
  const approvalQueue = reviewItems.map((record) => ({
    ...record,
    decisionOwner: record.approvalRole || (record.type?.toLowerCase().includes("leave") ? "Manager" : "Assigned reviewer"),
  }));
  const overviewActivity = getOverviewActivity(activity, currentUser, overviewMembers, visibleRecords);
  const activityFeed = overviewActivity.map((item) => ({
    id: item.id,
    title: item.title || item.action || item.message || "Workspace activity",
    meta: [item.moduleName, item.tabName, item.userName].filter(Boolean).join(" · ") || "Workspace update",
    tag: item.eventType || "Updated",
    at: formatHomeActivityTime(item.createdAt || item.at),
    searchText: [item.moduleName, item.tabName, item.title, item.action, item.message, item.eventType].filter(Boolean).join(" ").toLowerCase(),
  }));
  const activityFilters = ["All", "Work", "People", "Projects", "Approvals", "Settings"];
  const activityMatchesFilter = (item, filter) => {
    if (filter === "All") return true;
    if (filter === "Approvals") return /approval|approve|reject|review|leave|payout/.test(item.searchText);
    if (filter === "Settings") return /setting|administration|permission|security|branding|preference|integration/.test(item.searchText);
    return item.searchText.includes(filter.toLowerCase());
  };
  const filteredActivityFeed = activityFeed.filter((item) => activityMatchesFilter(item, activityFilter));
  const activityCountForFilter = (filter) => filter === "All"
    ? activityFeed.length
    : activityFeed.filter((item) => activityMatchesFilter(item, filter)).length;
  const visibleActivityFeed = filteredActivityFeed.slice(0, activityLimit);
  const overviewActivityItems = activityFeed.slice(0, 4);
  const unscheduledOpenCount = visibleRecords.filter((record) => isOverviewTicket(record) && !record.due && !isOverviewClosed(record)).length;
  const workspaceTaskRecords = visibleRecords.filter(isOverviewTicket);
  const workspaceOpenTasks = workspaceTaskRecords.filter((record) => !isOverviewClosed(record));
  const workspaceStatusCounts = generalizedBoardLanes.map((lane) => ({
    ...lane,
    count: workspaceTaskRecords.filter((record) => getHomeBoardLaneKey(record) === lane.key).length,
  }));
  const workspaceStatusMax = Math.max(1, ...workspaceStatusCounts.map((lane) => lane.count));
  const workspaceProjectRecords = visibleRecords.filter((record) => record.moduleName === "Projects");
  const assignedProjectCount = new Set([
    ...(currentUser?.projectRefs || []),
    ...(currentUser?.projectAssignments || []).map((assignment) => assignment.projectRef),
  ].map((value) => `${value || ""}`.trim().toLowerCase()).filter(Boolean)).size;
  const workspaceProjectCount = Math.max(workspaceProjectRecords.length, assignedProjectCount);
  const workspaceSprintRecords = visibleRecords.filter((record) => record.moduleName === "Work" && record.type === "Sprint");
  const workspaceEpicRecords = visibleRecords.filter((record) => record.moduleName === "Work" && record.type === "Epic");
  const workspaceClientRequests = visibleRecords.filter((record) => ["Client Request", "Support Request", "Customer Request", "Portal Request"].includes(record.type)
    || (["Client Admin", "Support Desk"].includes(record.moduleName) && record.tab === "Requests"));
  const workspaceDocs = visibleRecords.filter((record) => record.moduleName === "Knowledge");
  const workspaceFinanceRecords = visibleRecords.filter((record) => record.moduleName === "Finance");
  const workspaceMeetingRecords = visibleRecords.filter((record) => record.moduleName === "Schedule" && ["Meetings", "Bookings"].includes(record.tab));
  const workspaceApprovalQueue = visibleRecords.filter(isOverviewApproval)
    .map((record) => ({
      ...record,
      decisionOwner: record.approvalRole || (record.type?.toLowerCase().includes("leave") ? "Manager" : "Assigned reviewer"),
    }));
  const workspaceDueToday = workspaceOpenTasks.filter((record) => record.due === todayKey);
  const workspaceOverdueRecords = workspaceOpenTasks.filter((record) => record.due && record.due < todayKey);
  const workspaceBlockedRecords = workspaceOpenTasks.filter((record) => record.status === "Blocked");
  const workspaceMeetingsToday = workspaceMeetingRecords.filter((record) => record.due === todayKey && !isOverviewClosed(record));
  const workspaceDocsInReview = workspaceDocs.filter((record) => /review|draft|pending/i.test(`${record.status || ""}`));
  const workspaceFinancePending = workspaceFinanceRecords.filter((record) => !["Paid", "Completed", "Approved", "Done"].includes(record.status));
  const commandHealthScore = Math.max(0, Math.min(100, 100 - (workspaceBlockedRecords.length * 6) - (workspaceOverdueRecords.length * 4) - (workspaceApprovalQueue.length * 2)));
  const commandHealthTone = commandHealthScore >= 82 ? "on-track" : commandHealthScore >= 62 ? "watch" : "at-risk";
  const commandHealthLabel = commandHealthTone === "on-track" ? "On track" : commandHealthTone === "watch" ? "Watch" : "At risk";
  const commandHealthGradientId = `home-command-health-gradient-${homeScope}`;
  const focusWeekEndKey = addCalendarDays(todayKey, 6);
  const focusMonthEndDate = calendarDateFromKey(todayKey);
  focusMonthEndDate.setMonth(focusMonthEndDate.getMonth() + 1, 0);
  const focusRangeEndKeys = {
    today: todayKey,
    week: focusWeekEndKey,
    month: calendarDateKey(focusMonthEndDate),
  };
  const focusMembers = [{ ...currentUser, userId: currentUser?.userId || currentUser?._id || currentUser?.id, allProjects: true }, ...(overviewMembers || [])];
  const getFocusRecordDue = (record) => (/^\d{4}-\d{2}-\d{2}(?:$|T)/.test(record?.due || "") ? record.due.slice(0, 10) : "");
  const getFocusRecordSignal = (record, range) => {
    if (isOverviewClosed(record)) return null;
    const due = getFocusRecordDue(record);
    const status = `${record.status || ""}`.trim().toLowerCase();
    const isTicket = isOverviewTicket(record);
    const isBlocked = ["blocked", "on hold", "stuck"].includes(status);
    const isOverdue = Boolean(due && due < todayKey);
    const isWithinRange = Boolean(due && due <= focusRangeEndKeys[range]);
    const needsReview = focusMembers.some((member) => canReviewOverviewRecord(record, member));
    const isHighPriority = record.flagged || ["urgent", "critical", "highest", "high"].includes(`${record.priority || ""}`.trim().toLowerCase());

    if (isTicket && isBlocked) return { rank: 0, actionNote: "Blocked", actionTone: "blocked", sortDue: due || "9999-12-31" };
    if (isTicket && isOverdue) return { rank: 1, actionNote: "Overdue", actionTone: "overdue", sortDue: due };
    if (needsReview && (!due || isWithinRange)) return { rank: 2, actionNote: homeScope === "team" ? "Approval needed" : "Your approval", actionTone: "review", sortDue: due || "9999-12-31" };
    if (isTicket && isWithinRange) {
      return {
        rank: 3,
        actionNote: range === "today" ? "Due today" : range === "week" ? "Due this week" : "Due this month",
        actionTone: "normal",
        sortDue: due,
      };
    }
    if (isTicket && range === "today" && isHighPriority) return { rank: 4, actionNote: record.flagged ? "Flagged" : `${record.priority} priority`, actionTone: "normal", sortDue: due || "9999-12-31" };
    return null;
  };
  const getCommandFocusItems = (range) => {
    const items = new Map();
    visibleRecords.forEach((record) => {
      const signal = getFocusRecordSignal(record, range);
      if (!signal) return;
      items.set(record.id || record._id || record, { ...record, ...signal });
    });
    return [...items.values()]
      .sort((left, right) => left.rank - right.rank || left.sortDue.localeCompare(right.sortDue) || `${left.title || ""}`.localeCompare(`${right.title || ""}`))
      .slice(0, 6);
  };
  const focusItemsByRange = {
    today: getCommandFocusItems("today"),
    week: getCommandFocusItems("week"),
    month: getCommandFocusItems("month"),
  };
  const autoHomeFocusRange = focusItemsByRange.today.length ? "today" : focusItemsByRange.week.length ? "week" : focusItemsByRange.month.length ? "month" : "today";
  const selectedHomeFocusRange = homeFocusRangePreference === "auto" ? autoHomeFocusRange : homeFocusRangePreference;
  const commandFocusItems = focusItemsByRange[selectedHomeFocusRange] || focusItemsByRange.today;
  const selectedHomeFocusOption = homeFocusOptions.find((option) => option.key === selectedHomeFocusRange) || homeFocusOptions[0];
  const commandMetrics = [
    { label: "Projects", value: workspaceProjectCount, note: `${Math.max(workspaceProjectRecords.filter((record) => !closedStatuses.includes(record.status)).length, assignedProjectCount)} active`, action: () => onNavigateModule("Projects", "Overview") },
    { label: "Active work", value: workspaceOpenTasks.length, note: `${workspaceBlockedRecords.length} blocked`, tone: workspaceBlockedRecords.length ? "risk" : "", action: () => onNavigateModule("Work", "Overview") },
    { label: "Sprints", value: workspaceSprintRecords.length, note: `${workspaceSprintRecords.filter((record) => !closedStatuses.includes(record.status)).length} live/planned`, action: () => onNavigateModule("Work", "Sprints") },
    { label: "Epics", value: workspaceEpicRecords.length, note: `${workspaceEpicRecords.filter((record) => !closedStatuses.includes(record.status)).length} active`, action: () => onNavigateModule("Work", "Epics") },
    { label: "Client requests", value: workspaceClientRequests.length, note: `${workspaceClientRequests.filter((record) => !isOverviewClosed(record)).length} active requests`, tone: workspaceClientRequests.some((record) => record.status === "Blocked") ? "risk" : "", action: () => onNavigateModule("CRM", "Clients") },
    { label: "Decisions", value: workspaceApprovalQueue.length, note: "waiting approval", tone: workspaceApprovalQueue.length ? "watch" : "", action: () => setActiveTab("Approvals") },
  ];
  const clientFlowRows = [
    ["New", workspaceClientRequests.filter((record) => /new|requested|open|todo|backlog/i.test(`${record.status || ""}`)).length],
    ["In progress", workspaceClientRequests.filter((record) => /progress|active|working|contacted/i.test(`${record.status || ""}`)).length],
    ["Review", workspaceClientRequests.filter((record) => /review|pending|approval|waiting/i.test(`${record.status || ""}`)).length],
    ["Closed", workspaceClientRequests.filter((record) => closedStatuses.includes(record.status)).length],
  ];
  const commandInsightRows = [
    workspaceBlockedRecords.length || workspaceOverdueRecords.length
      ? `${workspaceBlockedRecords.length} blockers and ${workspaceOverdueRecords.length} overdue items need owner review.`
      : "Delivery health is clear: no blockers or overdue work detected.",
    unscheduledOpenCount
      ? `${unscheduledOpenCount} open work items need dates before planning stays reliable.`
      : "Active work has due-date coverage for the visible queue.",
    workspaceClientRequests.filter((record) => !closedStatuses.includes(record.status)).length
      ? `${workspaceClientRequests.filter((record) => !closedStatuses.includes(record.status)).length} client requests are still moving through the flow.`
      : "No open client request pressure is visible right now.",
  ];

  const renderCompactList = (items, emptyText) => (
    <div className="general-list">
      {items.map((record) => (
        <article key={record.id || `${record.title}-${record.meta}`}>
          <div>
            <strong>{record.title}</strong>
            <span>{record.meta || `${record.moduleName} · ${record.owner || userName}`}</span>
          </div>
          <em>{record.tag || record.status || record.priority || record.due || "Open"}</em>
        </article>
      ))}
      {!items.length ? <p className="empty-state">{emptyText}</p> : null}
    </div>
  );

  const openOverviewItem = (record) => {
    if (["Tasks & Tickets", "My Work", "Work"].includes(record.moduleName) || /task|ticket|bug/i.test(record.type)) {
      onRecordRouteChange(record);
      onNavigateModule("Work", "Tickets");
      return;
    }
    if (record.moduleName === "Schedule" || /meeting|event|calendar|session/i.test(record.type)) {
      setActiveTab("Calendar");
      return;
    }
    if (record.moduleName === "Projects" || /project/i.test(record.type)) {
      onNavigateModule("Projects", "Overview");
      return;
    }
    if (["Knowledge", "Docs", "Documents"].includes(record.moduleName) || /doc|knowledge|policy|note|wiki|file/i.test(record.type)) {
      onNavigateModule("Knowledge", "Documents");
      return;
    }
    if (record.moduleName === "Finance" || /invoice|payment|billing|finance|expense|payout|quote/i.test(record.type)) {
      onNavigateModule("Finance", "Overview");
      return;
    }
    if (["CRM", "Clients", "Client Requests"].includes(record.moduleName) || /client|request|lead|deal|customer/i.test(record.type)) {
      onNavigateModule("CRM", "Clients");
      return;
    }
    if (["Review", "In Review", "Client Review", "Pending", "Needs Review"].includes(record.status)) {
      setActiveTab("Approvals");
    }
  };

  const formatHomeBoardDate = (value) => {
    if (!value) return "No due date";
    const date = new Date(`${value}`.length === 10 ? `${value}T00:00:00` : value);
    return Number.isNaN(date.getTime()) ? `${value}` : date.toLocaleDateString([], { year: "numeric", month: "long", day: "numeric" });
  };
  const getHomeBoardAssigneeProfile = (record) => {
    const ownerName = `${record.owner || ""}`.trim().toLowerCase();
    if (ownerName && ownerName === userName.trim().toLowerCase()) return currentUser;
    return approvalPeople.find((person) => `${person.name || ""}`.trim().toLowerCase() === ownerName) || { name: record.owner || "Unassigned" };
  };
  const openHomeWorkRecord = (record) => {
    onRecordRouteChange(record);
    onNavigateModule("Work", "Overview");
  };
  const teamAnalyticsMembers = homeTeamDashboard?.members || [];
  const isTeamFormulaOwner = currentUser?.accessLevel === "Owner";
  const teamAnalyticsOpenRecords = workspaceOpenTasks;
  const teamAnalyticsClosedRecords = workspaceTaskRecords.filter(isOverviewClosed);
  const weekStartDate = calendarDateFromKey(todayKey);
  weekStartDate.setDate(weekStartDate.getDate() - ((weekStartDate.getDay() + 6) % 7));
  const teamWeekStartKey = calendarDateKey(weekStartDate);
  const monthStartKey = `${todayKey.slice(0, 7)}-01`;
  const buildRecognitionRows = (startKey) => teamAnalyticsMembers.map((member) => {
    const memberClosedRecords = teamAnalyticsClosedRecords.filter((record) => {
      const closedKey = getAnalyticsDateKey(record.updatedAt || record.createdAt);
      return closedKey >= startKey && closedKey <= todayKey && analyticsRecordBelongsToMember(record, member);
    });
    const weightedPoints = memberClosedRecords.reduce((total, record) => {
      const timeliness = getAnalyticsTimeliness(record);
      return total + (getAnalyticsPoints(record) * getAnalyticsPriorityWeight(record) * timeliness.multiplier);
    }, 0);
    const earlyCount = memberClosedRecords.filter((record) => getAnalyticsTimeliness(record).days > 0).length;
    const lateCount = memberClosedRecords.filter((record) => getAnalyticsTimeliness(record).days < 0).length;
    const reliability = memberClosedRecords.length ? Math.round(((memberClosedRecords.length - lateCount) / memberClosedRecords.length) * 100) : 0;
    const score = Math.max(0, weightedPoints + (reliability * 0.05) + (earlyCount * 1.2) - (lateCount * 1.5));
    return {
      ...member,
      earlyCount,
      lateCount,
      reliability,
      score,
      closedCount: memberClosedRecords.length,
      deliveredPoints: weightedPoints,
    };
  }).sort((left, right) => right.score - left.score || right.deliveredPoints - left.deliveredPoints || `${left.name || ""}`.localeCompare(`${right.name || ""}`));
  const teamRecognitionWeekRows = buildRecognitionRows(teamWeekStartKey);
  const teamRecognitionMonthRows = buildRecognitionRows(monthStartKey);
  const teamBaseCapacityRows = teamAnalyticsMembers.map((member) => {
    const memberOpenRecords = teamAnalyticsOpenRecords.filter((record) => analyticsRecordBelongsToMember(record, member));
    const distributedLoad = memberOpenRecords.reduce((total, record) => {
      const dueKey = getAnalyticsDateKey(record.due);
      const workingDays = getAnalyticsWorkingDaysUntil(todayKey, dueKey);
      return total + (getAnalyticsPoints(record) / workingDays);
    }, 0);
    const dueTodayPressure = memberOpenRecords.filter((record) => getAnalyticsDateKey(record.due) === todayKey).length * 0.8;
    const overduePressure = memberOpenRecords.filter((record) => getAnalyticsDateKey(record.due) && getAnalyticsDateKey(record.due) < todayKey).length * 1.4;
    const blockedPressure = memberOpenRecords.filter((record) => /blocked|stuck|hold/i.test(`${record.status || ""}`)).length * 1.2;
    const taskCountPressure = memberOpenRecords.length * 0.25;
    const dailyLoad = distributedLoad + dueTodayPressure + overduePressure + blockedPressure + taskCountPressure;
    const capacity = /leave|pto|absent/i.test(`${member.attendance || ""}`) ? 0 : teamAnalyticsCapacityPerDay;
    const occupancyPercent = capacity ? Math.round((dailyLoad / capacity) * 100) : 0;
    return {
      ...member,
      blockedPressure,
      capacity,
      dailyLoad,
      dueTodayPressure,
      memberOpenRecords,
      occupancyPercent,
      overduePressure,
      taskCountPressure,
    };
  });
  const maxTeamDailyLoad = Math.max(1, ...teamBaseCapacityRows.map((member) => member.dailyLoad));
  const teamCapacityRows = teamBaseCapacityRows.map((member) => ({
    ...member,
    relativeLoad: Math.round((member.dailyLoad / maxTeamDailyLoad) * 100),
    tone: getAnalyticsStatusTone(member.occupancyPercent),
  })).sort((left, right) => right.dailyLoad - left.dailyLoad || `${left.name || ""}`.localeCompare(`${right.name || ""}`));
  const projectLoadGroups = teamAnalyticsOpenRecords.reduce((groups, record) => {
    const projectName = getHomeProjectDisplayName(record) || "No project";
    if (!groups[projectName]) groups[projectName] = { count: 0, points: 0 };
    groups[projectName].count += 1;
    groups[projectName].points += getAnalyticsPoints(record);
    return groups;
  }, {});
  const projectLoadTotal = Math.max(1, Object.values(projectLoadGroups).reduce((total, group) => total + group.points, 0));
  const projectLoadRows = Object.entries(projectLoadGroups)
    .map(([name, group], index) => ({
      name,
      ...group,
      color: teamAnalyticsPalette[index % teamAnalyticsPalette.length],
      percent: Math.round((group.points / projectLoadTotal) * 100),
      share: (group.points / projectLoadTotal) * 100,
    }))
    .sort((left, right) => right.points - left.points || left.name.localeCompare(right.name))
    .slice(0, 6);
  let projectPieCursor = 0;
  const projectPieGradient = projectLoadRows.length ? `conic-gradient(${projectLoadRows.map((row) => {
    const start = projectPieCursor;
    projectPieCursor += row.share;
    return `${row.color} ${start}% ${Math.min(100, projectPieCursor)}%`;
  }).join(", ")})` : "conic-gradient(#dbe5e2 0% 100%)";
  const riskMixRows = [
    { label: "Overdue", count: workspaceOverdueRecords.length, color: "#dc2626" },
    { label: "Blocked", count: workspaceBlockedRecords.length, color: "#7c3aed" },
    { label: "Due 7d", count: teamAnalyticsOpenRecords.filter((record) => {
      const dueKey = getAnalyticsDateKey(record.due);
      return dueKey && dueKey >= todayKey && dueKey <= addCalendarDays(todayKey, 7);
    }).length, color: "#f97316" },
    { label: "No date", count: teamAnalyticsOpenRecords.filter((record) => !getAnalyticsDateKey(record.due)).length, color: "#94a3b8" },
  ];
  const riskMixTotal = Math.max(1, riskMixRows.reduce((total, row) => total + row.count, 0));
  let riskPieCursor = 0;
  const riskPieGradient = riskMixRows.some((row) => row.count) ? `conic-gradient(${riskMixRows.map((row) => {
    const start = riskPieCursor;
    const next = start + ((row.count / riskMixTotal) * 100);
    riskPieCursor = next;
    return `${row.color} ${start}% ${next}%`;
  }).join(", ")})` : "conic-gradient(#dbe5e2 0% 100%)";
  const velocityWeeks = Array.from({ length: 4 }, (_, index) => {
    const start = calendarDateFromKey(teamWeekStartKey);
    start.setDate(start.getDate() - ((3 - index) * 7));
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const startKey = calendarDateKey(start);
    const endKey = calendarDateKey(end);
    const weekRecords = teamAnalyticsClosedRecords.filter((record) => {
      const closedKey = getAnalyticsDateKey(record.updatedAt || record.createdAt);
      return closedKey >= startKey && closedKey <= endKey;
    });
    return {
      label: start.toLocaleDateString([], { month: "short", day: "numeric" }),
      points: weekRecords.reduce((total, record) => total + getAnalyticsPoints(record), 0),
      count: weekRecords.length,
    };
  });
  const velocityMax = Math.max(1, ...velocityWeeks.map((week) => week.points));
  const weeklyWinner = teamRecognitionWeekRows.find((member) => member.score > 0);
  const monthlyWinner = teamRecognitionMonthRows.find((member) => member.score > 0);

  const teamPerformancePanel = canManageTeam ? (
    <section className="functional-panel home-team-panel">
      <div className="functional-panel-header"><div><span>Team analytics</span><h3>Capacity, risk, and momentum</h3></div><small>{homeTeamDashboard?.members?.length || 0} members</small></div>
      {homeTeamDashboardLoading ? <p className="empty-state"><WorkspaceInlineLoader label="Loading team performance" /></p> : null}
      {homeTeamDashboardError ? <p className="workspace-error-notice">{homeTeamDashboardError}</p> : null}
      {!homeTeamDashboardLoading && !homeTeamDashboardError && homeTeamDashboard ? (
        <>
          <div className="home-team-analytics-grid">
            <article className="home-team-analytics-card home-team-capacity-card">
              <header><span>Capacity load</span><strong>{teamCapacityRows[0] ? `${teamCapacityRows[0].occupancyPercent}%` : "0%"}</strong><small>highest occupied</small></header>
              <div className="home-team-capacity-list">
                {teamCapacityRows.slice(0, 6).map((member) => (
                  <div className={`is-${member.tone}`} key={member.id}>
                    <span>{member.name}</span>
                    <b aria-hidden="true"><i style={{ width: `${Math.min(100, Math.max(4, member.occupancyPercent))}%` }} /></b>
                    <em>{member.occupancyPercent}%</em>
                    <small>{formatAnalyticsNumber(member.dailyLoad)} pts/day · {member.relativeLoad}% relative</small>
                  </div>
                ))}
              </div>
            </article>
            <article className="home-team-analytics-card home-team-pie-card">
              <header><span>Project load</span><strong>{teamAnalyticsOpenRecords.length}</strong><small>open items</small></header>
              <div className="home-team-pie-wrap">
                <div className="home-team-donut" style={{ "--team-donut": projectPieGradient }}><strong>{formatAnalyticsNumber(projectLoadTotal)}</strong><span>pts</span></div>
                <div className="home-team-legend">
                  {projectLoadRows.map((row) => <span key={row.name}><i style={{ backgroundColor: row.color }} />{row.name}<em>{row.percent}%</em></span>)}
                  {!projectLoadRows.length ? <p className="empty-state">No open project work yet.</p> : null}
                </div>
              </div>
            </article>
            <article className="home-team-analytics-card home-team-pie-card">
              <header><span>Delivery risk</span><strong>{riskMixRows.reduce((total, row) => total + row.count, 0)}</strong><small>signals</small></header>
              <div className="home-team-pie-wrap">
                <div className="home-team-donut is-risk" style={{ "--team-donut": riskPieGradient }}><strong>{commandHealthScore}%</strong><span>health</span></div>
                <div className="home-team-legend">
                  {riskMixRows.map((row) => <span key={row.label}><i style={{ backgroundColor: row.color }} />{row.label}<em>{row.count}</em></span>)}
                </div>
              </div>
            </article>
            <article className="home-team-analytics-card home-team-velocity-card">
              <header><span>Velocity</span><strong>{velocityWeeks.reduce((total, week) => total + week.points, 0)}</strong><small>closed pts / 4w</small></header>
              <div className="home-team-velocity-bars">
                {velocityWeeks.map((week) => <div key={week.label}><b aria-hidden="true"><i style={{ height: `${Math.max(6, (week.points / velocityMax) * 100)}%` }} /></b><span>{week.label}</span><small>{formatAnalyticsNumber(week.points, 0)}p</small></div>)}
              </div>
            </article>
          </div>
          <div className="home-team-recognition-grid">
            <article>
              <span>Employee of the week</span>
              <strong>{weeklyWinner?.name || "No winner yet"}</strong>
              <small>{weeklyWinner ? `${formatAnalyticsNumber(weeklyWinner.score)} score · ${weeklyWinner.closedCount} completed · ${weeklyWinner.reliability}% reliable` : "Complete work with clean timing to appear here."}</small>
            </article>
            <article>
              <span>Employee of the month</span>
              <strong>{monthlyWinner?.name || "No winner yet"}</strong>
              <small>{monthlyWinner ? `${formatAnalyticsNumber(monthlyWinner.score)} score · ${monthlyWinner.closedCount} completed · ${monthlyWinner.reliability}% reliable` : "Monthly recognition starts from completed work."}</small>
            </article>
          </div>
          {isTeamFormulaOwner ? (
            <details className="home-team-formula-card" open>
              <summary>Owner formula model</summary>
              <div>
                <code>daily load = sum(remaining points / working days until due) + task pressure + urgency pressure</code>
                <code>occupancy % = daily load / 8 point daily capacity</code>
                <code>recognition score = delivered points * priority weight * early/on-time multiplier + reliability bonus - late penalty</code>
                <p>Tickets without story points receive a small type-based fallback. Closed item date is inferred from last update until a real completed-at field exists.</p>
              </div>
            </details>
          ) : <p className="home-team-formula-locked">Formula details are visible to the workspace owner only.</p>}
          <div className="home-team-member-list">{(homeTeamDashboard.members || []).map((member) => <article key={member.id}><div><WorkspaceUserAvatar user={member} /><span><strong>{member.name}</strong><small>{[member.role, member.team || "No team"].filter(Boolean).join(" · ")}</small></span></div><div><em>{member.attendance}</em><small>{member.openCount} open · {member.blockedCount} blocked · {member.overdueCount} overdue</small></div></article>)}{!homeTeamDashboard.members?.length ? <p className="empty-state">No team members are assigned to this view yet.</p> : null}</div>
        </>
      ) : null}
    </section>
  ) : null;

  const tabContent = {
    Overview: (
      <>
        <section className="home-command-hero" aria-labelledby="home-overview-title">
          <div className="home-command-hero-copy"><span>{homeScope === "team" ? "Team overview" : "Personal overview"}</span><h2 id="home-overview-title">{homeScope === "team" ? "Your team" : `${getLocalGreeting(greetingDate)}, ${userName}`}</h2><p>{todayLabel}</p></div>
          <div
            className={`home-command-health is-${commandHealthTone}`}
            style={{ "--health-score": commandHealthScore }}
            aria-label={`${homeScope === "team" ? "Team" : "Personal"} operating health ${commandHealthScore}%`}
          >
            <div className="home-command-health-visual" aria-hidden="true">
              <svg className="home-command-health-ring" viewBox="0 0 108 108">
                <defs>
                  <linearGradient id={commandHealthGradientId} x1="16" x2="92" y1="92" y2="16" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="var(--health-start)" />
                    <stop offset="100%" stopColor="var(--health-end)" />
                  </linearGradient>
                </defs>
                <circle className="home-command-health-track" cx="54" cy="54" r="44" pathLength="100" />
                <circle
                  className="home-command-health-progress"
                  cx="54"
                  cy="54"
                  r="44"
                  pathLength="100"
                  stroke={`url(#${commandHealthGradientId})`}
                  strokeDasharray={`${commandHealthScore} 100`}
                />
              </svg>
              <strong className="home-command-health-score">{commandHealthScore}%</strong>
            </div>
            <div className="home-command-health-copy">
              <span>Operating health</span><em>{commandHealthLabel}</em>
              <p>{workspaceBlockedRecords.length} blocked · {workspaceOverdueRecords.length} overdue · {workspaceApprovalQueue.length} decisions</p>
            </div>
          </div>
        </section>
        <section className="home-command-metrics" aria-label={`${homeScope === "team" ? "Team" : "Personal"} operating metrics`}>
          {commandMetrics.map((metric) => <button className={metric.tone ? `is-${metric.tone}` : ""} key={metric.label} onClick={metric.action} type="button"><span>{metric.label}</span><strong>{metric.value}</strong><small>{metric.note}</small></button>)}
        </section>
        <div className="home-command-main-grid">
          <section className="functional-panel home-command-focus-panel">
            <div className="functional-panel-header">
              <div>
                <label className="home-focus-select">
                  <ExpandMoreOutlined aria-hidden="true" fontSize="small" />
                  <span>{selectedHomeFocusOption.label}</span>
                  <select aria-label="Choose focus period" onChange={(event) => setHomeFocusRangePreference(event.target.value)} value={selectedHomeFocusRange}>
                    {homeFocusOptions.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
                  </select>
                </label>
                <h3>What needs attention</h3>
              </div>
            </div>
            <div className="home-action-list">
              {commandFocusItems.map((record) => {
                const projectName = getHomeProjectDisplayName(record);
                const primaryMeta = projectName || (isOverviewTicket(record) ? "No project" : record.moduleName || "Workspace");
                const metaParts = [
                  primaryMeta,
                  record.due ? `Due ${record.due}` : record.owner || "No owner",
                ];
                return (
                  <button aria-label={`View ${record.title || "Untitled work item"}`} className={`home-action-row is-${record.actionTone || "normal"}`} key={record.id || `${record.title}-${record.actionNote}`} onClick={() => openOverviewItem(record)} type="button">
                    <WorkTypeMark type={record.type} />
                    <div><strong>{record.title || "Untitled work item"}</strong><small>{metaParts.filter(Boolean).join(" · ")}</small></div>
                    <em>{record.actionNote}</em>
                    <b aria-hidden="true">→</b>
                  </button>
                );
              })}
              {!commandFocusItems.length ? <div className="home-action-empty"><i aria-hidden="true">✓</i><div><strong>{homeScope === "team" ? `Nothing needs the team's attention ${selectedHomeFocusOption.emptyLabel}` : `Nothing needs your attention ${selectedHomeFocusOption.emptyLabel}`}</strong><span>No urgent assigned work or pending approvals.</span></div></div> : null}
            </div>
          </section>
          <section className="functional-panel home-command-health-panel">
            <div className="functional-panel-header home-work-health-header">
              <div><span>Work health</span><h3>Delivery control board</h3></div>
              <div className="home-work-health-review">
                <button onClick={() => onNavigateModule("Work", "Overview")} type="button">Review</button>
                <small className="home-work-health-total">{workspaceTaskRecords.length} tickets</small>
              </div>
            </div>
            <div className="home-command-health-grid">
              <div><span>Active tasks</span><strong>{workspaceOpenTasks.length}</strong></div>
              <div><span>Due today</span><strong>{workspaceDueToday.length}</strong></div>
              <div><span>Blocked</span><strong>{workspaceBlockedRecords.length}</strong></div>
              <div><span>Unscheduled</span><strong>{workspaceOpenTasks.filter((record) => !record.due).length}</strong></div>
            </div>
            <figure className="home-work-status-chart" aria-label={`${homeScope === "team" ? "Team" : "Personal"} work items by status`}>
              <ul>
                {workspaceStatusCounts.map((lane) => (
                  <li key={lane.key}>
                    <span>{lane.label}</span>
                    <div className="home-work-status-track" aria-hidden="true"><div style={{ width: `${(lane.count / workspaceStatusMax) * 100}%`, backgroundColor: workBoardLaneAccent[lane.key] || "#64748b" }} /></div>
                    <strong>{lane.count}</strong>
                  </li>
                ))}
              </ul>
              {!workspaceTaskRecords.length ? <p className="empty-state">No work items yet.</p> : null}
            </figure>
          </section>
        </div>
        <div className="home-command-secondary-grid">
          {homeScope === "team" ? teamPerformancePanel : null}
          <section className="functional-panel home-command-flow-panel">
            <div className="functional-panel-header"><div><span>Client request flow</span><h3>Intake to closure</h3></div><button onClick={() => onNavigateModule("CRM", "Clients")} type="button">Clients</button></div>
            <div className="home-command-flow">
              {clientFlowRows.map(([label, value], index) => {
                const total = Math.max(1, workspaceClientRequests.length);
                return <div key={label}><span>{label}</span><b aria-hidden="true"><i style={{ width: `${Math.round((value / total) * 100)}%` }} /></b><strong>{value}</strong>{index < clientFlowRows.length - 1 ? <em aria-hidden="true">→</em> : null}</div>;
              })}
            </div>
          </section>
          <section className="functional-panel home-decision-panel">
            <div className="functional-panel-header"><div><span>Approvals</span><h3>Manager, client, QA, and finance decisions</h3></div><button onClick={() => setActiveTab("Approvals")} type="button">Open</button></div>
            {approverError ? <p className="workspace-error-notice">{approverError}</p> : null}
            <div className="home-decision-list">
              {workspaceApprovalQueue.slice(0, 4).map((record) => {
                const isDecided = ["Approved", "Rejected"].includes(record.status);
                const isLeaveDecision = /leave|work from home/i.test(`${record.type} ${record.title}`);
                return <article className="home-decision-row" key={record.id}>
                  <button className="home-decision-main" onClick={() => { setHomeApprovalTab(isLeaveDecision ? "leaves" : "tickets"); setActiveTab("Approvals"); }} type="button"><strong>{isLeaveDecision ? record.owner || "Employee" : record.title}</strong><span>{isLeaveDecision ? String(record.title || "Leave request").split(" · ")[0] : [record.type, record.owner || "Unassigned"].filter(Boolean).join(" · ")}</span></button>
                  <em>{record.status || "Pending"}</em>
                  <small>{record.decisionOwner}</small>
                  {canReviewOverviewRecord(record, currentUser) ? <div className="home-approval-actions"><button disabled={isDecided || approvalDecisionId === record.id} onClick={() => decideApproval(record, "Approved")} type="button">Approve</button><button disabled={isDecided || approvalDecisionId === record.id} onClick={() => decideApproval(record, "Rejected")} type="button">Reject</button></div> : null}
                </article>;
              })}
              {!workspaceApprovalQueue.length ? <p className="empty-state">No approvals are waiting right now.</p> : null}
            </div>
          </section>
          <section className="functional-panel home-command-meetings-panel">
            <div className="functional-panel-header"><div><span>Meetings</span><h3>{todayLabel}</h3></div><button onClick={() => setActiveTab("Calendar")} type="button">Calendar</button></div>
            <div className="home-command-list">{workspaceMeetingsToday.slice(0, 4).map((record) => <button key={record.id || record.title} onClick={() => openOverviewItem(record)} type="button"><strong>{record.title}</strong><span>{[record.type, record.owner || record.location || "Schedule"].filter(Boolean).join(" · ")}</span><em>{record.status || "Today"}</em></button>)}{!workspaceMeetingsToday.length ? <p className="empty-state">No meetings scheduled for today.</p> : null}</div>
          </section>
          <section className="functional-panel home-command-docs-panel">
            <div className="functional-panel-header"><div><span>Docs</span><h3>Knowledge movement</h3></div><button onClick={() => onNavigateModule("Knowledge", "Documents")} type="button">Docs</button></div>
            <div className="home-command-list">{workspaceDocs.slice(0, 4).map((record) => <button key={record.id || record.title} onClick={() => openOverviewItem(record)} type="button"><strong>{record.title}</strong><span>{[record.type || "Document", record.owner].filter(Boolean).join(" · ")}</span><em>{record.status || "Open"}</em></button>)}{!workspaceDocs.length ? <p className="empty-state">No documents have been created yet.</p> : null}</div>
          </section>
          <section className="functional-panel home-command-finance-panel">
            <div className="functional-panel-header"><div><span>Finance signals</span><h3>Billing and payout attention</h3></div><button onClick={() => onNavigateModule("Finance", "Overview")} type="button">Finance</button></div>
            <div className="home-command-health-grid"><div><span>Records</span><strong>{workspaceFinanceRecords.length}</strong></div><div><span>Pending</span><strong>{workspaceFinancePending.length}</strong></div><div><span>Overdue</span><strong>{workspaceFinanceRecords.filter((record) => record.due && record.due < todayKey && !closedStatuses.includes(record.status)).length}</strong></div></div>
            <div className="home-command-list is-compact">{workspaceFinancePending.slice(0, 3).map((record) => <button key={record.id || record.title} onClick={() => openOverviewItem(record)} type="button"><strong>{record.title}</strong><span>{record.type || "Finance"}</span><em>{record.status || "Open"}</em></button>)}{!workspaceFinancePending.length ? <p className="empty-state">No pending finance signal visible.</p> : null}</div>
          </section>
        </div>
        <div className="home-command-insight-grid">
          <section className="functional-panel home-command-ai-panel">
            <div className="functional-panel-header"><div><span>AI insights</span><h3>Suggested operating moves</h3></div><small>{workspaceDocsInReview.length} docs in review</small></div>
            <div className="home-command-ai-list">{commandInsightRows.map((insight) => <article key={insight}><AutoAwesomeOutlined fontSize="small" /><span>{insight}</span></article>)}</div>
          </section>
          <section className="functional-panel home-command-activity-panel">
            <div className="functional-panel-header"><div><span>Activity</span><h3>{homeScope === "team" ? "Latest team activity" : "Your latest activity"}</h3></div><button onClick={() => setActiveTab("Activity")} type="button">Activity</button></div>
            {renderCompactList(overviewActivityItems, "No recent activity yet.")}
          </section>
        </div>
      </>
    ),
    "My Work": (
      <>
        <section className="functional-metrics home-work-metrics">
          <MetricTile label="Assigned" note="Open work in your queue" value={openTasks.length} />
          <MetricTile label="Due today" note={`${overdueRecords.length} overdue`} value={dueToday.length} />
          <MetricTile label="In review" note="Waiting for a decision" value={myTaskRecords.filter((record) => /review|pending/i.test(record.status)).length} />
          <MetricTile label="Story points" note="Open planned capacity" value={sumStoryPoints(openTasks)} />
        </section>
        <section className="functional-panel">
          <div className="functional-panel-header home-work-header"><div><span>Execution workspace</span><h3>My tickets across projects</h3></div><small>{homeFilteredTaskRecords.length} visible items</small></div>
          <div className="home-work-toolbar">
            <div className="work-active-board-toolbar-actions">
              <div className="work-active-board-panel-anchor" ref={homeFilterPanelRef}>
                <button aria-expanded={homeFilterPanelOpen} className={`work-active-board-panel-trigger ${homeFilterPanelOpen ? "is-open" : ""}`} onClick={() => setHomeFilterPanelOpen((current) => !current)} type="button"><FilterListOutlined fontSize="small" /><span>Filter</span><i>{homeAppliedFilterCount}</i></button>
                {homeFilterPanelOpen ? <div className="work-active-board-panel" role="dialog" aria-label="My Work filters">
                  <aside className="work-active-board-panel-sidebar"><div className="work-active-board-panel-field-list">{homeFilterDefinitions.map(([key, label]) => <button className={activeHomeFilterDefinition[0] === key ? "active" : ""} key={key} onClick={() => setActiveHomeFilterField(key)} type="button"><span>{label}</span>{homeWorkFilters[key].length ? <i>{homeWorkFilters[key].length}</i> : null}</button>)}</div><div className="work-active-board-panel-sidebar-footer"><button disabled={!homeAppliedFilterCount} onClick={() => setHomeWorkFilters({ projects: [], statuses: [], types: [], priorities: [], labels: [], severities: [], teams: [], sprints: [], epics: [], dueBuckets: [], flagged: [] })} type="button">Clear all</button></div></aside>
                  <section className="work-active-board-panel-content"><header><strong>{activeHomeFilterDefinition[1]}</strong><p>Select one or more values to refine your work.</p></header><div className="work-active-board-filter-bulk-actions is-panel"><button disabled={!activeHomeFilterOptions.length} onClick={() => setHomeWorkFilters((current) => ({ ...current, [activeHomeFilterDefinition[0]]: activeHomeFilterOptions.map((option) => typeof option === "string" ? option : option.key) }))} type="button">Select all</button><button disabled={!homeWorkFilters[activeHomeFilterDefinition[0]].length} onClick={() => setHomeWorkFilters((current) => ({ ...current, [activeHomeFilterDefinition[0]]: [] }))} type="button">Clear all</button></div><div className="work-active-board-panel-options">{activeHomeFilterOptions.length ? activeHomeFilterOptions.map((option) => { const value = typeof option === "string" ? option : option.key; const label = typeof option === "string" ? option : option.label; const selected = homeWorkFilters[activeHomeFilterDefinition[0]].includes(value); return <button className={selected ? "active" : ""} key={value} onClick={() => toggleHomeWorkFilter(activeHomeFilterDefinition[0], value)} type="button"><i className="work-active-board-option-check" aria-hidden="true">{selected ? "✓" : ""}</i><span>{label}</span></button>; }) : <span className="work-active-board-filter-empty">No options</span>}</div></section>
                </div> : null}
              </div>
              <details className="work-board-toolbar-dropdown work-board-toolbar-dropdown--inline"><summary aria-label="Sort My Work" title="Sort My Work"><SortOutlined fontSize="small" /></summary><div><span>Sort My Work</span><button onClick={() => setHomeSortKey("updated_desc")} type="button">Last updated</button><button onClick={() => setHomeSortKey("created_desc")} type="button">Newest created</button><button onClick={() => setHomeSortKey("priority_desc")} type="button">Priority</button><button onClick={() => setHomeSortKey("due_asc")} type="button">Due date</button><button onClick={() => setHomeSortKey("title_asc")} type="button">Title A–Z</button></div></details>
            </div>
            <div aria-label="Choose work view" className="home-work-view-toggle" role="tablist"><button aria-selected={homeWorkView === "kanban"} className={homeWorkView === "kanban" ? "active" : ""} onClick={() => setHomeWorkView("kanban")} role="tab" type="button">Kanban</button><button aria-selected={homeWorkView === "list"} className={homeWorkView === "list" ? "active" : ""} onClick={() => setHomeWorkView("list")} role="tab" type="button">List</button><button aria-selected={homeWorkView === "gantt"} className={homeWorkView === "gantt" ? "active" : ""} onClick={() => setHomeWorkView("gantt")} role="tab" type="button">Gantt</button></div>
          </div>
          {approverError ? <p className="workspace-error-notice">{approverError}</p> : null}
          {homeWorkView === "kanban" ? <WorkKanbanView ariaLabel="My work board" getAssigneeProfile={getHomeBoardAssigneeProfile} getDateLabel={formatHomeBoardDate} laneAccentMap={workBoardLaneAccent} lanes={homeBoardLanes} onOpenRecord={openHomeWorkRecord} renderTypeMark={(type) => <WorkTypeMark type={type} />} sortRecords={sortHomeWorkRecords} /> : homeWorkView === "gantt" ? <WorkGanttView emptyText="Set a start date or due date on your work items to view the timeline." onOpenRecord={openHomeWorkRecord} records={sortHomeWorkRecords(homeFilteredTaskRecords)} /> : <WorkListView emptyText="No work items match these filters." onOpenRecord={openHomeWorkRecord} openActionLabel="Open" records={sortHomeWorkRecords(homeFilteredTaskRecords)} />}
        </section>
        <div className="general-grid">
          <section className="functional-panel"><div className="functional-panel-header"><div><span>Priority queue</span><h3>Next work to move</h3></div><small>{priorityQueue.length} items</small></div>{renderCompactList(priorityQueue, "Your work queue is clear.")}</section>
          <section className="functional-panel"><div className="functional-panel-header"><div><span>Delivery risk</span><h3>Blocked and overdue</h3></div><small>{blockedRecords.length + overdueRecords.length} signals</small></div>{renderCompactList([...blockedRecords, ...overdueRecords].slice(0, 8), "No delivery risks detected.")}</section>
        </div>
      </>
    ),
    Calendar: (
      <>
        <HomeCalendarPlanner calendarRecords={calendarRecords} taskRecords={taskRecords} userName={userName} />
        <div className="general-grid">
          <section className="functional-panel"><div className="functional-panel-header"><div><span>Today</span><h3>Time-sensitive commitments</h3></div><small>{dueToday.length} due</small></div>{renderCompactList(sortByDue([...dueToday, ...todayCalendarRecords]).slice(0, 8), "No time-sensitive commitments today.")}</section>
          <section className="functional-panel"><div className="functional-panel-header"><div><span>Unscheduled work</span><h3>Items without a delivery date</h3></div><small>{taskRecords.filter((record) => !record.due && !closedStatuses.includes(record.status)).length} items</small></div>{renderCompactList(taskRecords.filter((record) => !record.due && !closedStatuses.includes(record.status)).slice(0, 8), "Every open work item has a due date.")}</section>
        </div>
      </>
    ),
    Approvals: (
      <>
        <section className="functional-metrics home-approval-metrics">
          <MetricTile label="Pending leaves" note="Leave and WFH requests awaiting a decision" value={approvalQueue.filter((record) => /leave|work from home/i.test(`${record.type} ${record.title}`) && !["Approved", "Rejected"].includes(record.status)).length} />
          <MetricTile label="My pending leaves" note="Your submitted leave requests" value={approvalQueue.filter((record) => /leave|work from home/i.test(`${record.type} ${record.title}`) && String(record.owner || "").toLowerCase() === String(currentUser?.name || "").toLowerCase() && !["Approved", "Rejected"].includes(record.status)).length} />
          <MetricTile label="Ready for my review" note="Requests routed to you" value={approvalQueue.filter((record) => [currentUser?.name, currentUser?.email, currentUser?.workspaceRole, currentUser?.accessLevel, currentUser?.role].filter(Boolean).some((identity) => String(identity).toLowerCase() === String(record.decisionOwner || "").toLowerCase())).length} />
          <MetricTile label="Needs routing" note="Requests without an assigned approver" value={approvalQueue.filter((record) => record.approvalRole === "Workspace owner" && /leave|work from home/i.test(`${record.type} ${record.title}`)).length} />
        </section>
        <section className="functional-panel home-approval-panel">
          {(() => {
            const leaveApprovals = approvalQueue.filter((record) => /leave|work from home/i.test(`${record.type} ${record.title}`));
            const ticketApprovals = approvalQueue.filter((record) => !leaveApprovals.includes(record));
            const activeApprovals = homeApprovalTab === "leaves" ? leaveApprovals : ticketApprovals;
            return <>
              <div className="functional-panel-header"><div><span>Decision queue</span><h3>{homeApprovalTab === "leaves" ? "Leave approvals" : "Ticket flow approvals"}</h3></div><small>{activeApprovals.length} visible decisions</small></div>
              <div className="approval-list-tabs" role="tablist"><button aria-selected={homeApprovalTab === "leaves"} className={homeApprovalTab === "leaves" ? "active" : ""} onClick={() => setHomeApprovalTab("leaves")} role="tab" type="button">Leaves <span>{leaveApprovals.length}</span></button><button aria-selected={homeApprovalTab === "tickets"} className={homeApprovalTab === "tickets" ? "active" : ""} onClick={() => setHomeApprovalTab("tickets")} role="tab" type="button">Ticket flow <span>{ticketApprovals.length}</span></button></div>
              {approverError ? <p className="workspace-error-notice">{approverError}</p> : null}
              <div className="home-approval-list">
                {activeApprovals.map((record) => {
                  const isDecided = ["Approved", "Rejected"].includes(record.status);
                  return <article className={homeApprovalTab === "leaves" ? "home-leave-approval" : ""} key={record.id}>
                    <div><strong>{homeApprovalTab === "leaves" ? record.owner || "Employee" : record.title}</strong><span>{homeApprovalTab === "leaves" ? `${String(record.title || "Leave request").split(" · ")[0]} · ${record.description || record.due || "Dates not provided"}` : `${record.type} · requested by ${record.owner || "Unassigned"}`}</span></div>
                    <small>{record.decisionOwner}</small><em>{record.status}</em>
                    {record.approvalRole === "Workspace owner" ? <label className="home-approver-picker"><span>Approver</span><input disabled={approverSavingId === record.id} list={`approvers-${record.id}`} onChange={(event) => assignLeaveApprover(record, event.target.value)} placeholder="Search user" /><datalist id={`approvers-${record.id}`}>{approvalPeople.filter((person) => person.id !== approvalPeople.find((item) => item.name === record.owner)?.id && !["Disabled", "Exited"].includes(person.status)).map((person) => <option key={person.id} value={person.name}>{person.email}</option>)}</datalist></label> : null}
                    {canReviewOverviewRecord(record, currentUser) ? <div className="home-approval-actions"><button disabled={isDecided || approvalDecisionId === record.id} onClick={() => decideApproval(record, "Approved")} type="button">Approve</button><button disabled={isDecided || approvalDecisionId === record.id} onClick={() => decideApproval(record, "Rejected")} type="button">Reject</button></div> : null}
                  </article>;
                })}
                {!activeApprovals.length ? <p className="empty-state">No {homeApprovalTab === "leaves" ? "leave" : "ticket flow"} approvals require attention.</p> : null}
              </div>
            </>;
          })()}
        </section>
      </>
    ),
    Activity: (
      <>
        <section className="functional-panel home-activity-panel"><div className="functional-panel-header"><div><span>Workspace timeline</span><h3>Recent movement across your work</h3></div><small>{filteredActivityFeed.length} matching events</small></div><div aria-label="Filter activity" className="approval-list-tabs activity-filter-tabs" role="tablist">{activityFilters.map((filter) => <button aria-selected={activityFilter === filter} className={activityFilter === filter ? "active" : ""} key={filter} onClick={() => setActivityFilter(filter)} role="tab" type="button"><span>{filter}</span><small>{activityCountForFilter(filter)}</small></button>)}</div><div aria-live="polite" className="home-activity-timeline">{visibleActivityFeed.map((item) => <button className="activity-record-link" key={item.id || `${item.title}-${item.at}`} onClick={() => onNavigateModule(/people/i.test(item.searchText) ? "People" : /project/i.test(item.searchText) ? "Projects" : /setting|administration|permission|security|branding|preference|integration/.test(item.searchText) ? "Administration" : "Work", /people/i.test(item.searchText) ? "Leaves" : /project/i.test(item.searchText) ? "Overview" : /setting|administration|permission|security|branding|preference|integration/.test(item.searchText) ? "Settings" : "Tickets")} type="button"><i aria-hidden="true" /><div><strong>{item.title}</strong><span>{item.meta}</span></div><small>{item.at}</small><em>{item.tag}</em></button>)}{!filteredActivityFeed.length ? <p className="empty-state">No {activityFilter === "All" ? "activity" : `${activityFilter.toLowerCase()} activity`} matches this filter.</p> : null}</div>{visibleActivityFeed.length < filteredActivityFeed.length ? <button className="home-activity-load-more" onClick={() => setActivityLimit((limit) => limit + homeActivityPageSize)} type="button">Load {Math.min(homeActivityPageSize, filteredActivityFeed.length - visibleActivityFeed.length)} more</button> : null}</section>
        <div className="general-grid"><section className="functional-panel"><div className="functional-panel-header"><div><span>Work movement</span><h3>Recently completed</h3></div><small>{completedTasks.length} total</small></div>{renderCompactList(completedTasks.slice(0, 6), "No completed work yet.")}</section><section className="functional-panel"><div className="functional-panel-header"><div><span>Attention</span><h3>Latest blocked work</h3></div><small>{blockedRecords.length} blocked</small></div>{renderCompactList(blockedRecords.slice(0, 6), "No blocked work right now.")}</section></div>
      </>
    ),
    Team: teamPerformancePanel,
    Capacity: canManageTeam ? (
      <section className="functional-panel home-capacity-panel">
        <div className="functional-panel-header"><div><span>Capacity</span><h3>Workload and coverage across the team</h3></div><small>{homeTeamDashboard?.metrics?.open || 0} open items</small></div>
        <section className="functional-metrics home-capacity-metrics"><MetricTile label="Unassigned" note="Needs an owner" value={homeTeamDashboard?.metrics?.unassigned || 0} /><MetricTile label="Blocked" note="Needs intervention" value={homeTeamDashboard?.metrics?.blocked || 0} /><MetricTile label="Overdue" note="Past target date" value={homeTeamDashboard?.metrics?.overdue || 0} /><MetricTile label="On leave" note="Coverage impact today" value={homeTeamDashboard?.metrics?.onLeave || 0} /></section>
        {homeTeamDashboardLoading ? <p className="empty-state"><WorkspaceInlineLoader label="Loading capacity" /></p> : <div className="home-capacity-list">{(homeTeamDashboard?.members || []).map((member) => <div key={member.id}><span>{member.name}</span><b><i style={{ width: `${Math.min(100, member.openCount * 16)}%` }} /></b><small>{member.openCount} open · {member.attendance}</small></div>)}{!(homeTeamDashboard?.members || []).length ? <p className="empty-state">No capacity data is available yet.</p> : null}</div>}
      </section>
    ) : null,
    Clients: canManageTeam ? (
      <section className="functional-panel home-team-panel">
        <div className="functional-panel-header"><div><span>Client success</span><h3>Client relationships</h3></div><small>{approvalPeople.filter((person) => person.employmentType === "Client").length} clients</small></div>
        <div className="home-team-member-list">{approvalPeople.filter((person) => person.employmentType === "Client").map((client) => <article key={client.id}><div><WorkspaceUserAvatar user={client} /><span><strong>{client.name}</strong><small>{[client.designation || "Client", client.projectRefs?.join(", ") || "No project"].filter(Boolean).join(" · ")}</small></span></div><div><em>{client.status}</em><small>{client.projectRefs?.length || 0} projects</small></div></article>)}{!approvalPeople.filter((person) => person.employmentType === "Client").length ? <p className="empty-state">Add client members in People to manage their project relationships here.</p> : null}</div>
      </section>
    ) : null,
  };

  return (
    <div className="functional-workspace general-workspace">
      <WorkspaceErrorNotice message={error} />
      {homeTeamDashboardError ? <WorkspaceErrorNotice message={homeTeamDashboardError} /> : isLoading || homeTeamDashboardLoading || homeTeamDashboard?.scope !== homeScope
        ? <p className="empty-state"><WorkspaceInlineLoader label="Loading your overview" /></p>
        : tabContent[activeTab] || tabContent.Overview}
    </div>
  );
}

function ProjectSettingsPanel({
  activeSection = "appearance",
  projectBranding = {},
  projectId,
  projectName,
  projectSlug,
  projectRecord = {},
  workspaceColor = "#7c3aed",
  workspacePeople = [],
  onSave,
  onSlugChange,
  onUpdateProjectPerson = async () => {},
  onProjectPersonUpdated = () => {},
}) {
  const [section, setSection] = useState("appearance");
  const [slug, setSlug] = useState(projectSlug || "");
  const [branding, setBranding] = useState({
    iconUrl: "",
    faviconIcoUrl: "",
    colorThemeMode: "default",
    primaryColor: "#7c3aed",
  });
  const [governance, setGovernance] = useState({
    roles: normalizeProjectRoles(),
    permissions: normalizeProjectPermissions(),
    workflow: createDefaultProjectWorkflow(),
    clientPortalEnabled: false,
    portalRoles: defaultClientPortalRoles,
  });
  const [saving, setSaving] = useState(false);
  const [accessSavingId, setAccessSavingId] = useState("");
  const [uploadingField, setUploadingField] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => setSection(activeSection || "appearance"), [activeSection]);
  useEffect(() => setSlug(projectSlug || ""), [projectSlug]);
  useEffect(() => {
    setBranding({
      iconUrl: projectBranding?.icon_url || "",
      faviconIcoUrl: projectBranding?.favicon_ico_url || projectBranding?.favicon_url || "",
      colorThemeMode: projectBranding?.color_theme_mode || "default",
      primaryColor: projectBranding?.primary_color || "#7c3aed",
    });
  }, [projectBranding?.color_theme_mode, projectBranding?.favicon_ico_url, projectBranding?.favicon_url, projectBranding?.icon_url, projectBranding?.primary_color]);
  useEffect(() => {
    setGovernance({
      roles: normalizeProjectRoles(projectRecord?.projectRoles),
      permissions: normalizeProjectPermissions(projectRecord?.projectPermissions),
      workflow: normalizeProjectWorkflow(projectRecord?.projectWorkflow),
      clientPortalEnabled: projectRecord?.clientPortalEnabled === true,
      portalRoles: Array.isArray(projectRecord?.portalRoles) && projectRecord.portalRoles.length ? projectRecord.portalRoles : defaultClientPortalRoles,
    });
  }, [
    projectRecord?.clientPortalEnabled,
    projectRecord?.id,
    projectRecord?.portalRoles,
    projectRecord?.projectPermissions,
    projectRecord?.projectRoles,
    projectRecord?.projectWorkflow,
    projectRecord?.updatedAt,
  ]);

  const handleAssetUpload = async (field, file) => {
    if (!file) return;
    setMessage({ type: "", text: "" });
    setUploadingField(field);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setBranding((current) => ({ ...current, [field]: dataUrl }));
    } catch (error) {
      setMessage({ type: "error", text: error?.message || "Could not read selected file." });
    } finally {
      setUploadingField("");
    }
  };

  if (!projectId) return <section className="functional-panel"><p className="empty-state">Select a project from the project switcher to manage its settings.</p></section>;

  const iconLabel = uploadingField === "iconUrl" ? "Preparing icon..." : branding.iconUrl ? "Replace icon" : "Upload icon";
  const faviconLabel = uploadingField === "faviconIcoUrl" ? "Preparing favicon..." : branding.faviconIcoUrl ? "Replace favicon" : "Upload favicon";
  const colorIsProject = branding.colorThemeMode === "project";
  const workspaceAccent = /^#[0-9a-fA-F]{6}$/.test(workspaceColor || "") ? workspaceColor : "#7c3aed";
  const projectAccent = /^#[0-9a-fA-F]{6}$/.test(branding.primaryColor || "") ? branding.primaryColor : "#7c3aed";
  const activeRoles = normalizeProjectRoles(governance.roles).filter((role) => role.archived !== true);
  const findProjectAssignment = (person) => (person?.projectAssignments || []).find((assignment) => {
    const assignmentProjectId = `${assignment.projectId || ""}`;
    const assignmentProjectRef = `${assignment.projectRef || ""}`.trim().toLowerCase();
    return (projectId && assignmentProjectId === `${projectId}`) || (projectName && assignmentProjectRef === `${projectName}`.trim().toLowerCase());
  }) || null;
  const hasProjectAccess = (assignment) => assignment && !["Disabled", "Exited"].includes(assignment.status || "Active");
  const saveProjectSettings = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const updated = await onSave(projectId, {
        projectSlug: slug,
        projectBranding: branding,
        projectRoles: normalizeProjectRoles(governance.roles),
        projectPermissions: normalizeProjectPermissions(governance.permissions),
        projectWorkflow: normalizeProjectWorkflow(governance.workflow),
        clientPortalEnabled: governance.clientPortalEnabled,
        portalRoles: governance.clientPortalEnabled ? governance.portalRoles : [],
      });
      const nextSlug = updated?.projectSlug || projectSlug;
      onSlugChange(nextSlug);
      setMessage({ type: "success", text: "Project settings saved." });
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || "Could not save project settings." });
    } finally {
      setSaving(false);
    }
  };
  const updateProjectRole = (index, patch) => setGovernance((current) => {
    const roles = normalizeProjectRoles(current.roles);
    roles[index] = { ...roles[index], ...patch };
    return { ...current, roles };
  });
  const toggleRolePermission = (index, permission) => setGovernance((current) => {
    const roles = normalizeProjectRoles(current.roles);
    const currentPermissions = new Set(roles[index]?.permissions || []);
    if (currentPermissions.has(permission)) currentPermissions.delete(permission);
    else currentPermissions.add(permission);
    roles[index] = { ...roles[index], permissions: Array.from(currentPermissions) };
    return { ...current, roles };
  });
  const updateWorkflowState = (index, patch) => setGovernance((current) => {
    const workflow = normalizeProjectWorkflow(current.workflow);
    workflow.states[index] = { ...workflow.states[index], ...patch };
    workflow.states = workflow.states.map((state, stateIndex) => ({
      ...state,
      order: stateIndex,
      isInitial: patch.isInitial && stateIndex !== index ? false : state.isInitial,
    }));
    return { ...current, workflow };
  });
  const handleProjectPersonUpdate = async (person, patch) => {
    setAccessSavingId(person.id);
    setMessage({ type: "", text: "" });
    try {
      const assignment = findProjectAssignment(person);
      const nextRole = patch.workspaceRole || assignment?.workspaceRole || activeRoles[0]?.name || person.workspaceRole || "Contributor";
      const nextStatus = patch.status || assignment?.status || "Active";
      const data = await onUpdateProjectPerson(person.id, {
        projectContext: true,
        projectIds: [projectId],
        projectRefs: [projectName || projectSlug || ""],
        workspaceRole: nextRole,
        status: nextStatus,
      });
      onProjectPersonUpdated(data?.person);
      setMessage({ type: "success", text: `${person.name || "Person"} project access updated.` });
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Could not update project access." });
    } finally {
      setAccessSavingId("");
    }
  };

  return <section className="functional-panel project-settings-panel" aria-label="Project settings">
    <div className="functional-panel-header"><div><span>Project settings</span><h3>{projectName || "Selected project"}</h3></div><small>Access, roles, workflow, and project identity</small></div>
    <form className="project-settings-form" onSubmit={saveProjectSettings}>
      {section === "appearance" ? <>
        <div className="project-settings-identity">
          <label>Project URL slug<input autoComplete="off" disabled={saving} onChange={(event) => setSlug(event.target.value)} placeholder="client-onboarding-2026" value={slug} /></label>
          <p className="project-settings-helper">Used in Work, tickets, comments, and every project tab.</p>
        </div>
        <div className="project-branding-studio" style={{ "--project-accent": colorIsProject ? projectAccent : workspaceAccent, "--workspace-accent": workspaceAccent }}>
          <div className="project-branding-preview">
            <div className="project-branding-icon-frame">
              {branding.iconUrl ? <img alt="" src={branding.iconUrl} /> : <span aria-hidden="true">{(projectName || "P").slice(0, 1).toUpperCase()}</span>}
            </div>
            <div><strong>{projectName || "Project"}</strong><span>/{slug || projectSlug || "project-url"}</span></div>
          </div>
          <div className="project-branding-controls">
            <div className="project-asset-actions">
              <label className="project-asset-button">{iconLabel}<input accept="image/png,image/jpeg,image/webp,image/svg+xml" disabled={saving || uploadingField === "iconUrl"} onChange={(event) => handleAssetUpload("iconUrl", event.target.files?.[0])} type="file" /></label>
              <label className="project-asset-button is-secondary">{faviconLabel}<input accept=".ico,image/x-icon,image/vnd.microsoft.icon,image/png" disabled={saving || uploadingField === "faviconIcoUrl"} onChange={(event) => handleAssetUpload("faviconIcoUrl", event.target.files?.[0])} type="file" /></label>
            </div>
            <div className="project-color-mode" aria-label="Color source">
              <button className={!colorIsProject ? "active" : ""} disabled={saving} onClick={() => setBranding((current) => ({ ...current, colorThemeMode: "default" }))} type="button"><i aria-hidden="true" style={{ background: workspaceAccent }} />Workspace</button>
              <button className={colorIsProject ? "active" : ""} disabled={saving} onClick={() => setBranding((current) => ({ ...current, colorThemeMode: "project" }))} type="button"><i aria-hidden="true" style={{ background: projectAccent }} />Project</button>
            </div>
            {colorIsProject ? <label className="project-color-picker">
              <span>Accent</span>
              <input disabled={saving} onChange={(event) => setBranding((current) => ({ ...current, primaryColor: event.target.value }))} type="color" value={projectAccent} />
              <strong>{projectAccent}</strong>
            </label> : <div className="project-workspace-color-note"><i aria-hidden="true" style={{ background: workspaceAccent }} /><span>Workspace colour is active</span></div>}
          </div>
        </div>
      </> : null}
      {section === "access" ? <div className="project-settings-grid">
        {workspacePeople.length ? workspacePeople.map((person) => {
          const assignment = findProjectAssignment(person);
          const hasAccess = hasProjectAccess(assignment);
          return <article className="project-access-row" key={person.id || person.email}>
            <div><strong>{person.name || person.email}</strong><span>{person.email}</span></div>
            <select disabled={accessSavingId === person.id || !hasAccess} onChange={(event) => handleProjectPersonUpdate(person, { workspaceRole: event.target.value, status: "Active" })} value={assignment?.workspaceRole || activeRoles[0]?.name || "Contributor"}>
              {activeRoles.map((role) => <option key={role.id} value={role.name}>{role.name}</option>)}
            </select>
            <button className={hasAccess ? "is-on" : ""} disabled={accessSavingId === person.id} onClick={() => handleProjectPersonUpdate(person, { status: hasAccess ? "Disabled" : "Active" })} type="button">{accessSavingId === person.id ? "Saving" : hasAccess ? "Access on" : "No access"}</button>
          </article>;
        }) : <p className="empty-state">No people have been added to this workspace yet.</p>}
      </div>
      : null}
      {section === "roles" ? <div className="project-role-list">
        {activeRoles.map((role, index) => <article className="project-role-card" key={role.id}>
          <div className="project-role-card-header">
            <input aria-label="Role name" disabled={saving} onChange={(event) => updateProjectRole(index, { name: event.target.value })} value={role.name} />
            <select aria-label="Access level" disabled={saving} onChange={(event) => updateProjectRole(index, { accessLevel: event.target.value })} value={role.accessLevel}>
              {["Owner", "Admin", "Manager", "Member", "Viewer", "Client"].map((level) => <option key={level} value={level}>{level}</option>)}
            </select>
            <button disabled={saving || activeRoles.length <= 1} onClick={() => setGovernance((current) => ({ ...current, roles: normalizeProjectRoles(current.roles).filter((_, roleIndex) => roleIndex !== index) }))} type="button">Remove</button>
          </div>
          <textarea aria-label="Role description" disabled={saving} onChange={(event) => updateProjectRole(index, { description: event.target.value })} value={role.description} />
        </article>)}
        <button className="project-secondary-action" disabled={saving} onClick={() => setGovernance((current) => ({ ...current, roles: [...normalizeProjectRoles(current.roles), { id: `project-role-${Date.now()}`, name: "New project role", accessLevel: "Member", description: "", permissions: ["work.ticket.view"] }] }))} type="button">Add role</button>
      </div> : null}
      {section === "permissions" ? <div className="project-permission-panel">
        <div className="project-permission-controls">
          <label>Project visibility<select disabled={saving} onChange={(event) => setGovernance((current) => ({ ...current, permissions: { ...current.permissions, visibility: event.target.value } }))} value={governance.permissions.visibility}>
            <option value="members">Project members</option>
            <option value="workspace_admins">Workspace admins</option>
            <option value="private">Owner only</option>
          </select></label>
          <label>Analytics formula visibility<select disabled={saving} onChange={(event) => setGovernance((current) => ({ ...current, permissions: { ...current.permissions, formulasVisibleTo: event.target.value } }))} value={governance.permissions.formulasVisibleTo}>
            <option value="owner">Owner only</option>
            <option value="managers">Managers and owner</option>
          </select></label>
        </div>
        <div className="project-permission-matrix">
          {activeRoles.map((role, index) => <article key={role.id}>
            <strong>{role.name}</strong>
            <div>{projectPermissionOptions.map((permission) => <label key={permission.key}><input checked={(role.permissions || []).includes(permission.key)} disabled={saving} onChange={() => toggleRolePermission(index, permission.key)} type="checkbox" />{permission.label}</label>)}</div>
          </article>)}
        </div>
      </div> : null}
      {section === "workflow" ? <div className="project-workflow-panel">
        <label>Project workflow type<select disabled={saving} onChange={(event) => {
          const template = getProjectWorkflowTemplate(event.target.value);
          setGovernance((current) => ({ ...current, workflow: { mode: "template", templateId: template.id, states: cloneWorkflowStates(template.states) } }));
        }} value={normalizeProjectWorkflow(governance.workflow).templateId}>
          {projectWorkflowTemplates.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}
        </select></label>
        <p className="project-settings-helper">{getProjectWorkflowTemplate(normalizeProjectWorkflow(governance.workflow).templateId).description}</p>
        <div className="project-color-mode project-workflow-mode" aria-label="Workflow edit mode">
          <button className={governance.workflow.mode !== "custom" ? "active" : ""} disabled={saving} onClick={() => setGovernance((current) => ({ ...current, workflow: { ...normalizeProjectWorkflow(current.workflow), mode: "template" } }))} type="button">Use template</button>
          <button className={governance.workflow.mode === "custom" ? "active" : ""} disabled={saving} onClick={() => setGovernance((current) => ({ ...current, workflow: { ...normalizeProjectWorkflow(current.workflow), mode: "custom" } }))} type="button">Customize this project</button>
        </div>
        <div className="project-workflow-state-list">
          {normalizeProjectWorkflow(governance.workflow).states.map((state, index) => <article key={`${state.id}-${index}`}>
            <input aria-label="Status name" disabled={saving || governance.workflow.mode !== "custom"} onChange={(event) => updateWorkflowState(index, { name: event.target.value, id: event.target.value })} value={state.name} />
            <select aria-label="Status category" disabled={saving || governance.workflow.mode !== "custom"} onChange={(event) => updateWorkflowState(index, { category: event.target.value })} value={state.category}>
              <option value="backlog">Backlog</option>
              <option value="todo">To do</option>
              <option value="in_progress">In progress</option>
              <option value="review">Review</option>
              <option value="done">Done</option>
            </select>
            <label><input checked={state.isInitial} disabled={saving || governance.workflow.mode !== "custom"} onChange={(event) => updateWorkflowState(index, { isInitial: event.target.checked })} type="checkbox" />Initial</label>
            <button disabled={saving || governance.workflow.mode !== "custom" || governance.workflow.states.length <= 2} onClick={() => setGovernance((current) => ({ ...current, workflow: { ...normalizeProjectWorkflow(current.workflow), mode: "custom", states: normalizeProjectWorkflow(current.workflow).states.filter((_, stateIndex) => stateIndex !== index) } }))} type="button">Remove</button>
          </article>)}
          <button className="project-secondary-action" disabled={saving || governance.workflow.mode !== "custom"} onClick={() => setGovernance((current) => ({ ...current, workflow: { ...normalizeProjectWorkflow(current.workflow), mode: "custom", states: [...normalizeProjectWorkflow(current.workflow).states, { id: `state-${Date.now()}`, name: "New status", category: "todo", order: normalizeProjectWorkflow(current.workflow).states.length, isInitial: false }] } }))} type="button">Add status</button>
        </div>
      </div> : null}
      {section === "portal" ? <div className="project-permission-panel">
        <label className="project-toggle-row"><input checked={governance.clientPortalEnabled} disabled={saving} onChange={(event) => setGovernance((current) => ({ ...current, clientPortalEnabled: event.target.checked }))} type="checkbox" />Enable client portal for this project</label>
        <label>Portal roles<input disabled={saving || !governance.clientPortalEnabled} onChange={(event) => setGovernance((current) => ({ ...current, portalRoles: event.target.value.split(",").map((role) => role.trim()).filter(Boolean) }))} value={(governance.portalRoles || []).join(", ")} /></label>
      </div> : null}
      {uploadingField ? <p className="project-settings-helper">Preparing {uploadingField.replace(/Url$/, "").replace(/[A-Z]/g, (match) => ` ${match.toLowerCase()}`).trim()}...</p> : null}
      <div className="workspace-dialog-actions"><button disabled={saving} type="submit">{saving ? "Saving…" : "Save project settings"}</button></div>
      {message.text ? <p className={`project-settings-status ${message.type}`}>{message.text}</p> : null}
    </form>
  </section>;
}

export function FunctionalWorkspaceView({
  moduleName,
  activeTab,
  setActiveTab = () => {},
  moduleTabs = [],
  moduleAccess = null,
  records,
  selectedProjectName = "",
  selectedProjectId = "",
  selectedProjectSlug = "",
  selectedProjectBranding = {},
  peopleSearchQuery = "",
  activity,
  error = "",
  isLoading = false,
  liveNow,
  onCreateRecord,
  onDeleteRecord,
  onUpdateRecord,
  onOverviewRecordsLoaded,
  onUpdateWorkspaceBranding,
  onRefreshWorkspaceAdministration,
  settingsProps,
  currentUser,
  workspaceAdministration,
  workspaceInfo,
  onNavigateModule = () => {},
  onProjectSlugChange = () => {},
  activeBoardId = "",
  onActiveBoardChange = () => {},
  routeRecordId = "",
  focusedCommentId = "",
  onRecordRouteChange = () => {},
}) {
  const config = getFunctionalConfig(moduleName);
  const aiByokEnabled = workspaceInfo?.aiByokEnabled === true;
  const selectedProjectRecord = records.find((record) => `${record.id || ""}` === `${selectedProjectId || ""}`) || null;
  const workspaceTicketWorkflowConfigs = workspaceAdministration?.ticketWorkflows?.length
    ? workspaceAdministration.ticketWorkflows
    : workTypeOptions.map((type) => ({ workType: type.value, states: primaryTicketWorkflow.map((name, index) => ({ id: name.toLowerCase().replace(/\s+/g, "-"), name, category: ["backlog", "todo", "in_progress", "review", "done"][index], isInitial: name === defaultActionableTicketStatus })) }));
  const selectedProjectWorkflow = normalizeProjectWorkflow(selectedProjectRecord?.projectWorkflow);
  const ticketWorkflowConfigs = selectedProjectWorkflow.mode === "custom" && selectedProjectWorkflow.states.length
    ? workTypeOptions.map((type) => ({ workType: type.value, states: selectedProjectWorkflow.states }))
    : workspaceTicketWorkflowConfigs;
  const getTicketWorkflow = (type) => ticketWorkflowConfigs.find((workflow) => workflow.workType === type)?.states || ticketWorkflowConfigs[0]?.states || [];
  const defaultOwner = currentUser?.name || workspaceOwners[0];
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [formOpen, setFormOpen] = useState(false);
  const [workTypePickerOpen, setWorkTypePickerOpen] = useState(false);
  const [modalError, setModalError] = useState("");
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(() => getInitialWorkspaceDraft(moduleName, defaultOwner));
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [ticketNavigationStack, setTicketNavigationStack] = useState([]);
  const [recentTicketHistory, setRecentTicketHistory] = useState([]);
  const [ticketHistoryMenu, setTicketHistoryMenu] = useState({ open: false, top: 0, left: 0 });
  const [ticketScopeFilter, setTicketScopeFilter] = useState("active");
  const [ticketTransitioning, setTicketTransitioning] = useState(false);
  const [recordTimeline, setRecordTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState("");
  const [detailDraft, setDetailDraft] = useState(null);
  const [detailAutosaveState, setDetailAutosaveState] = useState({ status: "idle", message: "" });
  const [ticketTitleEditing, setTicketTitleEditing] = useState(false);
  const [ticketTitleDraft, setTicketTitleDraft] = useState("");
  const [ticketDescriptionEditing, setTicketDescriptionEditing] = useState(false);
  const [ticketDescriptionDraft, setTicketDescriptionDraft] = useState("");
  const [sprintGoalEditing, setSprintGoalEditing] = useState(false);
  const [sprintGoalDraft, setSprintGoalDraft] = useState("");
  const [issueActivityTab, setIssueActivityTab] = useState("comments");
  const [commentDraft, setCommentDraft] = useState("");
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [commentAttachmentBusy, setCommentAttachmentBusy] = useState(false);
  const [commentSaving, setCommentSaving] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState("");
  const [editingCommentSavingId, setEditingCommentSavingId] = useState("");
  const [editingCommentDraft, setEditingCommentDraft] = useState("");
  const [replyToCommentId, setReplyToCommentId] = useState("");
  const [replyParentCommentId, setReplyParentCommentId] = useState("");
  const [reactionPickerCommentId, setReactionPickerCommentId] = useState("");
  const [copiedCommentId, setCopiedCommentId] = useState("");
  const [expandedReplyParents, setExpandedReplyParents] = useState({});
  const [expandedCommentBodies, setExpandedCommentBodies] = useState({});
  const [visibleCommentCount, setVisibleCommentCount] = useState(10);
  const [workspacePeople, setWorkspacePeople] = useState([]);
  const [linkedTicketSearch, setLinkedTicketSearch] = useState("");
  const [parentTicketSearch, setParentTicketSearch] = useState("");
  const [parentTicketPickerOpen, setParentTicketPickerOpen] = useState(false);
  const [createOwnerSearch, setCreateOwnerSearch] = useState("");
  const [createOwnerPickerOpen, setCreateOwnerPickerOpen] = useState(false);
  const [subtaskQuickTitle, setSubtaskQuickTitle] = useState("");
  const [pendingLinkedTicket, setPendingLinkedTicket] = useState(null);
  const [epicPopoverOpen, setEpicPopoverOpen] = useState(false);
  const [epicDraft, setEpicDraft] = useState({ title: "", description: "" });
  const [epicCreateError, setEpicCreateError] = useState("");
  const [epicCreateSaving, setEpicCreateSaving] = useState(false);
  const epicPopoverRef = useRef(null);
  const workTypePickerRef = useRef(null);
  const previousModuleTabRef = useRef({ moduleName, activeTab });
  const detailAutosaveTimerRef = useRef(null);
  const detailAutosaveRequestRef = useRef(0);
  const detailAutosaveLastPayloadRef = useRef("");
  const detailDraftRef = useRef(null);
  const ticketDescriptionInputRef = useRef(null);
  const ticketTitleInputRef = useRef(null);
  const ticketHistoryMenuRef = useRef(null);
  const commentAttachmentsRef = useRef([]);
  const previousRouteRecordIdRef = useRef(routeRecordId);
  const ticketWorkflow = getTicketWorkflow(detailDraft?.type || selectedRecord?.type || "Feature");
  const ticketWorkflowStatuses = ticketWorkflow.map((state) => state.name);
  const initialTicketStatus = getDefaultTicketStatus(ticketWorkflow, ticketWorkflowStatuses[0] || defaultActionableTicketStatus);
  const [scheduleCalendarView, setScheduleCalendarView] = useState("month");
  const [scheduleSelectedDate, setScheduleSelectedDate] = useState(() => calendarDateKey(new Date()));
  const [scheduleOwnerFilter, setScheduleOwnerFilter] = useState(defaultOwner || "All");
  const [scheduleTypeFilter, setScheduleTypeFilter] = useState("All");
  const [scheduleRequestScope, setScheduleRequestScope] = useState("pending");
  const [approvalListTab, setApprovalListTab] = useState("submitted");
  const [savedBoards, setSavedBoards] = useState([]);
  const [activeSavedBoard, setActiveSavedBoard] = useState(null);
  const [boardDraft, setBoardDraft] = useState(() => createEmptyWorkBoardDraft());
  const [currentBoardFilters, setCurrentBoardFilters] = useState(null);
  const [boardModalOpen, setBoardModalOpen] = useState(false);
  const [boardDeleteConfirmOpen, setBoardDeleteConfirmOpen] = useState(false);
  const [boardDeleteConfirmation, setBoardDeleteConfirmation] = useState("");
  const [boardBusy, setBoardBusy] = useState(false);
  const [boardError, setBoardError] = useState("");
  const [boardFilterPanelOpen, setBoardFilterPanelOpen] = useState(false);
  const [activeBoardFilterField, setActiveBoardFilterField] = useState("assigneeIds");
  const [boardAssigneeSearch, setBoardAssigneeSearch] = useState("");
  const [showBacklogLane, setShowBacklogLane] = useState(false);
  const [boardSortKey, setBoardSortKey] = useState("updated_desc");
  const [optimisticRecordPatches, setOptimisticRecordPatches] = useState({});
  const [draggedTicketId, setDraggedTicketId] = useState("");
  const [dragLaneKey, setDragLaneKey] = useState("");
  const [projectTaskCreateMode, setProjectTaskCreateMode] = useState(false);
  const boardFilterPanelRef = useRef(null);
  const previousActiveBoardIdRef = useRef(activeBoardId);
  const boardSelectionClearedByCustomFilterRef = useRef(false);
  const activeTicketDetailRef = useRef({ moduleName, recordId: "" });
  const getActiveTicketCommentDraftStorageKey = (recordId = selectedRecord?.id) => {
    if (!recordId || !currentUser?.id || typeof window === "undefined") return "";
    const workspaceSlug = window.location.pathname.split("/").filter(Boolean)[0] || "workspace";
    return getTicketCommentDraftStorageKey({
      workspaceSlug,
      userId: currentUser.id,
      recordId,
    });
  };
  const clearStoredTicketCommentDraft = (recordId = selectedRecord?.id) => {
    const storageKey = getActiveTicketCommentDraftStorageKey(recordId);
    if (!storageKey || typeof window === "undefined") return;
    try {
      window.localStorage.removeItem(storageKey);
    } catch (error) {
      // Ignore browser storage failures.
    }
  };
  const revokeCommentAttachmentPreview = useCallback((attachment) => {
    if (attachment?.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
  }, []);
  const clearCommentAttachments = useCallback(() => {
    setCommentAttachments((current) => {
      current.forEach(revokeCommentAttachmentPreview);
      return [];
    });
  }, [revokeCommentAttachmentPreview]);
  const removeCommentAttachment = useCallback((attachmentId) => {
    setCommentAttachments((current) => {
      const removed = current.find((attachment) => attachment.id === attachmentId);
      revokeCommentAttachmentPreview(removed);
      return current.filter((attachment) => attachment.id !== attachmentId);
    });
  }, [revokeCommentAttachmentPreview]);
  const activeTabConfig = (moduleTabs || []).find((tab) => tab.name === activeTab) || null;
  const moduleIsPremium = moduleAccess?.premium === true;
  const tabIsPremium = activeTabConfig?.premium === true;
  const projectLookupRecords = records.filter(isProjectLikeRecord);
  const getProjectDisplayName = (projectRef) => resolveProjectDisplayName(projectLookupRecords, projectRef) || projectRef;
  function getProjectIdForRef(projectRef) {
    const lookupValue = normalizeProjectLookupValue(projectRef);
    return projectLookupRecords.find((record) => getProjectLookupValues(record).includes(lookupValue))?.id || "";
  }
  activeTicketDetailRef.current = { moduleName, recordId: selectedRecord?.id || "" };
  const canApplyTicketSaveResult = (recordId) => {
    const activeDetail = activeTicketDetailRef.current;
    return activeDetail.moduleName === "Work" && activeDetail.recordId === recordId;
  };

  useEffect(() => {
    commentAttachmentsRef.current = commentAttachments;
  }, [commentAttachments]);

  useEffect(() => () => {
    commentAttachmentsRef.current.forEach(revokeCommentAttachmentPreview);
  }, [revokeCommentAttachmentPreview]);

  useEffect(() => {
    let active = true;
    getWorkspacePeople()
      .then((data) => {
        if (active) setWorkspacePeople(Array.isArray(data.people) ? data.people : []);
      })
      .catch(() => {
        if (active) setWorkspacePeople([]);
      });

    return () => {
      active = false;
    };
  }, [moduleName]);

  useEffect(() => {
    if (moduleName !== "Work" || activeTab !== "Board") return;
    getWorkspaceBoards()
      .then((boards) => {
        setSavedBoards(boards);
        if (activeBoardId) {
          const routedBoard = boards.find((board) => `${board.id}` === `${activeBoardId}`);
          if (routedBoard) {
            setActiveSavedBoard(routedBoard);
            setCurrentBoardFilters(normalizeWorkBoardFilters(routedBoard.filters));
          } else {
            onActiveBoardChange("");
          }
        }
        setBoardError("");
      })
      .catch(() => {
        setSavedBoards([]);
        setBoardError("Unable to load saved boards right now.");
      });
  }, [activeBoardId, activeTab, moduleName, onActiveBoardChange]);

  useEffect(() => {
    if (moduleName !== "Work" || activeTab !== "Board" || !activeBoardId) return;
    const routedBoard = savedBoards.find((board) => `${board.id}` === `${activeBoardId}`);
    if (!routedBoard) return;
    setActiveSavedBoard(routedBoard);
    setCurrentBoardFilters(normalizeWorkBoardFilters(routedBoard.filters));
  }, [activeBoardId, activeTab, moduleName, savedBoards]);

  useEffect(() => {
    const previousActiveBoardId = previousActiveBoardIdRef.current;
    previousActiveBoardIdRef.current = activeBoardId;
    if (moduleName !== "Work" || activeTab !== "Board" || !previousActiveBoardId || activeBoardId) return;
    if (boardSelectionClearedByCustomFilterRef.current) {
      boardSelectionClearedByCustomFilterRef.current = false;
      return;
    }
    setActiveSavedBoard(null);
    setCurrentBoardFilters(null);
  }, [activeBoardId, activeTab, moduleName]);

  useEffect(() => {
    if (!activeSavedBoard?.id) return;
    const refreshedBoard = savedBoards.find((board) => board.id === activeSavedBoard.id);
    if (refreshedBoard) setActiveSavedBoard(refreshedBoard);
  }, [activeSavedBoard?.id, savedBoards]);

  useEffect(() => {
    if (!boardFilterPanelOpen) return undefined;
    const handlePointerDown = (event) => {
      if (boardFilterPanelRef.current?.contains(event.target)) return;
      setBoardFilterPanelOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setBoardFilterPanelOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [boardFilterPanelOpen]);

  useEffect(() => {
    if (!ticketHistoryMenu.open) return undefined;
    const handlePointerDown = (event) => {
      if (ticketHistoryMenuRef.current?.contains(event.target)) return;
      setTicketHistoryMenu((current) => ({ ...current, open: false }));
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setTicketHistoryMenu((current) => ({ ...current, open: false }));
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [ticketHistoryMenu.open]);

  useEffect(() => {
    setDraft(getInitialWorkspaceDraft(moduleName, defaultOwner));
    setQuery("");
    setStatusFilter("All");
    setFormOpen(false);
    setWorkTypePickerOpen(false);
    setModalError("");
    setSelectedRecord(null);
    setTicketNavigationStack([]);
    setDetailDraft(null);
    setIssueActivityTab("comments");
    setCommentDraft("");
    clearCommentAttachments();
    setEditingCommentId("");
    setEditingCommentDraft("");
    setReplyToCommentId("");
    setReplyParentCommentId("");
    setReactionPickerCommentId("");
    setCopiedCommentId("");
    setExpandedCommentBodies({});
    setVisibleCommentCount(10);
    setExpandedReplyParents({});
    setLinkedTicketSearch("");
    setSubtaskQuickTitle("");
    setEpicPopoverOpen(false);
    setEpicDraft({ title: "", description: "" });
    setEpicCreateError("");
    setApprovalListTab("submitted");
    setDraggedTicketId("");
    setDragLaneKey("");
    if (moduleName !== "Work" || activeTab !== "Tickets") setTicketScopeFilter("active");
    if (moduleName !== "Schedule") {
      setScheduleCalendarView("month");
      setScheduleSelectedDate(calendarDateKey(new Date()));
      setScheduleOwnerFilter("All");
      setScheduleTypeFilter("All");
      setScheduleRequestScope("pending");
    } else {
      setScheduleOwnerFilter(activeTab === "Calendar" ? (defaultOwner || "All") : "All");
      setScheduleTypeFilter("All");
      if (activeTab !== "Requests") setScheduleRequestScope("pending");
    }
    if (moduleName !== "Work") {
      setActiveSavedBoard(null);
      setBoardDraft(createEmptyWorkBoardDraft());
      setBoardError("");
      if (activeBoardId) onActiveBoardChange("");
    }
  }, [activeBoardId, activeTab, clearCommentAttachments, defaultOwner, moduleName, onActiveBoardChange]);

  useEffect(() => {
    if (!selectedRecord) {
      setDetailDraft(null);
      setTicketTransitioning(false);
      detailAutosaveLastPayloadRef.current = "";
      setDetailAutosaveState({ status: "idle", message: "" });
      return;
    }
    setReplyToCommentId("");
    setReplyParentCommentId("");
    clearCommentAttachments();
    setEditingCommentId("");
    setEditingCommentDraft("");
    setReactionPickerCommentId("");
    setCopiedCommentId("");
    setLinkedTicketSearch("");
    setTicketTitleEditing(false);
    setTicketDescriptionEditing(false);
    setEpicPopoverOpen(false);
    setEpicDraft({ title: "", description: "" });
    setEpicCreateError("");
    const nextDraft = buildRecordDetailDraft(selectedRecord);
    setDetailDraft(nextDraft);
    setTicketTransitioning(false);
    setTicketTitleDraft(nextDraft.title || "");
    setTicketDescriptionDraft(nextDraft.description || "");
    detailAutosaveLastPayloadRef.current = isWorkItemRecord(selectedRecord)
      ? serializeRecordDetailPayload(buildRecordDetailPayload(nextDraft, getProjectIdForRef))
      : "";
    setDetailAutosaveState({ status: "idle", message: "" });
    // Intentional: the draft should reset only when a different ticket/record is opened.
    // Depending on the full selectedRecord object causes autosave refreshes to wipe local edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecord?.id]);

  useEffect(() => {
    if (!selectedRecord?.id || !isWorkItemRecord(selectedRecord)) return;
    setRecentTicketHistory((current) => [
      selectedRecord,
      ...current.filter((record) => record.id !== selectedRecord.id),
    ].slice(0, 12));
  }, [selectedRecord]);

  useEffect(() => {
    detailDraftRef.current = detailDraft;
  }, [detailDraft]);

  useEffect(() => {
    if (!ticketTitleEditing || !ticketTitleInputRef.current) return;
    const input = ticketTitleInputRef.current;
    input.focus();
    const contentLength = input.value?.length || 0;
    try {
      input.setSelectionRange(contentLength, contentLength);
    } catch (error) {
      // Ignore selection failures on unsupported browsers.
    }
  }, [ticketTitleDraft, ticketTitleEditing]);

  useEffect(() => {
    if (!ticketDescriptionEditing || !ticketDescriptionInputRef.current) return;
    const editor = ticketDescriptionInputRef.current;
    editor.focus();
    const contentLength = editor.value?.length || editor.innerText?.length || 0;
    editor.setSelectionRange?.(contentLength, contentLength);
  }, [ticketDescriptionEditing]);

  useEffect(() => {
    if (!selectedRecord?.id || !isWorkItemRecord(selectedRecord) || !currentUser?.id || typeof window === "undefined") {
      return;
    }
    const workspaceSlug = window.location.pathname.split("/").filter(Boolean)[0] || "workspace";
    const storageKey = getTicketCommentDraftStorageKey({
      workspaceSlug,
      userId: currentUser.id,
      recordId: selectedRecord.id,
    });
    try {
      const savedDraft = window.localStorage.getItem(storageKey) || "";
      setCommentDraft(savedDraft);
    } catch (error) {
      setCommentDraft("");
    }
    // Intentional: restore draft when the active record identity changes for the current user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, selectedRecord?.id]);

  useEffect(() => {
    if (!selectedRecord?.id || !isWorkItemRecord(selectedRecord) || !currentUser?.id || typeof window === "undefined") {
      return;
    }
    const workspaceSlug = window.location.pathname.split("/").filter(Boolean)[0] || "workspace";
    const storageKey = getTicketCommentDraftStorageKey({
      workspaceSlug,
      userId: currentUser.id,
      recordId: selectedRecord.id,
    });
    try {
      if (commentDraft.trim()) {
        window.localStorage.setItem(storageKey, commentDraft);
      } else {
        window.localStorage.removeItem(storageKey);
      }
    } catch (error) {
      // Ignore browser storage failures and keep in-memory draft behavior.
    }
    // Intentional: persist per-user draft for the currently opened record identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentDraft, currentUser?.id, selectedRecord?.id]);

  useEffect(() => {
    if (!selectedRecord?.id || !detailDraft || !isWorkItemRecord(selectedRecord)) return undefined;
    const autosaveRecordId = selectedRecord.id;
    const payload = buildRecordDetailPayload(detailDraft, getProjectIdForRef);
    const serializedPayload = serializeRecordDetailPayload(payload);
    if (serializedPayload === detailAutosaveLastPayloadRef.current) return undefined;

    if (detailAutosaveTimerRef.current) {
      window.clearTimeout(detailAutosaveTimerRef.current);
    }

    setDetailAutosaveState({ status: "saving", message: "Saving changes…" });
    detailAutosaveTimerRef.current = window.setTimeout(async () => {
      const requestId = detailAutosaveRequestRef.current + 1;
      detailAutosaveRequestRef.current = requestId;
      try {
        const updated = await onUpdateRecord?.(autosaveRecordId, payload, { localError: true });
        if (!canApplyTicketSaveResult(autosaveRecordId)) return;
        detailAutosaveLastPayloadRef.current = serializedPayload;
        const currentSerialized = serializeRecordDetailPayload(buildRecordDetailPayload(detailDraftRef.current, getProjectIdForRef));
        if (updated) {
          setSelectedRecord(updated);
        }
        if (currentSerialized === serializedPayload) {
          setDetailAutosaveState({ status: "saved", message: "Saved" });
        } else if (requestId === detailAutosaveRequestRef.current) {
          setDetailAutosaveState({ status: "saving", message: "Saving changes…" });
        }
      } catch (error) {
        if (requestId === detailAutosaveRequestRef.current && canApplyTicketSaveResult(autosaveRecordId)) {
          setDetailAutosaveState({
            status: "error",
            message: error?.response?.data?.message || "Autosave failed. Keep editing to retry.",
          });
        }
      }
    }, 650);

    return () => {
      if (detailAutosaveTimerRef.current) {
        window.clearTimeout(detailAutosaveTimerRef.current);
        detailAutosaveTimerRef.current = null;
      }
    };
    // Intentional: autosave is scoped to the opened record identity and local draft changes.
    // Depending on the refreshed selectedRecord object would re-arm save cycles unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailDraft, onUpdateRecord, selectedRecord?.id]);

  // A record detail view belongs to the tab from which it was opened. Leaving
  // that tab must not leave the old detail screen mounted beneath a newly
  // selected tab (for example, Tickets → Epics).
  useEffect(() => {
    const previous = previousModuleTabRef.current;
    const tabChanged = previous.moduleName !== moduleName || previous.activeTab !== activeTab;
    previousModuleTabRef.current = { moduleName, activeTab };
    if (!selectedRecord || !tabChanged) return;
    setSelectedRecord(null);
    setRecordTimeline(null);
    setTimelineError("");
    setDetailDraft(null);
    setIssueActivityTab("all");
    setCommentDraft("");
    onRecordRouteChange(null);
  }, [activeTab, moduleName, onRecordRouteChange, selectedRecord]);

  useEffect(() => {
    if (!selectedRecord?.id) return undefined;

    let active = true;
    let sessionId = "";

    const loadTimeline = async () => {
      setTimelineLoading(true);
      setTimelineError("");
      try {
        const [timelineData, sessionData] = await Promise.all([
          getWorkspaceRecordTimeline(selectedRecord.id),
          startWorkspaceRecordViewSession(selectedRecord.id),
          pingWorkspaceRecordPresence(selectedRecord.id, { context: "viewing history" }),
        ]);
        if (!active) return;
        sessionId = sessionData.session?.id || "";
        setRecordTimeline(timelineData);
      } catch (error) {
        if (!active) return;
        setTimelineError(error?.response?.data?.message || "Unable to load record history.");
      } finally {
        if (active) setTimelineLoading(false);
      }
    };

    loadTimeline();
    const pingTimer = window.setInterval(() => {
      pingWorkspaceRecordPresence(selectedRecord.id, { context: "viewing history" }).catch(() => {});
    }, 30000);

    return () => {
      active = false;
      window.clearInterval(pingTimer);
      if (sessionId) {
        endWorkspaceRecordViewSession(selectedRecord.id, sessionId).catch(() => {});
      }
    };
  }, [selectedRecord?.id]);

  useEffect(() => {
    if (!epicPopoverOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!epicPopoverRef.current?.contains(event.target)) {
        setEpicPopoverOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [epicPopoverOpen]);

  useEffect(() => {
    if (!workTypePickerOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!workTypePickerRef.current?.contains(event.target)) {
        setWorkTypePickerOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setWorkTypePickerOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [workTypePickerOpen]);

  useEffect(() => {
    if (!reactionPickerCommentId) return undefined;
    const handlePointerDown = (event) => {
      if (!event.target.closest?.(".comment-reaction-picker, .comment-reaction-trigger")) {
        setReactionPickerCommentId("");
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setReactionPickerCommentId("");
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [reactionPickerCommentId]);

  const routeRecordMatchesSelectedProject = useCallback((record = {}) => {
    const selectedProjectRouteKeys = [
      selectedProjectId,
      selectedProjectName,
      selectedProjectSlug,
    ].map((value) => `${value || ""}`.trim().toLowerCase()).filter(Boolean);
    if (!selectedProjectRouteKeys.length) return true;
    const recordProjectKeys = [
      record.projectId,
      record.projectRef,
      record.projectSlug,
      resolveProjectDisplayName(records, record.projectRef, record),
    ].map((value) => `${value || ""}`.trim().toLowerCase()).filter(Boolean);
    return recordProjectKeys.some((key) => selectedProjectRouteKeys.includes(key));
  }, [records, selectedProjectId, selectedProjectName, selectedProjectSlug]);

  useEffect(() => {
    if (!routeRecordId) return;
    const normalizedRouteRecordId = `${routeRecordId || ""}`.trim().toLowerCase();
    const currentRecordRouteKey = `${selectedRecord?.key || ""}`.trim().toLowerCase();
    if (selectedRecord?.id === routeRecordId || (currentRecordRouteKey && currentRecordRouteKey === normalizedRouteRecordId && routeRecordMatchesSelectedProject(selectedRecord))) return;
    const routeRecord = records.find((record) => {
      const exactIdMatch = `${record.id}` === `${routeRecordId}`;
      const exactKeyMatch = `${record.key || ""}`.trim().toLowerCase() === normalizedRouteRecordId;
      return (exactIdMatch || exactKeyMatch) && routeRecordMatchesSelectedProject(record);
    });
    if (routeRecord) {
      setSelectedRecord(routeRecord);
      setRecordTimeline(null);
      setTimelineError("");
    }
  }, [records, routeRecordId, routeRecordMatchesSelectedProject, selectedRecord]);

  useEffect(() => {
    const previousRouteRecordId = previousRouteRecordIdRef.current;
    previousRouteRecordIdRef.current = routeRecordId;
    if (routeRecordId || !previousRouteRecordId || !selectedRecord) return;
    setSelectedRecord(null);
    setRecordTimeline(null);
    setTimelineError("");
    setDetailDraft(null);
    setIssueActivityTab("all");
    setCommentDraft("");
    setTicketHistoryMenu((current) => ({ ...current, open: false }));
  }, [routeRecordId, selectedRecord]);

  useEffect(() => {
    if (!focusedCommentId || !selectedRecord?.id || !recordTimeline) return;
    const comment = document.getElementById(`comment-${focusedCommentId}`);
    if (comment) comment.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusedCommentId, recordTimeline, selectedRecord?.id]);

  useEffect(() => {
    setOptimisticRecordPatches((current) => {
      const next = { ...current };
      let changed = false;
      Object.entries(current).forEach(([recordId, patch]) => {
        const latest = records.find((record) => `${record.id}` === `${recordId}`);
        if (!latest) return;
        const patchApplied = Object.entries(patch || {}).every(([key, value]) => latest?.[key] === value);
        if (patchApplied) {
          delete next[recordId];
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [records]);

  if (moduleIsPremium || tabIsPremium) {
    return (
      <>
        <ModuleTabStrip moduleName={moduleName} activeTab={activeTab} setActiveTab={handleModuleTabChange} tabs={moduleTabs} />
        <section className="functional-panel workspace-premium-panel">
          <div className="functional-panel-header">
            <div>
              <span>Premium feature</span>
              <h3>{tabIsPremium ? `${activeTab} is available in premium` : `${moduleName} is available in premium`}</h3>
            </div>
          </div>
          <p className="empty-state">This area is intentionally visible so workspace clients can discover advanced capabilities. Enable the premium plan for this workspace to unlock full usage, actions, and records here.</p>
        </section>
      </>
    );
  }

  if (moduleName === "My Team") {
    const canManageTeamHome = currentUser?.canManageTeam === true || /\b(owner|admin|manager|lead|hr|human resources)\b/i.test(`${currentUser?.accessLevel || ""} ${currentUser?.workspaceRole || ""}`);
    if (!canManageTeamHome) return <section className="functional-panel"><p className="empty-state">My Team is available to managers, HR, leads, admins, and owners.</p></section>;
    return <><ModuleTabStrip moduleName="My Team" activeTab={activeTab} setActiveTab={handleModuleTabChange} tabs={moduleTabs} /><GeneralFunctionalView activeTab={activeTab} currentUser={currentUser} activity={activity} error={error} forceScope="team" isLoading={isLoading} liveNow={liveNow} onNavigateModule={onNavigateModule} onRecordRouteChange={onRecordRouteChange} onUpdateRecord={onUpdateRecord} onOverviewRecordsLoaded={onOverviewRecordsLoaded} records={records} setActiveTab={setActiveTab} /></>;
  }

  if (moduleName === "General" || moduleName === "Home") {
    return (
      <>
        <ModuleTabStrip moduleName="Home" activeTab={activeTab} setActiveTab={handleModuleTabChange} tabs={moduleTabs} />
        <GeneralFunctionalView
          activeTab={activeTab}
          currentUser={currentUser}
          activity={activity}
          error={error}
          isLoading={isLoading}
          onNavigateModule={onNavigateModule}
          onRecordRouteChange={onRecordRouteChange}
          onUpdateRecord={onUpdateRecord}
          onOverviewRecordsLoaded={onOverviewRecordsLoaded}
          setActiveTab={setActiveTab}
          liveNow={liveNow}
          records={records}
        />
      </>
    );
  }

  if (moduleName === "Dashboard") {
    return (
      <DashboardFunctionalView
        activeTab={activeTab}
        activity={activity}
        currentUser={currentUser}
        error={error}
        isLoading={isLoading}
        liveNow={liveNow}
        onCreateRecord={onCreateRecord}
        onUpdateRecord={onUpdateRecord}
        records={records}
      />
    );
  }

  if (moduleName === "People") {
    return (
      <div className="functional-workspace">
        <PeopleView
          activeTab={activeTab}
          currentUser={currentUser}
          defaultOwner={defaultOwner}
          moduleTabs={moduleTabs}
          onCreateRecord={onCreateRecord}
          onUpdateRecord={onUpdateRecord}
          activity={activity}
          records={records}
          selectedProjectName={selectedProjectName}
          selectedProjectId={selectedProjectId}
          searchQuery={peopleSearchQuery}
          setActiveTab={setActiveTab}
        />
      </div>
    );
  }

  if (moduleName === "Training") {
    return (
      <TrainingDeliveryView
        activeTab={activeTab}
        currentUser={currentUser}
        moduleTabs={moduleTabs}
        records={records}
        selectedProjectId={selectedProjectId}
        selectedProjectName={selectedProjectName}
        setActiveTab={setActiveTab}
        workspaceInfo={workspaceInfo}
      />
    );
  }

  if (moduleName === "Settings") {
    return (
      <>
        <ModuleTabStrip moduleName="Settings" activeTab={activeTab} setActiveTab={handleModuleTabChange} tabs={moduleTabs} />
        <div className="functional-workspace">
          <SettingsView activeTab={activeTab} {...settingsProps} />
        </div>
      </>
    );
  }

  if (moduleName === "Administration") {
    if (["Users", "Roles", "Permissions"].includes(activeTab)) {
      return (
        <>
          <ModuleTabStrip moduleName="Administration" activeTab={activeTab} setActiveTab={handleModuleTabChange} tabs={moduleTabs} />
          <div className="functional-workspace">
            <PeopleView
              activeTab={activeTab === "Users" ? "Directory" : "Roles & Access"}
              currentUser={currentUser}
              defaultOwner={defaultOwner}
              moduleTabs={[activeTab === "Users" ? "Directory" : "Roles & Access"]}
              onCreateRecord={onCreateRecord}
              onUpdateRecord={onUpdateRecord}
              activity={activity}
              records={records}
              selectedProjectName={selectedProjectName}
              selectedProjectId={selectedProjectId}
              setActiveTab={() => {}}
            />
          </div>
        </>
      );
    }
    return (
      <>
        <ModuleTabStrip moduleName="Administration" activeTab={activeTab} setActiveTab={handleModuleTabChange} tabs={moduleTabs} />
        <div className="functional-workspace">
          <AdministrationView
            activeTab={activeTab}
            administration={workspaceAdministration}
            currentUser={currentUser}
            error={error}
            isLoading={isLoading}
            workspaceInfo={workspaceInfo}
            branding={workspaceInfo?.branding}
            brandingSaveStatus={settingsProps?.themeSaveStatus}
            onNavigateModule={onNavigateModule}
            onRefresh={onRefreshWorkspaceAdministration}
            onUpdateBranding={onUpdateWorkspaceBranding}
            onUpdateAdministration={settingsProps?.onUpdateWorkspaceAdministration}
          />
        </div>
      </>
    );
  }

  if (moduleName === "Logout") {
    return (
      <section className="functional-hero">
        <div>
          <span>Session</span>
          <h2>Logout area</h2>
          <p>This screen will end the Workspace session after backend SSO is connected.</p>
        </div>
      </section>
    );
  }

  if (moduleName === "Projects" && projectSettingsTabSections[activeTab]) {
    return <><ModuleTabStrip moduleName={moduleName} activeTab={activeTab} setActiveTab={handleModuleTabChange} tabs={moduleTabs} /><div className="functional-workspace"><ProjectSettingsPanel activeSection={projectSettingsTabSections[activeTab]} onProjectPersonUpdated={(person) => { if (!person?.id) return; setWorkspacePeople((current) => current.map((item) => item.id === person.id ? person : item)); }} onSave={onUpdateRecord} onSlugChange={onProjectSlugChange} onUpdateProjectPerson={updateWorkspacePerson} projectBranding={selectedProjectBranding} projectId={selectedProjectId} projectName={selectedProjectName} projectRecord={selectedProjectRecord} projectSlug={selectedProjectSlug} workspaceColor={workspaceInfo?.branding?.primary_color || workspaceInfo?.branding?.primaryColor || "#7c3aed"} workspacePeople={workspacePeople} /></div></>;
  }

  const compatibleModuleNames = [moduleName, ...(moduleRecordAliases[moduleName] || [])];
  const applyOptimisticRecordPatch = (record) => {
    const patch = optimisticRecordPatches[record?.id];
    return patch ? { ...record, ...patch } : record;
  };
  const allModuleRecords = records.filter((record) => compatibleModuleNames.includes(record.moduleName)).map(applyOptimisticRecordPatch);
  const moduleRecords = allModuleRecords.filter((record) => {
    if (broadTabs.includes(activeTab)) return true;
    if (moduleName === "Projects" && record.status === activeTab) return true;
    if (moduleName === "Work") {
      if (activeTab === "Tickets") return /task|ticket|request|support|bug|defect|incident|improvement|feature|story/i.test(record.type);
      if (activeTab === "Labels") return /task|ticket|request|support|bug|defect|incident|improvement|feature|story/i.test(record.type) && asList(record.labels).length;
      if (activeTab === "Sprints") return /sprint/i.test(record.type) || Boolean(record.sprint);
      if (activeTab === "Epics") return /epic/i.test(record.type) || Boolean(record.epic);
      if (activeTab === "Backlog") return record.status === "Backlog" || /backlog/i.test(record.type);
      if (activeTab === "Approvals") return ["Review", "In Review", "Pending", "Needs Review"].includes(record.status) || /approval/i.test(record.type);
      if (activeTab === "Automation") return /automation|rule|trigger/i.test(`${record.type} ${record.automationTrigger}`);
      if (activeTab === "Templates") return /template/i.test(`${record.type} ${record.templateName}`);
      if (record.tab === activeTab) return true;
    }
    if (record.tab === activeTab) return true;
    if (moduleName === "CRM") {
      if (activeTab === "Leads") return /lead/i.test(record.type) || ["New Lead", "Contacted", "Demo Scheduled", "Proposal Sent", "Negotiation"].includes(record.status);
      if (activeTab === "Clients") return /client/i.test(record.type) || ["Won", "Active"].includes(record.status);
      if (activeTab === "Deals") return /deal|proposal/i.test(record.type);
      if (activeTab === "Follow Ups") return /follow/i.test(record.type);
      if (activeTab === "Contracts") return /contract/i.test(record.type);
    }
    if (moduleName === "Schedule") {
      if (activeTab === "Calendar") return isScheduleEventRecord(record);
      if (activeTab === "Bookings") return isBookingRecord(record);
      if (activeTab === "Availability") return isAvailabilityRecord(record);
      if (activeTab === "Rooms") return isRoomRecord(record);
      if (activeTab === "Requests") return isScheduleRequestRecord(record);
      if (activeTab === "History") return closedStatuses.includes(record.status) || /cancelled|rescheduled/i.test(record.status);
    }
    if (moduleName === "Training") {
      if (activeTab === "Companies") return /company|client/i.test(record.type);
      if (activeTab === "Requests") return /request/i.test(record.type) || ["Requested", "Pending"].includes(record.status);
      if (activeTab === "Programs") return /program/i.test(record.type);
      if (activeTab === "Batches") return /batch/i.test(record.type);
      if (activeTab === "Sessions") return /session|workshop|class/i.test(record.type);
      if (activeTab === "Instructors") return /instructor|trainer/i.test(record.type);
      if (activeTab === "Attendance") return /attendance/i.test(record.type);
      if (activeTab === "Certificates") return /certificate/i.test(record.type) || record.status === "Certificate Ready";
    }
    if (moduleName === "Knowledge") {
      if (activeTab === "Documents") return /document|doc|note/i.test(record.type);
      if (activeTab === "SOPs") return /sop|procedure/i.test(record.type);
      if (activeTab === "Templates") return /template/i.test(record.type);
      if (activeTab === "Policies") return /policy/i.test(record.type);
      if (activeTab === "Client Docs") return /client/i.test(record.type);
      if (activeTab === "Project Docs") return /project/i.test(record.type);
      if (activeTab === "Wiki") return /wiki|reference|faq/i.test(record.type);
      if (activeTab === "Versions") return /version|archive/i.test(record.type) || ["Archived", "Published"].includes(record.status);
    }
    if (moduleName === "Finance") {
      if (activeTab === "Invoices") return /invoice/i.test(record.type);
      if (activeTab === "Payments") return /payment/i.test(record.type) || ["Paid", "Partially Paid", "Overdue"].includes(record.status);
      if (activeTab === "Expenses") return /expense/i.test(record.type);
      if (activeTab === "Payouts") return /payout/i.test(record.type);
      if (activeTab === "Commissions") return /commission/i.test(record.type);
      if (activeTab === "Subscriptions") return /subscription|billing/i.test(record.type);
      if (activeTab === "Payroll") return /payroll|salary|compensation/i.test(record.type);
    }
    if (moduleName === "Marketing") {
      if (activeTab === "Email Marketing") return /email/i.test(record.type);
      if (activeTab === "WhatsApp Campaigns") return /whatsapp/i.test(record.type);
      if (activeTab === "SMS Campaigns") return /sms/i.test(record.type);
      if (activeTab === "Audience & Contacts") return /audience|contact|segment/i.test(record.type);
      if (activeTab === "Consent & Preferences") return /consent|preference|opt.?out/i.test(record.type);
      if (activeTab === "Deliverability") return /deliverability|bounce|unsubscribe/i.test(record.type);
      if (activeTab === "Blogs") return /blog|content/i.test(record.type);
      if (activeTab === "Landing Pages") return /landing|page/i.test(record.type);
      if (activeTab === "SEO") return /seo|open graph|keyword/i.test(record.type);
      if (activeTab === "Campaigns") return /campaign|mailer/i.test(record.type);
      if (activeTab === "Coupons") return /coupon|code/i.test(record.type);
      if (activeTab === "Forms") return /form/i.test(record.type);
      if (activeTab === "Analytics") return /analytics|report/i.test(record.type);
    }
    if (moduleName === "Analytics") {
      if (activeTab === "Exports") return /export|report/i.test(record.type) || record.status === "Exported";
      return `${record.type} ${record.title} ${record.tab}`.toLowerCase().includes(activeTab.toLowerCase());
    }
    return false;
  });
  const filteredRecords = moduleRecords.filter((record) => {
    const matchesSearch = `${record.title} ${record.owner} ${record.type} ${record.sprint || ""} ${record.epic || ""} ${record.accountClient || ""} ${record.endClient || ""} ${asList(record.labels).join(" ")} ${asList(record.portalRoles).join(" ")}`
      .toLowerCase()
      .includes(query.trim().toLowerCase());
    const matchesStatus = statusFilter === "All" || record.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const metricRows = (moduleName === "Work" && activeTab === "Overview" ? [
    { label: "Tickets", note: "Tasks, bugs, requests, and incidents", value: moduleRecords.filter((record) => /task|ticket|request|support|bug|defect|incident|improvement|feature/i.test(record.type)).length },
    { label: "Open incidents", note: "Incidents and defects needing resolution", value: moduleRecords.filter((record) => /bug|defect|incident/i.test(record.type) && !closedStatuses.includes(record.status)).length },
    { label: "Sprints", note: "Planned or active delivery cycles", value: moduleRecords.filter((record) => /sprint/i.test(record.type) || record.sprint).length },
    { label: "Epics", note: "Cross-work initiatives", value: moduleRecords.filter((record) => /epic/i.test(record.type) || record.epic).length },
  ] : moduleName === "Projects" ? [
    { label: "Projects", note: "Delivery engagements", value: moduleRecords.length },
    { label: "End clients", note: "Distinct client companies", value: new Set(moduleRecords.map((record) => record.endClient).filter(Boolean)).size },
    { label: "Client dashboards", note: "Projects with external access", value: moduleRecords.filter((record) => record.clientPortalEnabled).length },
    { label: "Portal members", note: "Invited end-client collaborators", value: moduleRecords.reduce((total, record) => total + Number(record.portalMembersCount || 0), 0) },
  ] : config.metrics.map((metric) => ({
    ...metric,
    value: countByStatuses(moduleRecords, metric.statuses),
  })));
  const noun = config.noun;
  const playbook = modulePlaybooks[moduleName] || {
    signals: ["Open records", "Review queue", "Blocked items", "Completed"],
    actions: [`Create ${noun}`, "Assign owner", "Move status", "Review history"],
    checklist: ["Owner assigned", "Priority set", "Status current", "Due date visible"],
  };
  const createAction = moduleTabCreateActions[`${moduleName}:${activeTab}`]
    || tabCreateActions[activeTab]
    || (moduleName === "Projects" ? ["Create project", "Client Project"] : [config.createLabel, config.types[0]]);
  const currentCreateAction = moduleName === "Work" && activeTab === "Board"
    ? ["Create board", "Board"]
    : createAction;
  const isSprintCreate = moduleName === "Work" && activeTab === "Sprints";
  const isEpicCreate = moduleName === "Work" && activeTab === "Epics";
  const isTicketCreate = (moduleName === "Work" && (currentCreateAction[0] === "Create ticket" || activeTab === "Backlog")) || projectTaskCreateMode;
  const isProjectCreate = moduleName === "Projects";
  const workProjectOptions = Array.from(new Set([
    selectedProjectName,
    draft.projectRef,
    detailDraft?.projectRef,
    ...records
      .filter((record) => record.moduleName === "Projects" || /project/i.test(`${record.type || ""}`))
      .map((record) => record.title),
  ].filter(Boolean))).sort();
  const workSprintOptions = Array.from(new Set([
    draft.sprint,
    detailDraft?.sprint,
    ...allModuleRecords
      .filter((record) => /sprint/i.test(`${record.type || ""}`) || record.sprint)
      .map((record) => record.sprint || record.title),
  ].filter(Boolean))).sort();
  const workEpicOptions = Array.from(new Set([
    draft.epic,
    detailDraft?.epic,
    ...allModuleRecords
      .filter((record) => /epic/i.test(`${record.type || ""}`) || record.epic)
      .map((record) => record.epic || record.title),
  ].filter(Boolean))).sort();
  const activeProjectName = detailDraft?.projectRef || draft.projectRef || selectedProjectName;
  const assignableWorkspacePeople = workspacePeople.filter((person) => !["Disabled", "Exited"].includes(person?.status || "Active"));
  const projectMemberOptions = Array.from(new Set([
    defaultOwner,
    detailDraft?.owner,
    ...asList(detailDraft?.assignees),
    ...assignableWorkspacePeople.map((person) => person.name || person.email),
    ...records.filter((record) => activeProjectName && (record.projectRef === activeProjectName || record.title === activeProjectName)).flatMap((record) => [record.owner, ...asList(record.assignees)]),
  ].filter(Boolean))).sort();
  const cleanMentionHandle = (value = "") => `@${`${value || "user"}`.trim().replace(/^@/, "").replace(/\s+/g, "").replace(/[^A-Za-z0-9._-]/g, "") || "user"}`;
  const getPersonName = (person = {}) => typeof person === "string" ? person : (person.name || person.fullName || person.displayName || person.email || person.handle || "");
  const getPersonKeys = (person = {}) => {
    const profile = typeof person === "string" ? { name: person } : (person || {});
    const name = getPersonName(profile);
    const email = profile.email || profile.workEmail || profile.primaryEmail || "";
    const rawHandle = profile.username || profile.handle || (email ? email.split("@")[0] : "") || name;
    return [
      name,
      email,
      email ? email.split("@")[0] : "",
      rawHandle,
      cleanMentionHandle(rawHandle),
    ].filter(Boolean).map((key) => `${key}`.trim().toLowerCase());
  };
  const workspacePeopleProfiles = [
    currentUser,
    ...workspacePeople,
    ...projectMemberOptions.map((name) => ({ name })),
  ].filter(Boolean).reduce((profiles, person) => {
    const profile = typeof person === "string" ? { name: person } : person;
    const name = getPersonName(profile);
    if (!name) return profiles;
    const existing = getPersonKeys(profile).map((key) => profiles.get(key)).find(Boolean) || {};
    const mergedProfile = { ...existing, ...profile, name: profile.name || name };
    getPersonKeys(mergedProfile).forEach((key) => profiles.set(key, mergedProfile));
    return profiles;
  }, new Map());
  const resolveWorkspacePersonProfile = (person = {}) => {
    const profile = typeof person === "string" ? { name: person } : (person || {});
    const matched = getPersonKeys(profile).map((key) => workspacePeopleProfiles.get(key)).find(Boolean);
    if (matched) return matched;
    const fallbackName = typeof person === "string" ? person.replace(/^@/, "") : getPersonName(profile);
    const name = fallbackName || "Workspace user";
    return { ...profile, name };
  };
  const workspaceBoardPeople = Array.from(workspacePeopleProfiles.values()).reduce((list, person) => {
    const profile = resolveWorkspacePersonProfile(person);
    const userId = profile.userId || profile.user?._id || profile.user?._id?.toString?.() || profile.id || "";
    if (!userId) return list;
    if (list.some((entry) => entry.userId === `${userId}`)) return list;
    list.push({
      userId: `${userId}`,
      name: profile.name || profile.email || "Workspace user",
      email: profile.email || "",
      avatarUrl: profile.avatarUrl || profile.avatar_url || profile.photoUrl || profile.avatar?.url || "",
    });
    return list;
  }, []);
  const workspaceUserOptions = Array.from(new Set([
    defaultOwner,
    ...workspaceOwners,
    ...Array.from(workspacePeopleProfiles.values()).map((person) => getPersonName(person)),
  ].filter(Boolean))).sort((left, right) => left.localeCompare(right));
  const visibleCreateOwnerOptions = workspaceUserOptions.filter((person) => person.toLowerCase().includes(createOwnerSearch.trim().toLowerCase()));
  const selectCreateOwner = (person = "") => {
    setDraft((current) => ({ ...current, owner: person }));
    setCreateOwnerSearch("");
    setCreateOwnerPickerOpen(false);
  };
  const renderCreateOwnerPicker = (label) => (
    <label>
      {label}
      <div className="workspace-user-picker">
        <input
          aria-autocomplete="list"
          aria-controls="workspace-create-owner-results"
          aria-expanded={createOwnerPickerOpen}
          aria-label={`Search ${label.toLowerCase()}`}
          onBlur={() => setCreateOwnerPickerOpen(false)}
          onChange={(event) => setCreateOwnerSearch(event.target.value)}
          onFocus={() => {
            setCreateOwnerSearch("");
            setCreateOwnerPickerOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") setCreateOwnerPickerOpen(false);
            if (event.key === "Enter" && visibleCreateOwnerOptions[0]) {
              event.preventDefault();
              selectCreateOwner(visibleCreateOwnerOptions[0]);
            }
          }}
          placeholder="Search workspace users"
          role="combobox"
          value={createOwnerPickerOpen ? createOwnerSearch : draft.owner || ""}
        />
        {createOwnerPickerOpen ? <div className="workspace-user-picker-results" id="workspace-create-owner-results" role="listbox">
          <button aria-selected={!draft.owner} onMouseDown={(event) => { event.preventDefault(); selectCreateOwner(""); }} role="option" type="button"><span>Unassigned</span></button>
          {visibleCreateOwnerOptions.map((person) => <button aria-selected={draft.owner === person} key={person} onMouseDown={(event) => { event.preventDefault(); selectCreateOwner(person); }} role="option" type="button"><span>{person}</span></button>)}
          {!visibleCreateOwnerOptions.length ? <p>No workspace users match.</p> : null}
        </div> : null}
      </div>
    </label>
  );
  const workBoardTypeOptions = Array.from(new Set([
    ...ticketWorkflowConfigs.map((workflow) => workflow.workType).filter(Boolean),
    ...allModuleRecords
      .filter((record) => /task|ticket|request|support|bug|defect|incident|improvement|feature|story|epic/i.test(`${record.type || ""}`))
      .map((record) => record.type)
      .filter(Boolean),
  ]));
  const inferWorkflowCategory = (status = "") => {
    const normalized = `${status || ""}`.trim().toLowerCase();
    if (!normalized) return "todo";
    if (/backlog|idea|triage|new|needs information/.test(normalized)) return "backlog";
    if (/review|qa|approval|pending|uat|client review/.test(normalized)) return "review";
    if (/progress|doing|active|build|develop|fix|working|blocked|hold|waiting/.test(normalized)) return "in_progress";
    if (/done|closed|resolved|completed|cancelled|duplicate|archived|paid|approved|rejected|published|exported|ready/.test(normalized)) return "done";
    return "todo";
  };
  const getWorkflowCategoryForRecord = (record) => {
    const workflowState = getTicketWorkflow(record.type).find((state) => `${state.name}`.trim().toLowerCase() === `${record.status || ""}`.trim().toLowerCase());
    return workflowState?.category || inferWorkflowCategory(record.status);
  };
  const getTargetStatusForLane = (record, laneKey) => {
    const workflowStates = getTicketWorkflow(record.type);
    const sameCategoryStates = workflowStates.filter((state) => state.category === laneKey);
    if (sameCategoryStates.some((state) => `${state.name}`.trim().toLowerCase() === `${record.status || ""}`.trim().toLowerCase())) {
      return record.status;
    }
    return sameCategoryStates[0]?.name || record.status;
  };
  const buildGeneralizedBoardLanes = (recordsForBoard) => generalizedBoardLanes.map((lane) => {
    const laneRecords = recordsForBoard.filter((record) => getWorkflowCategoryForRecord(record) === lane.key);
    const alignedStatuses = Array.from(new Set(laneRecords.map((record) => record.status).filter(Boolean)));
    return {
      ...lane,
      records: laneRecords,
      statuses: alignedStatuses,
    };
  });
  const resolveBoardAssigneeLabels = (assigneeIds = []) => assigneeIds
    .map((id) => workspaceBoardPeople.find((person) => person.userId === id))
    .filter(Boolean)
    .map((person) => person.name || person.email || "Workspace user");
  const isRecordInDueBucket = (record, bucket) => {
    const todayKey = new Date().toISOString().slice(0, 10);
    if (!record.due) return bucket === "no_due";
    if (bucket === "overdue") return record.due < todayKey && !closedStatuses.includes(record.status);
    if (bucket === "today") return record.due === todayKey;
    if (bucket === "this_week") {
      const today = new Date(`${todayKey}T00:00:00`);
      const dueDate = new Date(`${record.due}T00:00:00`);
      const diffDays = Math.round((dueDate.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
      return diffDays >= 0 && diffDays <= 7;
    }
    if (bucket === "upcoming") return record.due > todayKey;
    return false;
  };
  const getBoardFilterDefinitions = () => ([
    {
      key: "projects",
      label: "Projects",
      options: workProjectOptions.map((project) => ({ value: project, label: project })),
    },
    {
      key: "epics",
      label: "Epics",
      options: workEpicOptions.map((epic) => ({ value: epic, label: epic })),
    },
    {
      key: "sprints",
      label: "Sprints",
      options: workSprintOptions.map((sprint) => ({ value: sprint, label: sprint })),
    },
    {
      key: "statuses",
      label: "Status",
      options: generalizedBoardLanes.map((lane) => ({ value: lane.key, label: lane.label })),
    },
    {
      key: "assigneeIds",
      label: "Assignees",
      options: workspaceBoardPeople.map((person) => ({ value: person.userId, label: person.name || person.email || "Workspace user" })),
    },
    {
      key: "types",
      label: "Ticket types",
      options: workBoardTypeOptions.map((type) => ({ value: type, label: type })),
    },
    {
      key: "priorities",
      label: "Priorities",
      options: workspacePriorities.map((priority) => ({ value: priority, label: priority })),
    },
    {
      key: "severities",
      label: "Severities",
      options: severityOptions.map((severity) => ({ value: severity, label: severity })),
    },
    {
      key: "teams",
      label: "Teams",
      options: workspaceTeamOptions.map((team) => ({ value: team, label: team })),
    },
    {
      key: "dueBuckets",
      label: "Due date",
      options: boardDueBucketOptions.map((bucket) => ({ value: bucket.key, label: bucket.label })),
    },
    {
      key: "flagged",
      label: "Flagged",
      options: workBoardFlagOptions.map((flag) => ({ value: flag, label: flag })),
    },
    {
      key: "labels",
      label: "Labels",
      options: workspaceLabelOptions.map((label) => ({ value: label, label })),
    },
  ]);
  const getBoardFilterSummaryText = (definition, selectedValues = []) => {
    const availableValues = definition.options.map((option) => option.value);
    if (!selectedValues.length || (availableValues.length && selectedValues.length >= availableValues.length)) return `All ${definition.label.toLowerCase()}`;
    const selectedLabels = selectedValues
      .map((value) => definition.options.find((option) => option.value === value)?.label || value)
      .filter(Boolean);
    if (selectedLabels.length <= 2) return selectedLabels.join(", ");
    return `${selectedLabels.slice(0, 2).join(", ")} +${selectedLabels.length - 2}`;
  };
  const getBoardFilterFullText = (definition, selectedValues = []) => {
    const availableValues = definition.options.map((option) => option.value);
    if (!selectedValues.length || (availableValues.length && selectedValues.length >= availableValues.length)) return `All ${definition.label.toLowerCase()}`;
    return selectedValues
      .map((value) => definition.options.find((option) => option.value === value)?.label || value)
      .filter(Boolean)
      .join(", ");
  };
  const updateActiveBoardFilters = async (key, value) => {
    const currentFilters = normalizeWorkBoardFilters(currentBoardFilters || activeSavedBoard?.filters || workBoardDefaultFilters);
    const currentValues = Array.isArray(currentFilters[key]) ? currentFilters[key] : [];
    const isSelected = currentValues.includes(value);
    let nextValues;
    if (isSelected) {
      nextValues = currentValues.filter((item) => item !== value);
    } else {
      nextValues = [...currentValues, value];
    }
    const nextFilters = {
      ...currentFilters,
      [key]: nextValues,
    };
    setCurrentBoardFilters(nextFilters);
    if (activeSavedBoard?.id && !areWorkBoardFiltersEqual(nextFilters, activeSavedBoard.filters)) {
      boardSelectionClearedByCustomFilterRef.current = true;
      setActiveSavedBoard(null);
      onActiveBoardChange("");
    }
  };
  const setActiveBoardFilterValues = (key, values = []) => {
    const currentFilters = normalizeWorkBoardFilters(currentBoardFilters || activeSavedBoard?.filters || workBoardDefaultFilters);
    const nextFilters = {
      ...currentFilters,
      [key]: values,
    };
    setCurrentBoardFilters(nextFilters);
    if (activeSavedBoard?.id && !areWorkBoardFiltersEqual(nextFilters, activeSavedBoard.filters)) {
      boardSelectionClearedByCustomFilterRef.current = true;
      setActiveSavedBoard(null);
      onActiveBoardChange("");
    }
  };
  const renderActiveBoardFilterStrip = (filters) => {
    const definitions = getBoardFilterDefinitions();
    const normalizedFilters = normalizeWorkBoardFilters(filters || workBoardDefaultFilters);
    const appliedDefinitions = definitions.filter((definition) => {
      const selectedValues = Array.isArray(normalizedFilters[definition.key]) ? normalizedFilters[definition.key] : [];
      const availableValues = definition.options.map((option) => option.value);
      if (!selectedValues.length) return false;
      if (!availableValues.length) return true;
      return selectedValues.length < availableValues.length;
    });
    const totalAppliedFields = appliedDefinitions.length;
    const visibleChipDefinitions = appliedDefinitions.slice(0, 4);
    const activeDefinition =
      definitions.find((definition) => definition.key === activeBoardFilterField)
      || visibleChipDefinitions[0]
      || definitions[0]
      || null;
    const activeSelectedValues = activeDefinition ? (Array.isArray(normalizedFilters[activeDefinition.key]) ? normalizedFilters[activeDefinition.key] : []) : [];
    const normalizedAssigneeSearch = boardAssigneeSearch.trim().toLowerCase();
    const visibleActiveOptions = activeDefinition?.key === "assigneeIds" && normalizedAssigneeSearch
      ? activeDefinition.options.filter((option) => option.label.toLowerCase().includes(normalizedAssigneeSearch))
      : (activeDefinition?.options || []);
    const openBoardFilterPanel = (fieldKey = "") => {
      if (fieldKey && definitions.some((definition) => definition.key === fieldKey)) setActiveBoardFilterField(fieldKey);
      else if (!definitions.some((definition) => definition.key === activeBoardFilterField) && definitions[0]) setActiveBoardFilterField(definitions[0].key);
      setBoardFilterPanelOpen(true);
    };
    const getAppliedCount = (definition) => {
      const selectedValues = Array.isArray(normalizedFilters[definition.key]) ? normalizedFilters[definition.key] : [];
      const availableValues = definition.options.map((option) => option.value);
      if (!selectedValues.length) return 0;
      if (availableValues.length && selectedValues.length >= availableValues.length) return 0;
      return selectedValues.length;
    };
    return (
      <div className="work-active-board-filters" aria-label="Board filters">
        <div className="work-active-board-toolbar-row">
          <label className="work-active-board-checkbox">
            <input checked={showBacklogLane} onChange={(event) => setShowBacklogLane(event.target.checked)} type="checkbox" />
            <span>Show backlog</span>
          </label>
          <div className="work-active-board-toolbar-actions">
            <div className="work-active-board-panel-anchor" ref={boardFilterPanelRef}>
              <button
                aria-expanded={boardFilterPanelOpen}
                className={`work-active-board-panel-trigger ${boardFilterPanelOpen ? "is-open" : ""}`}
                onClick={() => setBoardFilterPanelOpen((current) => {
                  if (current) return false;
                  openBoardFilterPanel(activeDefinition?.key || definitions[0]?.key || "");
                  return true;
                })}
                type="button"
              >
                <FilterListOutlined fontSize="small" />
                <span>Filter</span>
                <i>{totalAppliedFields}</i>
              </button>
              {boardFilterPanelOpen ? (
                <div className="work-active-board-panel" role="dialog" aria-label="Board filters">
                  <aside className="work-active-board-panel-sidebar">
                    <div className="work-active-board-panel-field-list">
                      {definitions.map((definition) => {
                        const appliedCount = getAppliedCount(definition);
                        return (
                          <button
                            className={activeDefinition?.key === definition.key ? "active" : ""}
                            key={definition.key}
                            onClick={() => {
                              setActiveBoardFilterField(definition.key);
                              if (definition.key !== "assigneeIds") setBoardAssigneeSearch("");
                            }}
                            type="button"
                          >
                            <span>{definition.label}</span>
                            {appliedCount ? <i>{appliedCount}</i> : null}
                          </button>
                        );
                      })}
                    </div>
                    <div className="work-active-board-panel-sidebar-footer">
                      <button
                        disabled={!totalAppliedFields}
                        onClick={() => setCurrentBoardFilters({ ...workBoardDefaultFilters })}
                        type="button"
                      >
                        Clear all
                      </button>
                    </div>
                  </aside>
                  <section className="work-active-board-panel-content">
                    {activeDefinition ? (
                      <>
                        <header>
                          <strong>{activeDefinition.label}</strong>
                          <p>Select one or more values to refine this board.</p>
                        </header>
                        {activeDefinition.key === "assigneeIds" ? (
                          <label className="work-active-board-panel-search" htmlFor="board-assignee-filter-search">
                            <span>Search assignee</span>
                            <input
                              id="board-assignee-filter-search"
                              onChange={(event) => setBoardAssigneeSearch(event.target.value)}
                              placeholder="Search by name or email"
                              type="search"
                              value={boardAssigneeSearch}
                            />
                          </label>
                        ) : null}
                        <div className="work-active-board-filter-bulk-actions is-panel">
                          <button
                            disabled={boardBusy || !visibleActiveOptions.length}
                            onClick={() => setActiveBoardFilterValues(activeDefinition.key, visibleActiveOptions.map((option) => option.value))}
                            type="button"
                          >
                            Select all
                          </button>
                          <button
                            disabled={boardBusy || !activeSelectedValues.length}
                            onClick={() => setActiveBoardFilterValues(activeDefinition.key, [])}
                            type="button"
                          >
                            Clear all
                          </button>
                        </div>
                        <div className="work-active-board-panel-options">
                          {visibleActiveOptions.length ? visibleActiveOptions.map((option) => {
                            const selected = activeSelectedValues.includes(option.value);
                            const person = activeDefinition.key === "assigneeIds"
                              ? workspaceBoardPeople.find((entry) => entry.userId === option.value)
                              : null;
                            return (
                              <button
                                className={selected ? "active" : ""}
                                disabled={boardBusy}
                                key={`${activeDefinition.key}-${option.value}`}
                                onClick={() => updateActiveBoardFilters(activeDefinition.key, option.value)}
                                type="button"
                              >
                                <i className="work-active-board-option-check" aria-hidden="true">{selected ? "✓" : ""}</i>
                                {person ? <WorkspaceUserAvatar className="work-active-board-option-avatar" title={person.name || person.email || "Workspace user"} user={person} /> : null}
                                <span>{option.label}</span>
                              </button>
                            );
                          }) : <span className="work-active-board-filter-empty">{activeDefinition.key === "assigneeIds" ? "No assignees match the search" : "No options"}</span>}
                        </div>
                      </>
                    ) : (
                      <div className="work-active-board-panel-empty">Select a field to start creating a filter.</div>
                    )}
                  </section>
                </div>
              ) : null}
            </div>
            <details className="work-board-toolbar-dropdown work-board-toolbar-dropdown--inline">
              <summary aria-label="Sort board" title="Sort board"><SortOutlined fontSize="small" /></summary>
              <div>
                <span>Sort board</span>
                <button onClick={() => setBoardSortKey("updated_desc")} type="button">Last updated</button>
                <button onClick={() => setBoardSortKey("created_desc")} type="button">Newest created</button>
                <button onClick={() => setBoardSortKey("priority_desc")} type="button">Priority</button>
                <button onClick={() => setBoardSortKey("due_asc")} type="button">Due date</button>
                <button onClick={() => setBoardSortKey("title_asc")} type="button">Title A–Z</button>
              </div>
            </details>
            {hasCustomBoardFilters ? <button className="work-active-board-inline-action" disabled={boardBusy} onClick={() => {
              setBoardDraft({
                ...createEmptyWorkBoardDraft(),
                filters: normalizeWorkBoardFilters(currentBoardFilters || workBoardDefaultFilters),
              });
              setBoardError("");
              setBoardModalOpen(true);
            }} type="button">Save board</button> : null}
            {activeSavedBoard ? <button className="work-active-board-inline-action" disabled={boardBusy} onClick={clearSavedBoard} type="button">Clear board</button> : hasCustomBoardFilters ? <button className="work-active-board-inline-action" disabled={boardBusy} onClick={() => setCurrentBoardFilters({ ...workBoardDefaultFilters })} type="button">Reset filters</button> : null}
            {activeSavedBoard ? <button className="work-active-board-inline-action" disabled={boardBusy} onClick={() => beginEditBoard(activeSavedBoard)} type="button">Edit board</button> : null}
          </div>
        </div>
        <div className="work-active-board-applied-row">
          {visibleChipDefinitions.map((definition) => {
            const selectedValues = Array.isArray(normalizedFilters[definition.key]) ? normalizedFilters[definition.key] : [];
            const fullText = getBoardFilterFullText(definition, selectedValues);
            const selectedPeople = definition.key === "assigneeIds"
              ? selectedValues
                .map((value) => workspaceBoardPeople.find((person) => person.userId === value))
                .filter(Boolean)
              : [];
            return (
              <button className="work-active-board-control" key={definition.key} onClick={() => openBoardFilterPanel(definition.key)} type="button">
                <span className="sr-only">{`${definition.label}: ${fullText}`}</span>
                <span className="work-active-board-control-copy">
                  <strong className="work-active-board-control-inline-label">{definition.label}:</strong>
                  <span className="work-active-board-control-inline-value">{getBoardFilterSummaryText(definition, selectedValues)}</span>
                </span>
                {definition.key === "assigneeIds" && selectedPeople.length ? (
                  <span className="work-active-board-assignee-stack" aria-hidden="true">
                    {selectedPeople.slice(0, 4).map((person) => (
                      <WorkspaceUserAvatar className="work-active-board-assignee-avatar" key={person.userId} title={person.name || person.email || "Workspace user"} user={person} />
                    ))}
                    {selectedPeople.length > 4 ? <span className="work-active-board-assignee-more">+{selectedPeople.length - 4}</span> : null}
                  </span>
                ) : null}
                <span className="work-active-board-control-hovercard" role="presentation">{`${definition.label}: ${fullText}`}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };
  const toggleBoardFilterValue = (key, value) => {
    const availableValues = boardFilterAllOptionsByKey({
      workspaceBoardPeople,
      workProjectOptions,
      workBoardTypeOptions,
      workspaceTeamOptions,
      workSprintOptions,
      workEpicOptions,
      severityOptions,
      workBoardFlagOptions,
    })[key] || [];
    setBoardDraft((current) => {
      const currentValues = Array.isArray(current.filters?.[key]) ? current.filters[key] : [];
      const isSelected = currentValues.includes(value);
      const hasFullSelection = availableValues.length > 0 && currentValues.length >= availableValues.length;
      let nextValues;
      if (hasFullSelection && isSelected) {
        nextValues = [value];
      } else if (currentValues.length === 1 && isSelected && availableValues.length > 1) {
        nextValues = [...availableValues];
      } else if (isSelected) {
        nextValues = currentValues.filter((item) => item !== value);
      } else {
        nextValues = [...currentValues, value];
      }
      return {
        ...current,
        filters: {
          ...current.filters,
          [key]: nextValues,
        },
      };
    });
  };
  const toggleBoardSharedUser = (userId) => {
    setBoardDraft((current) => ({
      ...current,
      sharedWith: current.sharedWith.includes(userId)
        ? current.sharedWith.filter((item) => item !== userId)
        : [...current.sharedWith, userId],
    }));
  };
  const beginCreateBoard = () => {
    setBoardDraft({
      ...createEmptyWorkBoardDraft(),
      filters: normalizeWorkBoardFilters(currentBoardFilters || workBoardDefaultFilters),
    });
    setBoardError("");
    setBoardDeleteConfirmOpen(false);
    setBoardDeleteConfirmation("");
    setBoardModalOpen(true);
  };
  const beginEditBoard = (board) => {
    setBoardDraft(hydrateWorkBoardDraft(board));
    setBoardError("");
    setBoardDeleteConfirmOpen(false);
    setBoardDeleteConfirmation("");
    setBoardModalOpen(true);
  };
  const closeBoardModal = () => {
    setBoardModalOpen(false);
    setBoardDeleteConfirmOpen(false);
    setBoardDeleteConfirmation("");
    setBoardError("");
  };
  const openSavedBoard = (board) => {
    setActiveSavedBoard(board);
    setCurrentBoardFilters(normalizeWorkBoardFilters(board?.filters));
    onActiveBoardChange(board?.id || "");
    setBoardModalOpen(false);
  };
  const clearSavedBoard = () => {
    setActiveSavedBoard(null);
    setCurrentBoardFilters({ ...workBoardDefaultFilters });
    onActiveBoardChange("");
    setBoardError("");
  };
  const selectSavedBoardById = (boardId) => {
    if (boardId === "__custom__") return;
    if (!boardId) {
      clearSavedBoard();
      return;
    }
    const selectedBoard = savedBoards.find((board) => `${board.id}` === `${boardId}`);
    if (selectedBoard) openSavedBoard(selectedBoard);
  };
  const boardTabRecords = allModuleRecords.filter((record) => /task|ticket|request|support|bug|defect|incident|improvement|feature|story/i.test(`${record.type || ""}`));
  const activeBoardFilters = moduleName === "Work" && activeTab === "Board"
    ? normalizeWorkBoardFilters(currentBoardFilters || activeSavedBoard?.filters || {})
    : null;
  const boardDisplaySource = moduleName === "Work" && activeTab === "Board"
    ? boardTabRecords
    : filteredRecords;
  const filterRecordsForBoard = (recordsForBoard = [], filters = null) => {
    if (!filters) return recordsForBoard;
    const availableOptionsByKey = {
      ...boardFilterAllOptionsByKey({
        workspaceBoardPeople,
        workProjectOptions,
        workBoardTypeOptions,
        workspaceTeamOptions,
        workSprintOptions,
        workEpicOptions,
        severityOptions,
        workBoardFlagOptions,
      }),
      labels: workspaceLabelOptions,
    };
    const hasEffectiveFilter = (key) => {
      const selectedValues = Array.isArray(filters?.[key]) ? filters[key].filter(Boolean) : [];
      if (!selectedValues.length) return false;
      const availableValues = Array.isArray(availableOptionsByKey[key]) ? availableOptionsByKey[key].filter(Boolean) : [];
      if (!availableValues.length) return true;
      const normalizedSelected = new Set(selectedValues.map((value) => `${value}`.trim().toLowerCase()));
      const normalizedAvailable = new Set(availableValues.map((value) => `${value}`.trim().toLowerCase()));
      if (normalizedSelected.size < normalizedAvailable.size) return true;
      for (const value of normalizedAvailable) {
        if (!normalizedSelected.has(value)) return true;
      }
      return false;
    };
    return recordsForBoard.filter((record) => {
      const assigneeTokens = [
        record.owner,
        ...asList(record.assignees),
      ].filter(Boolean).map((value) => `${value}`.trim().toLowerCase());
      const requiredAssignees = resolveBoardAssigneeLabels(filters.assigneeIds)
        .map((value) => `${value}`.trim().toLowerCase());
      const labelTokens = asList(record.labels).map((value) => `${value}`.trim().toLowerCase());
      const workflowCategory = getWorkflowCategoryForRecord(record);
      const matchesAssignee = !hasEffectiveFilter("assigneeIds") || requiredAssignees.some((value) => assigneeTokens.includes(value));
      const matchesProject = !hasEffectiveFilter("projects") || filters.projects.includes(record.projectRef || "");
      const matchesType = !hasEffectiveFilter("types") || filters.types.includes(record.type);
      const normalizedStatusFilters = hasEffectiveFilter("statuses") ? filters.statuses.map((status) => `${status}`.trim().toLowerCase()) : [];
      const matchesWorkflowStatus = !normalizedStatusFilters.length || normalizedStatusFilters.some((status) => {
        if (generalizedBoardLanes.some((lane) => lane.key === status)) return status === workflowCategory;
        return status === `${record.status || ""}`.trim().toLowerCase();
      });
      const matchesPriority = !hasEffectiveFilter("priorities") || filters.priorities.includes(record.priority);
      const matchesLabels = !hasEffectiveFilter("labels") || filters.labels.every((value) => labelTokens.includes(`${value}`.trim().toLowerCase()));
      const matchesSeverity = !hasEffectiveFilter("severities") || filters.severities.includes(record.severity || "");
      const matchesTeam = !hasEffectiveFilter("teams") || filters.teams.includes(record.team || "");
      const matchesSprint = !hasEffectiveFilter("sprints") || filters.sprints.includes(record.sprint || "");
      const matchesEpic = !hasEffectiveFilter("epics") || filters.epics.includes(record.epic || "");
      const matchesDueBucket = !hasEffectiveFilter("dueBuckets") || filters.dueBuckets.some((bucket) => isRecordInDueBucket(record, bucket));
      const flagValue = record.flagged ? "Flagged" : "Not flagged";
      const matchesFlagged = !hasEffectiveFilter("flagged") || filters.flagged.includes(flagValue);
      return matchesAssignee && matchesProject && matchesType && matchesWorkflowStatus && matchesPriority && matchesLabels && matchesSeverity && matchesTeam && matchesSprint && matchesEpic && matchesDueBucket && matchesFlagged;
    });
  };
  const filterVisibleWorkBoardLanes = (lanes = []) => showBacklogLane ? lanes : lanes.filter((lane) => lane.key !== "backlog");
  const formatBoardCardDate = (value) => {
    if (!value) return "No due date";
    const date = /^\d{4}-\d{2}-\d{2}$/.test(`${value}`) ? new Date(`${value}T00:00:00`) : new Date(value);
    if (Number.isNaN(date.getTime())) return `${value}`;
    return date.toLocaleDateString([], { year: "numeric", month: "long", day: "numeric" });
  };
  const getBoardCardAssigneeProfile = (record) => {
    const ownerKey = `${record.owner || ""}`.trim().toLowerCase();
    return workspaceBoardPeople.find((person) => {
      const personName = `${person.name || ""}`.trim().toLowerCase();
      const personEmail = `${person.email || ""}`.trim().toLowerCase();
      return ownerKey && (personName === ownerKey || personEmail === ownerKey);
    }) || { name: record.owner || "Unassigned" };
  };
  function sortBoardRecords(recordsForBoard = []) {
    const list = [...recordsForBoard];
    const toTime = (value) => {
      if (!value) return 0;
      const date = /^\d{4}-\d{2}-\d{2}$/.test(`${value}`) ? new Date(`${value}T00:00:00`) : new Date(value);
      return Number.isNaN(date.getTime()) ? 0 : date.getTime();
    };
    const priorityScore = { low: 1, normal: 2, medium: 3, high: 4, urgent: 5, critical: 6 };
    if (boardSortKey === "title_asc") return list.sort((left, right) => `${left.title || ""}`.localeCompare(`${right.title || ""}`));
    if (boardSortKey === "priority_desc") return list.sort((left, right) => (priorityScore[`${right.priority || "medium"}`.toLowerCase()] || 0) - (priorityScore[`${left.priority || "medium"}`.toLowerCase()] || 0));
    if (boardSortKey === "due_asc") return list.sort((left, right) => {
      const leftTime = toTime(left.due);
      const rightTime = toTime(right.due);
      if (!leftTime && !rightTime) return 0;
      if (!leftTime) return 1;
      if (!rightTime) return -1;
      return leftTime - rightTime;
    });
    if (boardSortKey === "created_desc") return list.sort((left, right) => toTime(right.createdAt) - toTime(left.createdAt));
    return list.sort((left, right) => toTime(right.updatedAt || right.createdAt) - toTime(left.updatedAt || left.createdAt));
  }
  const onLaneDragOver = (event, laneKey) => {
    if (!draggedTicketId) return;
    event.preventDefault();
    if (dragLaneKey !== laneKey) setDragLaneKey(laneKey);
  };
  const onLaneDrop = async (event, laneKey) => {
    event.preventDefault();
    const recordId = draggedTicketId || event.dataTransfer.getData("text/work-ticket-id");
    setDragLaneKey("");
    setDraggedTicketId("");
    if (!recordId) return;
    const record = records.find((item) => `${item.id}` === `${recordId}`);
    if (!record) return;
    const targetStatus = getTargetStatusForLane(record, laneKey);
    if (!targetStatus || targetStatus === record.status) return;
    const previousStatus = record.status;
    setOptimisticRecordPatches((current) => ({
      ...current,
      [record.id]: { ...(current[record.id] || {}), status: targetStatus },
    }));
    if (`${selectedRecord?.id || ""}` === `${record.id}`) {
      setSelectedRecord((current) => current ? { ...current, status: targetStatus } : current);
      setDetailDraft((current) => current ? { ...current, status: targetStatus } : current);
    }
    try {
      await onUpdateRecord?.(record.id, { status: targetStatus });
    } catch (error) {
      setOptimisticRecordPatches((current) => {
        const next = { ...current };
        if (next[record.id]) next[record.id] = { ...next[record.id], status: previousStatus };
        return next;
      });
      if (canApplyTicketSaveResult(record.id)) {
        setSelectedRecord((current) => current ? { ...current, status: previousStatus } : current);
        setDetailDraft((current) => current ? { ...current, status: previousStatus } : current);
      }
    }
  };
  const onTicketDragStart = (event, record) => {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/work-ticket-id", `${record.id}`);
    setDraggedTicketId(`${record.id}`);
    setDragLaneKey(getWorkflowCategoryForRecord(record));
  };
  const onTicketDragEnd = () => {
    setDraggedTicketId("");
    setDragLaneKey("");
  };
  const ticketParentTypes = {
    Epic: [],
    Feature: ["Epic"],
    Story: ["Epic", "Feature"],
    Task: ["Feature", "Story"],
    Bug: ["Feature", "Story", "Task"],
    "Support Request": ["Feature", "Story"],
    Incident: ["Feature", "Story", "Task"],
    Improvement: ["Feature", "Story"],
  };
  const ticketParentOptions = records.filter((record) =>
    record.moduleName === "Work"
    && ticketParentTypes[draft.type]?.includes(record.type)
    && (!selectedProjectName || record.projectRef === selectedProjectName)
  );
  const ticketLinkOptions = records.filter((record) =>
    record.moduleName === "Work"
    && /feature|story|task|bug|support|incident|improvement/i.test(`${record.type || ""}`)
    && (!selectedProjectName || record.projectRef === selectedProjectName)
  );
  const workspaceTeamOptions = Array.from(new Set([
    ...(workspaceAdministration?.teams || []).map((team) => team.name || team.title || ""),
    ...records.filter((record) => record.moduleName === "People" && /team/i.test(`${record.type || ""}`)).map((record) => record.title),
  ].filter(Boolean))).sort();
  const ticketRestrictionRoleOptions = Array.from(new Set([
    ...(workspaceAdministration?.roleTemplates || []).map((role) => role.title || role.name || role.role || ""),
    "Workspace Members",
    "Project Members",
    "Assignees",
    "Client Reviewers",
  ].filter(Boolean))).sort();
  const severityOptions = ["Low", "Medium", "High", "Critical"];
  const workBoardFlagOptions = ["Flagged", "Not flagged"];
  const workspaceLabelOptions = uniqueLabelList([
    ...allModuleRecords.flatMap((record) => asList(record.labels)),
    ...asList(draft.labels),
    ...asList(detailDraft?.labels),
    ...asList(boardDraft.filters?.labels),
    ...asList(currentBoardFilters?.labels),
    ...asList(activeSavedBoard?.filters?.labels),
  ]).sort((left, right) => left.localeCompare(right));
  const workBoardDefaultFilters = {
    assigneeIds: workspaceBoardPeople.map((person) => person.userId),
    projects: workProjectOptions,
    types: workBoardTypeOptions,
    statuses: generalizedBoardLanes.map((lane) => lane.key),
    priorities: workspacePriorities,
    labels: [],
    severities: severityOptions,
    teams: workspaceTeamOptions,
    sprints: workSprintOptions,
    epics: workEpicOptions,
    dueBuckets: boardDueBucketOptions.map((option) => option.key),
    flagged: workBoardFlagOptions,
  };
  const displayedRecords = filterRecordsForBoard(boardDisplaySource, activeBoardFilters);
  const effectiveBoardFilterDefinitions = getBoardFilterDefinitions();
  const effectiveAppliedBoardFilters = activeBoardFilters
    ? effectiveBoardFilterDefinitions.filter((definition) => {
      const selectedValues = Array.isArray(activeBoardFilters[definition.key]) ? activeBoardFilters[definition.key] : [];
      const availableValues = definition.options.map((option) => option.value);
      if (!selectedValues.length) return false;
      if (!availableValues.length) return true;
      return selectedValues.length < availableValues.length;
    })
    : [];
  const isAllTicketsBoardState = effectiveAppliedBoardFilters.length === 0;
  const matchingSavedBoard = activeBoardFilters
    ? savedBoards.find((board) => areWorkBoardFiltersEqual(activeBoardFilters, board.filters))
    : null;
  const resolvedActiveSavedBoard = activeSavedBoard || matchingSavedBoard;
  const activeBoardBaseFilters = resolvedActiveSavedBoard?.filters || workBoardDefaultFilters;
  const hasCustomBoardFilters = activeBoardFilters
    ? !isAllTicketsBoardState && !areWorkBoardFiltersEqual(activeBoardFilters, activeBoardBaseFilters)
    : false;
  const boardDropdownValue = isAllTicketsBoardState ? "" : (hasCustomBoardFilters ? "__custom__" : (resolvedActiveSavedBoard?.id || ""));
  const allTicketsBoardCount = boardDisplaySource.length;
  const savedBoardCounts = Object.fromEntries(
    savedBoards.map((board) => [`${board.id}`, filterRecordsForBoard(boardDisplaySource, normalizeWorkBoardFilters(board.filters)).length]),
  );
  const boardDeleteTarget = boardDraft.id ? savedBoards.find((board) => board.id === boardDraft.id) || boardDraft : null;
  const generalizedDisplayedBoard = buildGeneralizedBoardLanes(displayedRecords).map((lane) => ({
    ...lane,
    records: sortBoardRecords(lane.records || []),
  }));
  const isScheduleCreate = moduleName === "Schedule";
  const activeScheduleDefinition = scheduleTabDefinitions[activeTab] || scheduleTabDefinitions.Calendar;
  const createTypeOptions = isTicketCreate ? workTypeOptions.map((item) => item.value)
    : isSprintCreate ? ["Sprint"]
      : isEpicCreate ? ["Epic"]
        : isScheduleCreate ? scheduleTypeOptionsByTab[activeTab] || config.types
          : Array.from(new Set([createAction[1], ...config.types]));
  const createTitleLabel = isSprintCreate ? "Sprint name" : isEpicCreate ? "Epic name" : isTicketCreate ? "Summary" : isScheduleCreate ? `${activeScheduleDefinition.title} title` : "Title";
  const createTitlePlaceholder = isSprintCreate ? "Example: Sprint 12 — Checkout improvements"
    : isEpicCreate ? "Example: Customer onboarding experience"
      : isTicketCreate ? "Briefly describe the request or issue"
        : isScheduleCreate ? "Example: Client review with VoltX" : config.placeholder;

  const closeCreateModal = () => {
    setFormOpen(false);
    setWorkTypePickerOpen(false);
    setCreateOwnerPickerOpen(false);
    setCreateOwnerSearch("");
    setModalError("");
    setProjectTaskCreateMode(false);
    setPendingLinkedTicket(null);
  };

  const getScheduleCreateDefaults = (tabName = activeTab, overrides = {}) => {
    const definition = scheduleTabDefinitions[tabName] || scheduleTabDefinitions.Calendar;
    const types = scheduleTypeOptionsByTab[tabName] || config.types;
    const statuses = scheduleStatusOptionsByTab[tabName] || config.statuses;
    const date = scheduleSelectedDate || calendarDateKey(new Date());
    const defaultDue = ["Bookings", "Rooms"].includes(tabName) ? "" : date;
    return {
      ...getInitialWorkspaceDraft("Schedule", defaultOwner),
      owner: defaultOwner,
      type: overrides.type || definition.defaultType || types[0],
      status: overrides.status || definition.defaultStatus || statuses[0],
      due: overrides.due ?? defaultDue,
      startDate: overrides.startDate ?? (tabName === "Availability" ? date : ""),
      priority: "Medium",
      projectRef: selectedProjectName,
      scheduleStartTime: overrides.scheduleStartTime || "",
      scheduleEndTime: overrides.scheduleEndTime || "",
      scheduleDuration: `${overrides.scheduleDuration || (tabName === "Calendar" ? 30 : tabName === "Availability" ? 480 : 30)}`,
      scheduleTimezone: typeof Intl !== "undefined"
        ? Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata"
        : "Asia/Kolkata",
      scheduleLocation: overrides.scheduleLocation || "",
      scheduleMeetingLink: overrides.scheduleMeetingLink || "",
      scheduleCapacity: overrides.scheduleCapacity || "",
      scheduleBuffer: `${overrides.scheduleBuffer || 15}`,
      scheduleVisibility: overrides.scheduleVisibility || "Workspace",
      scheduleRequestType: overrides.scheduleRequestType || (tabName === "Requests" ? definition.defaultType : ""),
      scheduleApprovalRequired: overrides.scheduleApprovalRequired ?? tabName === "Requests",
      scheduleRepeatRule: overrides.scheduleRepeatRule || "Does not repeat",
      labels: overrides.labels || "",
      description: overrides.description || "",
      ...overrides,
    };
  };

  const openScheduleCreateModal = (tabName = activeTab, overrides = {}) => {
    setDraft(getScheduleCreateDefaults(tabName, overrides));
    setFormOpen(true);
    setWorkTypePickerOpen(false);
    setCreateOwnerPickerOpen(false);
    setCreateOwnerSearch("");
    setModalError("");
    setProjectTaskCreateMode(false);
  };

  const openCreateModal = () => {
    if (moduleName === "Work" && activeTab === "Board") {
      beginCreateBoard();
      setBoardModalOpen(true);
      return;
    }
    if (moduleName === "Schedule") {
      openScheduleCreateModal(activeTab);
      return;
    }
    const recordType = createTypeOptions.includes(currentCreateAction[1]) ? currentCreateAction[1] : (isTicketCreate ? "Feature" : createTypeOptions[0]);
    const workflow = isTicketCreate ? getTicketWorkflow(recordType) : [];
    setDraft(() => ({
      ...getInitialWorkspaceDraft(moduleName, defaultOwner),
      owner: isTicketCreate ? "" : defaultOwner,
      type: recordType,
      status: isTicketCreate ? getDefaultTicketStatus(workflow, initialTicketStatus) : (["Sprints", "Epics"].includes(activeTab) ? "To Do" : config.statuses[0]),
      projectRef: moduleName === "Projects" ? "" : selectedProjectName,
      portalRoles: moduleName === "Projects" ? defaultClientPortalRoles : [],
    }));
    setFormOpen(true);
    setWorkTypePickerOpen(false);
    setCreateOwnerPickerOpen(false);
    setCreateOwnerSearch("");
    setModalError("");
    setProjectTaskCreateMode(false);
  };

  const openProjectTaskModal = () => {
    const workflow = getTicketWorkflow("Task");
    setProjectTaskCreateMode(true);
    setDraft(() => ({ ...getInitialWorkspaceDraft("Work", defaultOwner), owner: "", type: "Task", status: getDefaultTicketStatus(workflow, initialTicketStatus), projectRef: selectedProjectName }));
    setFormOpen(true);
    setWorkTypePickerOpen(false);
    setModalError("");
  };

  const openSubtaskModal = (parentRecord, seededTitle = "") => {
    const workflow = getTicketWorkflow("Task");
    setProjectTaskCreateMode(true);
    setDraft(() => ({
      ...getInitialWorkspaceDraft("Work", defaultOwner),
      title: seededTitle,
      owner: "",
      type: "Task",
      status: getDefaultTicketStatus(workflow, initialTicketStatus),
      projectRef: parentRecord?.projectRef || selectedProjectName,
      parentRecordId: parentRecord?.id || "",
      epic: parentRecord?.epic || "",
      sprint: parentRecord?.sprint || "",
    }));
    setFormOpen(true);
    setWorkTypePickerOpen(false);
    setModalError("");
    setSubtaskQuickTitle("");
    setPendingLinkedTicket(null);
  };

  const openLinkedTicketModal = (seededTitle = "", relationship = "relates to") => {
    const workflow = getTicketWorkflow("Task");
    setProjectTaskCreateMode(true);
    setDraft(() => ({
      ...getInitialWorkspaceDraft("Work", defaultOwner),
      title: seededTitle,
      owner: "",
      type: "Task",
      status: getDefaultTicketStatus(workflow, initialTicketStatus),
      projectRef: selectedRecord?.projectRef || selectedProjectName,
      epic: selectedRecord?.epic || "",
      sprint: selectedRecord?.sprint || "",
    }));
    setPendingLinkedTicket({ relationship });
    setFormOpen(true);
    setWorkTypePickerOpen(false);
    setModalError("");
    setLinkedTicketSearch("");
  };

  const openTicketEpicCreator = () => {
    const existingTitle = detailDraft?.epic || "";
    setEpicDraft({
      title: existingTitle,
      description: "",
    });
    setEpicCreateError("");
    setEpicPopoverOpen(true);
  };

  const submitTicketEpic = async (event) => {
    event.preventDefault();
    const title = epicDraft.title.trim();
    if (!selectedRecord?.id || !title) return;
    const recordId = selectedRecord.id;

    setEpicCreateSaving(true);
    setEpicCreateError("");
    try {
      const existingEpic = allModuleRecords.find((record) =>
        record.moduleName === "Work" &&
        /epic/i.test(`${record.type || ""}`) &&
        `${record.title || ""}`.trim().toLowerCase() === title.toLowerCase()
      );

      if (!existingEpic) {
        await onCreateRecord?.({
          moduleName: "Work",
          tab: "Epics",
          title,
          owner: detailDraft?.owner || selectedRecord.owner || defaultOwner,
          priority: detailDraft?.priority || selectedRecord.priority || "Medium",
          status: "To Do",
          due: detailDraft?.due || selectedRecord.due || "",
          type: "Epic",
          description: epicDraft.description.trim(),
          sprint: "",
          epic: title,
          storyPoints: 0,
          assignees: detailDraft?.assignees || "",
          labels: detailDraft?.labels || "",
          severity: "",
          parentRecordId: "",
          linkedRecordIds: [],
          linkedWorkType: "relates to",
          linkedWorkUrl: "",
          projectRef: detailDraft?.projectRef || selectedRecord.projectRef || selectedProjectName,
          projectId: selectedProjectId,
        }, { localError: true });
      }

      await onUpdateRecord?.(recordId, { epic: title }, { localError: true });
      if (!canApplyTicketSaveResult(recordId)) return;
      setDetailDraft((current) => ({ ...current, epic: title }));
      setSelectedRecord((current) => current ? { ...current, epic: title } : current);
      setEpicPopoverOpen(false);
      setEpicDraft({ title: "", description: "" });
    } catch (error) {
      if (canApplyTicketSaveResult(recordId)) setEpicCreateError(error?.response?.data?.message || error?.message || "Unable to create and link this epic.");
    } finally {
      if (canApplyTicketSaveResult(recordId)) setEpicCreateSaving(false);
    }
  };

  const updateRecord = async (id, patch) => {
    setSaving(true);
    try {
      await onUpdateRecord?.(id, patch);
    } catch (error) {
      // App-level handler displays the error message.
    } finally {
      setSaving(false);
    }
  };

  const submitRecord = async (event) => {
    event.preventDefault();
    const title = draft.title.trim();
    if (!title) return;

    setSaving(true);
    setModalError("");
    try {
      const schedulePageContext = moduleName === "Schedule" ? {
        scheduleKind: activeTab,
        startTime: draft.scheduleStartTime,
        endTime: draft.scheduleEndTime,
        durationMinutes: Number(draft.scheduleDuration || 0),
        timezone: draft.scheduleTimezone,
        meetingLink: draft.scheduleMeetingLink,
        location: draft.scheduleLocation,
        room: draft.scheduleLocation,
        capacity: Number(draft.scheduleCapacity || 0),
        bufferMinutes: Number(draft.scheduleBuffer || 0),
        visibility: draft.scheduleVisibility,
        requestType: draft.scheduleRequestType || draft.type,
        approvalRequired: Boolean(draft.scheduleApprovalRequired),
        repeatRule: draft.scheduleRepeatRule,
        equipment: asList(draft.labels),
        bookingUrl: draft.scheduleMeetingLink,
        bookingMode: draft.type,
        availabilityScope: draft.team || draft.owner,
        attendeeCount: asList(draft.assignees).length,
        notes: draft.description,
      } : undefined;
      const createTabName = projectTaskCreateMode
        ? "Tickets"
        : isTicketCreate
          ? (`${draft.status || ""}`.trim().toLowerCase() === "backlog" || /backlog/i.test(draft.type) ? "Backlog" : "Tickets")
          : activeTab;
      const createdRecord = await onCreateRecord?.({
        moduleName: projectTaskCreateMode ? "Work" : moduleName,
        tab: createTabName,
        title,
        owner: draft.owner,
        priority: draft.priority,
        status: draft.status,
        due: draft.due,
        type: draft.type,
        description: draft.description,
        sprint: draft.sprint,
        epic: draft.epic,
        storyPoints: draft.storyPoints,
        assignees: draft.assignees,
        labels: draft.labels,
        severity: draft.severity,
        approvalRole: draft.approvalRole,
        templateName: draft.templateName,
        automationTrigger: draft.automationTrigger,
        startDate: draft.startDate,
        team: draft.team,
        parentRecordId: draft.parentRecordId,
        linkedRecordIds: draft.linkedRecordIds,
        linkedWorkType: draft.linkedWorkType,
        linkedWorkUrl: moduleName === "Schedule" ? (draft.scheduleMeetingLink || draft.linkedWorkUrl) : draft.linkedWorkUrl,
        restrictRoles: draft.restrictRoles,
        flagged: draft.flagged,
        flagReason: draft.flagReason,
        projectRef: moduleName === "Projects" ? draft.projectRef : (draft.projectRef || selectedProjectName),
        projectSlug: moduleName === "Projects" ? draft.projectSlug : "",
        projectId: projectTaskCreateMode ? selectedProjectId : (moduleName === "Projects" ? "" : getProjectIdForRef(draft.projectRef || selectedProjectName)),
        accountClient: draft.accountClient,
        endClient: draft.endClient,
        clientPortalEnabled: draft.clientPortalEnabled,
        portalRoles: draft.portalRoles,
        portalMembers: parsePortalMembersInput(draft.portalMembersInput),
        pageContext: schedulePageContext,
      }, { localError: true });
      if (isTicketCreate && createdRecord?.id && draft.attachmentFiles?.length) {
        for (const file of draft.attachmentFiles.slice(0, 5)) {
          if (!file || file.size > 15 * 1024 * 1024) continue;
          const data = await readFileAsDataUrl(file);
          await onUpdateRecord?.(createdRecord.id, {
            addAttachment: { data, name: file.name, mimeType: file.type, sizeBytes: file.size },
          }, { localError: true });
        }
      }
      if (pendingLinkedTicket && createdRecord?.id && selectedRecord?.id) {
        const existingItems = detailDraft?.linkedItems?.length
          ? detailDraft.linkedItems
          : (detailDraft?.linkedRecordIds || []).map((recordId) => ({ recordId, relationship: detailDraft?.linkedWorkType || "relates to" }));
        const linkedItems = [...existingItems.filter((item) => item.recordId !== createdRecord.id), { recordId: createdRecord.id, relationship: pendingLinkedTicket.relationship }];
        const linkedRecordIds = linkedItems.map((item) => item.recordId);
        await onUpdateRecord?.(selectedRecord.id, { linkedItems, linkedRecordIds, linkedWorkType: pendingLinkedTicket.relationship }, { localError: true });
        setDetailDraft((current) => ({ ...current, linkedItems, linkedRecordIds, linkedWorkType: pendingLinkedTicket.relationship }));
      }
      setDraft(getInitialWorkspaceDraft(moduleName, defaultOwner));
      closeCreateModal();
    } catch (error) {
      setModalError(error?.response?.data?.message || error?.message || "Unable to create this record. Please review the form and try again.");
    } finally {
      setSaving(false);
    }
  };

  const moveRecord = async (record) => {
    const nextStatus = getNextStatus(record.status, moduleName);
    await updateRecord(record.id, { status: nextStatus });
  };

  const deleteRecord = async (record) => {
    setSaving(true);
    try {
      await onDeleteRecord?.(record.id);
    } catch (error) {
      // App-level handler displays the error message.
    } finally {
      setSaving(false);
    }
  };

  const openRecordHistory = (record, { returnToCurrent = false } = {}) => {
    if (returnToCurrent && selectedRecord?.id && selectedRecord.id !== record?.id) {
      setTicketNavigationStack((current) => [...current, selectedRecord]);
    } else if (!returnToCurrent) {
      setTicketNavigationStack([]);
    }
    setTicketTransitioning(true);
    setDetailDraft(null);
    setSelectedRecord(record);
    setRecordTimeline(null);
    setTimelineError("");
    onRecordRouteChange(record);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeRecordHistory = () => {
    const previousRecord = ticketNavigationStack[ticketNavigationStack.length - 1];
    if (previousRecord) {
      setTicketHistoryMenu((current) => ({ ...current, open: false }));
      setTicketNavigationStack((current) => current.slice(0, -1));
      setTicketTransitioning(true);
      setDetailDraft(null);
      setSelectedRecord(previousRecord);
      setRecordTimeline(null);
      setTimelineError("");
      setDetailDraft(null);
      setIssueActivityTab("comments");
      setCommentDraft("");
      onRecordRouteChange(previousRecord);
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSelectedRecord(null);
    setRecordTimeline(null);
    setTimelineError("");
    setDetailDraft(null);
    setTicketHistoryMenu((current) => ({ ...current, open: false }));
    setIssueActivityTab("all");
    setCommentDraft("");
    onRecordRouteChange(null);
  };

  const navigateBackFromTicket = () => {
    setTicketHistoryMenu((current) => ({ ...current, open: false }));
    if (ticketNavigationStack.length) {
      closeRecordHistory();
      return;
    }
    if (typeof window !== "undefined" && window.history.length > 1) {
      window.history.back();
      return;
    }
    closeRecordHistory();
  };

  const openTicketBackHistoryMenu = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const hasRecentTickets = ticketNavigationStack.length || recentTicketHistory.some((record) => record.id !== selectedRecord?.id);
    if (!hasRecentTickets) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setTicketHistoryMenu({
      open: true,
      top: Math.max(12, Math.min(rect.bottom + 8, window.innerHeight - 260)),
      left: Math.max(12, Math.min(rect.left, window.innerWidth - 320)),
    });
  };

  const openTicketFromBackHistory = (record) => {
    if (!record?.id) return;
    setTicketHistoryMenu((current) => ({ ...current, open: false }));
    openRecordHistory(record, { returnToCurrent: true });
  };

  function handleModuleTabChange(nextTab) {
    if (nextTab === activeTab && selectedRecord) {
      closeRecordHistory();
      return;
    }
    if (selectedRecord && nextTab !== activeTab) {
      setSelectedRecord(null);
      setRecordTimeline(null);
      setTimelineError("");
      setDetailDraft(null);
      setIssueActivityTab("all");
      setCommentDraft("");
      setTicketHistoryMenu((current) => ({ ...current, open: false }));
      onRecordRouteChange(null);
    }
    setActiveTab(nextTab);
  }

  const saveRecordDetails = async () => {
    if (!selectedRecord?.id || !detailDraft) return;
    const recordId = selectedRecord.id;
    setSaving(true);
    try {
      const updated = await onUpdateRecord?.(recordId, buildRecordDetailPayload(detailDraft, getProjectIdForRef));
      if (updated && canApplyTicketSaveResult(recordId)) {
        setSelectedRecord(updated);
      }
    } catch (error) {
      // App-level handler displays the error message.
    } finally {
      if (canApplyTicketSaveResult(recordId)) setSaving(false);
    }
  };

  const saveTicketLabelsNow = async (labels) => {
    if (!selectedRecord?.id || !detailDraftRef.current || !isWorkItemRecord(selectedRecord)) return;
    const recordId = selectedRecord.id;
    const nextDraft = { ...detailDraftRef.current, labels };
    const payload = buildRecordDetailPayload(nextDraft, getProjectIdForRef);
    const serializedPayload = serializeRecordDetailPayload(payload);
    if (detailAutosaveTimerRef.current) {
      window.clearTimeout(detailAutosaveTimerRef.current);
      detailAutosaveTimerRef.current = null;
    }
    const previousSerializedPayload = detailAutosaveLastPayloadRef.current;
    detailAutosaveLastPayloadRef.current = serializedPayload;
    setDetailAutosaveState({ status: "saving", message: "Saving labels..." });
    try {
      const updated = await onUpdateRecord?.(recordId, payload, { localError: true });
      if (!canApplyTicketSaveResult(recordId)) return;
      if (updated) setSelectedRecord(updated);
      setDetailAutosaveState({ status: "saved", message: "Saved" });
    } catch (error) {
      if (!canApplyTicketSaveResult(recordId)) return;
      detailAutosaveLastPayloadRef.current = previousSerializedPayload;
      setDetailAutosaveState({
        status: "error",
        message: error?.response?.data?.message || "Label save failed. Press Enter to retry.",
      });
    }
  };

  const refreshSelectedRecordTimeline = async () => {
    if (!selectedRecord?.id) return;
    setTimelineLoading(true);
    setTimelineError("");
    try {
      const timelineData = await getWorkspaceRecordTimeline(selectedRecord.id);
      setRecordTimeline(timelineData);
    } catch (error) {
      setTimelineError(error?.response?.data?.message || "Unable to load record history.");
    } finally {
      setTimelineLoading(false);
    }
  };

  const handleCommentAttachmentChange = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selectedFiles.length) return;

    setCommentAttachmentBusy(true);
    setTimelineError("");
    try {
      const remainingSlots = Math.max(0, 5 - commentAttachments.length);
      if (!remainingSlots) throw new Error("You can attach up to 5 files per comment.");
      const preparedAttachments = [];
      for (const file of selectedFiles.slice(0, remainingSlots)) {
        const prepared = await prepareCommentAttachment(file);
        if (prepared) preparedAttachments.push(prepared);
      }
      setCommentAttachments((current) => [...current, ...preparedAttachments].slice(0, 5));
    } catch (error) {
      setTimelineError(error?.message || "Unable to prepare this attachment.");
    } finally {
      setCommentAttachmentBusy(false);
    }
  };

  const renderPendingCommentAttachments = () => {
    if (!commentAttachments.length) return null;
    return (
      <div className="ticket-comment-attachment-strip">
        {commentAttachments.map((attachment) => (
          <span className="ticket-comment-attachment-chip" key={attachment.id}>
            {attachment.previewUrl ? <img alt="" src={attachment.previewUrl} /> : <AttachFileOutlined fontSize="inherit" />}
            <span>
              <strong>{attachment.name}</strong>
              <small>{formatFileSize(attachment.sizeBytes)}{attachment.originalSizeBytes > attachment.sizeBytes ? ` · compressed from ${formatFileSize(attachment.originalSizeBytes)}` : ""}</small>
            </span>
            <button aria-label={`Remove ${attachment.name}`} onClick={() => removeCommentAttachment(attachment.id)} type="button">×</button>
          </span>
        ))}
      </div>
    );
  };

  const renderCommentAttachments = (attachments = []) => {
    const files = Array.isArray(attachments) ? attachments.filter((attachment) => attachment?.url) : [];
    if (!files.length) return null;
    return (
      <div className="ticket-comment-attachments">
        {files.map((attachment) => {
          const isImage = `${attachment.mimeType || ""}`.startsWith("image/");
          return (
            <a className={isImage ? "is-image" : ""} href={attachment.url} key={attachment.id || attachment.url} rel="noreferrer" target="_blank">
              {isImage ? <img alt={attachment.name || "Comment attachment"} src={attachment.url} /> : <AttachFileOutlined fontSize="inherit" />}
              <span>
                <strong>{attachment.name || "Attachment"}</strong>
                <small>{formatFileSize(attachment.sizeBytes)}</small>
              </span>
            </a>
          );
        })}
      </div>
    );
  };

  const submitIssueComment = async () => {
    const recordId = selectedRecord?.id;
    const body = commentDraft.trim();
    const parentCommentId = replyParentCommentId || replyToCommentId || undefined;
    const pendingAttachments = commentAttachments;
    if (!recordId || (!body && !pendingAttachments.length) || commentSaving || commentAttachmentBusy) return;
    const optimisticCommentId = `optimistic-comment-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const now = new Date().toISOString();
    const optimisticComment = {
      id: optimisticCommentId,
      body,
      message: body,
      attachments: pendingAttachments.map((attachment) => ({
        id: attachment.id,
        name: attachment.name,
        url: attachment.previewUrl || attachment.data,
        mimeType: attachment.mimeType,
        sizeBytes: attachment.sizeBytes,
      })),
      parentCommentId,
      author: {
        id: currentUser?.id || currentUser?._id || "current-user",
        name: currentUser?.name || defaultOwner || "You",
        email: currentUser?.email || "",
        avatarUrl: currentUser?.avatarUrl || currentUser?.avatar_url || currentUser?.photoUrl || "",
      },
      createdAt: now,
      at: now,
      canManage: true,
      likedByMe: false,
      likeCount: 0,
      reactions: [],
    };
    setCommentSaving(true);
    setRecordTimeline((current) => ({
      ...(current || {}),
      comments: sortCommentsNewestFirst([optimisticComment, ...(current?.comments || [])]),
    }));
    clearStoredTicketCommentDraft(recordId);
    setCommentDraft("");
    setCommentAttachments([]);
    setReplyToCommentId("");
    setReplyParentCommentId("");
    setIssueActivityTab("comments");
    try {
      await addWorkspaceRecordComment(recordId, {
        attachments: pendingAttachments.map((attachment) => ({
          data: attachment.data,
          mimeType: attachment.mimeType,
          name: attachment.name,
          sizeBytes: attachment.sizeBytes,
        })),
        comment: body,
        parentCommentId,
      });
      await silentlyRefreshSelectedRecordTimeline(recordId);
      pendingAttachments.forEach(revokeCommentAttachmentPreview);
    } catch (error) {
      setCommentDraft(body);
      setCommentAttachments(pendingAttachments);
      setRecordTimeline((current) => ({
        ...(current || {}),
        comments: (current?.comments || []).filter((comment) => comment.id !== optimisticCommentId),
      }));
      setTimelineError(error?.response?.data?.message || "Unable to add comment.");
    } finally {
      setCommentSaving(false);
    }
  };

  const saveCommentEdit = async (commentId) => {
    if (!selectedRecord?.id || !editingCommentDraft.trim() || editingCommentSavingId) return;
    const nextBody = editingCommentDraft.trim();
    setEditingCommentSavingId(commentId);
    setTimelineError("");
    try {
      const response = await updateWorkspaceRecordComment(selectedRecord.id, commentId, { comment: nextBody });
      const updatedComment = response?.comment;
      setRecordTimeline((current) => ({
        ...(current || {}),
        comments: (current?.comments || []).map((comment) => (
          `${comment.id}` === `${commentId}`
            ? { ...comment, ...(updatedComment || {}), body: updatedComment?.body || nextBody, editedAt: updatedComment?.editedAt || new Date().toISOString() }
            : comment
        )),
      }));
      setEditingCommentId("");
      setEditingCommentDraft("");
    } catch (error) {
      setTimelineError(error?.response?.data?.message || "Unable to update comment.");
    } finally {
      setEditingCommentSavingId("");
    }
  };

  const deleteComment = async (commentId) => {
    if (!selectedRecord?.id || !window.confirm("Delete this comment?")) return;
    setCommentSaving(true);
    try {
      await deleteWorkspaceRecordComment(selectedRecord.id, commentId);
      await refreshSelectedRecordTimeline();
    } catch (error) {
      setTimelineError(error?.response?.data?.message || "Unable to delete comment.");
    } finally {
      setCommentSaving(false);
    }
  };

  const silentlyRefreshSelectedRecordTimeline = async (recordId) => {
    if (!recordId) return;
    try {
      const timelineData = await getWorkspaceRecordTimeline(recordId);
      setRecordTimeline((current) => (selectedRecord?.id === recordId ? timelineData : current));
    } catch (error) {
      // Keep optimistic UI. The next explicit refresh/open will re-sync the timeline.
    }
  };

  const patchTimelineComment = (commentId, patcher) => {
    setRecordTimeline((current) => {
      if (!current?.comments?.length) return current;
      return {
        ...current,
        comments: current.comments.map((comment) => (
          comment.id === commentId ? patcher(comment) : comment
        )),
      };
    });
  };

  const getCurrentUserReactionIdentity = () => ({
    id: currentUser?.id || currentUser?._id || "current-user",
    name: currentUser?.name || defaultOwner || "You",
    email: currentUser?.email || "",
    avatarUrl: currentUser?.avatarUrl || currentUser?.avatar_url || currentUser?.photoUrl || currentUser?.avatar?.url || "",
  });

  const likeComment = async (commentId) => {
    if (!selectedRecord?.id) return;
    const recordId = selectedRecord.id;
    const previousTimeline = recordTimeline;
    const currentReactionUser = getCurrentUserReactionIdentity();
    patchTimelineComment(commentId, (comment) => {
      const likedByMe = !comment.likedByMe;
      const currentCount = Number(comment.likeCount) || 0;
      const currentLikedBy = Array.isArray(comment.likedBy) ? comment.likedBy : [];
      return {
        ...comment,
        likedByMe,
        likeCount: Math.max(0, currentCount + (likedByMe ? 1 : -1)),
        likedBy: likedByMe
          ? [...currentLikedBy.filter((user) => `${user.id}` !== `${currentReactionUser.id}`), currentReactionUser]
          : currentLikedBy.filter((user) => `${user.id}` !== `${currentReactionUser.id}`),
      };
    });
    try {
      await toggleWorkspaceRecordCommentLike(recordId, commentId);
      silentlyRefreshSelectedRecordTimeline(recordId);
    } catch (error) {
      setRecordTimeline(previousTimeline);
      setTimelineError(error?.response?.data?.message || "Unable to update comment reaction.");
    }
  };

  const reactToComment = async (commentId, reactionKey) => {
    if (!selectedRecord?.id) return;
    const recordId = selectedRecord.id;
    const previousTimeline = recordTimeline;
    const emoji = commentReactionOptions.find((option) => option.key === reactionKey)?.icon || "👍";
    const currentReactionUser = getCurrentUserReactionIdentity();
    patchTimelineComment(commentId, (comment) => {
      const currentReactions = Array.isArray(comment.reactions) ? comment.reactions : [];
      const existingReaction = currentReactions.find((reaction) => reaction.emoji === emoji);
      const nextReactions = existingReaction
        ? currentReactions
          .map((reaction) => {
            if (reaction.emoji !== emoji) return reaction;
            const reactedByMe = !reaction.reactedByMe;
            const count = Math.max(0, (Number(reaction.count) || 0) + (reactedByMe ? 1 : -1));
            const currentUsers = Array.isArray(reaction.users) ? reaction.users : [];
            return {
              ...reaction,
              reactedByMe,
              count,
              users: reactedByMe
                ? [...currentUsers.filter((user) => `${user.id}` !== `${currentReactionUser.id}`), currentReactionUser]
                : currentUsers.filter((user) => `${user.id}` !== `${currentReactionUser.id}`),
            };
          })
          .filter((reaction) => Number(reaction.count) > 0)
        : [...currentReactions, { emoji, count: 1, reactedByMe: true, users: [currentReactionUser] }];

      return { ...comment, reactions: nextReactions };
    });
    setReactionPickerCommentId("");
    try {
      await toggleWorkspaceRecordCommentReaction(recordId, commentId, reactionKey);
      silentlyRefreshSelectedRecordTimeline(recordId);
    } catch (error) {
      setRecordTimeline(previousTimeline);
      setTimelineError(error?.response?.data?.message || "Unable to update comment reaction.");
    }
  };

  const toggleTicketWatch = async () => {
    if (!selectedRecord?.id) return;
    const recordId = selectedRecord.id;
    const updated = await onUpdateRecord?.(recordId, { toggleWatcher: true });
    if (updated && canApplyTicketSaveResult(recordId)) setSelectedRecord(updated);
  };

  const openTicketDescriptionEditor = () => {
    setTicketDescriptionDraft(detailDraft?.description || "");
    setTicketDescriptionEditing(true);
  };

  const openTicketTitleEditor = () => {
    setTicketTitleDraft(detailDraft?.title || "");
    setTicketTitleEditing(true);
  };

  const cancelTicketTitleEditor = () => {
    setTicketTitleDraft(detailDraft?.title || "");
    setTicketTitleEditing(false);
  };

  const saveTicketTitleEditor = () => {
    setDetailDraft((current) => ({
      ...current,
      title: ticketTitleDraft,
    }));
    setTicketTitleEditing(false);
  };

  const cancelTicketDescriptionEditor = () => {
    setTicketDescriptionDraft(detailDraft?.description || "");
    setTicketDescriptionEditing(false);
  };

  const saveTicketDescriptionEditor = () => {
    setDetailDraft((current) => ({
      ...current,
      description: ticketDescriptionDraft,
    }));
    setTicketDescriptionEditing(false);
  };

  const openSprintGoalEditor = () => {
    setSprintGoalDraft(detailDraft?.description || "");
    setSprintGoalEditing(true);
  };

  const cancelSprintGoalEditor = () => {
    setSprintGoalDraft(detailDraft?.description || "");
    setSprintGoalEditing(false);
  };

  const saveSprintGoalEditor = () => {
    setDetailDraft((current) => ({ ...current, description: sprintGoalDraft }));
    setSprintGoalEditing(false);
  };

  const attachTicketFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedRecord?.id) return;
    const recordId = selectedRecord.id;
    if (file.size > 15 * 1024 * 1024) { setTimelineError("Attachments must be 15 MB or smaller."); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const updated = await onUpdateRecord?.(recordId, { addAttachment: { data: reader.result, name: file.name, mimeType: file.type, sizeBytes: file.size } });
        if (updated && canApplyTicketSaveResult(recordId)) setSelectedRecord(updated);
      } catch (error) {
        if (canApplyTicketSaveResult(recordId)) setTimelineError(error?.response?.data?.message || "Unable to attach the file.");
      }
    };
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const copyCommentLink = async (commentId) => {
    onRecordRouteChange(selectedRecord, commentId);
    const projectSlug = selectedRecord?.projectSlug || selectedProjectSlug;
    if (!projectSlug) {
      setTimelineError("This ticket needs a project before a shareable link can be created.");
      return;
    }
    const url = new URL(window.location.href);
    const workspaceSlug = window.location.pathname.split("/").filter(Boolean)[0] || "workspace";
    url.pathname = `/${workspaceSlug}/${projectSlug}/${toTicketRouteSegment(selectedRecord)}`;
    url.searchParams.set("focusedCommentId", commentId);
    const link = url.toString();
    try {
      await navigator.clipboard.writeText(link);
      setCopiedCommentId(commentId);
      window.setTimeout(() => setCopiedCommentId((current) => (current === commentId ? "" : current)), 1600);
    } catch (error) {
      setTimelineError("Could not copy the comment link.");
    }
  };

  const showBoardView = (
    (moduleName === "CRM" && activeTab === "Pipeline") ||
    (moduleName === "Projects" && ["Overview", "Active", "Planning", "On Hold"].includes(activeTab))
  );

  const renderRecords = () => {
    const visibleRecords = filteredRecords;
    if (moduleName === "Projects" && activeTab === "Overview") {
      return <ProjectsView
        onAddTask={openProjectTaskModal}
        onOpenTask={openRecordHistory}
        onUpdateTask={updateRecord}
        projectName={selectedProjectName || "Project"}
        records={records.filter((record) => isWorkItemRecord(record))}
      />;
    }
    if (showBoardView) {
      return (
        <div className="functional-kanban" aria-label={`${moduleName} ${activeTab} board`}>
            {config.statuses.map((status) => ({ key: status, label: status, records: visibleRecords.filter((record) => record.status === status), statuses: [status] })).map((lane) => {
              const columnRecords = lane.records;
              return (
                <section className={dragLaneKey === lane.key ? "is-drag-target" : ""} key={lane.key}>
                  <header>
                    <strong>{lane.label}</strong>
                    <span>{columnRecords.length}</span>
                  </header>
                  <div>
                    {columnRecords.map((record) => (
                      <article className={draggedTicketId === `${record.id}` ? "is-dragging" : ""} key={record.id}>
                        <strong>{record.title}</strong>
                        <span>{record.type} · {record.owner || "Unassigned"}</span>
                        {moduleName === "Projects" ? (
                          <small>
                            {[record.accountClient, record.endClient, record.clientPortalEnabled ? `Client dashboard · ${record.portalMembersCount || 0} members` : ""]
                              .filter(Boolean)
                              .join(" · ") || "No client mapping yet"}
                          </small>
                        ) : null}
                        <small>{record.priority} · {record.due || "No due date"}</small>
                        <div className="work-card-context">
                          {record.sprint ? <em>{record.sprint}</em> : null}
                          {record.epic ? <em>{record.epic}</em> : null}
                          {record.storyPoints ? <em>{record.storyPoints} pts</em> : null}
                          {record.severity ? <em>{record.severity}</em> : null}
                        </div>
                        <div className="record-actions">
                          <button disabled={saving} onClick={() => openRecordHistory(record)} type="button">{isWorkItemRecord(record) ? "Edit" : "History"}</button>
                          {moduleName !== "Work" ? <>
                            <button disabled={saving} onClick={() => moveRecord(record)} type="button">Move</button>
                            <button disabled={saving} onClick={() => updateRecord(record.id, { status: "Blocked" })} type="button">Block</button>
                          </> : null}
                        </div>
                      </article>
                    ))}
                    {!columnRecords.length ? <p>No records</p> : null}
                  </div>
                </section>
              );
            })}
          </div>
      );
    }

    return (
      <div className="functional-list">
        {visibleRecords.map((record) => (
          <article key={record.id}>
            <div>
              <strong>{record.title}</strong>
              <span>{record.type} · {record.owner}</span>
              {moduleName === "Projects" ? (
                <small>
                  {[record.accountClient && `Account: ${record.accountClient}`, record.endClient && `End client: ${record.endClient}`, record.clientPortalEnabled ? `Client dashboard enabled · ${record.portalMembersCount || 0} members` : ""]
                    .filter(Boolean)
                    .join(" · ") || "No end-client details"}
                </small>
              ) : null}
              {moduleName === "Work" ? (
                <small>
                  {[record.sprint, record.epic, record.storyPoints ? `${record.storyPoints} pts` : "", asList(record.assignees).join(", ")]
                    .filter(Boolean)
                    .join(" · ") || "No sprint metadata"}
                </small>
              ) : null}
              {moduleName === "Knowledge" && record.description ? (
                <WorkspaceRichText className="functional-record-rich-snippet" value={record.description} />
              ) : null}
            </div>
            <div className="record-meta">
              <em className={`priority ${record.priority.toLowerCase()}`}>{record.priority}</em>
              {moduleName === "Work" ? <span className="people-table-status">{record.status}</span> : <select
                aria-label={`Status for ${record.title}`}
                onChange={(event) => {
                  updateRecord(record.id, { status: event.target.value });
                }}
                disabled={saving}
                value={record.status}
              >
                {config.statuses.map((status) => (
                  <option key={status}>{status}</option>
                ))}
              </select>}
              <span>{record.due}</span>
            </div>
            <div className="record-actions">
              <button disabled={saving} onClick={() => openRecordHistory(record)} type="button">{isWorkItemRecord(record) ? "Edit" : "History"}</button>
              {moduleName !== "Work" ? <>
                <button disabled={saving} onClick={() => moveRecord(record)} type="button">Move</button>
                <button disabled={saving} onClick={() => updateRecord(record.id, { status: "Blocked" })} type="button">Block</button>
              </> : null}
              <button disabled={saving} onClick={() => deleteRecord(record)} type="button">Delete</button>
            </div>
          </article>
        ))}
        {!visibleRecords.length ? <p className="empty-state">No matching records. Create one to start this subtab.</p> : null}
      </div>
    );
  };

  const buildIssueActivityFeed = () => {
    if (!recordTimeline) {
      return { all: [], comments: [], transitions: [], activity: [] };
    }
    const historyItems = (recordTimeline.history || []).map((item) => ({ ...item, source: "history" }));
    const activityItems = (recordTimeline.activity || []).map((item) => ({ ...item, source: "activity" }));
    const comments = sortCommentsNewestFirst(Array.isArray(recordTimeline.comments) ? recordTimeline.comments : [...historyItems, ...activityItems]
      .filter((item) => item.action === "comment_added")
    );
    const transitions = historyItems
      .filter((item) => item.action === "status_changed" || item.field === "status")
      .sort((a, b) => getCommentTimeValue(b) - getCommentTimeValue(a));
    const activityFeed = activityItems
      .filter((item) => item.action !== "comment_added")
      .sort((a, b) => getCommentTimeValue(b) - getCommentTimeValue(a));
    const all = [...comments, ...transitions, ...activityFeed]
      .sort((a, b) => getCommentTimeValue(b) - getCommentTimeValue(a));
    return { all, comments, transitions, activity: activityFeed };
  };

  const startQuickAction = (action) => {
    const actionText = action.toLowerCase();
    const matchingType = config.types.find((type) => {
      const typeText = type.toLowerCase();
      if (typeText === "backlog item") return actionText.includes("backlog");
      return actionText.includes(typeText.split(" ")[0]);
    }) || config.types[0];
    const quickActionWorkflow = moduleName === "Work" ? getTicketWorkflow(matchingType) : [];
    setDraft((current) => ({
      ...current,
      title: "",
      type: matchingType,
      owner: moduleName === "Work" ? "" : defaultOwner,
      status: moduleName === "Work"
        ? getDefaultTicketStatus(quickActionWorkflow, current.status || config.statuses[0])
        : config.statuses[0],
      portalRoles: moduleName === "Projects" ? (current.portalRoles?.length ? current.portalRoles : defaultClientPortalRoles) : current.portalRoles,
      sprint: matchingType === "Sprint" ? current.sprint || "Sprint 1" : current.sprint,
      epic: matchingType === "Epic" ? current.epic || "Core delivery" : current.epic,
      templateName: matchingType === "Template" ? current.templateName || "Reusable work template" : current.templateName,
      automationTrigger: matchingType === "Automation" ? current.automationTrigger || "When status changes" : current.automationTrigger,
    }));
    setFormOpen(true);
  };

  const renderModuleCommandCenter = () => {
    if (["Work", "Schedule"].includes(moduleName)) return null;

    return (
      <section className="module-command-center" aria-label={`${moduleName} command center`}>
      <div className="module-signal-grid">
        {playbook.signals.map((signal) => (
          <article key={signal}>
            <span>{signal}</span>
            <strong>{getRecordCountForSignal(allModuleRecords, signal)}</strong>
            <small>{activeTab} context</small>
          </article>
        ))}
      </div>
      <div className="module-action-panel">
        <div>
          <span>Quick actions</span>
          <h3>{moduleName} workflow</h3>
        </div>
        <div className="module-action-buttons">
          {playbook.actions.map((action) => (
            <button disabled={saving || isLoading} key={action} onClick={() => startQuickAction(action)} type="button">
              {action}
            </button>
          ))}
        </div>
      </div>
      <div className="module-checklist-panel">
        <div>
          <span>Required hygiene</span>
          <h3>Before this moves forward</h3>
        </div>
        <div>
          {playbook.checklist.map((item) => (
            <p key={item}>{item}</p>
          ))}
        </div>
      </div>
      </section>
    );
  };

  const renderTabBrief = () => {
    if (["Work", "People", "Schedule"].includes(moduleName)) return null;
    const openRecords = moduleRecords.filter((record) => !closedStatuses.includes(record.status)).length;
    const pendingRecords = moduleRecords.filter((record) => /review|pending|blocked|hold/i.test(record.status)).length;
    const scope = moduleName === "Knowledge" ? "Knowledge" : moduleName;
    const description = tabFocusCopy[activeTab] || `Manage ${activeTab.toLowerCase()} records and keep the next action clear.`;

    return (
      <section className="functional-panel tab-brief-panel">
        <div className="functional-panel-header">
          <div>
            <span>{scope} · {activeTab}</span>
            <h3>{activeTab} operations</h3>
          </div>
          <button className="secondary-action" disabled={saving || isLoading} onClick={openCreateModal} type="button">{createAction[0]}</button>
        </div>
        <p>{description}</p>
        <div className="tab-brief-metrics">
          <span><strong>{moduleRecords.length}</strong> records</span>
          <span><strong>{openRecords}</strong> open</span>
          <span><strong>{pendingRecords}</strong> needs attention</span>
        </div>
      </section>
    );
  };

  const saveBoardDraft = async (event) => {
    event?.preventDefault?.();
    const name = boardDraft.name.trim();
    if (!name) {
      setBoardError("Board name is required.");
      return;
    }
    setBoardBusy(true);
    setBoardError("");
    const payload = {
      name,
      filters: boardDraft.filters,
      sharedWith: boardDraft.sharedWith,
    };
    try {
      const board = boardDraft.id
        ? await updateWorkspaceBoard(boardDraft.id, payload)
        : await createWorkspaceBoard(payload);
      setSavedBoards((current) => {
        const next = boardDraft.id
          ? current.map((item) => (item.id === board.id ? board : item))
          : [board, ...current];
        return next.sort((left, right) => new Date(right.updatedAt || 0).getTime() - new Date(left.updatedAt || 0).getTime());
      });
      setBoardDraft(hydrateWorkBoardDraft(board));
      openSavedBoard(board);
      closeBoardModal();
    } catch (error) {
      setBoardError(error?.response?.data?.message || "Unable to save this board right now.");
    } finally {
      setBoardBusy(false);
    }
  };

  const removeBoard = async (board) => {
    const persistedBoard = savedBoards.find((item) => item.id === board.id) || board;
    if (boardDeleteConfirmation.trim() !== persistedBoard.name) {
      setBoardError(`Type “${persistedBoard.name}” exactly to delete this board.`);
      return;
    }
    setBoardBusy(true);
    setBoardError("");
    try {
      await deleteWorkspaceBoard(board.id);
      setSavedBoards((current) => current.filter((item) => item.id !== board.id));
      if (boardDraft.id === board.id) setBoardDraft(createEmptyWorkBoardDraft());
      if (activeSavedBoard?.id === board.id) setActiveSavedBoard(null);
      closeBoardModal();
    } catch (error) {
      setBoardError(error?.response?.data?.message || "Unable to delete this board right now.");
    } finally {
      setBoardBusy(false);
    }
  };

  const renderWorkDeepPanel = () => {
    if (moduleName !== "Work") return null;

    const workRecords = allModuleRecords;
    const groupedBySprint = workRecords
      .filter((record) => record.sprint || /sprint/i.test(record.type))
      .reduce((groups, record) => {
        const key = record.sprint || record.title || "Unassigned sprint";
        if (!groups[key]) groups[key] = [];
        groups[key].push(record);
        return groups;
      }, {});
    const groupedByEpic = workRecords
      .filter((record) => record.epic || /epic/i.test(record.type))
      .reduce((groups, record) => {
        const key = record.epic || record.title || "Unassigned epic";
        if (!groups[key]) groups[key] = [];
        groups[key].push(record);
        return groups;
      }, {});
    const backlog = workRecords.filter((record) => record.status === "Backlog" || /backlog/i.test(record.type));
    const approvals = workRecords.filter((record) =>
      ["Review", "In Review", "Pending", "Needs Review"].includes(record.status) || /approval/i.test(record.type)
    );
    const ticketRecordPattern = /task|ticket|request|support|bug|defect|incident|improvement|feature|story/i;
    const tickets = workRecords.filter((record) => ticketRecordPattern.test(record.type));
    const automations = workRecords.filter((record) => /automation|rule|trigger/i.test(`${record.type} ${record.automationTrigger}`));
    const templates = workRecords.filter((record) => /template/i.test(`${record.type} ${record.templateName}`));
    const renderMiniList = (items, emptyText) => (
      <div className="work-mini-list">
        {items.slice(0, 6).map((record) => (
          <button className="work-mini-list-row" disabled={saving} key={record.id} onClick={() => openRecordHistory(record)} type="button">
            <div>
              <strong>{record.title}</strong>
              <span>{[record.type, record.owner || "Unassigned", record.sprint, record.epic].filter(Boolean).join(" · ")}</span>
            </div>
            <em>{record.status}</em>
          </button>
        ))}
        {!items.length ? <p className="empty-state">{emptyText}</p> : null}
      </div>
    );

    const renderGroupCards = (groups, emptyText, typeLabel) => {
      const entries = Object.entries(groups);
      if (!entries.length) return <p className="empty-state">{emptyText}</p>;

      return (
        <div className="work-planning-grid">
          {entries.map(([name, items]) => {
            const done = items.filter((record) => closedStatuses.includes(record.status)).length;
            const points = sumStoryPoints(items);
            const completion = items.length ? Math.round((done / items.length) * 100) : 0;

            return (
              <article key={name} className="work-planning-card">
                <header>
                  <div>
                    <span>{typeLabel}</span>
                    <h3>{name}</h3>
                  </div>
                  <strong>{completion}%</strong>
                </header>
                <div className="work-progress-track">
                  <i style={{ width: `${completion}%` }} />
                </div>
                <div className="work-planning-stats">
                  <span>{items.length} items</span>
                  <span>{points} pts</span>
                  <span>{items.filter((record) => record.status === "Blocked").length} blocked</span>
                </div>
                {renderMiniList(items, "No linked work yet.")}
              </article>
            );
          })}
        </div>
      );
    };

    const renderWorkQueue = (items, kind, emptyText) => (
      <section className="functional-panel work-queue-panel">
        <div className="functional-panel-header"><div><span>{kind} queue</span><h3>{kind} that need a clear next action</h3></div><small>{items.length} records</small></div>
        <div className="work-queue-list">
          {items.map((record) => <button className="work-queue-row" disabled={saving} key={record.id} onClick={() => openRecordHistory(record)} type="button"><div><strong>{record.title}</strong><span>{[getProjectDisplayName(record.projectRef), record.sprint, record.epic, record.owner || "Unassigned"].filter(Boolean).join(" · ") || "No delivery context"}</span></div><em className={`priority ${(record.priority || "medium").toLowerCase()}`}>{record.priority || "Medium"}</em><small>{record.due || "No due date"}</small></button>)}
          {!items.length ? <p className="empty-state">{emptyText}</p> : null}
        </div>
      </section>
    );

    if (activeTab === "Board") {
      return (
        <section className="functional-panel work-deep-panel">
          {boardError ? <p className="workspace-inline-error">{boardError}</p> : null}
          <div className="functional-panel-header work-board-section-header">
            <div className="work-board-toolbar-shell">
              <div className="work-board-toolbar-main">
                <div className="work-board-toolbar-title-row">
                  <label className="work-board-toolbar-select">
                    <select onChange={(event) => selectSavedBoardById(event.target.value)} value={boardDropdownValue}>
                      <option value="">All tickets ({allTicketsBoardCount})</option>
                      {savedBoards.map((board) => <option key={board.id} value={board.id}>{board.name} ({savedBoardCounts[`${board.id}`] || 0})</option>)}
                      {hasCustomBoardFilters && !isAllTicketsBoardState ? <option value="__custom__">Custom filter ({displayedRecords.length})</option> : null}
                    </select>
                  </label>
                </div>
                {renderActiveBoardFilterStrip(currentBoardFilters || workBoardDefaultFilters)}
              </div>
            </div>
          </div>
          <WorkKanbanView
            activeBoardSelected={Boolean(activeSavedBoard)}
            ariaLabel="Work saved board preview"
            draggedTicketId={draggedTicketId}
            dragLaneKey={dragLaneKey}
            getAssigneeProfile={getBoardCardAssigneeProfile}
            getDateLabel={formatBoardCardDate}
            hideEmptyWhenBoardSelected
            laneAccentMap={workBoardLaneAccent}
            lanes={generalizedDisplayedBoard}
            onLaneDragOver={onLaneDragOver}
            onLaneDrop={onLaneDrop}
            onOpenRecord={openRecordHistory}
            onTicketDragEnd={onTicketDragEnd}
            onTicketDragStart={onTicketDragStart}
            renderTypeMark={(type) => <WorkTypeMark type={type} />}
            showLane={filterVisibleWorkBoardLanes}
            sortRecords={sortBoardRecords}
          />
        </section>
      );
    }

    if (activeTab === "Tickets") {
      const makeDateKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const today = new Date();
      const nextSevenDays = new Date(today);
      nextSevenDays.setDate(today.getDate() + 7);
      const todayKey = makeDateKey(today);
      const nextSevenDaysKey = makeDateKey(nextSevenDays);
      const isClosedTicket = (record) => closedStatuses.includes(record.status);
      const ticketDueTime = (record) => {
        if (!record.due) return Number.MAX_SAFE_INTEGER;
        const date = /^\d{4}-\d{2}-\d{2}$/.test(`${record.due}`) ? new Date(`${record.due}T00:00:00`) : new Date(record.due);
        return Number.isNaN(date.getTime()) ? Number.MAX_SAFE_INTEGER : date.getTime();
      };
      const todayStartTime = new Date(`${todayKey}T00:00:00`).getTime();
      const nextSevenEndTime = new Date(`${nextSevenDaysKey}T23:59:59`).getTime();
      const isTicketOverdue = (record) => ticketDueTime(record) < todayStartTime && !isClosedTicket(record);
      const isTicketDueSoon = (record) => {
        const dueTime = ticketDueTime(record);
        return dueTime !== Number.MAX_SAFE_INTEGER && dueTime >= todayStartTime && dueTime <= nextSevenEndTime && !isClosedTicket(record);
      };
      const isTicketBlocked = (record) => /blocked|hold|stuck/i.test(record.status);
      const hasTicketOwner = (record) => Boolean(record.owner || asList(record.assignees).length);
      const ticketPriorityScore = { low: 1, normal: 2, medium: 3, high: 4, urgent: 5, critical: 6 };
      const ticketRiskScore = (record) =>
        (isTicketBlocked(record) ? 50 : 0)
        + (isTicketOverdue(record) ? 40 : 0)
        + (/bug|defect|incident/i.test(record.type) ? 20 : 0)
        + (!hasTicketOwner(record) && !isClosedTicket(record) ? 15 : 0)
        + (record.flagged ? 14 : 0)
        + (ticketPriorityScore[`${record.priority || "medium"}`.toLowerCase()] || 0);
      const isTicketAtRisk = (record) => !isClosedTicket(record) && (
        isTicketBlocked(record)
        || isTicketOverdue(record)
        || /bug|defect|incident/i.test(record.type)
        || record.flagged
        || (ticketPriorityScore[`${record.priority || ""}`.toLowerCase()] || 0) >= 4
      );
      const filteredTicketRecords = filteredRecords.filter((record) => ticketRecordPattern.test(record.type));
      const openTicketRecords = filteredTicketRecords.filter((record) => !isClosedTicket(record));
      const ticketScopeOptions = [
        { key: "active", label: "Active", count: openTicketRecords.length, predicate: (record) => !isClosedTicket(record) },
        { key: "blocked", label: "Blocked", count: filteredTicketRecords.filter((record) => isTicketBlocked(record) && !isClosedTicket(record)).length, predicate: (record) => isTicketBlocked(record) && !isClosedTicket(record) },
        { key: "overdue", label: "Overdue", count: filteredTicketRecords.filter(isTicketOverdue).length, predicate: isTicketOverdue },
        { key: "risk", label: "At risk", count: filteredTicketRecords.filter(isTicketAtRisk).length, predicate: isTicketAtRisk },
        { key: "due_soon", label: "Due soon", count: filteredTicketRecords.filter(isTicketDueSoon).length, predicate: isTicketDueSoon },
        { key: "incidents", label: "Incidents", count: filteredTicketRecords.filter((record) => /bug|defect|incident/i.test(record.type) && !isClosedTicket(record)).length, predicate: (record) => /bug|defect|incident/i.test(record.type) && !isClosedTicket(record) },
        { key: "unassigned", label: "Unassigned", count: filteredTicketRecords.filter((record) => !hasTicketOwner(record) && !isClosedTicket(record)).length, predicate: (record) => !hasTicketOwner(record) && !isClosedTicket(record) },
        { key: "no_due", label: "No due date", count: filteredTicketRecords.filter((record) => !record.due && !isClosedTicket(record)).length, predicate: (record) => !record.due && !isClosedTicket(record) },
        { key: "done", label: "Completed", count: filteredTicketRecords.filter(isClosedTicket).length, predicate: isClosedTicket },
        { key: "all", label: "All", count: filteredTicketRecords.length, predicate: () => true },
      ];
      const selectedTicketScope = ticketScopeOptions.find((option) => option.key === ticketScopeFilter) || ticketScopeOptions[0];
      const visibleTickets = filteredTicketRecords
        .filter(selectedTicketScope.predicate)
        .sort((left, right) => {
          const riskDifference = ticketRiskScore(right) - ticketRiskScore(left);
          if (riskDifference) return riskDifference;
          const dueDifference = ticketDueTime(left) - ticketDueTime(right);
          if (dueDifference) return dueDifference;
          return new Date(right.updatedAt || right.createdAt || 0).getTime() - new Date(left.updatedAt || left.createdAt || 0).getTime();
        });
      const totalTickets = tickets.length;
      const totalOpenTickets = tickets.filter((record) => !isClosedTicket(record)).length;
      const totalRiskTickets = tickets.filter(isTicketAtRisk).length;
      const totalUnassignedTickets = tickets.filter((record) => !isClosedTicket(record) && !hasTicketOwner(record)).length;
      const totalDueSoonTickets = tickets.filter(isTicketDueSoon).length;
      const ticketDisplayKey = (record) => record?.key || record?.id || "Ticket";
      const ticketStatusClass = (record) => isTicketBlocked(record)
        ? "is-blocked"
        : isClosedTicket(record)
        ? "is-done"
        : isTicketOverdue(record)
        ? "is-overdue"
        : "is-active";
      const applyTicketScopeFilter = (scope) => {
        setTicketScopeFilter(scope);
        if (statusFilter !== "All") setStatusFilter("All");
      };

      return (
        <section className="functional-panel work-ticket-list-panel is-command-center">
          <div className="functional-panel-header">
            <div>
              <span>Ticket list</span>
              <h3>Search, audit, and triage</h3>
            </div>
            <small>{visibleTickets.length} shown · {totalTickets} total · {query || statusFilter !== "All" ? "filtered" : "risk-first"}</small>
          </div>
          <div className="work-ticket-command-strip" aria-label="Ticket quick scopes">
            <button aria-pressed={ticketScopeFilter === "active"} className={ticketScopeFilter === "active" ? "active" : ""} onClick={() => applyTicketScopeFilter("active")} type="button"><span>Open</span><strong>{totalOpenTickets}</strong></button>
            <button aria-pressed={ticketScopeFilter === "risk"} className={ticketScopeFilter === "risk" ? "active" : ""} onClick={() => applyTicketScopeFilter("risk")} type="button"><span>At risk</span><strong>{totalRiskTickets}</strong></button>
            <button aria-pressed={ticketScopeFilter === "due_soon"} className={ticketScopeFilter === "due_soon" ? "active" : ""} onClick={() => applyTicketScopeFilter("due_soon")} type="button"><span>Due soon</span><strong>{totalDueSoonTickets}</strong></button>
            <button aria-pressed={ticketScopeFilter === "unassigned"} className={ticketScopeFilter === "unassigned" ? "active" : ""} onClick={() => applyTicketScopeFilter("unassigned")} type="button"><span>Unassigned</span><strong>{totalUnassignedTickets}</strong></button>
          </div>
          <div className="work-ticket-scope-tabs" aria-label="Ticket scope">
            {ticketScopeOptions.map((option) => (
              <button
                aria-pressed={selectedTicketScope.key === option.key}
                className={selectedTicketScope.key === option.key ? "active" : ""}
                key={option.key}
                onClick={() => applyTicketScopeFilter(option.key)}
                type="button"
              >
                <span>{option.label}</span>
                <strong>{option.count}</strong>
              </button>
            ))}
          </div>
          <div className="work-ticket-table" aria-label="Tickets">
            <div className="work-ticket-table-head" aria-hidden="true">
              <span>Ticket</span>
              <span>Project</span>
              <span>State</span>
              <span>Owner</span>
              <span>Due</span>
              <span>Priority</span>
            </div>
            <div className="work-ticket-table-body">
              {visibleTickets.map((record) => {
                const ownerLabel = record.owner || asList(record.assignees)[0] || "Unassigned";
                const projectName = getProjectDisplayName(record.projectRef) || "No project";
                const meta = [record.sprint, record.epic, record.type].filter(Boolean).join(" · ") || "No planning context";
                return (
                  <button aria-label={`Open ${ticketDisplayKey(record)} ${record.title || "ticket"}`} className={`work-ticket-row ${ticketStatusClass(record)}`} key={record.id} onClick={() => openRecordHistory(record)} type="button">
                    <span className="work-ticket-row-main">
                      <i><WorkTypeMark type={record.type} />{ticketDisplayKey(record)}</i>
                      <strong>{record.title || "Untitled ticket"}</strong>
                      <small>{meta}</small>
                    </span>
                    <span className="work-ticket-row-project">{projectName}</span>
                    <span className="work-ticket-row-status">{record.status || "Open"}</span>
                    <span className="work-ticket-row-owner">{ownerLabel}</span>
                    <span className={`work-ticket-row-due ${isTicketOverdue(record) ? "is-overdue" : ""}`}>{formatBoardCardDate(record.due)}</span>
                    <span className={`priority ${(record.priority || "medium").toLowerCase()}`}>{record.priority || "Medium"}</span>
                  </button>
                );
              })}
              {!visibleTickets.length ? <p className="empty-state">No tickets match this view.</p> : null}
            </div>
          </div>
        </section>
      );
    }

    if (activeTab === "Labels") {
      const filterIsApplied = Boolean(query.trim() || statusFilter !== "All");
      const filteredLabelRecords = filteredRecords.filter((record) => ticketRecordPattern.test(record.type));
      const labelGroups = workspaceLabelOptions
        .map((label) => {
          const labelKey = label.toLowerCase();
          const allItems = tickets.filter((record) => asList(record.labels).some((item) => item.toLowerCase() === labelKey));
          const items = filterIsApplied
            ? filteredLabelRecords.filter((record) => asList(record.labels).some((item) => item.toLowerCase() === labelKey))
            : allItems;
          return { label, allItems, items };
        })
        .filter((group) => group.allItems.length && group.items.length)
        .sort((left, right) => right.items.length - left.items.length || left.label.localeCompare(right.label));
      const ticketDisplayKey = (record) => record?.key || record?.id || "Ticket";

      return (
        <section className="functional-panel work-deep-panel work-labels-panel">
          <div className="functional-panel-header">
            <div>
              <span>Labels</span>
              <h3>Ticket labels across active work</h3>
            </div>
            <small>{labelGroups.length} labels</small>
          </div>
          <div className="work-label-grid">
            {labelGroups.map((group) => (
              <article className="work-label-card" key={group.label}>
                <header>
                  <div>
                    <span className="ticket-label-chip">{group.label}</span>
                    <small>{group.items.length}{filterIsApplied && group.items.length !== group.allItems.length ? ` of ${group.allItems.length}` : ""} tickets</small>
                  </div>
                  <button onClick={() => { setQuery(group.label); setStatusFilter("All"); }} type="button">Filter</button>
                </header>
                <div className="work-mini-list">
                  {group.items.slice(0, 5).map((record) => (
                    <button className="work-mini-list-row" disabled={saving} key={record.id} onClick={() => openRecordHistory(record)} type="button">
                      <div>
                        <strong>{record.title || "Untitled ticket"}</strong>
                        <span>{[ticketDisplayKey(record), record.type, record.owner || "Unassigned"].filter(Boolean).join(" · ")}</span>
                      </div>
                      <em>{record.status || "Open"}</em>
                    </button>
                  ))}
                </div>
              </article>
            ))}
            {!labelGroups.length ? <p className="empty-state">No ticket labels match this view. Add labels from a ticket sidebar or while creating a ticket.</p> : null}
          </div>
        </section>
      );
    }

    if (activeTab === "Backlog") {
      const ready = backlog.filter((record) => record.owner && record.priority && (record.description || record.storyPoints));
      const needsRefinement = backlog.filter((record) => !ready.includes(record));
      return <div className="work-tab-grid">{renderWorkQueue(ready.slice(0, 12), "Sprint-ready", "No backlog items are sprint-ready.")}{renderWorkQueue(needsRefinement.slice(0, 12), "Refinement", "Backlog refinement is complete.")}</div>;
    }

    if (activeTab === "Sprints") {
      return (
        <section className="functional-panel work-deep-panel">
          <div className="functional-panel-header">
            <div>
              <span>Sprint planning</span>
              <h3>Sprint scope across all epics, points, blockers, and completion</h3>
            </div>
            <small>{Object.keys(groupedBySprint).length} sprints</small>
          </div>
          {renderGroupCards(groupedBySprint, "No sprint records yet. Create a Sprint or add Sprint on tasks.", "Sprint")}
        </section>
      );
    }

    if (activeTab === "Epics") {
      return (
        <section className="functional-panel work-deep-panel">
          <div className="functional-panel-header">
            <div>
              <span>Epic roadmap</span>
              <h3>Initiatives across every sprint, including unscheduled work</h3>
            </div>
            <small>{Object.keys(groupedByEpic).length} epics</small>
          </div>
          {renderGroupCards(groupedByEpic, "No epic records yet. Create an Epic or link tasks to an epic.", "Epic")}
        </section>
      );
    }

    if (activeTab === "Templates") {
      return (
        <section className="functional-panel work-deep-panel">
          <div className="functional-panel-header">
            <div>
              <span>Template library</span>
              <h3>Repeatable formats for ticket types, delivery, and approvals</h3>
            </div>
            <small>{templates.length} templates</small>
          </div>
          {renderMiniList(templates, "No work templates yet. Create a Template record to reuse team formats.")}
        </section>
      );
    }

    if (activeTab === "Automation") {
      return (
        <section className="functional-panel work-deep-panel">
          <div className="functional-panel-header">
            <div>
              <span>Automation rules</span>
              <h3>Triggers that move, notify, assign, or escalate work</h3>
            </div>
            <small>{automations.length} rules</small>
          </div>
          <div className="work-rule-grid">
            {automations.map((record) => (
              <article key={record.id}>
                <strong>{record.title}</strong>
                <span>{record.automationTrigger || "No trigger configured"}</span>
                <em>{record.status}</em>
              </article>
            ))}
            {!automations.length ? <p className="empty-state">No automation rules yet. Create an Automation record with a trigger.</p> : null}
          </div>
        </section>
      );
    }

    if (activeTab === "Approvals") {
      const assignedToMe = approvals.filter((record) =>
        [...asList(record.assignees), record.approvalRole].some((value) => {
          const target = String(value || "").toLowerCase();
          return target === String(defaultOwner || "").toLowerCase()
            || [currentUser?.workspaceRole, currentUser?.accessLevel, currentUser?.role]
              .filter(Boolean).some((role) => target === String(role).toLowerCase());
        })
      );
      const awaitingApproval = approvals.filter((record) => !assignedToMe.includes(record));
      const visibleApprovals = approvalListTab === "submitted" ? awaitingApproval : assignedToMe;
      const canDecide = approvalListTab === "decide";
      return (
        <section className="functional-panel work-deep-panel">
          <div className="functional-panel-header">
            <div>
              <span>Approval queue</span>
              <h3>Manager, client, QA, and finance decisions</h3>
            </div>
            <small>{approvals.length} waiting</small>
          </div>
          <div className="approval-list-tabs" role="tablist">
            <button aria-selected={approvalListTab === "submitted"} className={approvalListTab === "submitted" ? "active" : ""} onClick={() => setApprovalListTab("submitted")} role="tab" type="button">To be approved <span>{awaitingApproval.length}</span></button>
            <button aria-selected={approvalListTab === "decide"} className={approvalListTab === "decide" ? "active" : ""} onClick={() => setApprovalListTab("decide")} role="tab" type="button">To approve <span>{assignedToMe.length}</span></button>
          </div>
          <div className="people-data-table-wrap"><table className="people-data-table"><thead><tr><th>Request</th><th>Requested by</th><th>Approver</th><th>Status</th><th aria-label="Actions" /></tr></thead><tbody>{visibleApprovals.map((record) => <tr key={record.id}><td><strong>{record.title}</strong><small>{record.type}</small></td><td>{record.owner || "Unassigned"}</td><td>{record.approvalRole || "Not set"}</td><td><span className="people-table-status">{record.status}</span></td><td>{canDecide ? <button disabled={saving} onClick={() => openRecordHistory(record)} type="button">Review in modal</button> : <span className="approval-waiting">Awaiting decision</span>}</td></tr>)}</tbody></table>{!visibleApprovals.length ? <p className="empty-state">Nothing waiting in this section.</p> : null}</div>
        </section>
      );
    }

    if (activeTab !== "Overview") return null;

    const makeDateKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const today = new Date();
    const nextSevenDays = new Date(today);
    nextSevenDays.setDate(today.getDate() + 7);
    const staleCutoff = new Date(today);
    staleCutoff.setDate(today.getDate() - 7);
    const todayKey = makeDateKey(today);
    const nextSevenDaysKey = makeDateKey(nextSevenDays);
    const openTickets = tickets.filter((record) => !closedStatuses.includes(record.status));
    const doneTickets = tickets.filter((record) => closedStatuses.includes(record.status));
    const blockedTickets = openTickets.filter((record) => /blocked|hold|stuck/i.test(record.status));
    const highPriorityTickets = openTickets.filter((record) => ["High", "Urgent"].includes(record.priority));
    const incidentTickets = openTickets.filter((record) => /bug|defect|incident/i.test(record.type));
    const overdueTickets = openTickets.filter((record) => record.due && record.due < todayKey);
    const dueSoonTickets = openTickets.filter((record) => record.due && record.due >= todayKey && record.due <= nextSevenDaysKey);
    const staleTickets = openTickets.filter((record) => {
      const date = new Date(record.updatedAt || record.createdAt || "");
      return !Number.isNaN(date.getTime()) && date < staleCutoff;
    });
    const unassignedTickets = openTickets.filter((record) => !record.owner && !asList(record.assignees).length);
    const noDueTickets = openTickets.filter((record) => !record.due);
    const unsprintedTickets = openTickets.filter((record) => !record.sprint && !/sprint/i.test(record.type));
    const unepicTickets = openTickets.filter((record) => !record.epic && !/epic/i.test(record.type));
    const readyBacklog = backlog.filter((record) => record.owner && record.priority && (record.description || record.storyPoints));
    const needsRefinement = backlog.filter((record) => !readyBacklog.includes(record));
    const approvalOpenCount = approvals.filter((record) => !closedStatuses.includes(record.status)).length;
    const totalTicketPoints = sumStoryPoints(tickets);
    const completedTicketPoints = sumStoryPoints(doneTickets);
    const completionPercent = tickets.length ? Math.round((doneTickets.length / tickets.length) * 100) : 0;
    const pointCompletionPercent = totalTicketPoints ? Math.round((completedTicketPoints / totalTicketPoints) * 100) : completionPercent;
    const remainingTicketPoints = Math.max(totalTicketPoints - completedTicketPoints, 0);
    const activeSprintSummaries = Object.entries(groupedBySprint)
      .map(([name, items]) => {
        const open = items.filter((record) => !closedStatuses.includes(record.status));
        const blocked = open.filter((record) => /blocked|hold|stuck/i.test(record.status));
        const overdue = open.filter((record) => record.due && record.due < todayKey);
        const done = items.length - open.length;
        return {
          name,
          count: items.length,
          open: open.length,
          blocked: blocked.length,
          overdue: overdue.length,
          points: sumStoryPoints(items),
          completion: items.length ? Math.round((done / items.length) * 100) : 0,
        };
      })
      .filter((item) => item.count)
      .sort((left, right) => (right.blocked + right.overdue + right.open) - (left.blocked + left.overdue + left.open));
    const epicSummaries = Object.entries(groupedByEpic)
      .map(([name, items]) => {
        const open = items.filter((record) => !closedStatuses.includes(record.status));
        const blocked = open.filter((record) => /blocked|hold|stuck/i.test(record.status));
        const overdue = open.filter((record) => record.due && record.due < todayKey);
        return {
          name,
          count: items.length,
          open: open.length,
          blocked: blocked.length,
          overdue: overdue.length,
          completion: items.length ? Math.round(((items.length - open.length) / items.length) * 100) : 0,
        };
      })
      .filter((item) => item.count)
      .sort((left, right) => (right.blocked + right.overdue + right.open) - (left.blocked + left.overdue + left.open));
    const ownerSummaries = Object.entries(groupWorkRecords(openTickets, "owner", "Unassigned"))
      .map(([owner, items]) => {
        const open = items.filter((record) => !closedStatuses.includes(record.status));
        const blocked = open.filter((record) => /blocked|hold|stuck/i.test(record.status));
        const overdue = open.filter((record) => record.due && record.due < todayKey);
        const urgent = open.filter((record) => record.priority === "Urgent");
        return { owner, open: open.length, blocked: blocked.length, overdue: overdue.length, urgent: urgent.length, points: sumStoryPoints(open), risk: blocked.length + overdue.length + urgent.length };
      })
      .filter((item) => item.open || item.blocked || item.overdue || item.urgent)
      .sort((left, right) => (right.risk * 3 + right.open + right.points) - (left.risk * 3 + left.open + left.points));
    const planningGapCount = unassignedTickets.length + noDueTickets.length + unsprintedTickets.length + unepicTickets.length + needsRefinement.length;
    const planningChecks = openTickets.length * 4 + backlog.length;
    const planningCleanPercent = planningChecks ? Math.max(0, Math.round(100 - (planningGapCount / planningChecks) * 100)) : 100;
    const ownershipCoveragePercent = openTickets.length ? Math.round(((openTickets.length - unassignedTickets.length) / openTickets.length) * 100) : 100;
    const dateCoveragePercent = openTickets.length ? Math.round(((openTickets.length - noDueTickets.length) / openTickets.length) * 100) : 100;
    const roadmapCoveragePercent = openTickets.length ? Math.round(((openTickets.length - unepicTickets.length) / openTickets.length) * 100) : 100;
    const backlogReadyPercent = backlog.length ? Math.round((readyBacklog.length / backlog.length) * 100) : 100;
    const deliveryRiskCount = blockedTickets.length + overdueTickets.length + incidentTickets.length + staleTickets.length;
    const sprintRiskCount = activeSprintSummaries.reduce((total, item) => total + item.blocked + item.overdue, 0);
    const riskScore = blockedTickets.length * 5 + overdueTickets.length * 4 + incidentTickets.length * 3 + approvalOpenCount * 2 + unassignedTickets.length * 2 + staleTickets.length + highPriorityTickets.length;
    const confidenceLabel = riskScore >= 14 ? "Low" : riskScore >= 5 ? "Moderate" : "High";
    const workHealthTone = riskScore >= 14 ? "danger" : riskScore >= 5 ? "warning" : "safe";
    const primaryFocus = blockedTickets.length
      ? "Unblock blocked work before adding more scope."
      : overdueTickets.length
      ? "Reset overdue commitments with owners and dates."
      : approvalOpenCount
      ? "Clear pending approvals blocking execution."
      : unassignedTickets.length
      ? "Assign ownership to open work."
      : needsRefinement.length
      ? "Refine backlog before sprint planning."
      : "Maintain current execution rhythm.";
    const conclusionText = openTickets.length
      ? `${openTickets.length} open work items, ${deliveryRiskCount} delivery risk signals, ${planningGapCount} planning gaps, and ${approvalOpenCount} pending decision${approvalOpenCount === 1 ? "" : "s"}. ${primaryFocus}`
      : "No active work is currently visible. Start by creating planned tickets, backlog items, or a sprint.";
    const commitmentMetrics = [
      { label: "Blocked", value: blockedTickets.length, note: "cannot progress", tab: "Tickets", scope: "blocked", tone: blockedTickets.length ? "danger" : "safe" },
      { label: "Overdue", value: overdueTickets.length, note: "missed target", tab: "Tickets", scope: "overdue", tone: overdueTickets.length ? "danger" : "safe" },
      { label: "Due soon", value: dueSoonTickets.length, note: "next 7 days", tab: "Tickets", tone: dueSoonTickets.length ? "warning" : "neutral" },
      { label: "Incidents", value: incidentTickets.length, note: "defect/support load", tab: "Tickets", scope: "incidents", tone: incidentTickets.length ? "warning" : "safe" },
    ];
    const planningMetrics = [
      { label: "Planning clean", value: `${planningCleanPercent}%`, note: `${planningGapCount} total gaps`, tab: "Backlog", tone: planningGapCount ? "warning" : "safe" },
      { label: "Owner coverage", value: `${ownershipCoveragePercent}%`, note: `${unassignedTickets.length} unassigned`, tab: "Tickets", scope: "unassigned", tone: unassignedTickets.length ? "danger" : "safe" },
      { label: "Date coverage", value: `${dateCoveragePercent}%`, note: `${noDueTickets.length} no due date`, tab: "Tickets", scope: "no_due", tone: noDueTickets.length ? "warning" : "safe" },
      { label: "Roadmap coverage", value: `${roadmapCoveragePercent}%`, note: `${unepicTickets.length} no epic`, tab: "Epics", tone: unepicTickets.length ? "warning" : "safe" },
    ];
    const nextActions = [
      blockedTickets.length ? { action: `Unblock ${blockedTickets.length} blocked ticket${blockedTickets.length === 1 ? "" : "s"}`, tab: "Tickets", scope: "blocked" } : null,
      overdueTickets.length ? { action: `Recommit ${overdueTickets.length} overdue item${overdueTickets.length === 1 ? "" : "s"}`, tab: "Tickets", scope: "overdue" } : null,
      approvalOpenCount ? { action: `Clear ${approvalOpenCount} pending approval${approvalOpenCount === 1 ? "" : "s"}`, tab: "Approvals" } : null,
      unassignedTickets.length ? { action: `Assign owners to ${unassignedTickets.length} open item${unassignedTickets.length === 1 ? "" : "s"}`, tab: "Tickets", scope: "unassigned" } : null,
      needsRefinement.length ? { action: `Refine ${needsRefinement.length} backlog item${needsRefinement.length === 1 ? "" : "s"}`, tab: "Backlog" } : null,
      !activeSprintSummaries.length ? { action: "Create or populate the next sprint", tab: "Sprints" } : null,
    ].filter(Boolean).slice(0, 3);
    const openWorkOverviewTarget = (item) => {
      if (item.tab === "Tickets") setTicketScopeFilter(item.scope || "all");
      setActiveTab(item.tab);
    };
    const renderMetric = (item) => (
      <button className={`work-owner-metric is-${item.tone || "neutral"}`} key={item.label} onClick={() => openWorkOverviewTarget(item)} type="button">
        <span>{item.label}</span>
        <strong>{item.value}</strong>
        <small>{item.note}</small>
      </button>
    );
    const overviewMetrics = [
      { label: "Open tickets", value: openTickets.length, note: `${doneTickets.length} completed`, tab: "Tickets", tone: openTickets.length ? "neutral" : "safe" },
      { label: "Delivery risks", value: deliveryRiskCount, note: `${blockedTickets.length} blocked · ${overdueTickets.length} overdue`, tab: "Tickets", scope: blockedTickets.length ? "blocked" : overdueTickets.length ? "overdue" : "all", tone: deliveryRiskCount ? "warning" : "safe" },
      { label: "Point progress", value: `${pointCompletionPercent}%`, note: `${remainingTicketPoints} pts remaining`, tab: "Tickets", tone: pointCompletionPercent >= 70 ? "safe" : pointCompletionPercent >= 35 ? "warning" : "danger" },
      { label: "Planning gaps", value: planningGapCount, note: `${planningCleanPercent}% clean`, tab: "Backlog", tone: planningGapCount ? "warning" : "safe" },
    ];

    return (
      <section className="work-owner-overview work-overview" aria-label="Work owner overview">
        <article className={`functional-panel work-owner-summary work-overview-hero is-${workHealthTone}`}>
          <div className="work-owner-summary-copy">
            <span>Work overview</span>
            <h3>{confidenceLabel} delivery confidence</h3>
            <p>{conclusionText}</p>
          </div>
          <dl className="work-owner-summary-score" aria-label="Executive work metrics">
            <div><dt>Risk score</dt><dd>{riskScore}</dd></div>
            <div><dt>Open work</dt><dd>{openTickets.length}</dd></div>
            <div><dt>Item progress</dt><dd>{completionPercent}%</dd></div>
            <div><dt>Point progress</dt><dd>{pointCompletionPercent}%</dd></div>
          </dl>
        </article>

        <section className="work-overview-kpi-grid" aria-label="Work overview metrics">
          {overviewMetrics.map(renderMetric)}
        </section>

        <section className="work-overview-main-grid">
          <article className="functional-panel work-owner-card">
            <div className="functional-panel-header">
              <div>
                <span>Commitment risk</span>
                <h3>Can current work be delivered safely?</h3>
              </div>
              <small>{deliveryRiskCount} signals</small>
            </div>
            <div className="work-owner-metric-grid">
              {commitmentMetrics.map(renderMetric)}
            </div>
          </article>

          <article className="functional-panel work-owner-card">
            <div className="functional-panel-header">
              <div>
                <span>Planning hygiene</span>
                <h3>Is the team working from clean inputs?</h3>
              </div>
              <small>{backlogReadyPercent}% backlog ready</small>
            </div>
            <div className="work-owner-metric-grid">
              {planningMetrics.map(renderMetric)}
            </div>
          </article>
        </section>

        <section className="work-overview-secondary-grid">
          <article className="functional-panel work-owner-card">
            <div className="functional-panel-header">
              <div>
                <span>Scope confidence</span>
                <h3>Sprint, roadmap, and remaining effort</h3>
              </div>
              <small>{remainingTicketPoints} pts remaining</small>
            </div>
            <div className="work-owner-scope-list">
              <button onClick={() => setActiveTab("Sprints")} type="button">
                <span><strong>{activeSprintSummaries.length || 0}</strong><small>active sprint groups</small></span>
                <em>{sprintRiskCount ? `${sprintRiskCount} sprint risks` : "clean"}</em>
              </button>
              <button onClick={() => setActiveTab("Epics")} type="button">
                <span><strong>{epicSummaries.length || 0}</strong><small>roadmap groups</small></span>
                <em>{roadmapCoveragePercent}% mapped</em>
              </button>
              {(activeSprintSummaries.length ? activeSprintSummaries.slice(0, 2) : [{ name: "No sprint planned", completion: 0, open: openTickets.length, blocked: 0, overdue: 0, points: remainingTicketPoints }]).map((item) => (
                <button key={item.name} onClick={() => setActiveTab("Sprints")} type="button">
                  <span><strong>{item.name}</strong><small>{item.open} open · {item.blocked} blocked · {item.overdue} overdue · {item.points} pts</small></span>
                  <em>{item.completion}%</em>
                </button>
              ))}
            </div>
          </article>

          <article className="functional-panel work-owner-card">
            <div className="functional-panel-header">
              <div>
                <span>People load</span>
                <h3>Where ownership pressure is building</h3>
              </div>
              <small>{ownerSummaries.length || 0} active owners</small>
            </div>
            <div className="work-owner-load-list">
              {ownerSummaries.slice(0, 4).map((item) => (
                <span key={item.owner}>
                  <strong>{item.owner}</strong>
                  <small>{item.open} open · {item.points} pts · {item.risk} risk</small>
                </span>
              ))}
              {!ownerSummaries.length ? <p className="empty-state">No active owner load to review.</p> : null}
            </div>
          </article>
        </section>

        <section className="work-overview-bottom-grid">
          <article className="functional-panel work-owner-card work-owner-card-wide">
            <div className="functional-panel-header">
              <div>
                <span>Management focus</span>
                <h3>Actions that change the outcome</h3>
              </div>
              <small>{nextActions.length || 0} recommended</small>
            </div>
            <div className="work-owner-action-strip">
              {nextActions.map((item) => (
                <button key={item.action} onClick={() => openWorkOverviewTarget(item)} type="button">
                  <span>{item.action}</span>
                  <small>{item.tab}</small>
                </button>
              ))}
              {!nextActions.length ? <p className="empty-state">No immediate management action detected.</p> : null}
            </div>
          </article>

          <article className="functional-panel work-owner-card work-owner-card-process">
            <div className="functional-panel-header">
              <div>
                <span>Process support</span>
                <h3>Reusable workflow coverage</h3>
              </div>
              <small>{automations.length + templates.length + approvalOpenCount} total</small>
            </div>
            <div className="work-owner-process-strip">
              <button onClick={() => setActiveTab("Automation")} type="button"><strong>{automations.length}</strong><span>automation rules</span></button>
              <button onClick={() => setActiveTab("Templates")} type="button"><strong>{templates.length}</strong><span>templates</span></button>
              <button onClick={() => setActiveTab("Approvals")} type="button"><strong>{approvalOpenCount}</strong><span>open approvals</span></button>
            </div>
          </article>
        </section>
      </section>
    );
  };

  const renderScheduleDeepPanel = () => {
    if (moduleName !== "Schedule") return null;

    const todayKey = calendarDateKey(new Date());
    const weekEnd = new Date(`${todayKey}T00:00:00`);
    weekEnd.setDate(weekEnd.getDate() + 7);
    const weekEndKey = calendarDateKey(weekEnd);
    const matchesHeaderProject = (record) => !selectedProjectName || record.projectRef === selectedProjectName;
    const scheduleRecords = allModuleRecords.filter(matchesHeaderProject);
    const eventRecords = scheduleRecords.filter(isScheduleEventRecord);
    const bookingRecords = scheduleRecords.filter(isBookingRecord);
    const availabilityRecords = scheduleRecords.filter(isAvailabilityRecord);
    const roomRecords = scheduleRecords.filter(isRoomRecord);
    const requestRecords = scheduleRecords.filter(isScheduleRequestRecord);
    const activeDefinition = scheduleTabDefinitions[activeTab] || scheduleTabDefinitions.Calendar;
    const ownerOptions = ["All", ...Array.from(new Set([...workspaceUserOptions, ...scheduleRecords.map((record) => record.owner)].filter(Boolean))).sort((left, right) => left.localeCompare(right))];
    const typeOptions = ["All", ...Array.from(new Set([...(scheduleTypeOptionsByTab[activeTab] || []), ...scheduleRecords.map((record) => record.type)].filter(Boolean))).sort((left, right) => left.localeCompare(right))];
    const scheduleDefaultOwnerFilter = activeTab === "Calendar" ? (defaultOwner || "All") : "All";
    const dateToTime = (record) => {
      if (!record?.due) return Number.MAX_SAFE_INTEGER;
      const date = /^\d{4}-\d{2}-\d{2}$/.test(`${record.due}`) ? new Date(`${record.due}T00:00:00`) : new Date(record.due);
      if (Number.isNaN(date.getTime())) return Number.MAX_SAFE_INTEGER;
      return date.getTime() + (getScheduleTimeMinutes(getScheduleContext(record).startTime) || 0);
    };
    const sortScheduleRecords = (items = []) => [...items].sort((left, right) => {
      const leftTime = dateToTime(left);
      const rightTime = dateToTime(right);
      if (leftTime !== rightTime) return leftTime - rightTime;
      return `${left.title || ""}`.localeCompare(`${right.title || ""}`);
    });
    const matchesScheduleFilters = (record) => {
      const matchesOwner = scheduleOwnerFilter === "All" || record.owner === scheduleOwnerFilter;
      const matchesType = scheduleTypeFilter === "All" || record.type === scheduleTypeFilter;
      return matchesHeaderProject(record) && matchesOwner && matchesType;
    };
    const visibleRecords = sortScheduleRecords(filteredRecords.filter(matchesScheduleFilters));
    const visibleEvents = sortScheduleRecords(visibleRecords.filter(isScheduleEventRecord));
    const selectedDateEvents = visibleEvents.filter((record) => record.due === scheduleSelectedDate);
    const upcomingEvents = visibleEvents.filter((record) => record.due && record.due >= todayKey);
    const thisWeekEvents = eventRecords.filter((record) => record.due && record.due >= todayKey && record.due <= weekEndKey && !["Cancelled", "Completed"].includes(record.status));
    const pendingRequests = requestRecords.filter((record) => ["Requested", "Pending", "Review"].includes(record.status));
    const unconfirmedEvents = eventRecords.filter((record) => ["Scheduled", "Requested", "Pending", "Rescheduled"].includes(record.status));
    const getRoomSignal = (record) => {
      const context = getScheduleContext(record);
      const roomLabel = asList(record.labels).find((label) => /^room:/i.test(label));
      return `${context.room || context.location || record.scheduleLocation || roomLabel || (isRoomRecord(record) ? record.title : "") || ""}`.trim().toLowerCase();
    };
    const conflictRecords = eventRecords.filter((record) => eventRecords.some((candidate) => {
      if (candidate.id === record.id || !scheduleRecordsOverlap(record, candidate)) return false;
      const sameOwner = record.owner && candidate.owner && record.owner.toLowerCase() === candidate.owner.toLowerCase();
      const room = getRoomSignal(record);
      const sameRoom = room && room === getRoomSignal(candidate);
      return sameOwner || sameRoom;
    }));
    const peopleWithoutAvailability = workspaceUserOptions.filter((person) => !availabilityRecords.some((record) => `${record.owner || ""}`.trim().toLowerCase() === person.trim().toLowerCase()));
    const formatDuration = (record) => {
      const minutes = Number(getScheduleContext(record).durationMinutes || 0);
      if (!minutes) return "Duration not set";
      return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} hr${minutes % 60 ? ` ${minutes % 60} min` : ""}`;
    };
    const actionButton = (label, handler, disabled = false) => <button disabled={saving || disabled} onClick={handler} type="button">{label}</button>;
    const copyScheduleUrl = async (url) => {
      if (!url || typeof navigator === "undefined" || !navigator.clipboard) return;
      try {
        await navigator.clipboard.writeText(url);
      } catch (error) {
        // Non-blocking: the user can still open/copy the visible URL manually.
      }
    };
    const renderScheduleFilters = () => (
      <div className="schedule-filter-row" aria-label="Schedule filters">
        <label>Owner<select onChange={(event) => setScheduleOwnerFilter(event.target.value)} value={scheduleOwnerFilter}>{ownerOptions.map((option) => <option key={option}>{option}</option>)}</select></label>
        <label>Type<select onChange={(event) => setScheduleTypeFilter(event.target.value)} value={scheduleTypeFilter}>{typeOptions.map((option) => <option key={option}>{option}</option>)}</select></label>
        <button disabled={scheduleOwnerFilter === scheduleDefaultOwnerFilter && scheduleTypeFilter === "All"} onClick={() => { setScheduleOwnerFilter(scheduleDefaultOwnerFilter); setScheduleTypeFilter("All"); }} type="button">Reset</button>
      </div>
    );
    const renderScheduleCard = (record, actions = null) => {
      const context = getScheduleContext(record);
      return (
        <article className="schedule-record-card" key={record.id}>
          <div className="schedule-record-main">
            <span>{record.type || "Schedule item"}</span>
            <strong>{record.title}</strong>
            <small>{[record.owner || "Unassigned", formatScheduleDate(record.due), formatScheduleTimeRange(record), getProjectDisplayName(record.projectRef)].filter(Boolean).join(" · ")}</small>
          </div>
          <div className="schedule-record-meta">
            <em className={`schedule-status is-${`${record.status || "active"}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{record.status || "Active"}</em>
            {context.location || context.meetingLink ? <span>{context.location || context.meetingLink}</span> : null}
            {formatDuration(record) !== "Duration not set" ? <span>{formatDuration(record)}</span> : null}
          </div>
          <div className="schedule-record-actions">
            <button onClick={() => openRecordHistory(record)} type="button">Open</button>
            {actions}
          </div>
        </article>
      );
    };
    const renderScheduleHero = (stats) => (
      <section className="functional-panel schedule-hero-panel">
        <div className="functional-panel-header">
          <div>
            <span>{activeDefinition.eyebrow}</span>
            <h3>{activeDefinition.title}</h3>
          </div>
          <button className="secondary-action" disabled={saving || isLoading} onClick={() => openScheduleCreateModal(activeTab)} type="button">{activeDefinition.createLabel}</button>
        </div>
        <p>{activeDefinition.description}</p>
        <div className="schedule-summary-grid">
          {stats.map((stat) => (
            <button disabled={!stat.onClick} key={stat.label} onClick={stat.onClick || undefined} type="button">
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
              <small>{stat.note}</small>
            </button>
          ))}
        </div>
      </section>
    );

    if (activeTab === "Calendar") {
      const selectedDate = calendarDateFromKey(scheduleSelectedDate);
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth();
      const monthStartOffset = new Date(year, month, 1).getDay();
      const monthDayCount = new Date(year, month + 1, 0).getDate();
      const monthCells = Array.from({ length: Math.ceil((monthStartOffset + monthDayCount) / 7) * 7 }, (_, index) => index - monthStartOffset + 1);
      const weekStart = new Date(selectedDate);
      weekStart.setDate(selectedDate.getDate() - selectedDate.getDay());
      const weekDates = Array.from({ length: 7 }, (_, index) => {
        const date = new Date(weekStart);
        date.setDate(weekStart.getDate() + index);
        return date;
      });
      const calendarStats = [
        { label: "Today", value: eventRecords.filter((record) => record.due === todayKey).length, note: "events" },
        { label: "This week", value: thisWeekEvents.length, note: "scheduled" },
        { label: "Conflicts", value: conflictRecords.length, note: "owner/room" },
        { label: "Unconfirmed", value: unconfirmedEvents.length, note: "needs check" },
      ];
      const renderDayBlock = (date, compact = false) => {
        const key = calendarDateKey(date);
        const dayEvents = visibleEvents.filter((record) => record.due === key);
        return (
          <button className={`${scheduleSelectedDate === key ? "is-selected" : ""} ${compact ? "is-compact" : ""}`.trim()} key={key} onClick={() => setScheduleSelectedDate(key)} type="button">
            <span>{compact ? date.toLocaleDateString([], { weekday: "short" }) : date.getDate()}</span>
            {dayEvents.slice(0, compact ? 2 : 3).map((record) => <em key={record.id}>{record.title}</em>)}
            {dayEvents.length > (compact ? 2 : 3) ? <small>+{dayEvents.length - (compact ? 2 : 3)} more</small> : null}
          </button>
        );
      };
      return (
        <section className="schedule-workspace">
          {renderScheduleHero(calendarStats)}
          <section className="functional-panel schedule-calendar-panel">
            <div className="functional-panel-header">
              <div><span>Calendar view</span><h3>{selectedDate.toLocaleDateString([], { month: "long", year: "numeric" })}</h3></div>
              <div className="schedule-view-switch" aria-label="Calendar view">
                {["day", "week", "month", "agenda"].map((view) => <button aria-pressed={scheduleCalendarView === view} className={scheduleCalendarView === view ? "active" : ""} key={view} onClick={() => setScheduleCalendarView(view)} type="button">{view}</button>)}
              </div>
            </div>
            {renderScheduleFilters()}
            <div className="schedule-calendar-layout">
              <div className={`schedule-calendar-main is-${scheduleCalendarView}`}>
                {scheduleCalendarView === "month" ? <div className="schedule-month-grid">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => <b key={day}>{day}</b>)}{monthCells.map((day, index) => day > 0 && day <= monthDayCount ? renderDayBlock(new Date(year, month, day)) : <i aria-hidden="true" key={`blank-${index}`} />)}</div> : null}
                {scheduleCalendarView === "week" ? <div className="schedule-week-grid">{weekDates.map((date) => renderDayBlock(date, true))}</div> : null}
                {scheduleCalendarView === "day" ? <div className="schedule-agenda-list">{selectedDateEvents.map((record) => renderScheduleCard(record, <>{["Scheduled", "Rescheduled"].includes(record.status) ? actionButton("Confirm", () => updateRecord(record.id, { status: "Confirmed" })) : null}{!["Completed", "Cancelled"].includes(record.status) ? actionButton("Complete", () => updateRecord(record.id, { status: "Completed" })) : null}{record.status !== "Cancelled" ? actionButton("Cancel", () => updateRecord(record.id, { status: "Cancelled" })) : null}</>))}{!selectedDateEvents.length ? <p className="empty-state">No events on this date.</p> : null}</div> : null}
                {scheduleCalendarView === "agenda" ? <div className="schedule-agenda-list">{upcomingEvents.slice(0, 30).map((record) => renderScheduleCard(record, <>{["Scheduled", "Rescheduled"].includes(record.status) ? actionButton("Confirm", () => updateRecord(record.id, { status: "Confirmed" })) : null}{!["Completed", "Cancelled"].includes(record.status) ? actionButton("Complete", () => updateRecord(record.id, { status: "Completed" })) : null}</>))}{!upcomingEvents.length ? <p className="empty-state">No upcoming events match these filters.</p> : null}</div> : null}
              </div>
              <aside className="schedule-side-panel">
                <div><span>Selected day</span><strong>{formatScheduleDate(scheduleSelectedDate)}</strong></div>
                <button className="primary-action" onClick={() => openScheduleCreateModal("Calendar", { due: scheduleSelectedDate })} type="button">Add event</button>
                {selectedDateEvents.map((record) => renderScheduleCard(record))}
                {!selectedDateEvents.length ? <p className="empty-state">No scheduled records for this date.</p> : null}
              </aside>
            </div>
          </section>
        </section>
      );
    }

    if (activeTab === "Bookings") {
      const visibleBookings = visibleRecords.filter(isBookingRecord);
      const bookingLinks = visibleBookings.filter(isBookingLinkRecord);
      const bookedSlots = visibleBookings.filter(isBookedSlotRecord);
      const getBookingUrl = (record) => getScheduleContext(record).bookingUrl || record.linkedWorkUrl || "";
      const missingUrlLinks = bookingLinks.filter((record) => !getBookingUrl(record));
      const bookingHosts = new Set(bookingLinks.map((record) => record.owner).filter(Boolean));
      const renderBookingLinkCard = (record) => {
        const context = getScheduleContext(record);
        const url = getBookingUrl(record);
        return (
          <article className="schedule-record-card schedule-booking-card" key={record.id}>
            <div className="schedule-record-main">
              <span>{record.type || "Booking link"}</span>
              <strong>{record.title}</strong>
              <small>{[record.owner || "Unassigned", formatDuration(record), context.bufferMinutes ? `${context.bufferMinutes} min buffer` : "", context.visibility || "Workspace"].filter(Boolean).join(" · ")}</small>
              {url ? <a href={url} rel="noreferrer" target="_blank">{url}</a> : <small className="schedule-warning-text">Booking URL missing</small>}
            </div>
            <div className="schedule-record-meta">
              <em className={`schedule-status is-${`${record.status || "active"}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{record.status || "Active"}</em>
              {context.timezone ? <span>{context.timezone}</span> : null}
              {record.due ? <span>Available from {formatScheduleDate(record.due)}</span> : null}
            </div>
            <div className="schedule-record-actions">
              <button onClick={() => openRecordHistory(record)} type="button">Open</button>
              {url ? actionButton("Copy link", () => copyScheduleUrl(url)) : null}
              {record.status !== "Disabled" ? actionButton("Disable", () => updateRecord(record.id, { status: "Disabled" })) : actionButton("Enable", () => updateRecord(record.id, { status: "Active" }))}
            </div>
          </article>
        );
      };
      return (
        <section className="schedule-workspace">
          {renderScheduleHero([
            { label: "Active links", value: bookingRecords.filter((record) => isBookingLinkRecord(record) && record.status === "Active").length, note: "bookable" },
            { label: "This week", value: bookingRecords.filter((record) => isBookedSlotRecord(record) && record.due && record.due >= todayKey && record.due <= weekEndKey).length, note: "booked slots" },
            { label: "Pending", value: bookingRecords.filter((record) => isBookedSlotRecord(record) && ["Requested", "Pending", "Scheduled"].includes(record.status)).length, note: "confirm" },
            { label: "Missing URL", value: missingUrlLinks.length, note: "links" },
          ])}
          {renderScheduleFilters()}
          <div className="schedule-quick-actions">
            <button onClick={() => openScheduleCreateModal("Bookings", { type: "Booking Link", status: "Active", title: "" })} type="button">Create booking link</button>
            <button onClick={() => openScheduleCreateModal("Bookings", { type: "Booked Slot", status: "Scheduled", due: scheduleSelectedDate })} type="button">Add booked slot</button>
            <button onClick={() => openScheduleCreateModal("Bookings", { type: "Paid Consultation", status: "Active", title: "" })} type="button">Paid consultation route</button>
          </div>
          <section className="schedule-two-column">
            <div className="functional-panel">
              <div className="functional-panel-header"><div><span>Booking links</span><h3>Reusable booking routes</h3></div><small>{bookingLinks.length} links</small></div>
              <div className="schedule-agenda-list">{bookingLinks.map(renderBookingLinkCard)}{!bookingLinks.length ? <p className="empty-state">No booking links yet. Create one for public or internal scheduling.</p> : null}</div>
            </div>
            <div className="functional-panel">
              <div className="functional-panel-header"><div><span>Booked slots</span><h3>Confirmed and pending bookings</h3></div><small>{bookedSlots.length} slots</small></div>
              <div className="schedule-agenda-list">{bookedSlots.map((record) => renderScheduleCard(record, <>{["Scheduled", "Requested", "Pending"].includes(record.status) ? actionButton("Confirm", () => updateRecord(record.id, { status: "Confirmed" })) : null}{record.status === "Confirmed" ? actionButton("Complete", () => updateRecord(record.id, { status: "Completed" })) : null}{!["Cancelled", "Completed"].includes(record.status) ? actionButton("Reschedule", () => updateRecord(record.id, { status: "Rescheduled" })) : null}{record.status !== "Cancelled" ? actionButton("Cancel", () => updateRecord(record.id, { status: "Cancelled" })) : null}</>))}{!bookedSlots.length ? <p className="empty-state">No booked slots match these filters.</p> : null}</div>
            </div>
          </section>
          <section className="schedule-booking-insights" aria-label="Booking health">
            <article><span>Needs URL</span><strong>{missingUrlLinks.length}</strong><small>Booking links that cannot be shared yet.</small></article>
            <article><span>Host coverage</span><strong>{bookingHosts.size}</strong><small>People or teams accepting bookings.</small></article>
            <article><span>Cancelled</span><strong>{bookingRecords.filter((record) => record.status === "Cancelled").length}</strong><small>Slots cancelled inside this project.</small></article>
          </section>
        </section>
      );
    }

    if (activeTab === "Availability") {
      const visibleAvailability = visibleRecords.filter(isAvailabilityRecord);
      const exceptions = visibleAvailability.filter((record) => /unavailable|holiday|exception/i.test(`${record.type}`));
      const rules = visibleAvailability.filter((record) => !exceptions.includes(record));
      const activeAvailabilityPeople = new Set(availabilityRecords.filter((record) => record.status === "Active").map((record) => `${record.owner || ""}`.trim().toLowerCase()).filter(Boolean));
      const renderCoverageCard = (person) => {
        const personKey = person.trim().toLowerCase();
        const personRecords = availabilityRecords.filter((record) => `${record.owner || ""}`.trim().toLowerCase() === personKey);
        const personExceptions = personRecords.filter((record) => /unavailable|holiday|exception/i.test(`${record.type}`) && record.status !== "Cancelled");
        const hasSetup = personRecords.length > 0;
        return (
          <article className={!hasSetup ? "needs-setup" : ""} key={person}>
            <div>
              <span>{person}</span>
              <strong>{activeAvailabilityPeople.has(personKey) ? "Available" : hasSetup ? "Limited" : "Missing setup"}</strong>
              <small>{hasSetup ? `${personRecords.length} rule${personRecords.length === 1 ? "" : "s"} · ${personExceptions.length} exception${personExceptions.length === 1 ? "" : "s"}` : "No working hours saved"}</small>
            </div>
            <button onClick={() => openScheduleCreateModal("Availability", hasSetup
              ? { owner: person, title: `${person} unavailable time`, type: "Unavailable Slot", status: "Blocked", due: scheduleSelectedDate }
              : { owner: person, title: `${person} working hours`, type: "Working Hours", status: "Active", scheduleStartTime: "09:00", scheduleEndTime: "18:00", scheduleDuration: 480, scheduleRepeatRule: "Weekly" })} type="button">{hasSetup ? "Add exception" : "Add hours"}</button>
          </article>
        );
      };
      return (
        <section className="schedule-workspace">
          {renderScheduleHero([
            { label: "Rules", value: availabilityRecords.filter((record) => record.status === "Active").length, note: "active" },
            { label: "Unavailable", value: exceptions.filter((record) => record.status !== "Cancelled").length, note: "exceptions" },
            { label: "Missing setup", value: peopleWithoutAvailability.length, note: "people" },
            { label: "Blocked", value: availabilityRecords.filter((record) => record.status === "Blocked").length, note: "coverage" },
          ])}
          {renderScheduleFilters()}
          <div className="schedule-quick-actions">
            <button onClick={() => openScheduleCreateModal("Availability", { type: "Working Hours", status: "Active", title: "", scheduleStartTime: "09:00", scheduleEndTime: "18:00", scheduleDuration: 480, scheduleRepeatRule: "Weekly" })} type="button">Add working hours</button>
            <button onClick={() => openScheduleCreateModal("Availability", { type: "Unavailable Slot", status: "Blocked", title: "", due: scheduleSelectedDate })} type="button">Block time</button>
            <button onClick={() => openScheduleCreateModal("Availability", { type: "Holiday Exception", status: "Blocked", title: "", due: scheduleSelectedDate })} type="button">Add holiday</button>
            <button onClick={() => openScheduleCreateModal("Availability", { type: "Buffer Rule", status: "Active", title: "", scheduleBuffer: 15 })} type="button">Set buffer rule</button>
          </div>
          <section className="schedule-two-column">
            <div className="functional-panel">
              <div className="functional-panel-header"><div><span>Availability rules</span><h3>People and team working windows</h3></div><small>{rules.length} rules</small></div>
              <div className="schedule-agenda-list">{rules.map((record) => renderScheduleCard(record, <>{record.status !== "Active" ? actionButton("Activate", () => updateRecord(record.id, { status: "Active" })) : null}{record.status !== "Blocked" ? actionButton("Block", () => updateRecord(record.id, { status: "Blocked" })) : null}</>))}{!rules.length ? <p className="empty-state">No availability rules yet. Add working hours for people or teams.</p> : null}</div>
            </div>
            <div className="functional-panel">
              <div className="functional-panel-header"><div><span>Exceptions</span><h3>Unavailable slots and holidays</h3></div><small>{exceptions.length} exceptions</small></div>
              <div className="schedule-agenda-list">{exceptions.map((record) => renderScheduleCard(record, <>{record.status !== "Cancelled" ? actionButton("Cancel exception", () => updateRecord(record.id, { status: "Cancelled" })) : null}</>))}{!exceptions.length ? <p className="empty-state">No exceptions match these filters.</p> : null}</div>
              <div className="schedule-missing-list"><span>Without setup</span>{peopleWithoutAvailability.slice(0, 10).map((person) => <button key={person} onClick={() => openScheduleCreateModal("Availability", { owner: person, title: `${person} availability`, type: "Working Hours", status: "Active" })} type="button">{person}</button>)}{!peopleWithoutAvailability.length ? <small>Every listed workspace user has availability setup.</small> : null}</div>
            </div>
          </section>
          <section className="functional-panel">
            <div className="functional-panel-header"><div><span>People coverage</span><h3>Availability setup by person</h3></div><small>{workspaceUserOptions.length} people</small></div>
            <div className="schedule-coverage-grid">{workspaceUserOptions.map(renderCoverageCard)}{!workspaceUserOptions.length ? <p className="empty-state">No workspace people found for availability setup.</p> : null}</div>
          </section>
        </section>
      );
    }

    if (activeTab === "Rooms") {
      const visibleRooms = visibleRecords.filter(isRoomRecord);
      const activeRooms = roomRecords.filter((record) => record.status === "Active");
      const roomConflicts = roomRecords.filter((room) => eventRecords.some((event) => {
        const roomSignal = getRoomSignal(room);
        return roomSignal && event.due === scheduleSelectedDate && getRoomSignal(event) === roomSignal;
      }));
      const selectedRoomBookings = eventRecords.filter((event) => event.due === scheduleSelectedDate && getRoomSignal(event));
      const getRoomBookings = (room) => {
        const roomSignal = getRoomSignal(room);
        return roomSignal ? selectedRoomBookings.filter((event) => getRoomSignal(event) === roomSignal) : [];
      };
      return (
        <section className="schedule-workspace">
          {renderScheduleHero([
            { label: "Resources", value: roomRecords.length, note: "total" },
            { label: "Available", value: activeRooms.length, note: "active" },
            { label: "Booked today", value: roomConflicts.length, note: "selected day" },
            { label: "Blocked", value: roomRecords.filter((record) => ["Blocked", "In Maintenance", "Disabled"].includes(record.status)).length, note: "unavailable" },
          ])}
          {renderScheduleFilters()}
          <div className="schedule-room-tools">
            <label>Check date<input onChange={(event) => setScheduleSelectedDate(event.target.value)} type="date" value={scheduleSelectedDate} /></label>
            <div className="schedule-quick-actions">
              <button onClick={() => openScheduleCreateModal("Rooms", { type: "Meeting Room", status: "Active", title: "" })} type="button">Add meeting room</button>
              <button onClick={() => openScheduleCreateModal("Rooms", { type: "Training Room", status: "Active", title: "" })} type="button">Add training room</button>
              <button onClick={() => openScheduleCreateModal("Rooms", { type: "Virtual Room", status: "Active", title: "" })} type="button">Add virtual room</button>
              <button onClick={() => openScheduleCreateModal("Rooms", { type: "Equipment Resource", status: "Active", title: "" })} type="button">Add equipment</button>
            </div>
          </div>
          <section className="functional-panel">
            <div className="functional-panel-header"><div><span>Rooms and resources</span><h3>Bookable spaces and equipment</h3></div><small>{visibleRooms.length} resources</small></div>
            <div className="schedule-room-grid">{visibleRooms.map((record) => {
              const context = getScheduleContext(record);
              const bookings = getRoomBookings(record);
              const isUnavailable = ["Blocked", "In Maintenance", "Disabled"].includes(record.status);
              return (
                <article className={`schedule-room-card ${bookings.length ? "is-booked" : ""} ${isUnavailable ? "is-unavailable" : ""}`.trim()} key={record.id}>
                  <div><span>{record.type}</span><strong>{record.title}</strong><small>{[context.location, context.capacity ? `${context.capacity} capacity` : "", bookings.length ? `${bookings.length} booking${bookings.length === 1 ? "" : "s"} on ${formatScheduleDate(scheduleSelectedDate)}` : "Free on selected date", asList(record.labels).join(", ")].filter(Boolean).join(" · ")}</small></div>
                  <em className={`schedule-status is-${`${record.status || "active"}`.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{record.status}</em>
                  {bookings.length ? <div className="schedule-room-bookings">{bookings.slice(0, 2).map((booking) => <button key={booking.id} onClick={() => openRecordHistory(booking)} type="button">{formatScheduleTimeRange(booking)} · {booking.title}</button>)}{bookings.length > 2 ? <small>+{bookings.length - 2} more bookings</small> : null}</div> : null}
                  <div className="schedule-record-actions">
                    <button onClick={() => openRecordHistory(record)} type="button">Open</button>
                    <button disabled={isUnavailable} onClick={() => openScheduleCreateModal("Calendar", { title: `Book ${record.title}`, type: "Group Meeting", status: "Scheduled", scheduleLocation: record.title, labels: `room:${record.title}`, due: scheduleSelectedDate })} type="button">Book</button>
                    {record.status !== "In Maintenance" ? actionButton("Maintenance", () => updateRecord(record.id, { status: "In Maintenance" })) : actionButton("Set active", () => updateRecord(record.id, { status: "Active" }))}
                    {record.status !== "Disabled" ? actionButton("Disable", () => updateRecord(record.id, { status: "Disabled" })) : null}
                  </div>
                </article>
              );
            })}{!visibleRooms.length ? <p className="empty-state">No rooms or resources match these filters.</p> : null}</div>
          </section>
          <section className="functional-panel">
            <div className="functional-panel-header"><div><span>Selected date usage</span><h3>{formatScheduleDate(scheduleSelectedDate)}</h3></div><small>{selectedRoomBookings.length} bookings</small></div>
            <div className="schedule-agenda-list">{selectedRoomBookings.map((record) => renderScheduleCard(record))}{!selectedRoomBookings.length ? <p className="empty-state">No room or resource bookings on this date.</p> : null}</div>
          </section>
        </section>
      );
    }

    if (activeTab === "Requests") {
      const requestScopes = [
        { key: "pending", label: "Pending", filter: (record) => ["Requested", "Pending", "Review"].includes(record.status) },
        { key: "needs_date", label: "Needs date", filter: (record) => !record.due },
        { key: "reschedule", label: "Reschedule", filter: (record) => /reschedule/i.test(`${record.type} ${record.title}`) },
        { key: "cancel", label: "Cancel", filter: (record) => /cancel/i.test(`${record.type} ${record.title}`) },
        { key: "approved", label: "Approved", filter: (record) => ["Approved", "Scheduled", "Confirmed"].includes(record.status) },
        { key: "all", label: "All", filter: () => true },
      ];
      const activeScope = requestScopes.find((scope) => scope.key === scheduleRequestScope) || requestScopes[0];
      const visibleRequests = visibleRecords.filter(isScheduleRequestRecord).filter(activeScope.filter);
      const requestsMissingDate = requestRecords.filter((record) => !record.due);
      const approveRequest = (record) => updateRecord(record.id, { status: "Approved", pageContext: { ...getScheduleContext(record), requestDecision: "Approved" } });
      const rejectRequest = (record) => updateRecord(record.id, { status: "Rejected", pageContext: { ...getScheduleContext(record), requestDecision: "Rejected" } });
      const convertRequest = (record) => {
        const nextType = /training/i.test(`${record.type} ${record.title}`) ? "Training Session" : /client|review/i.test(`${record.type} ${record.title}`) ? "Client Review" : "1:1 Meeting";
        updateRecord(record.id, {
          type: nextType,
          status: "Scheduled",
          due: record.due || todayKey,
          pageContext: { ...getScheduleContext(record), scheduleKind: "Calendar", requestDecision: "Scheduled", scheduledFromRequest: true },
        });
      };
      return (
        <section className="schedule-workspace">
          {renderScheduleHero([
            { label: "Pending", value: pendingRequests.length, note: "needs action" },
            { label: "Reschedule", value: requestRecords.filter((record) => /reschedule/i.test(`${record.type} ${record.title}`)).length, note: "changes" },
            { label: "Needs date", value: requestsMissingDate.length, note: "missing" },
            { label: "Approved", value: requestRecords.filter((record) => ["Approved", "Scheduled", "Confirmed"].includes(record.status)).length, note: "ready" },
          ])}
          {renderScheduleFilters()}
          <div className="schedule-quick-actions">
            <button onClick={() => openScheduleCreateModal("Requests", { type: "Schedule Request", status: "Requested", title: "" })} type="button">New meeting request</button>
            <button onClick={() => openScheduleCreateModal("Requests", { type: "Reschedule Request", status: "Requested", title: "" })} type="button">Reschedule request</button>
            <button onClick={() => openScheduleCreateModal("Requests", { type: "Cancel Request", status: "Requested", title: "" })} type="button">Cancel request</button>
            <button onClick={() => openScheduleCreateModal("Requests", { type: "Room Booking Request", status: "Requested", title: "" })} type="button">Room request</button>
            <button onClick={() => openScheduleCreateModal("Requests", { type: "Training Session Request", status: "Requested", title: "" })} type="button">Training session request</button>
          </div>
          <section className="functional-panel">
            <div className="functional-panel-header"><div><span>Request queue</span><h3>Scheduling decisions</h3></div><small>{visibleRequests.length} requests</small></div>
            <div className="schedule-scope-tabs">{requestScopes.map((scope) => <button aria-pressed={activeScope.key === scope.key} className={activeScope.key === scope.key ? "active" : ""} key={scope.key} onClick={() => setScheduleRequestScope(scope.key)} type="button"><span>{scope.label}</span><strong>{visibleRecords.filter(isScheduleRequestRecord).filter(scope.filter).length}</strong></button>)}</div>
            <div className="schedule-agenda-list">{visibleRequests.map((record) => {
              const context = getScheduleContext(record);
              const requiresApproval = context.approvalRequired === true;
              const canSchedule = !requiresApproval || record.status === "Approved";
              return renderScheduleCard(record, <>{["Requested", "Pending", "Review"].includes(record.status) ? actionButton("Approve", () => approveRequest(record)) : null}{!["Rejected", "Cancelled", "Scheduled", "Confirmed"].includes(record.status) ? actionButton("Reject", () => rejectRequest(record)) : null}{!["Scheduled", "Confirmed"].includes(record.status) ? canSchedule ? actionButton("Schedule", () => convertRequest(record)) : actionButton("Approve first", () => approveRequest(record), true) : null}{["Rejected", "Cancelled"].includes(record.status) ? actionButton("Reopen", () => updateRecord(record.id, { status: "Requested", pageContext: { ...context, requestDecision: "Reopened" } })) : null}</>);
            })}{!visibleRequests.length ? <p className="empty-state">No requests match this queue.</p> : null}</div>
          </section>
        </section>
      );
    }

    return null;
  };

  if (selectedRecord && /sprint/i.test(`${selectedRecord.type || ""}`)) {
    const sprintItems = records.filter((record) => record.id !== selectedRecord.id && record.sprint === selectedRecord.title);
    const completedSprintItems = sprintItems.filter((record) => closedStatuses.includes(record.status)).length;
    return (
      <>
        <ModuleTabStrip moduleName={moduleName} activeTab={activeTab} setActiveTab={handleModuleTabChange} tabs={moduleTabs} />
        <main className="ticket-page sprint-page">
          <header className="ticket-page-header">
            <button className="ticket-back" onClick={closeRecordHistory} type="button">← Back to sprints</button>
            <div className="ticket-page-actions"><button aria-label="Save sprint" className="record-modal-icon-action" disabled={saving} onClick={saveRecordDetails} title="Save sprint" type="button">✓</button></div>
          </header>
          <div className="ticket-page-key">Sprint · {selectedRecord.id}</div>
          <div className="ticket-page-title-row">
            <input aria-label="Sprint name" onChange={(event) => setDetailDraft((current) => ({ ...current, title: event.target.value }))} value={detailDraft?.title || ""} />
            <select aria-label="Sprint status" onChange={(event) => setDetailDraft((current) => ({ ...current, status: event.target.value }))} value={detailDraft?.status || "To Do"}>{config.statuses.map((status) => <option key={status}>{status}</option>)}</select>
          </div>
          <div className="ticket-page-layout">
            <section className="ticket-page-main">
              <section className="ticket-page-section ticket-description-section">
                <div className="ticket-description-header"><h2>Sprint goal</h2>{!sprintGoalEditing ? <button className="ticket-description-edit-trigger" onClick={openSprintGoalEditor} type="button"><EditOutlined fontSize="inherit" /><span>Edit</span></button> : null}</div>
                {sprintGoalEditing ? (
                  <div className="ticket-description-editor-shell">
                    <WorkspaceEditor
                      className="ticket-description-workspace-editor"
                      minRows={4}
                      onChange={setSprintGoalDraft}
                      onEscape={cancelSprintGoalEditor}
                      onSubmitShortcut={saveSprintGoalEditor}
                      placeholder="Describe the sprint outcome, scope, and success measures"
                      rows={4}
                      value={sprintGoalDraft}
                    />
                    <div className="ticket-description-editor-actions"><small>Cmd/Ctrl + Enter to save</small><div><button className="secondary-action" onClick={cancelSprintGoalEditor} type="button">Cancel</button><button className="primary-action" onClick={saveSprintGoalEditor} type="button">Save goal</button></div></div>
                  </div>
                ) : (
                  <div className={`ticket-description-read ${detailDraft?.description?.trim() ? "" : "is-empty"}`} onDoubleClick={openSprintGoalEditor} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openSprintGoalEditor(); } }} role="button" tabIndex={0}>
                    <WorkspaceRichText emptyText="Add the sprint outcome, scope, and success measures" value={detailDraft?.description || ""} />
                  </div>
                )}
              </section>
              <section className="ticket-page-section">
                <div className="ticket-activity-heading"><h2>Sprint scope</h2><span>{sprintItems.length} linked items</span></div>
                <div className="work-mini-list">
                  {sprintItems.map((record) => <article key={record.id}><div><strong>{record.title}</strong><span>{record.type} · {record.status}</span></div><button onClick={() => openRecordHistory(record)} type="button">Open ticket</button></article>)}
                  {!sprintItems.length ? <p className="empty-state">Link tickets to this sprint to build its scope.</p> : null}
                </div>
              </section>
            </section>
            <aside className="ticket-page-sidebar">
              <h2>Sprint health</h2>
              <p>{completedSprintItems} of {sprintItems.length} linked items complete</p>
              <label>Start date<input disabled value={detailDraft?.startDate || "Not set"} /></label>
              <label>End date<input disabled value={detailDraft?.due || "Not set"} /></label>
              <label>Planned capacity<input disabled value={detailDraft?.storyPoints || 0} /></label>
            </aside>
          </div>
        </main>
      </>
    );
  }

  if (selectedRecord && /epic/i.test(`${selectedRecord.type || ""}`)) {
    const epicChildren = records.filter((record) => isWorkItemRecord(record) && record.epic === selectedRecord.title);
    const completedChildren = epicChildren.filter((record) => closedStatuses.includes(record.status)).length;
    return <><ModuleTabStrip moduleName={moduleName} activeTab={activeTab} setActiveTab={handleModuleTabChange} tabs={moduleTabs} /><main className="ticket-page epic-page"><header className="ticket-page-header"><button className="ticket-back" onClick={closeRecordHistory} type="button">← Back to epics</button><div className="ticket-page-actions"><button aria-label="Save epic" className="record-modal-icon-action" disabled={saving} onClick={saveRecordDetails} title="Save epic" type="button">✓</button></div></header><div className="ticket-page-key">Epic · {selectedRecord.id}</div><div className="ticket-page-title-row"><input aria-label="Epic name" onChange={(event) => setDetailDraft((current) => ({ ...current, title: event.target.value }))} value={detailDraft?.title || ""} /><select onChange={(event) => setDetailDraft((current) => ({ ...current, status: event.target.value }))} value={detailDraft?.status || "Planned"}>{config.statuses.map((status) => <option key={status}>{status}</option>)}</select></div><div className="ticket-page-layout"><section className="ticket-page-main"><section className="ticket-page-section"><h2>Epic objective</h2><WorkspaceEditor ariaLabel="Epic objective" className="ticket-description-workspace-editor" minRows={6} onChange={(value) => setDetailDraft((current) => ({ ...current, description: value }))} placeholder="Describe the outcome, scope, links, and success measures" rows={6} value={detailDraft?.description || ""} /></section><section className="ticket-page-section"><div className="ticket-activity-heading"><h2>Child work</h2><span>{epicChildren.length} linked items</span></div><div className="work-mini-list">{epicChildren.map((record) => <article key={record.id}><div><strong>{record.title}</strong><span>{record.type} · {record.status}</span></div><button onClick={() => openRecordHistory(record)} type="button">Open ticket</button></article>)}{!epicChildren.length ? <p className="empty-state">Create a story, feature, or task and link it to this epic.</p> : null}</div></section></section><aside className="ticket-page-sidebar"><h2>Epic health</h2><p>{completedChildren} of {epicChildren.length} linked items complete</p><label>Project<input disabled value={selectedRecord.projectRef || "No project"} /></label><label>Target date<input disabled value={selectedRecord.due || "No target date"} /></label><label>Estimated points<input disabled value={selectedRecord.storyPoints || 0} /></label></aside></div></main></>;
  }

  if (selectedRecord && isWorkItemRecord(selectedRecord)) {
    const comments = sortCommentsNewestFirst(recordTimeline?.comments || []);
    const ticketMentionOptions = Array.from(new Map([
      ...workspacePeople.map((person) => person),
      ...projectMemberOptions.map((name) => ({ name })),
      ...comments.map((comment) => ({ name: comment.author?.name, email: comment.author?.email })),
    ]
      .filter((person) => getPersonName(person) || person.email)
      .map((person) => {
        const profile = resolveWorkspacePersonProfile(person);
        const label = profile.name || profile.email;
        const rawHandle = profile.username || profile.handle || `${profile.email || ""}`.split("@")[0] || label;
        const handle = cleanMentionHandle(rawHandle);
        return [handle.toLowerCase(), {
          handle,
          avatarUrl: profile.avatarUrl,
          label,
          profile,
          subtitle: profile.email && profile.email !== label ? profile.email : "",
          initial: `${label}`.trim().charAt(0).toUpperCase() || "@",
        }];
      })).values());
    const ticketMentionProfiles = ticketMentionOptions.reduce((profiles, option) => {
      profiles.set(option.handle.toLowerCase(), option.profile);
      getPersonKeys(option.profile).forEach((key) => profiles.set(key, option.profile));
      return profiles;
    }, new Map());
    const commentById = comments.reduce((indexed, comment) => {
      indexed[comment.id] = comment;
      return indexed;
    }, {});
    const getRootCommentId = (comment) => {
      let current = comment;
      const visitedIds = new Set();

      while (current?.parentCommentId && commentById[current.parentCommentId] && !visitedIds.has(current.parentCommentId)) {
        visitedIds.add(current.id);
        current = commentById[current.parentCommentId];
      }

      return current?.id || comment.id;
    };
    const getMentionHandle = (author = {}) => {
      const profile = resolveWorkspacePersonProfile(author);
      const rawHandle = profile.username || profile.handle || `${profile.email || ""}`.split("@")[0] || profile.name || "user";
      return cleanMentionHandle(rawHandle);
    };
    const renderCommentTextSegment = (segment, segmentKey) =>
        `${segment}`.split(/(@[A-Za-z0-9._-]+)/g).map((part, index) => {
          if (!part) return null;
          if (!part.startsWith("@")) return <React.Fragment key={`${segmentKey}-${index}`}>{part}</React.Fragment>;
          const profile = ticketMentionProfiles.get(part.toLowerCase()) || resolveWorkspacePersonProfile(part);
          const displayName = `${profile.name || part.replace(/^@/, "")}`.replace(/^@/, "");
          return (
            <WorkspaceUserHoverCard className="ticket-mention-user" key={`${segmentKey}-${part}-${index}`} user={profile}>
              <span className="ticket-mention">{displayName}</span>
            </WorkspaceUserHoverCard>
          );
        });
    const renderCommentBody = (comment) => {
      const body = `${comment?.body || ""}`;
      const readableText = body.replace(/```[\s\S]*?```/g, "").replace(/[`*_~#[\]()>]/g, "").trim();
      const canCollapse = !body.includes("```") && (readableText.length > 700 || body.split("\n").length > 10);
      const expanded = Boolean(expandedCommentBodies[comment.id]);
      return <div className="ticket-comment-body-shell">
        <WorkspaceRichText className={`ticket-comment-body ${canCollapse && !expanded ? "is-collapsed" : ""}`} renderTextSegment={renderCommentTextSegment} value={body} />
        {canCollapse ? <button aria-expanded={expanded} className="ticket-comment-expand" onClick={() => setExpandedCommentBodies((current) => ({ ...current, [comment.id]: !current[comment.id] }))} type="button">{expanded ? "Read less" : "... Read more"}</button> : null}
      </div>;
    };
    const parentCommentIds = new Set(comments.map((comment) => comment.id));
    const repliesByParent = comments.reduce((grouped, comment) => {
      if (!comment.parentCommentId) return grouped;
      const rootCommentId = getRootCommentId(comment);
      if (rootCommentId === comment.id) return grouped;
      grouped[rootCommentId] = [...(grouped[rootCommentId] || []), comment];
      return grouped;
    }, {});
    Object.keys(repliesByParent).forEach((parentId) => {
      repliesByParent[parentId] = sortCommentsNewestFirst(repliesByParent[parentId]);
    });
    const topLevelComments = sortCommentsNewestFirst(comments.filter((comment) => !comment.parentCommentId || !parentCommentIds.has(comment.parentCommentId)));
    const visibleTopLevelComments = topLevelComments.slice(0, visibleCommentCount);
    const remainingTopLevelCommentCount = Math.max(0, topLevelComments.length - visibleTopLevelComments.length);
    const orphanReplyComments = [];
    const getReactionUsers = (users = [], reactedByMe = false, count = 0) => {
      const normalizedUsers = (Array.isArray(users) ? users : []).map(resolveWorkspacePersonProfile);
      if (reactedByMe && !normalizedUsers.some((user) => `${user.id || user._id || user.name}` === `${currentUser?.id || currentUser?._id || "current-user"}`)) {
        normalizedUsers.push(getCurrentUserReactionIdentity());
      }
      if (!normalizedUsers.length && count) {
        return [{ name: count === 1 ? "1 teammate" : `${count} teammates` }];
      }
      return normalizedUsers;
    };
    const renderReactionTooltip = (label, users, count) => (
      <span className="comment-reaction-tooltip" role="tooltip">
        <strong>{label}</strong>
        <span>{users.length ? users.map((user) => user.name || user.email || "Workspace user").join(", ") : `${count || 0} reactions`}</span>
      </span>
    );
    const renderTicketComment = (comment, nested = false) => {
      const authorProfile = resolveWorkspacePersonProfile(comment.author || {});
      const authorName = authorProfile.name || "Workspace user";
      const authorMention = getMentionHandle(authorProfile);
      const activityType = comment.parentCommentId ? "Reply" : "Comment";
      const replies = repliesByParent[comment.id] || [];
      const hiddenReplyCount = Math.max(0, replies.length - 1);
      const repliesExpanded = Boolean(expandedReplyParents[comment.id]);
      const visibleReplies = !nested && replies.length
        ? (repliesExpanded ? replies : replies.slice(0, 1))
        : [];
      const reactions = Array.isArray(comment.reactions) ? comment.reactions : [];
      const likeUsers = getReactionUsers(comment.likedBy, comment.likedByMe, comment.likeCount);

      return (
        <article className={nested ? "ticket-comment-reply" : ""} id={`comment-${comment.id}`} key={comment.id}>
          <header>
            <div className="record-comment-author-line"><WorkspaceUserHoverCard className="record-comment-author-profile" user={authorProfile}><WorkspaceUserAvatar className="record-comment-author-avatar" user={authorProfile} /><strong>{authorName}</strong></WorkspaceUserHoverCard><span>{activityType}</span>{renderCommentCreatedTime(comment)}{comment.editedAt ? renderEditedCommentLabel(comment) : null}</div>
            {!comment.deletedAt ? <div className="record-comment-owner-actions">
              <button aria-label="Like comment" className={`comment-icon-action ${comment.likedByMe ? "active" : ""} ${comment.likeCount ? "has-likes" : ""}`} disabled={commentSaving} onClick={() => likeComment(comment.id)} type="button">{comment.likedByMe ? <span className="comment-like-emoji">👍</span> : <ThumbUpOutlined fontSize="small" />}{comment.likeCount ? <span>{comment.likeCount}</span> : null}{comment.likeCount ? renderReactionTooltip("Thumbs up", likeUsers, comment.likeCount) : null}</button>
              <button aria-label="React to comment" className={`comment-icon-action comment-reaction-trigger ${reactionPickerCommentId === comment.id ? "active" : ""}`} disabled={commentSaving} onClick={() => setReactionPickerCommentId((current) => (current === comment.id ? "" : comment.id))} title="React to comment" type="button"><AddReactionOutlined fontSize="small" /></button>
              <button aria-label="Reply to comment" className="comment-icon-action" disabled={commentSaving} onClick={() => { setReplyToCommentId(comment.id); setReplyParentCommentId(nested ? getRootCommentId(comment) : comment.id); setCommentDraft(`${authorMention} `); }} title="Reply to comment" type="button"><ReplyOutlined fontSize="small" /></button>
              <button aria-label="Copy comment link" className={`comment-icon-action ${copiedCommentId === comment.id ? "is-copied" : ""}`} onClick={() => copyCommentLink(comment.id)} title={copiedCommentId === comment.id ? "Copied" : "Copy comment link"} type="button"><LinkOutlined fontSize="small" /></button>
              {comment.canManage ? <button aria-label="Edit comment" className="comment-icon-action" disabled={commentSaving} onClick={() => { setEditingCommentId(comment.id); setEditingCommentDraft(comment.body || ""); }} title="Edit comment" type="button"><EditOutlined fontSize="small" /></button> : null}
              {comment.canManage ? <button aria-label="Delete comment" className="comment-icon-action comment-delete-action" disabled={commentSaving} onClick={() => deleteComment(comment.id)} title="Delete comment" type="button"><DeleteOutlineOutlined fontSize="small" /></button> : null}
            </div> : null}
          </header>
          {editingCommentId === comment.id ? <div className="record-comment-edit"><WorkspaceEditor autoFocus className="record-comment-edit-editor" compact disabled={editingCommentSavingId === comment.id} mentionOptions={ticketMentionOptions} onChange={setEditingCommentDraft} onEscape={() => setEditingCommentId("")} onSubmitShortcut={() => saveCommentEdit(comment.id)} placeholder="Update comment" rows={3} value={editingCommentDraft} /><div className="record-comment-edit-actions"><button aria-busy={editingCommentSavingId === comment.id} className={editingCommentSavingId === comment.id ? "is-saving" : ""} disabled={editingCommentSavingId === comment.id || !editingCommentDraft.trim()} onClick={() => saveCommentEdit(comment.id)} title="Save comment" type="button">{editingCommentSavingId === comment.id ? <WorkspaceInlineLoader compact label="Save" /> : "Save"}</button><button disabled={editingCommentSavingId === comment.id} onClick={() => setEditingCommentId("")} title="Cancel edit" type="button">Cancel</button></div></div> : <>{comment.body ? renderCommentBody(comment) : null}{renderCommentAttachments(comment.attachments)}</>}
          {reactions.length ? <div className="comment-reaction-summary">{reactions.map((reaction) => {
            const reactionUsers = getReactionUsers(reaction.users, reaction.reactedByMe, reaction.count);
            return <button className={reaction.reactedByMe ? "active" : ""} disabled={commentSaving} key={reaction.emoji} onClick={() => reactToComment(comment.id, getCommentReactionKey(reaction.emoji))} type="button"><span>{reaction.emoji}</span>{reaction.count}{renderReactionTooltip(commentReactionLabels[reaction.emoji] || "Reaction", reactionUsers, reaction.count)}</button>;
          })}</div> : null}
          {reactionPickerCommentId === comment.id ? <div className="comment-reaction-picker" role="menu">{commentReactionGroups.map((group) => <section key={group.label}><strong>{group.label}</strong><div>{group.emojis.map((emoji) => <button disabled={commentSaving} key={`${group.label}-${emoji}`} onClick={() => reactToComment(comment.id, getCommentReactionKey(emoji))} title={`React with ${emoji}`} type="button"><span>{emoji}</span></button>)}</div></section>)}</div> : null}
          {replyToCommentId === comment.id ? <TicketReplyComposer attachmentDisabled={commentAttachmentBusy || commentSaving} attachmentPreview={renderPendingCommentAttachments()} authorName={authorName} canSubmit={Boolean(commentDraft.trim() || commentAttachments.length)} draft={commentDraft} mentionOptions={ticketMentionOptions} onAttachmentChange={handleCommentAttachmentChange} onCancel={() => { setReplyToCommentId(""); setReplyParentCommentId(""); setCommentDraft(""); clearCommentAttachments(); }} onChange={setCommentDraft} onSubmit={submitIssueComment} saving={commentSaving || commentAttachmentBusy} /> : null}
          {!nested && replies.length ? <div className="ticket-comment-replies">{hiddenReplyCount ? <button className="ticket-show-replies" onClick={() => setExpandedReplyParents((current) => ({ ...current, [comment.id]: !current[comment.id] }))} title={repliesExpanded ? "Show latest reply only" : `Show ${hiddenReplyCount} more replies`} type="button">{repliesExpanded ? "Show fewer replies" : `Show more replies${hiddenReplyCount > 1 ? ` (${hiddenReplyCount})` : ""}`}{repliesExpanded ? <ExpandLessOutlined fontSize="small" /> : <ExpandMoreOutlined fontSize="small" />}</button> : null}{visibleReplies.map((reply) => renderTicketComment(reply, true))}</div> : null}
        </article>
      );
    };
    const ticketRecords = records.filter((record) => isWorkItemRecord(record));
    const ticketIndex = ticketRecords.findIndex((record) => record.id === selectedRecord.id);
    const parentTicket = ticketRecords.find((record) => record.id === detailDraft?.parentRecordId);
    const getLinkedItems = (record) => record?.linkedItems?.length
      ? record.linkedItems
      : (record?.linkedRecordIds || []).map((recordId) => ({ recordId, relationship: record?.linkedWorkType || "relates to" }));
    const inverseLinkRelationship = {
      blocks: "is blocked by",
      "is blocked by": "blocks",
      clones: "is cloned by",
      "is cloned by": "clones",
      duplicates: "is duplicated by",
      "is duplicated by": "duplicates",
      "relates to": "relates to",
    };
    const outboundLinkedItems = getLinkedItems(detailDraft).map((item) => ({ ...item, direction: "outbound", sourceRecordId: selectedRecord.id }));
    const inboundLinkedItems = ticketRecords
      .filter((record) => record.id !== selectedRecord.id)
      .flatMap((record) => getLinkedItems(record)
        .filter((item) => item.recordId === selectedRecord.id)
        .map((item) => ({ recordId: record.id, relationship: inverseLinkRelationship[item.relationship] || "relates to", direction: "inbound", sourceRecordId: record.id })));
    const linkedItemsForDisplay = Array.from(new Map([...outboundLinkedItems, ...inboundLinkedItems]
      .filter((item) => item.recordId && item.recordId !== selectedRecord.id)
      .map((item) => [item.recordId, item])).values());
    const linkedTickets = ticketRecords.filter((record) => linkedItemsForDisplay.some((item) => item.recordId === record.id));
    const linkedTicketsByRelationship = linkedItemsForDisplay.reduce((groups, item) => {
      const record = ticketRecords.find((ticket) => ticket.id === item.recordId);
      if (!record) return groups;
      const relationship = item.relationship || "relates to";
      if (!groups[relationship]) groups[relationship] = [];
      groups[relationship].push({ ...item, record });
      return groups;
    }, {});
    const childTickets = ticketRecords.filter((record) => record.parentRecordId === selectedRecord.id);
    const ticketDisplayKey = (record) => record?.key || record?.id || "Ticket";
    const ticketBackHistoryItems = Array.from(new Map([
      ...ticketNavigationStack.slice().reverse(),
      ...recentTicketHistory,
    ]
      .filter((record) => record?.id && record.id !== selectedRecord.id)
      .map((record) => [record.id, record])).values()).slice(0, 8);
    const epicOptions = Array.from(new Set(records.filter((record) => record.moduleName === "Work" && (/epic/i.test(`${record.type || ""}`) || record.epic)).map((record) => record.type === "Epic" ? record.title : record.epic).filter(Boolean))).sort();
    const linkSearchText = linkedTicketSearch.trim().toLowerCase();
    const linkSearchResults = ticketRecords
      .filter((record) => record.id !== selectedRecord.id && !(detailDraft?.linkedRecordIds || []).includes(record.id))
      .filter((record) => {
        if (!linkSearchText) return true;
        return [record.title, record.key, record.id, record.type, record.projectRef].some((value) => `${value || ""}`.toLowerCase().includes(linkSearchText));
      })
      .slice(0, 5);
    const subtaskSearchText = subtaskQuickTitle.trim().toLowerCase();
    const subtaskSearchResults = ticketRecords
      .filter((record) => record.id !== selectedRecord.id && record.parentRecordId !== selectedRecord.id)
      .filter((record) => {
        if (!subtaskSearchText) return true;
        return [record.title, record.key, record.id, record.type, record.projectRef].some((value) => `${value || ""}`.toLowerCase().includes(subtaskSearchText));
      })
      .slice(0, 5);
    const parentTicketSearchText = parentTicketSearch.trim().toLowerCase();
    const parentTicketResults = ticketRecords
      .filter((record) => record.id !== selectedRecord.id)
      .filter((record) => !parentTicketSearchText || [ticketDisplayKey(record), record.title].some((value) => `${value || ""}`.toLowerCase().includes(parentTicketSearchText)))
      .slice(0, 12);
    const selectParentTicket = (recordId = "") => {
      setDetailDraft((current) => ({ ...current, parentRecordId: recordId }));
      setParentTicketSearch("");
      setParentTicketPickerOpen(false);
    };
    const renderParentTicketPicker = () => (
      <label>
        Parent ticket
        <div className="ticket-parent-picker">
          <input
            aria-autocomplete="list"
            aria-controls="ticket-parent-picker-results"
            aria-expanded={parentTicketPickerOpen}
            aria-label="Search parent ticket by number or title"
            onBlur={() => setParentTicketPickerOpen(false)}
            onChange={(event) => setParentTicketSearch(event.target.value)}
            onFocus={() => {
              setParentTicketSearch("");
              setParentTicketPickerOpen(true);
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") setParentTicketPickerOpen(false);
              if (event.key === "Enter" && parentTicketResults[0]) {
                event.preventDefault();
                selectParentTicket(parentTicketResults[0].id);
              }
            }}
            placeholder="Search ticket number or title"
            role="combobox"
            value={parentTicketPickerOpen ? parentTicketSearch : parentTicket ? `${ticketDisplayKey(parentTicket)} · ${parentTicket.title}` : ""}
          />
          {parentTicketPickerOpen ? (
            <div className="ticket-parent-picker-results" id="ticket-parent-picker-results" role="listbox">
              <button aria-selected={!detailDraft?.parentRecordId} onMouseDown={(event) => { event.preventDefault(); selectParentTicket(""); }} role="option" type="button">
                <span><strong>No parent ticket</strong><small>Keep this ticket at the top level</small></span>
              </button>
              {parentTicketResults.map((record) => (
                <button aria-selected={detailDraft?.parentRecordId === record.id} key={record.id} onMouseDown={(event) => { event.preventDefault(); selectParentTicket(record.id); }} role="option" type="button">
                  <WorkTypeMark type={record.type} />
                  <span><strong>{ticketDisplayKey(record)} · {record.title}</strong><small>{record.type || "Ticket"} · {ticketDisplayKey(record)}</small></span>
                </button>
              ))}
              {!parentTicketResults.length ? <p>No matching tickets.</p> : null}
            </div>
          ) : null}
        </div>
      </label>
    );
    const addLinkedTicket = (recordId) => {
      if (!recordId) return;
      setDetailDraft((current) => ({
        ...current,
        linkedRecordIds: Array.from(new Set([...(current?.linkedRecordIds || []), recordId])),
        linkedItems: [
          ...(current?.linkedItems || (current?.linkedRecordIds || []).map((id) => ({ recordId: id, relationship: current?.linkedWorkType || "relates to" }))).filter((item) => item.recordId !== recordId),
          { recordId, relationship: current?.linkedWorkType || "relates to" },
        ],
      }));
      setLinkedTicketSearch("");
    };
    const removeLinkedTicket = (recordId) => {
      setDetailDraft((current) => ({
        ...current,
        linkedRecordIds: (current?.linkedRecordIds || []).filter((id) => id !== recordId),
        linkedItems: (current?.linkedItems || []).filter((item) => item.recordId !== recordId),
      }));
    };
    const removeInboundLinkedTicket = async (sourceRecordId) => {
      const sourceRecord = ticketRecords.find((record) => record.id === sourceRecordId);
      if (!sourceRecord) return;
      const linkedItems = getLinkedItems(sourceRecord).filter((item) => item.recordId !== selectedRecord.id);
      try {
        setSaving(true);
        await onUpdateRecord?.(sourceRecordId, { linkedItems, linkedRecordIds: linkedItems.map((item) => item.recordId) }, { localError: true });
      } catch (error) {
        setTimelineError(error?.response?.data?.message || error?.message || "Unable to remove this linked ticket.");
      } finally {
        setSaving(false);
      }
    };
    const addExistingSubtask = async (recordId) => {
      if (!recordId || !selectedRecord?.id) return;
      try {
        setSaving(true);
        await onUpdateRecord?.(recordId, { parentRecordId: selectedRecord.id }, { localError: true });
        setSubtaskQuickTitle("");
      } catch (error) {
        setTimelineError(error?.response?.data?.message || error?.message || "Unable to add this subtask.");
      } finally {
        setSaving(false);
      }
    };
    const removeSubtask = async (recordId) => {
      if (!recordId) return;
      try {
        setSaving(true);
        await onUpdateRecord?.(recordId, { parentRecordId: "" }, { localError: true });
      } catch (error) {
        setTimelineError(error?.response?.data?.message || error?.message || "Unable to remove this subtask.");
      } finally {
        setSaving(false);
      }
    };
    const renderLinkedTicketGroups = () => Object.entries(linkedTicketsByRelationship).map(([relationship, tickets]) => (
      <section className="ticket-linked-type-group" key={relationship}>
        <div className="ticket-linked-type-heading"><span>{relationship}</span><small>{tickets.length}</small></div>
      {tickets.map((item) => (
          <article className="ticket-work-link is-linked" key={item.record.id}>
            <button aria-label={`Open ${ticketDisplayKey(item.record)}: ${item.record.title}`} className="ticket-work-link-open" onClick={() => openRecordHistory(item.record)} type="button">
              <span className="ticket-work-link-key">{ticketDisplayKey(item.record)}</span>
              <span className="ticket-work-link-title">{item.record.title}</span>
            </button>
            <button aria-label={`Remove linked ticket ${ticketDisplayKey(item.record)}`} className="ticket-work-link-remove" onClick={() => item.direction === "inbound" ? removeInboundLinkedTicket(item.sourceRecordId) : removeLinkedTicket(item.record.id)} title="Remove link" type="button">×</button>
          </article>
        ))}
      </section>
    ));
    const applyLinkedSearch = () => {
      if (linkSearchResults[0]) {
        addLinkedTicket(linkSearchResults[0].id);
        return;
      }
      const externalUrl = linkedTicketSearch.trim();
      if (/^https?:\/\//i.test(externalUrl)) {
        setDetailDraft((current) => ({ ...current, linkedWorkUrl: externalUrl }));
        setLinkedTicketSearch("");
        return;
      }
      if (externalUrl) openLinkedTicketModal(externalUrl, detailDraft?.linkedWorkType || "relates to");
    };
    const previousTicket = ticketIndex > 0 ? ticketRecords[ticketIndex - 1] : null;
    const nextTicket = ticketIndex >= 0 && ticketIndex < ticketRecords.length - 1 ? ticketRecords[ticketIndex + 1] : null;
    const watcherIds = Array.isArray(selectedRecord.watcherIds) ? selectedRecord.watcherIds : [];
    const currentUserId = currentUser?.id || currentUser?._id || "";
    const isFollowingTicket = Boolean(currentUserId) && watcherIds.some((watcherId) => `${watcherId}` === `${currentUserId}`);
    const watcherCount = watcherIds.length;
    const descriptionText = (detailDraft?.description || "").trim();
    const getTicketHistoryMessage = (event) => {
      if (event.action === "assigned_to_changed") {
        const previousAssignee = event.oldValue || "Unassigned";
        const nextAssignee = event.newValue || "Unassigned";
        const changedBy = event.performedBy?.name || "System";
        return `Assignee changed from ${previousAssignee} to ${nextAssignee} by ${changedBy}.`;
      }
      return event.body || event.message;
    };
    const getTicketEventTime = (event = {}) => event.at || event.createdAt || event.updatedAt || event.editedAt || "";
    const getTicketEventActor = (event = {}) => event.performedBy || event.author || null;
    const formatTicketEventTime = (event = {}) => {
      const rawTime = getTicketEventTime(event);
      const parsedTime = rawTime ? new Date(rawTime) : null;
      return parsedTime && !Number.isNaN(parsedTime.getTime())
        ? parsedTime.toLocaleString()
        : "Time unavailable";
    };
    const commenterNames = Array.from(new Set(comments.map((comment) => resolveWorkspacePersonProfile(comment.author || {}).name).filter(Boolean)));
    const explicitContributorNames = asList(detailDraft?.assignees).filter((name) => name && name !== detailDraft?.owner);
    const contributorNames = Array.from(new Set([...commenterNames, ...explicitContributorNames]));
    const currentAssigneeName = currentUser?.name || currentUser?.user_name || currentUser?.email || defaultOwner;
    const ticketProjectMetaName = detailDraft?.projectRef ? getProjectDisplayName(detailDraft.projectRef) : "";
    const addExplicitContributor = (name) => {
      if (!name || name === detailDraft?.owner || commenterNames.includes(name)) return;
      setDetailDraft((current) => ({ ...current, assignees: Array.from(new Set([...asList(current.assignees), name])).join(", ") }));
    };
    const removeExplicitContributor = (name) => setDetailDraft((current) => ({ ...current, assignees: asList(current.assignees).filter((entry) => entry !== name).join(", ") }));
    const renderContributorControl = () => <div className="ticket-contributors-control"><span className="ticket-detail-label">Contributors</span>{contributorNames.length ? <div className="ticket-contributor-list">{contributorNames.map((name) => <span key={name}>{name}{!commenterNames.includes(name) ? <button aria-label={`Remove ${name} as contributor`} onClick={() => removeExplicitContributor(name)} title="Remove contributor" type="button">×</button> : null}</span>)}</div> : null}<select aria-label="Add contributor" className="ticket-add-contributor" defaultValue="" onChange={(event) => { addExplicitContributor(event.target.value); event.target.value = ""; }}><option value="">Add contributor</option>{projectMemberOptions.filter((person) => person !== detailDraft?.owner && !contributorNames.includes(person)).map((person) => <option key={person} value={person}>{person}</option>)}</select></div>;

    return (
      <>
        <ModuleTabStrip moduleName={moduleName} activeTab={activeTab} setActiveTab={handleModuleTabChange} tabs={moduleTabs} />
        <main className={`ticket-page ${ticketTransitioning ? "is-transitioning" : ""}`.trim()} aria-busy={ticketTransitioning} aria-label={`${selectedRecord.title} ticket`}>
          <div className="ticket-page-layout">
            <section className="ticket-page-main">
              <header className="ticket-page-header ticket-page-context-header">
                <div className="ticket-path-row">
                  <button
                    aria-expanded={ticketHistoryMenu.open}
                    aria-haspopup="menu"
                    className="ticket-back"
                    onClick={navigateBackFromTicket}
                    onContextMenu={openTicketBackHistoryMenu}
                    title="Click to go back. Right-click to see recent tickets."
                    type="button"
                  >
                    ‹ Back
                  </button>
                  {ticketHistoryMenu.open ? (
                    <div
                      aria-label="Recent ticket history"
                      className="ticket-back-history-menu"
                      ref={ticketHistoryMenuRef}
                      role="menu"
                      style={{ top: ticketHistoryMenu.top, left: ticketHistoryMenu.left }}
                    >
                      <div className="ticket-back-history-heading">Recent tickets</div>
                      {ticketBackHistoryItems.map((record) => (
                        <button key={record.id} onClick={() => openTicketFromBackHistory(record)} role="menuitem" type="button">
                          <span>{ticketDisplayKey(record)}</span>
                          <strong>{record.title || "Untitled ticket"}</strong>
                          <small>{[getProjectDisplayName(record.projectRef), record.status].filter(Boolean).join(" · ") || record.type || "Ticket"}</small>
                        </button>
                      ))}
                      {!ticketBackHistoryItems.length ? <p>No recent tickets in this tab.</p> : null}
                    </div>
                  ) : null}
                  <div className="ticket-epic-menu" ref={epicPopoverRef}>
                    <button className={`ticket-epic-trigger ${detailDraft?.epic ? "has-epic" : ""}`} onClick={openTicketEpicCreator} title={detailDraft?.epic ? "Change epic" : "Add epic"} type="button">
                      <EditOutlined fontSize="small" />
                      <span>{detailDraft?.epic || "Add epic"}</span>
                    </button>
                    {epicPopoverOpen ? (
                      <form className="ticket-epic-popover" onSubmit={submitTicketEpic}>
                        {!epicOptions.length ? <p>No epic has been created in this space. Create your first <strong>epic</strong>.</p> : (
                          <section>
                            <span>Link existing epic</span>
                            <div>{epicOptions.slice(0, 6).map((epic) => <button key={epic} onClick={() => { setDetailDraft((current) => ({ ...current, epic })); setSelectedRecord((current) => current ? { ...current, epic } : current); onUpdateRecord?.(selectedRecord.id, { epic }, { localError: true }); setEpicPopoverOpen(false); }} type="button">{epic}</button>)}</div>
                          </section>
                        )}
                        <label>Epic name<input autoFocus onChange={(event) => setEpicDraft((current) => ({ ...current, title: event.target.value }))} placeholder="Example: Customer onboarding experience" value={epicDraft.title} /></label>
                        <WorkspaceEditor compact label="Objective" minRows={3} onChange={(value) => setEpicDraft((current) => ({ ...current, description: value }))} placeholder="Outcome, scope, and success measure" rows={3} value={epicDraft.description} />
                        {epicCreateError ? <em>{epicCreateError}</em> : null}
                        <div><button disabled={epicCreateSaving} onClick={() => setEpicPopoverOpen(false)} type="button">Cancel</button><button disabled={epicCreateSaving || !epicDraft.title.trim()} type="submit">{epicCreateSaving ? "Creating…" : "Create and link epic"}</button></div>
                      </form>
                    ) : null}
                  </div>
                  <span className="ticket-path-reference"><span className="ticket-path-separator">/</span><span className="ticket-path-key"><WorkTypeMark type={selectedRecord.type} />{ticketDisplayKey(selectedRecord)}</span></span>
                  <div className="ticket-sequence-switcher" aria-label="Switch ticket">
                    <button aria-label="Previous ticket" className="ticket-sequence-button" disabled={!previousTicket} onClick={() => previousTicket && openRecordHistory(previousTicket)} title={previousTicket ? `Previous: ${previousTicket.title}` : "No previous ticket"} type="button">
                      <span aria-hidden="true">←</span>
                    </button>
                    <button aria-label="Next ticket" className="ticket-sequence-button" disabled={!nextTicket} onClick={() => nextTicket && openRecordHistory(nextTicket)} title={nextTicket ? `Next: ${nextTicket.title}` : "No next ticket"} type="button">
                      <span aria-hidden="true">→</span>
                    </button>
                  </div>
                </div>
              </header>
              <div className="ticket-page-title-row"><div className="ticket-title-shell">{ticketTitleEditing ? <div className="ticket-title-editor-shell"><input aria-label="Ticket title" className="ticket-title-input" onChange={(event) => setTicketTitleDraft(event.target.value)} onKeyDown={(event) => { if ((event.metaKey || event.ctrlKey) && event.key === "Enter") { event.preventDefault(); saveTicketTitleEditor(); } if (event.key === "Escape") { event.preventDefault(); cancelTicketTitleEditor(); } }} ref={ticketTitleInputRef} value={ticketTitleDraft} /><div className="ticket-title-editor-actions"><small>Cmd/Ctrl + Enter to save</small><div><button className="secondary-action" onClick={cancelTicketTitleEditor} type="button">Cancel</button><button className="primary-action" onClick={saveTicketTitleEditor} type="button">Save</button></div></div></div> : <div className={`ticket-title-read ${ticketTitleDraft?.trim() ? "" : "is-empty"}`} onDoubleClick={openTicketTitleEditor} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); openTicketTitleEditor(); } }} role="button" tabIndex={0}><div className="ticket-title-header"><h1>{detailDraft?.title || "Add ticket title"}</h1><button className="ticket-title-edit-trigger" onClick={openTicketTitleEditor} type="button"><EditOutlined fontSize="inherit" /><span>Edit</span></button></div></div>}</div></div>
              {timelineError ? <WorkspaceErrorNotice message={timelineError} /> : null}
              <section className="ticket-page-section ticket-description-section">
                <div className="ticket-description-header">
                  <h2>Description</h2>
                  {!ticketDescriptionEditing ? <button className="ticket-description-edit-trigger" onClick={openTicketDescriptionEditor} type="button"><EditOutlined fontSize="inherit" /><span>Edit</span></button> : null}
                </div>
                {ticketDescriptionEditing ? (
                  <div className="ticket-description-editor-shell">
                    <WorkspaceEditor
                      className="ticket-description-workspace-editor"
                      inputClassName="ticket-description-editor"
                      inputRef={ticketDescriptionInputRef}
                      minRows={4}
                      onChange={setTicketDescriptionDraft}
                      onEscape={cancelTicketDescriptionEditor}
                      onSubmitShortcut={saveTicketDescriptionEditor}
                      placeholder="Add context, expected outcome, acceptance criteria, links, and useful notes"
                      rows={4}
                      value={ticketDescriptionDraft}
                    />
                    <div className="ticket-description-editor-actions">
                      <small>Cmd/Ctrl + Enter to save</small>
                      <div>
                        <button className="secondary-action" onClick={cancelTicketDescriptionEditor} type="button">Cancel</button>
                        <button className="primary-action" onClick={saveTicketDescriptionEditor} type="button">Save</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`ticket-description-read ${descriptionText ? "" : "is-empty"}`} onDoubleClick={openTicketDescriptionEditor} onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openTicketDescriptionEditor();
                    }
                  }} role="button" tabIndex={0}>
                    <WorkspaceRichText emptyText="Add context, expected outcome, and acceptance criteria" value={descriptionText} />
                  </div>
                )}
              </section>
              <section className="ticket-page-section ticket-linked-work">
                <div className="ticket-linked-heading ticket-linked-subtasks"><h2>Subtasks</h2><div className="ticket-linked-heading-meta"><button aria-label={aiByokEnabled ? "AI suggestions" : "Ask Admin To enable by adding BYOK"} className={`ticket-link-heading-ai ${aiByokEnabled ? "" : "is-disabled"}`.trim()} disabled={!aiByokEnabled} title={aiByokEnabled ? "AI suggestions" : "Ask Admin To enable by adding BYOK"} type="button"><AutoAwesomeOutlined fontSize="inherit" /><span className="ticket-link-heading-ai-label">AI suggestions</span></button><span>{childTickets.length} subtasks</span></div></div>
                <div className="ticket-link-composer ticket-subtask-composer">
                  <div className="ticket-link-search-field ticket-subtask-search-field">
                    <input aria-label="Search or create subtask" onChange={(event) => setSubtaskQuickTitle(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); if (subtaskSearchResults[0]) addExistingSubtask(subtaskSearchResults[0].id); else if (subtaskQuickTitle.trim()) openSubtaskModal(selectedRecord, subtaskQuickTitle.trim()); } }} placeholder="Search ticket or create subtask" value={subtaskQuickTitle} />
                    {subtaskQuickTitle ? <div className="ticket-link-results">{subtaskSearchResults.map((record) => <button key={record.id} onMouseDown={(event) => { event.preventDefault(); addExistingSubtask(record.id); }} type="button"><WorkTypeMark type={record.type} /><span><strong>{ticketDisplayKey(record)} · {record.title}</strong><small>{record.type} · {record.status}</small></span><AddOutlined fontSize="small" /></button>)}{!subtaskSearchResults.length ? <button className="ticket-link-create-result" onMouseDown={(event) => { event.preventDefault(); openSubtaskModal(selectedRecord, subtaskQuickTitle.trim()); }} type="button"><AddOutlined fontSize="small" /><span><strong>Create subtask</strong><small>{subtaskQuickTitle.trim()}</small></span></button> : null}</div> : null}
                  </div>
                  <button aria-label="Add matching subtask or create a new subtask" className="ticket-link-apply" disabled={!subtaskQuickTitle.trim() || saving} onClick={() => { if (subtaskSearchResults[0]) addExistingSubtask(subtaskSearchResults[0].id); else openSubtaskModal(selectedRecord, subtaskQuickTitle.trim()); }} title="Add subtask" type="button"><SaveOutlined fontSize="small" /></button>
                  <button aria-label="Clear subtask summary" className="ticket-link-cancel" onClick={() => setSubtaskQuickTitle("")} title="Clear subtask summary" type="button">×</button>
                </div>
                <div className="ticket-linked-list ticket-subtask-list">{childTickets.map((record) => <article className="ticket-work-link is-linked is-subtask" key={record.id}><button aria-label={`Open ${ticketDisplayKey(record)}: ${record.title}`} className="ticket-work-link-open" onClick={() => openRecordHistory(record, { returnToCurrent: true })} type="button"><span className="ticket-work-link-key">{ticketDisplayKey(record)}</span><span className="ticket-work-link-title">{record.title}</span></button><button aria-label={`Remove ${ticketDisplayKey(record)} as a subtask`} className="ticket-work-link-remove" disabled={saving} onClick={() => removeSubtask(record.id)} title="Remove subtask" type="button">×</button></article>)}</div>
                <div className="ticket-linked-heading"><h2>Linked work items</h2><div className="ticket-linked-heading-meta"><button aria-label={aiByokEnabled ? "AI suggestions" : "Ask Admin To enable by adding BYOK"} className={`ticket-link-heading-ai ${aiByokEnabled ? "" : "is-disabled"}`.trim()} disabled={!aiByokEnabled} title={aiByokEnabled ? "AI suggestions" : "Ask Admin To enable by adding BYOK"} type="button"><AutoAwesomeOutlined fontSize="inherit" /><span className="ticket-link-heading-ai-label">AI suggestions</span></button><span>{linkedTickets.length + (detailDraft?.linkedWorkUrl ? 1 : 0)} linked</span></div></div>
                <div className="ticket-link-composer">
                  <select aria-label="Link relationship" onChange={(event) => setDetailDraft((current) => ({ ...current, linkedWorkType: event.target.value }))} value={detailDraft?.linkedWorkType || "relates to"}>{linkedWorkRelationOptions.map((option) => <option key={option}>{option}</option>)}</select>
                  <div className="ticket-link-search-field">
                    <input aria-label="Search tickets to link" onChange={(event) => setLinkedTicketSearch(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); applyLinkedSearch(); } }} placeholder="Type, search by ticket number, name, or paste URL" value={linkedTicketSearch} />
                    {linkedTicketSearch ? <div className="ticket-link-results">{linkSearchResults.map((record) => <button key={record.id} onMouseDown={(event) => { event.preventDefault(); addLinkedTicket(record.id); }} type="button"><WorkTypeMark type={record.type} /><span><strong>{ticketDisplayKey(record)} · {record.title}</strong><small>{record.type} · {record.status}</small></span><AddOutlined fontSize="small" /></button>)}{!linkSearchResults.length ? <button className="ticket-link-create-result" onMouseDown={(event) => { event.preventDefault(); applyLinkedSearch(); }} type="button"><AddOutlined fontSize="small" /><span><strong>{/^https?:\/\//i.test(linkedTicketSearch.trim()) ? "Link URL" : "Create linked ticket"}</strong><small>{linkedTicketSearch.trim()}</small></span></button> : null}</div> : null}
                  </div>
                  <button aria-label="Save linked work item" className="ticket-link-apply" disabled={!linkedTicketSearch.trim()} onClick={applyLinkedSearch} title="Save linked work item" type="button"><SaveOutlined fontSize="small" /></button>
                  <button aria-label="Clear linked work search" className="ticket-link-cancel" onClick={() => setLinkedTicketSearch("")} title="Clear linked work search" type="button">×</button>
                </div>
                <div className="ticket-linked-list">{renderLinkedTicketGroups()}{detailDraft?.linkedWorkUrl ? <article className="ticket-work-link is-linked"><a className="ticket-work-link-open" href={detailDraft.linkedWorkUrl} rel="noreferrer" target="_blank"><span className="ticket-work-link-key">URL</span><span className="ticket-work-link-title">{detailDraft.linkedWorkUrl}</span></a><button aria-label="Remove linked URL" className="ticket-work-link-remove" onClick={() => setDetailDraft((current) => ({ ...current, linkedWorkUrl: "" }))} title="Remove link" type="button">×</button></article> : null}</div>
              </section>
              <section className="ticket-page-section"><div className="ticket-activity-heading"><h2>Attachments</h2><label className="ticket-file-button">Attach file<input accept="*/*" onChange={attachTicketFile} type="file" /></label></div><div className="ticket-attachments">{(selectedRecord.attachments || []).map((attachment) => <a href={attachment.url} key={attachment.id} rel="noreferrer" target="_blank">⌁ {attachment.name}</a>)}{!(selectedRecord.attachments || []).length ? <p className="empty-state">No files attached.</p> : null}</div></section>
              <section className="ticket-page-section"><div className="ticket-activity-heading"><h2>Activity</h2><div className="record-issue-tabs">{["all", "comments", "transitions", "activity"].map((tab) => <button className={issueActivityTab === tab ? "active" : ""} key={tab} onClick={() => setIssueActivityTab(tab)} type="button">{tab[0].toUpperCase() + tab.slice(1)}</button>)}</div></div>
                {!replyToCommentId ? <div className="record-comment-composer"><TicketMentionTextarea attachmentDisabled={commentAttachmentBusy || commentSaving} mentionOptions={ticketMentionOptions} onAttachmentChange={handleCommentAttachmentChange} onChange={setCommentDraft} placeholder="Add a comment… use @name to mention a teammate" rows={3} value={commentDraft} />{renderPendingCommentAttachments()}<div className="record-detail-actions"><button className="primary-action" disabled={commentSaving || commentAttachmentBusy || (!commentDraft.trim() && !commentAttachments.length)} onClick={submitIssueComment} title="Post comment" type="button">{commentSaving ? "Posting..." : "Comment"}</button></div></div> : null}
                {issueActivityTab === "comments" ? <div className="record-comment-thread">{visibleTopLevelComments.map((comment) => renderTicketComment(comment))}{orphanReplyComments.map((comment) => renderTicketComment(comment))}{remainingTopLevelCommentCount ? <div className="ticket-load-more-comments"><span aria-hidden="true" /><button onClick={() => setVisibleCommentCount((count) => count + 10)} type="button">Load {Math.min(10, remainingTopLevelCommentCount)} more comments</button><span aria-hidden="true" /></div> : null}{!comments.length ? <p className="empty-state">No comments yet.</p> : null}</div> : <div className="record-timeline-list">{(buildIssueActivityFeed()[issueActivityTab] || buildIssueActivityFeed().all).map((event) => { const actor = getTicketEventActor(event); return <article key={event.id}><time>{formatTicketEventTime(event)}</time><div><strong>{getTicketHistoryMessage(event) || "Ticket updated."}</strong><span>{actor?.name || actor?.email || "System"}</span></div></article>; })}</div>}</section>
            </section>
            <aside className="ticket-page-sidebar ticket-details-sidebar">
              <section className="ticket-sidebar-controls" aria-label="Ticket controls">
                <div className="ticket-sidebar-action-row">
                  <button aria-label={detailAutosaveState.message || "Autosave on"} className={`ticket-action-chip ticket-action-icon is-autosave is-${detailAutosaveState.status || "idle"}`} disabled type="button" title={detailAutosaveState.message || "Autosave on"}>
                    <span aria-hidden="true">{detailAutosaveState.status === "error" ? "!" : detailAutosaveState.status === "saved" ? "✓" : <SaveOutlined fontSize="inherit" />}</span>
                  </button>
                  <button aria-label={isFollowingTicket ? "Unfollow ticket updates" : "Follow ticket updates"} className={`ticket-action-chip ticket-action-icon ${isFollowingTicket ? "is-active" : ""}`} disabled={saving} onClick={toggleTicketWatch} title={isFollowingTicket ? `Following${watcherCount ? ` · ${watcherCount} watchers` : ""}` : `Follow ticket${watcherCount ? ` · ${watcherCount} watchers` : ""}`} type="button">
                    <span aria-hidden="true">{isFollowingTicket ? "★" : "☆"}</span>
                  </button>
                  <button aria-label="Refresh ticket activity and latest details" className={`ticket-action-chip ticket-action-icon ${timelineLoading ? "is-loading" : ""}`} disabled={timelineLoading} onClick={refreshSelectedRecordTimeline} title={timelineLoading ? "Refreshing ticket" : "Refresh ticket"} type="button">
                    <span aria-hidden="true">↻</span>
                  </button>
                </div>
                <label className="ticket-status-select-shell"><span>Ticket status</span><select aria-label="Ticket status" onChange={(event) => setDetailDraft((current) => ({ ...current, status: event.target.value }))} value={detailDraft?.status || initialTicketStatus}>{ticketWorkflowStatuses.map((status) => <option key={status}>{status}</option>)}</select><i aria-hidden="true">⌄</i></label>
              </section>
              <div className="ticket-core-details">
                <div className="ticket-sidebar-field ticket-assignee-field"><label htmlFor="ticket-detail-assignee">Assignee</label><select id="ticket-detail-assignee" onChange={(event) => setDetailDraft((current) => ({ ...current, owner: event.target.value }))} value={detailDraft?.owner || ""}><option value="">Unassigned</option>{projectMemberOptions.map((person) => <option key={person}>{person}</option>)}</select>{currentAssigneeName ? <button className="ticket-assignee-me-action" disabled={detailDraft?.owner === currentAssigneeName || saving} onClick={() => setDetailDraft((current) => ({ ...current, owner: currentAssigneeName }))} type="button">{detailDraft?.owner === currentAssigneeName ? "Assigned to you" : "Assign to me"}</button> : null}</div>
                <label>Priority<select onChange={(event) => setDetailDraft((current) => ({ ...current, priority: event.target.value }))} value={detailDraft?.priority || "Medium"}>{workspacePriorities.map((priority) => <option key={priority}>{priority}</option>)}</select></label>
                <label>Due date<input onChange={(event) => setDetailDraft((current) => ({ ...current, due: event.target.value }))} type="date" value={detailDraft?.due || ""} /></label>
                {detailDraft?.parentRecordId ? renderParentTicketPicker() : null}
                <TicketLabelPicker onChange={(labels) => setDetailDraft((current) => ({ ...current, labels }))} onCommit={saveTicketLabelsNow} options={workspaceLabelOptions} placeholder="Add or create label" value={detailDraft?.labels || ""} />
                {contributorNames.length ? renderContributorControl() : null}
              </div>
              <details className="ticket-secondary-details"><summary><span>Advanced</span><i aria-hidden="true">⌄</i></summary><div>
                {!contributorNames.length ? renderContributorControl() : null}
                <label>Ticket type<input list="workspace-ticket-type-options" onChange={(event) => setDetailDraft((current) => ({ ...current, type: event.target.value.slice(0, 80) }))} value={detailDraft?.type || "Ticket"} /></label>
                {!detailDraft?.parentRecordId ? renderParentTicketPicker() : null}
              </div></details>
              <div className="ticket-minor-details"><span>Project {ticketProjectMetaName || "No project"}</span><span>Created by {selectedRecord.creator || "Workspace member"}</span><span>Created on {selectedRecord.createdAt ? new Date(selectedRecord.createdAt).toLocaleString() : "—"}</span><span>Last updated on {selectedRecord.updatedAt ? new Date(selectedRecord.updatedAt).toLocaleString() : "—"}</span></div>
            </aside>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <ModuleTabStrip moduleName={moduleName} activeTab={activeTab} setActiveTab={handleModuleTabChange} tabs={moduleTabs} />
      <div className="functional-workspace">
        <WorkspaceErrorNotice message={error} />
        {isLoading ? <p className="empty-state"><WorkspaceInlineLoader label="Loading records" /></p> : null}

      {!(moduleName === "Work" && ["Overview", "Board"].includes(activeTab)) && moduleName !== "Schedule" ? <section className="functional-toolbar">
        {
          <>
            <label>
              Search
              <input
                onChange={(event) => setQuery(event.target.value)}
                placeholder={`Search ${activeTab.toLowerCase()}`}
                type="search"
                value={query}
              />
            </label>
            <label>
              Status
              <select onChange={(event) => setStatusFilter(event.target.value)} value={statusFilter}>
                <option>All</option>
                {config.statuses.map((status) => (
                  <option key={status}>{status}</option>
                ))}
              </select>
            </label>
            <button aria-label={currentCreateAction[0]} className="primary-action work-toolbar-icon-action" disabled={saving || isLoading || boardBusy} onClick={openCreateModal} title={currentCreateAction[0]} type="button">
              <AddOutlined fontSize="small" />
            </button>
          </>
        }
      </section> : null}

      {formOpen ? (
        <ModalPortal>
        <div className="workspace-modal-backdrop" onMouseDown={closeCreateModal} role="presentation">
        <section aria-labelledby="create-record-title" aria-modal="true" className={`workspace-modal ${isTicketCreate ? "workspace-ticket-create-modal" : ""} ${isSprintCreate ? "workspace-sprint-create-modal" : ""} ${isEpicCreate ? "workspace-epic-create-modal" : ""} ${isScheduleCreate ? "workspace-schedule-create-modal" : ""}`.trim()} onMouseDown={(event) => event.stopPropagation()} role="dialog" style={modalSurfaceStyle}>
          <header><div><span>{moduleName}</span><h3 id="create-record-title">{isTicketCreate ? `Create ${draft.type || "work item"}` : isScheduleCreate ? activeScheduleDefinition.createLabel : currentCreateAction[0]}</h3></div><button aria-label="Close" onClick={closeCreateModal} type="button">×</button></header>
        <form className={`functional-create-form ${isTicketCreate ? "ticket-create-form" : ""} ${isSprintCreate ? "workspace-sprint-create-form" : ""} ${isEpicCreate ? "workspace-epic-create-form" : ""} ${isScheduleCreate ? "workspace-schedule-create-form" : ""}`.trim()} onSubmit={submitRecord} style={modalContentStyle}>
          {isTicketCreate ? <div className="form-wide ticket-create-intro"><div><span>New work item</span><strong>Capture the ticket clearly, assign ownership, and place it into the right workflow from the start.</strong></div><em>{draft.status || initialTicketStatus}</em></div> : null}
          {isTicketCreate ? <label className="form-wide ticket-context-field">Workspace<input disabled value={workspaceInfo?.name || "Lyriclious Workspace"} /></label> : null}
          {isTicketCreate ? <div className="form-wide work-type-field">
            <span className="ticket-field-label">Work type <b aria-hidden="true">*</b></span>
            <div className="work-type-picker" ref={workTypePickerRef}>
              <button aria-controls="work-type-options" aria-expanded={workTypePickerOpen} className="work-type-picker-trigger" onClick={() => setWorkTypePickerOpen((current) => !current)} type="button">
                <WorkTypeMark type={draft.type} />
                <span>{draft.type}</span>
                <i aria-hidden="true" className="work-type-picker-chevron">⌄</i>
              </button>
              {workTypePickerOpen ? <div className="work-type-picker-menu" id="work-type-options" role="listbox">
                {workTypeOptions.map((type) => <button aria-selected={draft.type === type.value} className={draft.type === type.value ? "is-selected" : ""} key={type.value} onClick={() => {
                  const workflow = getTicketWorkflow(type.value);
                  setDraft((current) => ({ ...current, type: type.value, status: getDefaultTicketStatus(workflow, current.status || defaultActionableTicketStatus) }));
                  setWorkTypePickerOpen(false);
                }} role="option" type="button">
                  <WorkTypeMark type={type.value} />
                  <span><strong>{type.value}</strong><small>{type.description}</small></span>
                </button>)}
              </div> : null}
            </div>
            <small id="work-type-description">{workTypeOptions.find((type) => type.value === draft.type)?.description}</small>
            <label className="ticket-custom-type-field">Custom type / key prefix
              <input aria-describedby="ticket-type-help" list="workspace-ticket-type-options" onChange={(event) => {
                const type = event.target.value.slice(0, 80);
                const workflow = getTicketWorkflow(type);
                setDraft((current) => ({ ...current, type, status: getDefaultTicketStatus(workflow, current.status || defaultActionableTicketStatus) }));
              }} placeholder="Ticket, DEVOPS, CLOUD, UX…" value={draft.type} />
              <datalist id="workspace-ticket-type-options">{workTypeOptions.map((type) => <option key={type.value} value={type.value} />)}</datalist>
              <small id="ticket-type-help">Use client-specific types like DEVOPS or CLOUD when needed. The ID is assigned automatically and never reused.</small>
            </label>
          </div> : null}
          {isTicketCreate ? <label className="form-wide ticket-status-field">Initial status <span aria-hidden="true">*</span><select onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value }))} value={draft.status}>{getTicketWorkflow(draft.type).map((state) => <option key={state.id || state.name}>{state.name}</option>)}</select><small className="workflow-status-help">This is the starting state for the selected work type workflow.</small></label> : null}
          <label className={(isSprintCreate || isEpicCreate || isTicketCreate) ? `form-wide ${isTicketCreate ? "ticket-summary-field" : ""}` : ""}>
            {createTitleLabel}
            <input
              autoFocus
              required
              onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
              placeholder={createTitlePlaceholder}
              value={draft.title}
            />
            {isTicketCreate ? <small className="ticket-field-validation">Summary is required</small> : null}
          </label>
          {!isTicketCreate && !isSprintCreate && !isEpicCreate && !isScheduleCreate ? <label>
            Type
            <select onChange={(event) => setDraft((current) => ({ ...current, type: event.target.value }))} value={draft.type}>
              {createTypeOptions.map((type) => (
                <option key={type}>{type}</option>
              ))}
            </select>
          </label> : null}
          {!isTicketCreate && !isSprintCreate && !isScheduleCreate ? <>
            {!isEpicCreate ? <label>
              Owner
              <select onChange={(event) => setDraft((current) => ({ ...current, owner: event.target.value }))} value={draft.owner}>
                {workspaceUserOptions.map((owner) => (
                  <option key={owner}>{owner}</option>
                ))}
              </select>
            </label> : null}
            <label>
              Priority
              <select onChange={(event) => setDraft((current) => ({ ...current, priority: event.target.value }))} value={draft.priority}>
                {workspacePriorities.map((priority) => (
                  <option key={priority}>{priority}</option>
                ))}
              </select>
            </label>
            <label>
              Status
              <select onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value }))} value={draft.status}>
                {config.statuses.map((status) => <option key={status}>{status}</option>)}
              </select>
            </label>
            <label>
              Due
              <input onChange={(event) => setDraft((current) => ({ ...current, due: event.target.value }))} type="date" value={draft.due} />
            </label>
          </> : null}
          {!isTicketCreate && !isSprintCreate && !isEpicCreate && !isScheduleCreate && !isProjectCreate ? (
            <WorkspaceEditor
              className="form-wide"
              label={moduleName === "Knowledge" ? "Document content" : "Description"}
              minRows={moduleName === "Knowledge" ? 6 : 4}
              onChange={(value) => setDraft((current) => ({ ...current, description: value }))}
              placeholder={moduleName === "Knowledge" ? "Write the document body, notes, links, decisions, or operating steps." : "Add context, notes, links, next steps, or useful details."}
              rows={moduleName === "Knowledge" ? 6 : 4}
              value={draft.description}
            />
          ) : null}
          {isScheduleCreate ? <>
            <section className="form-wide schedule-form-intro">
              <div>
                <span>{activeScheduleDefinition.eyebrow}</span>
                <strong>{activeScheduleDefinition.description}</strong>
              </div>
              <em>{activeTab}</em>
            </section>
            <section className="form-wide schedule-section-card">
              <div className="ticket-form-section"><div><span>Schedule basics</span><small>Define what is being scheduled, who owns it, and its current state.</small></div></div>
              <div className="form-wide ticket-field-grid">
                <label>Record type<select onChange={(event) => setDraft((current) => ({ ...current, type: event.target.value, scheduleRequestType: activeTab === "Requests" ? event.target.value : current.scheduleRequestType }))} value={draft.type}>{createTypeOptions.map((type) => <option key={type}>{type}</option>)}</select></label>
                {renderCreateOwnerPicker(activeTab === "Rooms" ? "Resource owner" : activeTab === "Requests" ? "Requested with / host" : activeTab === "Availability" ? "Person" : "Host")}
                <label>Status<select onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value }))} value={draft.status}>{(scheduleStatusOptionsByTab[activeTab] || config.statuses).map((status) => <option key={status}>{status}</option>)}</select></label>
                <label>Visibility<select onChange={(event) => setDraft((current) => ({ ...current, scheduleVisibility: event.target.value }))} value={draft.scheduleVisibility}>{scheduleVisibilityOptions.map((visibility) => <option key={visibility}>{visibility}</option>)}</select></label>
              </div>
            </section>
            <section className="form-wide schedule-section-card">
              <div className="ticket-form-section"><div><span>Date and time</span><small>Use dates for launch-ready scheduling. Exact time, duration, and timezone are stored as schedule metadata.</small></div></div>
              <div className="form-wide ticket-field-grid">
                {activeTab === "Availability" ? <label>Start date<input onChange={(event) => setDraft((current) => ({ ...current, startDate: event.target.value }))} type="date" value={draft.startDate} /></label> : null}
                <label>{activeTab === "Availability" ? "End date" : activeTab === "Rooms" ? "Next review date" : activeTab === "Bookings" ? "Available from" : "Date"}<input onChange={(event) => setDraft((current) => ({ ...current, due: event.target.value }))} type="date" value={draft.due} /></label>
                <label>Start time<input onChange={(event) => setDraft((current) => ({ ...current, scheduleStartTime: event.target.value }))} type="time" value={draft.scheduleStartTime} /></label>
                <label>End time<input onChange={(event) => setDraft((current) => ({ ...current, scheduleEndTime: event.target.value }))} type="time" value={draft.scheduleEndTime} /></label>
                <label>Duration<select onChange={(event) => setDraft((current) => ({ ...current, scheduleDuration: event.target.value }))} value={draft.scheduleDuration}>{scheduleDurationOptions.map((minutes) => <option key={minutes} value={minutes}>{minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} hr${minutes % 60 ? ` ${minutes % 60} min` : ""}`}</option>)}</select></label>
                <label>Timezone<input onChange={(event) => setDraft((current) => ({ ...current, scheduleTimezone: event.target.value }))} placeholder="Asia/Kolkata" value={draft.scheduleTimezone} /></label>
              </div>
            </section>
            <section className="form-wide schedule-section-card">
              <div className="ticket-form-section"><div><span>Context and access</span><small>Connect the schedule item with its team, project, room/link, attendees, and notes.</small></div></div>
              <div className="form-wide ticket-field-grid">
                <label>{activeTab === "Rooms" ? "Location / room link" : "Meeting link / location"}<input onChange={(event) => setDraft((current) => ({ ...current, scheduleLocation: event.target.value }))} placeholder={activeTab === "Rooms" ? "Room name, address, or virtual resource" : "Google Meet, Zoom, Teams, room, or location"} value={draft.scheduleLocation} /></label>
                <label>{activeTab === "Bookings" ? "Booking URL" : "Reference URL"}<input onChange={(event) => setDraft((current) => ({ ...current, scheduleMeetingLink: event.target.value }))} placeholder="https://..." value={draft.scheduleMeetingLink} /></label>
                <label>Project<input disabled value={selectedProjectName || "Header project not selected"} /></label>
                <label>Team<select onChange={(event) => setDraft((current) => ({ ...current, team: event.target.value }))} value={draft.team}><option value="">No team</option>{workspaceTeamOptions.map((team) => <option key={team}>{team}</option>)}</select></label>
                <label>{activeTab === "Rooms" ? "Capacity" : "Buffer"}<input min="0" onChange={(event) => setDraft((current) => activeTab === "Rooms" ? ({ ...current, scheduleCapacity: event.target.value }) : ({ ...current, scheduleBuffer: event.target.value }))} type="number" value={activeTab === "Rooms" ? draft.scheduleCapacity : draft.scheduleBuffer} /></label>
                <label>{activeTab === "Rooms" ? "Equipment" : "Attendees / tags"}<input onChange={(event) => setDraft((current) => ({ ...current, labels: event.target.value }))} placeholder={activeTab === "Rooms" ? "Projector, whiteboard, 12 seats" : "Client, team, email, tag"} value={draft.labels} /></label>
                {activeTab === "Requests" ? <label className="ticket-flag-control"><span>Approval</span><span className="ticket-checkbox-row"><input checked={Boolean(draft.scheduleApprovalRequired)} onChange={(event) => setDraft((current) => ({ ...current, scheduleApprovalRequired: event.target.checked }))} type="checkbox" /><b>Requires approval before scheduling</b></span></label> : null}
                <label className="ticket-field-wide">Repeat<input onChange={(event) => setDraft((current) => ({ ...current, scheduleRepeatRule: event.target.value }))} placeholder="Does not repeat, weekly, monthly…" value={draft.scheduleRepeatRule} /></label>
                <WorkspaceEditor className="ticket-field-wide" compact label="Notes" minRows={3} onChange={(value) => setDraft((current) => ({ ...current, description: value }))} placeholder="Purpose, agenda, requester note, joining instructions, room details, or exception reason." rows={3} value={draft.description} />
              </div>
            </section>
          </> : null}
          {isProjectCreate ? (
            <>
              <WorkspaceEditor
                className="form-wide"
                compact
                label="Delivery summary"
                minRows={3}
                onChange={(value) => setDraft((current) => ({ ...current, description: value }))}
                placeholder="Describe the engagement, delivery scope, stakeholders, milestones, or how this client work is structured."
                rows={3}
                value={draft.description}
              />
              <label>
                Account client
                <input
                  onChange={(event) => setDraft((current) => ({ ...current, accountClient: event.target.value }))}
                  placeholder="The company we onboarded"
                  value={draft.accountClient}
                />
              </label>
              <label>
                End client
                <input
                  onChange={(event) => setDraft((current) => ({ ...current, endClient: event.target.value }))}
                  placeholder="Their customer for this project"
                  value={draft.endClient}
                />
              </label>
              <label>
                Delivery reference
                <input
                  onChange={(event) => setDraft((current) => ({ ...current, projectRef: event.target.value }))}
                  placeholder="Project key, contract ref, or dashboard slug"
                  value={draft.projectRef}
                />
              </label>
              <label>
                Project URL slug
                <input
                  onChange={(event) => setDraft((current) => ({ ...current, projectSlug: event.target.value }))}
                  placeholder="client-onboarding-2026"
                  value={draft.projectSlug}
                />
              </label>
              <label className="functional-checkbox">
                <span>Client dashboard</span>
                <button
                  className={draft.clientPortalEnabled ? "is-active" : ""}
                  onClick={() => setDraft((current) => ({
                    ...current,
                    clientPortalEnabled: !current.clientPortalEnabled,
                    portalRoles: !current.clientPortalEnabled
                      ? (current.portalRoles?.length ? current.portalRoles : defaultClientPortalRoles)
                      : current.portalRoles,
                  }))}
                  type="button"
                >
                  {draft.clientPortalEnabled ? "Enabled" : "Disabled"}
                </button>
              </label>
              <div className="form-wide project-portal-panel">
                <span>End-client member roles</span>
                <div className="project-role-grid">
                  {defaultClientPortalRoles.map((role) => {
                    const active = draft.portalRoles.includes(role);
                    return (
                      <button
                        className={active ? "active" : ""}
                        key={role}
                        onClick={() => setDraft((current) => ({
                          ...current,
                          portalRoles: active
                            ? current.portalRoles.filter((item) => item !== role)
                            : [...current.portalRoles, role],
                        }))}
                        type="button"
                      >
                        {role}
                      </button>
                    );
                  })}
                </div>
                <small>Viewer can follow progress. Ticket Requester raises tickets. Ticket Editor updates tickets. Approver reviews decisions. Client Admin manages members and full project dashboard access.</small>
              </div>
              <label className="form-wide">
                End-client members
                <textarea
                  onChange={(event) => setDraft((current) => ({ ...current, portalMembersInput: event.target.value }))}
                  placeholder={"One per line: Name | email@company.com | Ticket Editor | Invited"}
                  rows={4}
                  value={draft.portalMembersInput}
                />
              </label>
            </>
          ) : null}
          {moduleName === "Work" ? (isTicketCreate ? <>
            <section className="form-wide ticket-section-card">
            <div className="ticket-form-section"><div><span>Workflow and scheduling</span><small>Set urgency, timeline, and the core execution context before the ticket starts moving.</small></div></div>
            <div className="form-wide ticket-field-grid">
              <label>Priority<select onChange={(event) => setDraft((current) => ({ ...current, priority: event.target.value }))} value={draft.priority}>{workspacePriorities.map((priority) => <option key={priority}>{priority}</option>)}</select><small>Priority controls operational attention.</small></label>
              <label>Due date<input onChange={(event) => setDraft((current) => ({ ...current, due: event.target.value }))} type="date" value={draft.due} /><small>Expected completion date for the ticket.</small></label>
              <label>Start date<input onChange={(event) => setDraft((current) => ({ ...current, startDate: event.target.value }))} type="date" value={draft.startDate} /><small>Planned date work should begin.</small></label>
              <label>Severity<select onChange={(event) => setDraft((current) => ({ ...current, severity: event.target.value }))} value={draft.severity}><option value="">None</option>{severityOptions.map((severity) => <option key={severity}>{severity}</option>)}</select><small>Use severity when the impact level matters beyond normal priority.</small></label>
            </div>
            <WorkspaceEditor className="form-wide ticket-description-field" label="Description" minRows={5} onChange={(value) => setDraft((current) => ({ ...current, description: value }))} placeholder="State the request or problem, expected outcome, acceptance criteria, links, and useful context." rows={5} value={draft.description} />
            </section>
            <section className="form-wide ticket-section-card">
            <div className="ticket-form-section"><div><span>People and ownership</span><small>Assign clear responsibility so the right people see, own, and contribute to the work.</small></div></div>
            <div className="form-wide ticket-field-grid">
              <label>Assignee<select onChange={(event) => setDraft((current) => ({ ...current, owner: event.target.value }))} value={draft.owner}><option value="">Unassigned</option>{projectMemberOptions.map((person) => <option key={person}>{person}</option>)}</select><button className="ticket-inline-action" onClick={() => setDraft((current) => ({ ...current, owner: defaultOwner }))} type="button">Assign to me</button></label>
              <label>Contributors<select multiple onChange={(event) => setDraft((current) => ({ ...current, assignees: Array.from(event.target.selectedOptions).map((option) => option.value).join(", ") }))} size={4} value={asList(draft.assignees)}>{projectMemberOptions.filter((person) => person !== draft.owner).map((person) => <option key={person}>{person}</option>)}</select><small>Select each person contributing to the ticket.</small></label>
              <label>Team<select onChange={(event) => setDraft((current) => ({ ...current, team: event.target.value }))} value={draft.team}><option value="">Choose a team</option>{workspaceTeamOptions.map((team) => <option key={team}>{team}</option>)}</select><small>Use team filters across work views and reports.</small></label>
              <label>Reporter <span aria-hidden="true">*</span><input disabled value={currentUser?.name || "Workspace member"} /></label>
            </div>
            </section>
            <section className="form-wide ticket-section-card">
            <div className="ticket-form-section"><div><span>Hierarchy and planning</span><small>Place this ticket in the right sprint, epic, and parent structure for reporting and tracking.</small></div></div>
            <div className="form-wide ticket-field-grid">
              <label>Parent<select onChange={(event) => setDraft((current) => ({ ...current, parentRecordId: event.target.value }))} value={draft.parentRecordId}><option value="">Select parent</option>{ticketParentOptions.map((record) => <option key={record.id} value={record.id}>{record.type} · {record.title}</option>)}</select><small>Your work type hierarchy controls valid parent work.</small></label>
              <label>Sprint<select onChange={(event) => setDraft((current) => ({ ...current, sprint: event.target.value }))} value={draft.sprint}><option value="">No sprint</option>{workSprintOptions.map((sprint) => <option key={sprint} value={sprint}>{sprint}</option>)}</select></label>
              <label>Epic<select onChange={(event) => setDraft((current) => ({ ...current, epic: event.target.value }))} value={draft.epic}><option value="">No epic</option>{workEpicOptions.map((epic) => <option key={epic} value={epic}>{epic}</option>)}</select></label>
              <label>Story points<input min="0" onChange={(event) => setDraft((current) => ({ ...current, storyPoints: event.target.value }))} type="number" value={draft.storyPoints} /></label>
              <div className="ticket-field-wide"><TicketLabelPicker onChange={(labels) => setDraft((current) => ({ ...current, labels }))} options={workspaceLabelOptions} placeholder="Add or create label" value={draft.labels} /></div>
            </div>
            </section>
            <section className="form-wide ticket-section-card">
            <div className="ticket-form-section"><div><span>Linked work and controls</span><small>Capture dependencies, references, access limits, and any delivery risk that needs attention.</small></div></div>
            <div className="form-wide ticket-field-grid">
              <label>Link relationship<select onChange={(event) => setDraft((current) => ({ ...current, linkedWorkType: event.target.value }))} value={draft.linkedWorkType}><option value="blocks">blocks</option><option value="is blocked by">is blocked by</option><option value="relates to">relates to</option><option value="duplicates">duplicates</option></select><small>Choose how this ticket relates to the linked work.</small></label>
              <label>Linked work items<select multiple onChange={(event) => setDraft((current) => ({ ...current, linkedRecordIds: Array.from(event.target.selectedOptions).map((option) => option.value) }))} size={4} value={draft.linkedRecordIds}>{ticketLinkOptions.map((record) => <option key={record.id} value={record.id}>{record.type} · {record.title}</option>)}</select><small>Select one or more related tickets.</small></label>
              <label className="ticket-field-wide">Link or reference URL<input onChange={(event) => setDraft((current) => ({ ...current, linkedWorkUrl: event.target.value }))} placeholder="Paste a related work URL, document, or external reference" value={draft.linkedWorkUrl} /></label>
              <label>Restrict to<select multiple onChange={(event) => setDraft((current) => ({ ...current, restrictRoles: Array.from(event.target.selectedOptions).map((option) => option.value) }))} size={4} value={draft.restrictRoles}>{ticketRestrictionRoleOptions.map((role) => <option key={role}>{role}</option>)}</select><small>Limit visibility to selected roles when needed.</small></label>
              <label className="ticket-flag-control">
                <span>Delivery risk</span>
                <span className="ticket-checkbox-row">
                  <input
                    checked={Boolean(draft.flagged)}
                    onChange={(event) => setDraft((current) => ({ ...current, flagged: event.target.checked }))}
                    type="checkbox"
                  />
                  <b>Mark this ticket as an impediment</b>
                </span>
                <small>Use this when the ticket is blocked, at risk, or needs escalation.</small>
              </label>
              <label className="ticket-attachment-field ticket-field-wide">Attachment<input accept="*/*" multiple onChange={(event) => { const files = Array.from(event.target.files || []).filter((file) => file.size <= 15 * 1024 * 1024); setDraft((current) => ({ ...current, attachmentFiles: [...(current.attachmentFiles || []), ...files].slice(0, 5) })); event.target.value = ""; }} type="file" /><span>Drop files to attach or <b>Browse</b></span>{draft.attachmentFiles?.length ? <small>{draft.attachmentFiles.map((file) => file.name).join(" · ")}</small> : null}</label>
            </div>
            </section>
          </> : isSprintCreate ? <>
            <WorkspaceEditor className="form-wide planning-objective-editor" label="Sprint goal" minRows={4} onChange={(value) => setDraft((current) => ({ ...current, description: value }))} placeholder="What outcome should the team deliver in this sprint?" rows={4} value={draft.description} />
            <label>Start date<input onChange={(event) => setDraft((current) => ({ ...current, startDate: event.target.value }))} type="date" value={draft.startDate} /></label>
            <label>End date<input onChange={(event) => setDraft((current) => ({ ...current, due: event.target.value }))} type="date" value={draft.due} /></label>
            <label>Planned capacity (points)<input min="0" onChange={(event) => setDraft((current) => ({ ...current, storyPoints: event.target.value }))} type="number" value={draft.storyPoints} /></label>
            {renderCreateOwnerPicker("Sprint lead")}
            <label>Status<select onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value }))} value={draft.status}>{config.statuses.map((status) => <option key={status}>{status}</option>)}</select></label>
            <div className="form-wide"><TicketLabelPicker onChange={(labels) => setDraft((current) => ({ ...current, labels }))} options={workspaceLabelOptions} placeholder="Add or create label" value={draft.labels} /></div>
          </> : <>
            <WorkspaceEditor className={isEpicCreate ? "form-wide planning-objective-editor" : "form-wide"} label={isSprintCreate ? "Sprint goal" : isEpicCreate ? "Epic objective" : "Description"} minRows={isEpicCreate ? 4 : 3} onChange={(value) => setDraft((current) => ({ ...current, description: value }))} placeholder={isSprintCreate ? "What should the team accomplish during this sprint?" : isEpicCreate ? "Describe the business outcome and scope of this epic" : "Scope, acceptance criteria, client note, QA note, or automation detail"} rows={isEpicCreate ? 4 : 3} value={draft.description} />
            {!isSprintCreate && !isEpicCreate ? <label>Sprint link<select onChange={(event) => setDraft((current) => ({ ...current, sprint: event.target.value }))} value={draft.sprint}><option value="">No sprint</option>{workSprintOptions.map((sprint) => <option key={sprint} value={sprint}>{sprint}</option>)}</select></label> : null}
            {!isSprintCreate && !isEpicCreate ? <label>Epic link<select onChange={(event) => setDraft((current) => ({ ...current, epic: event.target.value }))} value={draft.epic}><option value="">No epic</option>{workEpicOptions.map((epic) => <option key={epic} value={epic}>{epic}</option>)}</select></label> : null}
            <label>{isSprintCreate ? "Planned capacity (points)" : isEpicCreate ? "Estimated points" : "Story points"}<input min="0" onChange={(event) => setDraft((current) => ({ ...current, storyPoints: event.target.value }))} type="number" value={draft.storyPoints} /></label>
            {isEpicCreate ? renderCreateOwnerPicker("Epic owner") : <label>Assignees<input onChange={(event) => setDraft((current) => ({ ...current, assignees: event.target.value }))} placeholder="Amit, Sarah, Manager" value={draft.assignees} /></label>}
            <TicketLabelPicker onChange={(labels) => setDraft((current) => ({ ...current, labels }))} options={workspaceLabelOptions} placeholder="Add or create label" value={draft.labels} />
            {!isSprintCreate && !isEpicCreate ? <label>Severity<select onChange={(event) => setDraft((current) => ({ ...current, severity: event.target.value }))} value={draft.severity}><option value="">None</option>{severityOptions.map((severity) => <option key={severity}>{severity}</option>)}</select></label> : null}
            {!isSprintCreate && !isEpicCreate ? <label>Approval role<input onChange={(event) => setDraft((current) => ({ ...current, approvalRole: event.target.value }))} placeholder="Manager, Client, QA, Finance" value={draft.approvalRole} /></label> : null}
            {!isSprintCreate && !isEpicCreate ? <label>Template<input onChange={(event) => setDraft((current) => ({ ...current, templateName: event.target.value }))} placeholder="Bug triage, Client request, Sprint task" value={draft.templateName} /></label> : null}
            {!isSprintCreate && !isEpicCreate ? <label className="form-wide">Automation trigger<input onChange={(event) => setDraft((current) => ({ ...current, automationTrigger: event.target.value }))} placeholder="When status becomes blocked, notify manager" value={draft.automationTrigger} /></label> : null}
          </>) : null}
          <footer className="functional-create-footer form-wide">
            <div className="functional-create-footer-context">{modalError ? <p className="functional-create-error" role="alert"><span aria-hidden="true">!</span>{modalError}</p> : null}</div>
            <div><button className="secondary-action" onClick={closeCreateModal} type="button">Cancel</button><button className="primary-action" disabled={saving || isLoading} type="submit">{saving ? <WorkspaceInlineLoader compact label="Saving" /> : isTicketCreate ? `Create ${draft.type || "ticket"}` : isScheduleCreate ? activeScheduleDefinition.createLabel : currentCreateAction[0]}</button></div>
          </footer>
        </form>
        </section>
        </div>
        </ModalPortal>
      ) : null}

      {boardModalOpen ? (
        <ModalPortal>
          <div className="workspace-modal-backdrop" onMouseDown={closeBoardModal} role="presentation">
            <section aria-labelledby="board-modal-title" aria-modal="true" className="workspace-modal" onMouseDown={(event) => event.stopPropagation()} role="dialog" style={modalSurfaceStyle}>
              <header>
                <div>
                  <span>Work</span>
                  <h3 id="board-modal-title">{boardDraft.id ? "Edit board" : "Create board"}</h3>
                </div>
                <button aria-label="Close" onClick={closeBoardModal} type="button">×</button>
              </header>
              <form className="work-board-editor" onSubmit={saveBoardDraft} style={modalContentStyle}>
                <div className="work-board-editor-header">
                  <div>
                    <span>{boardDraft.id ? "Saved board" : "New board"}</span>
                    <h4>{boardDraft.name || "Board setup"}</h4>
                  </div>
                </div>
                {boardError ? <p className="workspace-inline-error">{boardError}</p> : null}
                <div className="work-board-editor-grid">
                  <label className="work-board-field-wide">
                    Board name
                    <input autoFocus onChange={(event) => setBoardDraft((current) => ({ ...current, name: event.target.value }))} placeholder="e.g. FIFA board" value={boardDraft.name} />
                  </label>
                </div>
                <div className="work-board-filter-block">
                  <span>Project</span>
                  <div className="work-board-chip-row">
                    {workProjectOptions.map((project) => <button className={boardDraft.filters.projects.includes(project) ? "active" : ""} key={project} onClick={() => toggleBoardFilterValue("projects", project)} type="button">{getProjectDisplayName(project)}</button>)}
                    {!workProjectOptions.length ? <em>No projects yet</em> : null}
                  </div>
                </div>
                <div className="work-board-filter-block">
                  <span>Epic</span>
                  <div className="work-board-chip-row">
                    {workEpicOptions.map((epic) => <button className={boardDraft.filters.epics.includes(epic) ? "active" : ""} key={epic} onClick={() => toggleBoardFilterValue("epics", epic)} type="button">{epic}</button>)}
                    {!workEpicOptions.length ? <em>No epics yet</em> : null}
                  </div>
                </div>
                <div className="work-board-filter-block">
                  <span>Sprint</span>
                  <div className="work-board-chip-row">
                    {workSprintOptions.map((sprint) => <button className={boardDraft.filters.sprints.includes(sprint) ? "active" : ""} key={sprint} onClick={() => toggleBoardFilterValue("sprints", sprint)} type="button">{sprint}</button>)}
                    {!workSprintOptions.length ? <em>No sprints yet</em> : null}
                  </div>
                </div>
                <div className="work-board-filter-block">
                  <span>Status</span>
                  <div className="work-board-chip-row">
                    {generalizedBoardLanes.map((lane) => <button className={boardDraft.filters.statuses.includes(lane.key) ? "active" : ""} key={lane.key} onClick={() => toggleBoardFilterValue("statuses", lane.key)} type="button">{lane.label}</button>)}
                  </div>
                </div>
                <div className="work-board-filter-block">
                  <span>Assignees</span>
                  <div className="work-board-chip-row">
                    {workspaceBoardPeople.map((person) => <button className={boardDraft.filters.assigneeIds.includes(person.userId) ? "active" : ""} key={person.userId} onClick={() => toggleBoardFilterValue("assigneeIds", person.userId)} type="button">{person.name}</button>)}
                  </div>
                </div>
                <div className="work-board-filter-block">
                  <span>Ticket types</span>
                  <div className="work-board-chip-row">
                    {workBoardTypeOptions.map((type) => <button className={boardDraft.filters.types.includes(type) ? "active" : ""} key={type} onClick={() => toggleBoardFilterValue("types", type)} type="button">{type}</button>)}
                  </div>
                </div>
                <div className="work-board-filter-block">
                  <span>Priorities</span>
                  <div className="work-board-chip-row">
                    {workspacePriorities.map((priority) => <button className={boardDraft.filters.priorities.includes(priority) ? "active" : ""} key={priority} onClick={() => toggleBoardFilterValue("priorities", priority)} type="button">{priority}</button>)}
                  </div>
                </div>
                <div className="work-board-filter-block">
                  <span>Severity</span>
                  <div className="work-board-chip-row">
                    {severityOptions.map((severity) => <button className={boardDraft.filters.severities.includes(severity) ? "active" : ""} key={severity} onClick={() => toggleBoardFilterValue("severities", severity)} type="button">{severity}</button>)}
                  </div>
                </div>
                <div className="work-board-filter-block">
                  <span>Team</span>
                  <div className="work-board-chip-row">
                    {workspaceTeamOptions.map((team) => <button className={boardDraft.filters.teams.includes(team) ? "active" : ""} key={team} onClick={() => toggleBoardFilterValue("teams", team)} type="button">{team}</button>)}
                    {!workspaceTeamOptions.length ? <em>No teams yet</em> : null}
                  </div>
                </div>
                <div className="work-board-filter-block">
                  <span>Due date</span>
                  <div className="work-board-chip-row">
                    {boardDueBucketOptions.map((bucket) => <button className={boardDraft.filters.dueBuckets.includes(bucket.key) ? "active" : ""} key={bucket.key} onClick={() => toggleBoardFilterValue("dueBuckets", bucket.key)} type="button">{bucket.label}</button>)}
                  </div>
                </div>
                <div className="work-board-filter-block">
                  <span>Flagged</span>
                  <div className="work-board-chip-row">
                    {workBoardFlagOptions.map((flag) => <button className={boardDraft.filters.flagged.includes(flag) ? "active" : ""} key={flag} onClick={() => toggleBoardFilterValue("flagged", flag)} type="button">{flag}</button>)}
                  </div>
                </div>
                <div className="work-board-field-wide">
                  <TicketLabelPicker label="Labels" onChange={(labels) => setBoardDraft((current) => ({ ...current, filters: { ...current.filters, labels } }))} options={workspaceLabelOptions} outputFormat="array" placeholder="Add or create label" value={boardDraft.filters.labels} />
                </div>
                <div className="work-board-filter-block">
                  <span>Share with</span>
                  <div className="work-board-chip-row">
                    {workspaceBoardPeople.map((person) => <button className={boardDraft.sharedWith.includes(person.userId) ? "active" : ""} key={`share-${person.userId}`} onClick={() => toggleBoardSharedUser(person.userId)} type="button">{person.name}</button>)}
                  </div>
                </div>
                {boardDraft.id ? <section className="work-board-danger-zone" aria-labelledby="delete-board-title">
                  <div>
                    <strong id="delete-board-title">Delete this board</strong>
                    <small>This removes only the saved board and its filters. Tickets, sprints, and epics are not deleted.</small>
                  </div>
                  <button className="work-board-delete-toggle" disabled={boardBusy} onClick={() => {
                    setBoardDeleteConfirmOpen((current) => !current);
                    setBoardDeleteConfirmation("");
                    setBoardError("");
                  }} type="button">{boardDeleteConfirmOpen ? "Cancel deletion" : "Delete board"}</button>
                  {boardDeleteConfirmOpen ? <div className="work-board-delete-confirm">
                    <label>
                      Type <strong>{boardDeleteTarget?.name}</strong> to confirm
                      <input autoComplete="off" onChange={(event) => setBoardDeleteConfirmation(event.target.value)} value={boardDeleteConfirmation} />
                    </label>
                    <button className="work-board-delete-confirm-button" disabled={boardBusy || boardDeleteConfirmation.trim() !== boardDeleteTarget?.name} onClick={() => removeBoard(boardDraft)} type="button">{boardBusy ? "Deleting…" : "Permanently delete board"}</button>
                  </div> : null}
                </section> : null}
                <footer className="functional-create-footer form-wide">
                  <div className="functional-create-footer-context" />
                  <div>
                    <button className="secondary-action" onClick={closeBoardModal} type="button">Cancel</button>
                    <button className="primary-action" disabled={boardBusy} type="submit">{boardBusy ? <WorkspaceInlineLoader compact label="Saving" /> : boardDraft.id ? "Save board" : "Create board"}</button>
                  </div>
                </footer>
              </form>
            </section>
          </div>
        </ModalPortal>
      ) : null}

      {!["Work", "Schedule"].includes(moduleName) ? <section className={`functional-metrics ${moduleName === "Analytics" ? "analytics-metrics" : ""}`.trim()} aria-label={`${moduleName} ${activeTab} metrics`}>
        {metricRows.map((metric) => (
          <article key={metric.label}>
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
            <small>{metric.note}</small>
          </article>
        ))}
      </section> : null}

      {renderModuleCommandCenter()}
      {renderTabBrief()}
      {renderWorkDeepPanel()}
      {renderScheduleDeepPanel()}

      {!["Work", "Schedule"].includes(moduleName) ? <section className="functional-panel">
        <div className="functional-panel-header">
          <div>
            <span>{activeTab}</span>
            <h3>{showBoardView ? `${activeTab} workflow board` : `${activeTab} records`}</h3>
          </div>
          <small>{filteredRecords.length} shown</small>
        </div>
        {renderRecords()}
      </section> : null}

        {selectedRecord ? (
        <ModalPortal>
        <div className="workspace-modal-backdrop" onMouseDown={closeRecordHistory} role="presentation">
        <section aria-label={`${selectedRecord.title} history`} aria-modal="true" className="workspace-modal workspace-record-modal record-history-drawer" onMouseDown={(event) => event.stopPropagation()} role="dialog" style={{ ...modalSurfaceStyle, width: "min(1120px, 100%)" }}>
          <header>
            <div>
              <span>{isWorkItemRecord(selectedRecord) ? selectedRecord.type : `${selectedRecord.type} history`}</span>
              <h3>{selectedRecord.title}</h3>
            </div>
            <div className="record-modal-header-actions">
              {isWorkItemRecord(selectedRecord) && detailDraft ? <>
                <span className={`ticket-autosave-indicator is-${detailAutosaveState.status || "idle"}`}>{detailAutosaveState.message || "Autosave on"}</span>
                <button aria-label="Refresh ticket" className="record-modal-icon-action" disabled={saving || timelineLoading} onClick={refreshSelectedRecordTimeline} title="Refresh ticket" type="button">↻</button>
              </> : null}
              <button aria-label="Close" onClick={closeRecordHistory} type="button">×</button>
            </div>
          </header>

            {timelineLoading ? <p className="empty-state"><WorkspaceInlineLoader label="Loading ticket history" /></p> : null}
          {timelineError ? <WorkspaceErrorNotice message={timelineError} /> : null}

          {recordTimeline ? (
            <div className="record-history-grid" style={modalContentStyle}>
              {isWorkItemRecord(selectedRecord) && detailDraft ? (
                <>
                <section className="record-detail-editor ticket-main-panel">
                  <div className="record-history-section-title">
                    <span>Ticket details</span>
                    <small>Ticket information</small>
                  </div>
                  <div className="record-detail-form">
                    <label className="form-wide">
                      Summary
                      <input onChange={(event) => setDetailDraft((current) => ({ ...current, title: event.target.value }))} value={detailDraft.title} />
                    </label>
                    <WorkspaceEditor
                      className="form-wide"
                      label="Description"
                      minRows={4}
                      onChange={(value) => setDetailDraft((current) => ({ ...current, description: value }))}
                      placeholder="Add context, outcome, acceptance criteria, links, and notes"
                      rows={4}
                      value={detailDraft.description}
                    />
                    <label>
                      Status
                      <select onChange={(event) => setDetailDraft((current) => ({ ...current, status: event.target.value }))} value={detailDraft.status}>
                        {config.statuses.map((status) => (
                          <option key={status}>{status}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Priority
                      <select onChange={(event) => setDetailDraft((current) => ({ ...current, priority: event.target.value }))} value={detailDraft.priority}>
                        {workspacePriorities.map((priority) => (
                          <option key={priority}>{priority}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Due date
                      <input onChange={(event) => setDetailDraft((current) => ({ ...current, due: event.target.value }))} type="date" value={detailDraft.due} />
                    </label>
                    <label>
                      Severity
                      <select onChange={(event) => setDetailDraft((current) => ({ ...current, severity: event.target.value }))} value={detailDraft.severity}>
                        <option value="">None</option>
                        {severityOptions.map((severity) => <option key={severity}>{severity}</option>)}
                      </select>
                    </label>
                    <label>
                      Sprint
                      <select onChange={(event) => setDetailDraft((current) => ({ ...current, sprint: event.target.value }))} value={detailDraft.sprint}>
                        <option value="">No sprint</option>
                        {workSprintOptions.map((sprint) => <option key={sprint} value={sprint}>{sprint}</option>)}
                      </select>
                    </label>
                    <label>
                      Epic
                      <select onChange={(event) => setDetailDraft((current) => ({ ...current, epic: event.target.value }))} value={detailDraft.epic}>
                        <option value="">No epic</option>
                        {workEpicOptions.map((epic) => <option key={epic} value={epic}>{epic}</option>)}
                      </select>
                    </label>
                    <label>
                      Story points
                      <input min="0" onChange={(event) => setDetailDraft((current) => ({ ...current, storyPoints: event.target.value }))} type="number" value={detailDraft.storyPoints} />
                    </label>
                    <div className="form-wide">
                      <TicketLabelPicker onChange={(labels) => setDetailDraft((current) => ({ ...current, labels }))} onCommit={saveTicketLabelsNow} options={workspaceLabelOptions} placeholder="Add or create label" value={detailDraft.labels} />
                    </div>
                    <label className="form-wide ticket-flag-control ticket-flag-control-inline">
                      <span>Delivery risk</span>
                      <span className="ticket-checkbox-row">
                        <input
                          checked={Boolean(detailDraft.flagged)}
                          onChange={(event) => setDetailDraft((current) => ({ ...current, flagged: event.target.checked }))}
                          type="checkbox"
                        />
                        <b>Mark this ticket as an impediment</b>
                      </span>
                      <small>Use this when the ticket is blocked, at risk, or needs escalation.</small>
                    </label>
                  </div>
                </section>
                <aside className="record-assignment-panel">
                  <div className="record-history-section-title"><span>People</span><small>Project team</small></div>
                  <div className="ticket-person-meta"><span>Creator</span><strong>{selectedRecord.creator || "Workspace member"}</strong></div>
                  <label>
                    Project
                    <select onChange={(event) => setDetailDraft((current) => ({ ...current, projectRef: event.target.value, owner: "", assignees: "" }))} value={detailDraft.projectRef}>
                      <option value="">Select project</option>
                      {workProjectOptions.map((project) => <option key={project} value={project}>{getProjectDisplayName(project)}</option>)}
                    </select>
                  </label>
                  <label>
                    Assignee
                    <select onChange={(event) => setDetailDraft((current) => ({ ...current, owner: event.target.value }))} value={detailDraft.owner}>
                      <option value="">Unassigned</option>
                      {projectMemberOptions.map((person) => <option key={person} value={person}>{person}</option>)}
                    </select>
                  </label>
                  <label>
                    Contributors
                    <select multiple onChange={(event) => setDetailDraft((current) => ({ ...current, assignees: Array.from(event.target.selectedOptions).map((option) => option.value).join(", ") }))} size={Math.min(Math.max(projectMemberOptions.length, 3), 6)} value={asList(detailDraft.assignees)}>
                      {projectMemberOptions.filter((person) => person !== detailDraft.owner).map((person) => <option key={person} value={person}>{person}</option>)}
                    </select>
                    <small>Choose one or more project members.</small>
                  </label>
                </aside>
                </>
              ) : null}
              {isWorkItemRecord(selectedRecord) ? (
                <section className="record-issue-activity">
                  <div className="record-history-section-title">
                    <span>Ticket activity</span>
                    <small>Conversation and history</small>
                  </div>
                  <div className="record-issue-tabs">
                    {[
                      { key: "all", label: "All" },
                      { key: "comments", label: "Comments" },
                      { key: "transitions", label: "Transitions" },
                      { key: "activity", label: "Activity" },
                      { key: "viewers", label: "Viewers" },
                      { key: "analytics", label: "Analytics" },
                    ].map((tab) => (
                      <button
                        className={issueActivityTab === tab.key ? "active" : ""}
                        key={tab.key}
                        onClick={() => setIssueActivityTab(tab.key)}
                        type="button"
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                  {issueActivityTab === "comments" ? (
                    <div className="record-comment-composer">
                      <WorkspaceEditor
                        attachmentDisabled={commentAttachmentBusy || commentSaving}
                        compact
                        onAttachmentChange={handleCommentAttachmentChange}
                        onChange={setCommentDraft}
                        placeholder="Add a comment for the team, requester, or reviewer"
                        rows={3}
                        value={commentDraft}
                      />
                      {renderPendingCommentAttachments()}
                      <div className="record-detail-actions">
                        <button className="primary-action" disabled={commentSaving || commentAttachmentBusy || (!commentDraft.trim() && !commentAttachments.length)} onClick={submitIssueComment} type="button">
                          {commentSaving ? "Posting..." : "Add comment"}
                        </button>
                      </div>
                    </div>
                  ) : null}
                  {(() => {
                    const feeds = buildIssueActivityFeed();
                    if (issueActivityTab === "viewers") {
                      return (
                        <div className="record-presence-list">
                          {recordTimeline.presence?.map((item) => (
                            <article key={item.id}>
                              <strong>{item.user?.name || "User"}</strong>
                              <span>{item.context} · {new Date(item.lastPing).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                            </article>
                          ))}
                          {!recordTimeline.presence?.length ? <p className="empty-state">No active viewers.</p> : null}
                        </div>
                      );
                    }
                    if (issueActivityTab === "analytics") {
                      return (
                        <div className="record-analytics-grid">
                          <article>
                            <span>This week</span>
                            <strong>{recordTimeline.analytics?.totalViewsThisWeek || 0}</strong>
                            <small>Total views</small>
                          </article>
                          <article>
                            <span>Viewers</span>
                            <strong>{recordTimeline.analytics?.uniqueViewersThisWeek || 0}</strong>
                            <small>Unique users</small>
                          </article>
                          <article>
                            <span>Time</span>
                            <strong>{Math.round((recordTimeline.analytics?.totalTimeSecondsThisWeek || 0) / 60)}m</strong>
                            <small>Total viewed</small>
                          </article>
                        </div>
                      );
                    }
                    if (issueActivityTab === "comments") {
                      return (
                        <div className="record-comment-thread">
                          {feeds.comments.map((comment) => (
                            <article id={`comment-${comment.id}`} key={comment.id}>
                              <header>
                                <div className="record-comment-author-line"><strong>{comment.author?.name || comment.performedBy?.name || "Workspace user"}</strong><span>{comment.parentCommentId ? "Reply" : "Comment"}</span>{renderCommentCreatedTime(comment)}{comment.editedAt ? renderEditedCommentLabel(comment) : null}</div>
                                {comment.canManage ? <div className="record-comment-owner-actions"><button disabled={commentSaving} onClick={() => { setEditingCommentId(comment.id); setEditingCommentDraft(comment.body || ""); }} type="button">Edit</button><button disabled={commentSaving} onClick={() => deleteComment(comment.id)} type="button">Delete</button></div> : null}
                              </header>
                              {editingCommentId === comment.id ? <div className="record-comment-edit"><WorkspaceEditor autoFocus className="record-comment-edit-editor" compact disabled={editingCommentSavingId === comment.id} onChange={setEditingCommentDraft} onEscape={() => { setEditingCommentId(""); setEditingCommentDraft(""); }} onSubmitShortcut={() => saveCommentEdit(comment.id)} placeholder="Update comment" rows={3} value={editingCommentDraft} /><div className="record-comment-edit-actions"><button aria-busy={editingCommentSavingId === comment.id} className={editingCommentSavingId === comment.id ? "is-saving" : ""} disabled={editingCommentSavingId === comment.id || !editingCommentDraft.trim()} onClick={() => saveCommentEdit(comment.id)} type="button">{editingCommentSavingId === comment.id ? <WorkspaceInlineLoader compact label="Save" /> : "Save"}</button><button disabled={editingCommentSavingId === comment.id} onClick={() => { setEditingCommentId(""); setEditingCommentDraft(""); }} type="button">Cancel</button></div></div> : <>{comment.body || comment.message ? <WorkspaceRichText className="ticket-comment-body" value={comment.body || comment.message} /> : null}{renderCommentAttachments(comment.attachments)}</>}
                              {!comment.deletedAt ? <footer><button onClick={() => likeComment(comment.id)} type="button">{comment.likedByMe ? "Liked" : "Like"}{comment.likeCount ? ` · ${comment.likeCount}` : ""}</button></footer> : null}
                            </article>
                          ))}
                          {!feeds.comments.length ? <p className="empty-state">No comments yet. Start the conversation above.</p> : null}
                        </div>
                      );
                    }
                    const selectedFeed = feeds[issueActivityTab] || feeds.all;
                    return (
                      <div className={issueActivityTab === "all" || issueActivityTab === "transitions" ? "record-timeline-list" : "record-activity-list"}>
                        {selectedFeed.map((event) => (
                          <article key={`${issueActivityTab}-${event.id}`}>
                            {issueActivityTab === "all" || issueActivityTab === "transitions" ? (
                              <time>{new Date(event.at).toLocaleString([], { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" })}</time>
                            ) : null}
                            <div>
                              <strong>{event.message}</strong>
                              <span>
                                {event.performedBy?.name || "System"}
                                {event.action ? ` · ${event.action.replaceAll("_", " ")}` : ""}
                              </span>
                            </div>
                          </article>
                        ))}
                        {!selectedFeed.length ? <p className="empty-state">No {issueActivityTab} yet.</p> : null}
                      </div>
                    );
                  })()}
                </section>
              ) : (
                <>
                  <section>
                    <div className="record-history-section-title">
                      <span>Audit History</span>
                      <small>Permanent</small>
                    </div>
                    <div className="record-timeline-list">
                      {recordTimeline.history?.map((event) => (
                        <article key={event.id}>
                          <time>{new Date(event.at).toLocaleString([], { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" })}</time>
                          <div>
                            <strong>{event.message}</strong>
                            <span>{event.performedBy?.name || "System"}</span>
                          </div>
                        </article>
                      ))}
                      {!recordTimeline.history?.length ? <p className="empty-state">No history yet.</p> : null}
                    </div>
                  </section>

                  <section>
                    <div className="record-history-section-title">
                      <span>Activity Feed</span>
                      <small>Readable</small>
                    </div>
                    <div className="record-activity-list">
                      {recordTimeline.activity?.map((event) => (
                        <article key={event.id}>
                          <strong>{event.message}</strong>
                          <span>{event.performedBy?.name || "System"} · {new Date(event.at).toLocaleString()}</span>
                        </article>
                      ))}
                      {!recordTimeline.activity?.length ? <p className="empty-state">No activity yet.</p> : null}
                    </div>
                  </section>

                  <section>
                    <div className="record-history-section-title">
                      <span>Currently Viewing</span>
                      <small>Presence</small>
                    </div>
                    <div className="record-presence-list">
                      {recordTimeline.presence?.map((item) => (
                        <article key={item.id}>
                          <strong>{item.user?.name || "User"}</strong>
                          <span>{item.context} · {new Date(item.lastPing).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                        </article>
                      ))}
                      {!recordTimeline.presence?.length ? <p className="empty-state">No active viewers.</p> : null}
                    </div>
                  </section>

                  <section>
                    <div className="record-history-section-title">
                      <span>View Analytics</span>
                      <small>Aggregated</small>
                    </div>
                    <div className="record-analytics-grid">
                      <article>
                        <span>This week</span>
                        <strong>{recordTimeline.analytics?.totalViewsThisWeek || 0}</strong>
                        <small>Total views</small>
                      </article>
                      <article>
                        <span>Viewers</span>
                        <strong>{recordTimeline.analytics?.uniqueViewersThisWeek || 0}</strong>
                        <small>Unique users</small>
                      </article>
                      <article>
                        <span>Time</span>
                        <strong>{Math.round((recordTimeline.analytics?.totalTimeSecondsThisWeek || 0) / 60)}m</strong>
                        <small>Total viewed</small>
                      </article>
                    </div>
                  </section>
                </>
              )}
            </div>
          ) : null}
        </section>
        </div>
        </ModalPortal>
        ) : null}
      </div>
    </>
  );
}
